/**
 * AllJourneys — the Appointment Page Designer (rail "All Journeys", route `alljourneys`).
 *
 * Ported 1:1 from the Gold Master prototype `Appointment Page Designer.dc.html`
 * (2026-09-03, operator: "exactly the same way I designed it"). Two mechanical steps,
 * so there is nothing here that was re-drawn by hand:
 *  • the prototype's template was TRANSPILED to the JSX in render() — every inline
 *    style literal is the prototype's; every `<x-import>` is the same design-system
 *    component from @v0y4ge/jrni-ui;
 *  • the prototype's logic class is carried over VERBATIM as the class body below
 *    (React state/setState/lifecycle were already its contract).
 *
 * What is REAL here:
 *  • Save / Publish go through alljourneys-api — one record per journey per tenant,
 *    published from the first save (no draft state, 2026-09-08) and a public slug per record, registered on publish
 *    (local stub until CD-API-REQUEST-page-designer-2026-09-03 lands).
 *  • Kiosk check-in looks a code up with lookupPublicCheckIn from the typed api-client
 *    (read-only) and shows back only what it returned. This renderer is the designer
 *    preview, so it never posts performPublicCheckIn; the arrived transition (decision 94,
 *    channel "kiosk" — decision-59 value pending, decision 178) belongs to a live kiosk mount.
 *  • A kiosk learns its location from /kiosk?location={external_key}, never from the
 *    record and never from a seed id.
 *
 * Sixth tab (2026-09-04): OneStop — delegated to onestop-tab.tsx, which edits the tenant's
 * One-Stop EXPERIENCE DOCUMENT (onestop-page-api), not a page-designer record.
 * Five journeys: Appointments · Walk-Ins · Event List Page · Event Landing Page · Kiosk.
 * Legacy Journey and Journey Builder rail items are untouched (operator: add, don't replace).
 */
/* eslint-disable */
// @ts-nocheck — the class body is the prototype's JavaScript carried over verbatim; typing it
// would mean editing it, and the ruling is that it comes in exactly as designed.
import { t as i18nT } from "../../lib/i18n";
import { useTenantModulesState } from "../../lib/tenant-modules";
import { audienceRefusalCopy } from "../../lib/audience";
import React from "react";
import * as UI from "@v0y4ge/jrni-ui";
import {
  Button, SegmentedControl, SectionLabel, EditorSectionNav, Section, Field, Dropdown, Input, ToggleRow,
  SelectableTile, Checkbox, ImageDropField, Banner, MediaRow, MultiSelect, Toggle, Textarea, AddRowButton,
  CopyableUrl, DevicePreviewToggle, resolveDeviceWidths, resolveDeviceHeights, Pill, Avatar, MeterBar, DatePicker, ChipToggleGroup, InlineEmptyState,
  Pagination, Chip, ChoiceTile, SlotPicker, FileUpload, ConfirmDialog, RadioGroup, RadioGroupItem,
} from "@v0y4ge/jrni-ui";
import { QrCode } from "../../shell/QrCode";
/* `OneStopDoneButton` is NO LONGER IMPORTED (KP, 2026-09-22): the bottom
   "Done — back to One Stop" is gone from all three confirmations, leaving the
   TOP `ReturnToOneStopBar` as the single return affordance. The component stays
   exported from ReturnToOneStop.tsx on purpose — it gets its own review. */
import { setJourneyComplete } from "./ReturnToOneStop";
import { BookingQuestionFields, questionFileConfig, questionFileTooLarge } from "./BookingQuestionFields";
import { LANDING_BLOCKS_HIDDEN, moveVisibleLandingBlock } from "./event-landing-block-visibility";
import { DIAL, DIAL_RAW } from "./dial-codes";
import { AppointmentFooter } from "./AppointmentFooter";
import { BrandedHeaderMount, BrandedFooterMount, loadTenantChrome, onTenantChromeChange, tenantChromeStatus, useTenantChrome } from "../../lib/branded-chrome";
import { serviceCategoryOptions, serviceFilterId, serviceMatchesCategory } from "./appointment-service-filters";
import { effectiveReminderPreference, reminderChannels, reminderPreferenceForSubmit, toggleReminderChannel, normalizeReminderPreference } from "./appointment-reminder-preference";
import {
  eligibilityKey, eligibilityStatus, eligibleLocations, isBookableOffer, isVirtualLocation, locationById,
  mapPinsFor, missingPinCaption, offeredLocationIds, reconcileSelectedLocation,
} from "./appointment-location-eligibility";
import { findDeepLinkedSlot, readSlotDeepLink } from "./booking-slot-deeplink";
import { Screen } from "../../shell/chrome";
import { useTenant } from "../../shell/access";
import { cancelEventRegistration, lookupPublicCheckIn, manageSurface, createQuestionFileUpload, createJourneyQuestionFileUpload, uploadQuestionFileContent, getPublicBookingConfirmationPage } from "@v0y4ge/api-client";
import { WalletPassBadge } from "../../lib/WalletPassBadge";
import { listPageDesigner, putPageDesigner, publishPageDesigner } from "./alljourneys-api";
import { loadPreviewCatalog, newestFirst, createdTime, SAMPLE_COUNT } from "./alljourneys-catalog";
import { ErrorBoundary } from "../../shell/ErrorBoundary";
import {
  resolveAppointmentJourney, loadAppointmentJourney, designerFromJourney,
  createAppointmentJourney, saveAppointmentSettings, publishAppointmentJourney,
  publicLinkFor, unmappedKeys, notYetLiveKeys, inertTextKeys, appointmentManageUrl, appointmentManageAction,
  openManageInNewTab, safeManageUrl, slotIntervalMinutes, SLOT_INTERVAL_DEFAULT,
} from "./alljourneys-appointments-seam";
import {
  EMPTY_AVAILABILITY, buildCatalog, loadAvailability, loadAvailabilityPreview, loadPublicCatalog,
  loadPublishedJourney, partOfDay, submitBooking, type PortalBookingSessionContext,
} from "./appointment-data";
import {
  WALKIN_DEFAULT_SLUG, createWalkInJourney, designerFromWalkInJourney,
  listWalkInQueueOptions, loadWalkInJourney, loadWalkInQueues,
  publishWalkInJourney, resolveWalkInJourney, saveWalkInSettings, serviceOptionsOf,
  walkInBrandFrom, walkInConfigFromDesigner,
} from "./alljourneys-walkins";
import { WalkInJourney, PHONE_COUNTRY_STYLE, PHONE_NUMBER_STYLE } from "./WalkInJourney";
import { PayPalCheckout } from "../landing/PayPalCheckout";
import { AdyenCheckout } from "../landing/AdyenCheckout";

import { OneStopTab } from "./onestop-tab";
import { CustomHtmlTemplatesPanel } from "./templates/CustomHtmlTemplatesPanel";
import { TemplatePreviewController } from "./templates/template-preview-controller";
import { tf as templateTf } from "./templates/template-data";
import {
  CustomTemplateCanvas, CustomTemplateNotice, TemplateCanvasTitle, TemplateCheckNotice, TemplateDesignSwitch, TemplateHeaderStatus,
} from "./templates/CustomTemplateCanvas";
import { tzContext, friendlyClock, friendlyDateShort, civilNoon, friendlyDate, friendlyDateNoYear, friendlyDateTime, friendlyTime, friendlyCalendarWeekday } from "../../lib/datetime";
import { GhostJourneyStep, LoadingRegion, SkeletonBox } from "../../lib/loading-skeletons";
import { toast } from "../../shell/toast";
import { publicHost, ensurePublicConfig, onPublicConfigChange } from "../../lib/public-url";
import { eventSaleState, type SaleTier } from "../../lib/event-on-sale";
import { designerLayoutFor } from "./designer-layout";
import LocationsMap from "../../components/maps/LocationsMap";
import { getTenantVocab, useTenantVocab } from "../../lib/tenant-vocab";
import {
  loadPublicEventsList, loadPublicEventDetail, submitEventRegistration as submitEventRegistrationOp,
  registerPortalEventSession as registerPortalEventSessionOp,
  loadPortalEventSessionTicketTiers, startPortalEventSessionCheckout as startPortalEventSessionCheckoutOp,
  loadPublicEventsPage, loadEventsDocument, saveEventsDocument, publishEventsDocument,
  loadLandingDocument, createLandingDocument, updateLandingDocument, updateLandingBlocks, publishLandingDocument,
  loadEventPaymentConfig, downloadEventCalendar,
  EVENT_LANDING_DEFAULT_KEY, EVENTS_BLOCK_SEMANTIC_MAP, NEUTRAL_EVENTS_BLOCK_CONFIG, EVENTS_PAGE_SLUG,
  neutralLandingPresentationDefaults, projectPublicEventToDesignerRow,
  designerFieldsFromEventsBlockConfig, dateRangeFromEventsWindow, formatSpotsRemaining, formatEventPrice, newIdempotencyKey, civilDateInZone,
  type EventsBlockConfig, type EventsDocumentDraft, type LandingDraft, type DesignerEventRow,
} from "../../lib/events-public-data";
import type { EventListPresentation, EventLandingPresentation } from "@v0y4ge/api-contracts";
import {
  CustomLayoutCanvas, CustomLayoutPalette, CustomLayoutInspector,
  DEVICE_COLS, blankCustomLayout, makeBlock, placeWithPush,
  nextFreeRow, toContractBlocks, fromContractBlocks, carriesCustomLayout,
  type CustomBlock, type DeviceId,
} from "./landing-custom-canvas";

/** The branded-chrome keys for a strict document schema: present only when an
 *  author has turned one on, or the stored document already carries them, so a
 *  server on the older contract keeps accepting every save that does not opt in. */
function brandedChromeFields(s: Record<string, unknown>, stored: boolean): { brandedHeader?: boolean; brandedFooter?: boolean } {
  const out: { brandedHeader?: boolean; brandedFooter?: boolean } = {};
  if (stored || s.brandedHeader === true) out.brandedHeader = s.brandedHeader === true;
  if (stored || s.brandedFooter === true) out.brandedFooter = s.brandedFooter === true;
  return out;
}

/* THE PUBLIC GRIDS, named once. The loaded service and event grids and the
 * ghosts that stand in for them read these, so a pending page wraps its cards
 * at exactly the widths the loaded page does and the two cannot drift. */

/** Appointment services: one column on a phone, otherwise as many 230px-minimum
 *  columns as fit, 16px apart. */
const SERVICE_GRID_GAP = "16px";
function serviceGridColumns(device: string): string {
  return device === "mobile" ? "minmax(0, 1fr)" : "repeat(auto-fit, minmax(230px, 1fr))";
}

/** Event cards: one column on a phone, otherwise the journey's configured
 *  `cardCols`, each at least 190px, 28px between rows and 24px between columns. */
const EVENT_GRID_GAP = "28px 24px";
function eventCardGridColumns(device: string, cardCols: number): string {
  return device === "mobile"
    ? "minmax(0, 1fr)"
    : "repeat(auto-fit, minmax(min(100%, max(190px, calc((100% - " + ((cardCols - 1) * 24) + "px) / " + cardCols + "))), 1fr))";
}

/* CREATE YOUR OWN TEMPLATE — ON FOR EVERY TENANT (operator ruling, 2026-09-23;
 * supersedes the 2026-09-19 served-capability gate and the 2026-09-17 slug set).
 *
 * The fifth landing template (the drag-and-resize 12-column canvas) is offered
 * unconditionally. It was gated on a served `customLandingTemplate` flag that no
 * deployment ever stated, so it showed in design-time Studio (mock seed) and on
 * no live tenant. No tenant name and no flag is read here now. */

interface DesignerProps {
  tenant: string; tenantName?: string; brandColor?: string; publicHost?: string;
  vocab?: TenantWords; initialSection?: string;
  /** The tenant's composed modules (getTenantProfile). null/absent = unknown: every journey tab shows.
   *  The Studio route mounts the designer only with a KNOWN list that composes a journey module;
   *  loading, refused, failed, malformed and journey-less reads never reach this prop. */
  modules?: string[] | null;
  /** "public" mounts the customer page standalone: no editor, no Studio auth,
   *  published settings for `slug` rather than the tab's draft. */
  mode?: "preview" | "public";
  slug?: string;
  /** Which public journey this standalone mount renders. Defaults to
   *  "appointment" — the existing `PublicAppointment` behavior is unchanged
   *  when this prop is omitted. "events" is the Attend root event list (`/`);
   *  "landing" is one event's detail route (`/{eventId}`), which reads
   *  `eventId` below rather than `slug`. */
  journey?: "appointment" | "events" | "landing";
  /** The opaque public event id for `journey="landing"` (e.g. `cu-event-04`).
   *  Not a slug and never UUID-validated. */
  eventId?: string;
  /** DRAFT PREVIEW (2026-09-18). A public mount normally hydrates its layout
   *  from the PUBLISHED surface, which is exactly wrong for the Preview dialog:
   *  an author wants to see what is on the page in front of them, before
   *  publishing. When this is supplied the mount keeps reading the real event
   *  (dates, tickets, capacity, questions all still come off the wire) but draws
   *  it with THIS layout instead of the published one. Omit it and the published
   *  behaviour is unchanged. */
  draft?: { s: Record<string, unknown>; ct: Record<string, unknown> };
  /** DRAFT MOUNT (2026-09-19). With this set the customer page is drawn purely
   *  from `draft` — the journey the editor is on, its settings, its custom text
   *  and the event rows it has already loaded — and NO public read runs at all.
   *  This is what the Preview dialog uses: every public bootstrap it used to go
   *  through could fail to settle on a slug that is not that journey's kind,
   *  which is how Preview came to sit on "Loading…" (appointments, walk-ins,
   *  event list, kiosk) or land on the appointment link-error card (landing).
   *  A draft mount cannot hang, because it waits for nothing. */
  previewJourney?: string;
  /** Preview dialog only: the queues the editor is drawing (attached, or the
   *  builder's sample queues when none are attached). */
  draftWalkIn?: { queues: any[]; queueIds: string[]; sample: boolean };
  /** The event rows and selection the editor already holds, so a landing draft
   *  preview has the event's real facts without re-reading them. */
  draftRows?: unknown[];
  draftEvent?: string;
  /** Draft mount only (2026-10-05): which customer step to show, and follow it as it changes —
   *  the event Setup preview column points this at the screen the author is editing. */
  draftStep?: string;
  /** Draft mount only (2026-10-05): override a device frame's size — the event Setup preview
   *  shows Tablet in landscape (1180×820) rather than the house portrait frame. */
  frameSize?: Partial<Record<"tablet" | "mobile", { w: number; h: number }>>;
  /** Width the customer page should lay itself out for. A standalone page takes
   *  the browser's own width (the default); the Preview dialog passes the device
   *  the operator picked, so one mount can be shown at all three. */
  device?: "desktop" | "tablet" | "mobile";
  /** Verified same-origin One Stop handoff context. The cookie remains the
   *  authority; this supplies only the profile shown in the details step and
   *  the in-memory CSRF value used by the authenticated booking route. */
  bookingSession?: PortalBookingSessionContext;
  /** The journey the session is bound to, when the page is addressed by another
   *  slug (an event's own public address). The signed-in write goes there. */
  bookingSessionSlug?: string;
  /** Move the standalone page to its recoverable session-ended state when the
   *  cookie expires after the profile was already loaded. */
  onBookingSessionEnded?: (message: string) => void;
}

/**
 * CD PREVIEW ONLY — same arrangement as `LEGACY_PREVIEW` in shell/screens.tsx.
 *
 * Deployed, Preview opens the journey's public link in a new tab, which is what
 * an operator wants and what the platform's `book` entry serves. CD's harness
 * cannot reach `book.<tenant>.jrni.ai`, so the tab would open on nothing. When
 * the harness sets `window.__CD_PREVIEW_DIALOG__ = true` the same customer page
 * is mounted in a dialog instead, at the three device widths.
 *
 * Read per click rather than at module load so the harness can set it late, and
 * never set in a production build — a deployed Studio keeps the new tab.
 */
function previewInDialog() {
  try { return typeof window !== "undefined" && (window as { __CD_PREVIEW_DIALOG__?: boolean }).__CD_PREVIEW_DIALOG__ === true; } catch (err) { return false; }
}
/** Nouns the header sub-lines and kiosk copy use.
 *
 *  TENANT-AGNOSTIC (2026-09-08). This file previously carried a `TENANT_WORDS`
 *  table and a `PUBLIC_HOST` table both keyed by REAL TENANT SLUGS, plus
 *  higher-ed defaults ("student", "campus", "adviser"). Three things wrong with
 *  that, and the platform's lint is red on all of them: a tenant that is not in
 *  the table gets another tenant's vocabulary; onboarding a tenant becomes a
 *  code change in a designer screen; and the defaults name one vertical, so
 *  every unlisted tenant reads as a university.
 *
 *  Nouns now come from the served vocabulary (lib/tenant-vocab) and hosts from
 *  lib/public-url, which resolves the tenant's real public config. The defaults
 *  below are deliberately GENERIC — the neutral word for the thing, not a guess
 *  at the industry. */
interface TenantWords { customer: string; customers: string; location: string; locations: string; staff: string; advising: string; events: string; }
/* Kiosk still stores designer settings only in the browser — do not announce a
   customer-facing save until its own contract lands. Appointments, Event List
   (`events`), Event Landing (`landing`) and now Walk-Ins (`queue`, through the
   typed admin journey door in alljourneys-walkins.ts) all write through real
   server contracts. */
const PD_STUB_ONLY: Record<string, true> = { kiosk: true };
const PD_STUB_REASON = "Publishing is not available for this page type yet. Changes here will not appear on your customer pages.";

/** `EventJourneyCommonPresentationShape` keys — shared by the list and
 *  landing presentation schemas (`packages/api-contracts/src/
 *  event-journey-presentation.ts`). One list, both pickers below. */
const EVENT_JOURNEY_COMMON_KEYS = [
  "progressType", "askEmail", "emailOptional", "askPhone", "phoneOptional",
  "askAddress", "addressOptional", "countryFlags", "customCountries",
  "ticketQuantity", "readOnly", "takePayment", "showQr", "addToCalendar",
  "allowCancelReg", "showManage", "showResource", "showStaff", "embedWidthUnit",
  "embedWidth", "embedHeight", "embedResize",
];
const EVENT_LIST_PRESENTATION_KEYS = [
  ...EVENT_JOURNEY_COMMON_KEYS,
  "eventPaging", "eventDisplay", "cardCols", "eventImages", "showEventDate",
  "showEventTime", "showEventPrice", "showTimezone", "filterBar",
  "filterLocation", "filterType", "filterDates", "eventFilters", "notifPref",
  "notifDefault", "fileUpload", "maxAttachments", "skipSingleEventSlot",
  "customText",
];
const EVENT_LANDING_PRESENTATION_KEYS = [
  ...EVENT_JOURNEY_COMMON_KEYS,
  "showHeader", "showFooter", "landingTemplate", "heroHeight", "heroTreatment",
  "stickyBar", "coverImage", "blockOrder", "blocksOn", "soldOutMode",
  "waitlist", "relatedRule", "relatedCount", "relatedManual", "lpDescText",
  "lpAgendaRows", "lpSpeakerRows", "lpIncludedRows", "lpFaqRows",
  "lpSponsorRows", "sponsorTiers", "lpShareChannels", "customText",
];
/** Pick exactly the declared keys of a `.strict()` presentation schema out of
 *  the Designer's wider `s` state — the schema rejects unknown keys, and `s`
 *  carries every journey's fields on one object. */
function pickPresentationFields(s: Record<string, unknown>, keys: readonly string[]): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  keys.forEach((k) => { if (k in s) out[k] = s[k]; });
  return out;
}
/** Parses an event's stored bound (`start_at`/`end_at`) into a comparable
 *  number of minutes WITHOUT going through `new Date(string)`. These bounds
 *  are civil (wall-clock) date/times in the tenant's own display zone — the
 *  same zone `civilDateInZone`/`tzContext` work in — so handing an offsetless
 *  string to the Date constructor would reinterpret it in the VISITOR's
 *  browser zone, and on engines that reject the `YYYY-MM-DD HH:MM` spelling
 *  outright would make a perfectly valid schedule parse as NaN and be
 *  rejected here. An explicit `Z`/`±HH:MM` offset, when one IS present, is
 *  honoured so both bounds are normalised onto the same line before they are
 *  compared. */
function eventBoundMinutes(value: unknown): number | null {
  if (typeof value !== "string") return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?(?:\.\d+)?\s*(Z|z|[+-]\d{2}:?\d{2})?$/.exec(value.trim());
  if (!m) return null;
  const year = Number(m[1]), month = Number(m[2]), day = Number(m[3]);
  const hour = Number(m[4]), minute = Number(m[5]), second = Number(m[6] || "0");
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  if (hour > 23 || minute > 59 || second > 59) return null;
  const utc = Date.UTC(year, month - 1, day, hour, minute, second);
  if (!isFinite(utc)) return null;
  // Round-trip check: rejects real-calendar impossibilities (31 February,
  // 29 February in a common year) that the range tests above let through.
  const back = new Date(utc);
  if (back.getUTCFullYear() !== year || back.getUTCMonth() !== month - 1 || back.getUTCDate() !== day) return null;
  let offsetMinutes = 0;
  const zone = m[7];
  if (zone && zone !== "Z" && zone !== "z") {
    const digits = zone.slice(1).replace(":", "");
    const zoneHour = Number(digits.slice(0, 2)), zoneMinute = Number(digits.slice(2, 4));
    if (zoneHour > 23 || zoneMinute > 59) return null;
    offsetMinutes = (zone.charAt(0) === "-" ? -1 : 1) * (zoneHour * 60 + zoneMinute);
  }
  return utc / 60000 - offsetMinutes;
}
/** True only when the authoritative event detail carries exactly ONE valid
 *  one-off schedule: the contract's `event_type` is the literal `one_off`,
 *  both stored bounds are non-empty strings that actually parse as a
 *  date/time, and the end is strictly after the start — an inverted or
 *  zero-length range is not a schedule anyone can be registered into.
 *  Deliberately NOT derived from ticket-tier count, capacity, the number of
 *  rows on the list, or any synthetic slot — none of those is a schedule,
 *  and a multi-tier or sold-out event still has exactly one date and time. */
function hasSingleOneOffSchedule(detail: { event_type?: unknown; start_at?: unknown; end_at?: unknown } | null | undefined): boolean {
  if (!detail) return false;
  if (detail.event_type !== "one_off") return false;
  const startAt = detail.start_at;
  const endAt = detail.end_at;
  if (typeof startAt !== "string" || startAt.trim() === "") return false;
  if (typeof endAt !== "string" || endAt.trim() === "") return false;
  const start = eventBoundMinutes(startAt);
  const end = eventBoundMinutes(endAt);
  if (start === null || end === null) return false;
  return end > start;
}
/** Designer field name → registered `events` block config key (see
 *  `EVENTS_BLOCK_SEMANTIC_MAP` in events-public-data.ts). */
function eventsBlockConfigFromDesignerFields(s: Record<string, any>): EventsBlockConfig {
  const mapped: Record<string, unknown> = { ...NEUTRAL_EVENTS_BLOCK_CONFIG };
  Object.keys(EVENTS_BLOCK_SEMANTIC_MAP).forEach((designerKey) => {
    const blockKey = (EVENTS_BLOCK_SEMANTIC_MAP as Record<string, string>)[designerKey];
    if (designerKey in s) mapped[blockKey] = s[designerKey];
  });
  /* "Which events" (CC 0359): all → null (clears the window), a day count → { horizonDays }, custom → kept. */
  if ("eventsWindow" in s) mapped.dateRange = dateRangeFromEventsWindow(s.eventsWindow);
  return mapped as unknown as EventsBlockConfig;
}
const VOCAB_DEFAULTS: TenantWords = { customer: "customer", customers: "customers", location: "location", locations: "locations", staff: "team member", advising: "appointments", events: "Events" };
/** Served nouns → the words this screen uses. Only what the vocabulary actually
 *  carries is taken; the rest stays generic rather than being invented. */
function wordsFromVocab(v: ReturnType<typeof getTenantVocab>): TenantWords {
  const c = v?.customers ?? null;
  return {
    ...VOCAB_DEFAULTS,
    ...(c?.entity ? { customer: c.entity } : {}),
    ...(c?.entityPlural ? { customers: c.entityPlural } : {}),
  };
}
/* TENANT_WORDS removed 2026-09-08 — see the note above. A
   `Record<string, …>` keyed by real tenant slugs is the exact shape the
   tenant-agnosticism lint refuses; nouns come from the served vocabulary. */

/* Prototype helmet CSS — the only rules that cannot be inline. */
const APD_CSS = `
    html, body { margin: 0; padding: 0; height: 100%; background: var(--jrni-color-surface-canvas); }
    a { color: var(--jrni-color-primary-1); text-decoration: none; }
    a:hover { color: var(--jrni-color-primary-1); text-decoration: underline; }
    button { font-family: inherit; }
    /* The zone's display name moves up beside the label, so the component's own line is redundant. */
    [data-jrni-component="timezone-select"] > div[id$="-selected"] { display: none; }
    @keyframes apd-pulse { 0%, 100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.35; transform: scale(0.8); } }
    /* The journey tab strip scrolls but shows NO scrollbar: a 10px track under six
       tabs added a whole row to the page chrome at narrow widths, and the strip is
       reachable by wheel, drag and keyboard without it. */
    .apd-tabscroll { overflow-x: auto; overflow-y: hidden; scrollbar-width: none; -ms-overflow-style: none; }
    .apd-tabscroll::-webkit-scrollbar { width: 0; height: 0; display: none; }
    @media (max-width: 480px) { .apd-step:not(.apd-step-current) .apd-step-label { display: none; } }
    .apd-scroll::-webkit-scrollbar { width: 10px; height: 10px; }
    .apd-scroll::-webkit-scrollbar-thumb { background: var(--jrni-color-neutral-4); border-radius: 999px; border: 3px solid transparent; background-clip: content-box; }
    /* Device-frame viewport: a real phone/tablet has no visible scrollbar
       track, and a visible one would eat into the frame's exact device
       width. Content still scrolls (wheel/trackpad/touch); the bar is just
       never drawn or given layout space. (2026-09-28, CD) */
    .apd-devframe { scrollbar-width: none; }
    .apd-devframe::-webkit-scrollbar { display: none; width: 0; height: 0; }
    /* Events filter row — SCREEN-SCOPED geometry only, never a change to the
       shared design system. DatePicker is the one control in that row that
       cannot be aligned from the call site: it exposes no \`style\` prop and
       PAINTS a 4px trigger radius INLINE (DatePicker.tsx:256), while every
       sibling (Input, Dropdown) reads --jrni-radius-default (6px).

       \`!important\` is load-bearing and is the reason this is CSS at all: an
       inline style outranks every selector, however specific, so the first cut
       of these rules without it changed nothing — measured in the rendered
       page as a 4px DatePicker beside five 6px controls. This is the narrow
       case the keyword exists for, not a specificity shortcut.

       Both rules are keyed on the row's own class, so they reach nothing else
       on the screen and nothing outside it. The DURABLE fix is a radius token
       inside DatePicker — a CD change to jrni-ui, which is a docket, not a
       hand-edit of a CD-owned surface. */
    .apd-evt-filters [data-jrni-component="date-picker"] > button[aria-haspopup="dialog"] { border-radius: var(--jrni-radius-default) !important; }
    /* DatePicker stacks label+control at an inline gap of 4; Field (which wraps
       the Input and the Dropdown beside it) uses 6. Matching it lines the
       LABELS up as well as the control bottoms the row's align-items already
       handles. Same inline-style reason for the keyword. */
    .apd-evt-filters [data-jrni-component="date-picker"] { gap: 6px !important; }
    /* Browser QA: the Dropdown trigger draws a black inline focus outline while
       its siblings show the purple focus token. Screen-scoped correction; same
       inline-style reason for the keyword. */
    .apd-evt-filters button:focus-visible { outline: 1px solid var(--jrni-color-utility-focus) !important; outline-offset: 1px !important; }
    /* Row layout responds to the PREVIEW FRAME, not the viewport: the frame is a
       resizable device canvas inside the page, so a media query would read the
       wrong box. The frame declares container-type: inline-size. */
    .apd-evt-filters { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 16px 12px; align-items: end; }
    .apd-evt-filters > * { min-width: 0; }
    .apd-evt-search { grid-column: 1 / -1; width: 100%; }
    @container journey-preview (min-width: 1100px) {
      .apd-evt-filters { grid-template-columns: minmax(0, 1.2fr) repeat(3, minmax(0, 1fr)) auto; }
      .apd-evt-search { grid-column: auto; width: auto; }
    }
    @container journey-preview (max-width: 540px) {
      .apd-evt-filters { grid-template-columns: minmax(0, 1fr); }
    }
  `;

/** Inclusive [min, max] for the numeric settings that have real limits. */
const NUMBER_BOUNDS: Record<string, [number, number]> = {
  slotInterval: [1, 1140],
}; // ui-config

const NS = () => UI as unknown as Record<string, any>;

/** The route this Attend mount is showing, parsed from a pathname.
 *
 *  Attend is a dedicated domain, so its ROOT is the event list and the first
 *  path segment is an event's opaque public id:
 *
 *    /          -> list
 *    /{eventId} -> that event's landing + registration
 *
 *  Those are the ONLY two shapes. Anything deeper is `invalid` — NOT silently
 *  the list and NOT some other event, so a mistyped or truncated link renders
 *  the honest not-found state. The retired `/events` and `/events/{id}` paths
 *  are invalid too: `events` is never read as an event id, and there is no
 *  compatibility redirect. Every other reserved name (assets, embed, api…)
 *  never reaches this mount — the attend worker routes those itself, and
 *  inventing a second list of them here would be a policy guess that silently
 *  disagrees with the router's.
 *
 *  ONE definition, exported: `public-events.tsx` (the standalone mount) and
 *  `Designer.handlePublicEventsPopState` (back/forward inside the mount) both
 *  read it. Two copies of a route parser drifting apart is exactly how a link
 *  starts resolving differently on first load than on a back button. */
export interface PublicEventsRoute {
  /** "" for the list route; a non-empty opaque id for the detail route. */
  eventId: string;
  /** Too many segments, or an id segment that is not a decodable percent-escape. */
  invalid: boolean;
}

export function parsePublicEventsPath(pathname: string): PublicEventsRoute {
  const parts = String(pathname || "").split("/").filter(Boolean);
  if (parts.length === 0) return { eventId: "", invalid: false };
  if (parts.length > 1) return { eventId: "", invalid: true };
  try {
    const eventId = decodeURIComponent(parts[0]);
    // The retired list path (including an escaped spelling such as `%65vents`)
    // is not an event id. Refused, never reinterpreted.
    if (eventId === EVENTS_PAGE_SLUG) return { eventId: "", invalid: true };
    return eventId ? { eventId, invalid: false } : { eventId: "", invalid: true };
  } catch {
    // Malformed percent-escape (e.g. a truncated `%E0`) — a decode failure here
    // must not fall back to the list route or to whatever the raw, still-escaped
    // segment happens to collide with.
    return { eventId: "", invalid: true };
  }
}

/** The canonical Attend path for one event — `/{eventId}` — from the path the
 *  server declared (`row.public_path`), or "" when it declared none.
 *
 *  The server stays the authority for WHICH event. Only the one canonical
 *  shape is printed: a declared path that is not exactly one segment, or that
 *  names the retired `/events` list path, yields "" — an unavailable link,
 *  never a rewritten or guessed one. */
function canonicalEventPath(publicPath: string): string {
  const parts = String(publicPath || "").split("/").filter(Boolean);
  if (parts.length !== 1 || parts[0] === EVENTS_PAGE_SLUG) return "";
  return "/" + parts[0];
}

/** True when at least one pin carries finite coordinates — the same test LocationsMap uses to
 *  decide whether it has anything to place (CC, 2026-10-05). */
export function hasLocatedPin(pins: { latitude?: unknown; longitude?: unknown }[]): boolean {
  return pins.some((pin) => typeof pin.latitude === "number" && typeof pin.longitude === "number"
    && Number.isFinite(pin.latitude) && Number.isFinite(pin.longitude));
}

/** Split a configured public authority into scheme (if it declared one) and
 *  host — PORT INCLUDED. The port is the whole point: a local Attend runs on
 *  `localhost:18811`, and the first cut printed `http://localhost/events`
 *  because the authority was truncated to a bare hostname somewhere between
 *  config and link. Nothing here invents a port: if the configured authority
 *  carries none, none is printed, because a port cannot be inferred from
 *  Studio's own origin (Studio and Attend are different ports on the same
 *  local hostname — inheriting Studio's would produce a confidently wrong
 *  link rather than an obviously incomplete one). */
function splitPublicAuthority(configured: string): { scheme: string; host: string } {
  const raw = String(configured || "").trim();
  if (!raw) return { scheme: "", host: "" };
  const m = raw.match(/^(https?:)\/\/(.*)$/i);
  const scheme = m ? m[1].toLowerCase() : "";
  const rest = m ? m[2] : raw.replace(/^\/\//, "");
  // Authority only — drop any path/query/hash the config happened to carry.
  const host = rest.split(/[/?#]/)[0];
  return { scheme, host };
}

const DEFAULTS = {
  multiService: false, firstStep: "service",
  skipService: true, skipLocation: true, showHeader: true, showFooter: false,
  /* Branded chrome (2026-09-23): draw the tenant's Branding header/footer instead
     of the basic bar. Off by default everywhere, so nothing live changes until an
     author opts in. Only meaningful while the matching show* is on. */
  brandedHeader: false, brandedFooter: false,
  /* Master switch for the service/event images this journey shows (Setup →
     [journey] → General, operator ask 2026-09-28). Off by default. Narrows
     the Service/Events tabs' own "Show service images" / "Cover image"
     toggles — never widens them: an image only ever shows when BOTH are on.
     DEFAULT ON (CC, 28 Sept): a journey stored before the switch keeps its images. */
  showMediaImages: true,
  staffSelect: false, reschedule: "same", errBookStaff: true, errOtherServices: true,
  progressType: "titles",
  showCategories: false, showServiceImages: true, serviceDisplay: "list", showDuration: true, showPrice: true,
  nextAvailable: true, quickbook: false, extraLocationInfo: false, loadMore: false,
  initialCount: 2, radius: 0, radiusUnit: "auto", showMap: true, mapHeight: 190,
  calendarLayout: "month", slotGrouping: "partofday", showTimezone: true, defaultDate: "today",
  /* How far apart the offered start times are, in minutes. Bounds are the
     operator's: 1..1140, default 60. The grid is anchored at the STORE's
     opening time, not at midnight, and a start is only offered if the whole
     appointment fits before closing — see the availability builder. */
  slotInterval: 60,
  askEmail: true, emailOptional: false, askPhone: true, phoneOptional: false, askAddress: false, addressOptional: true,
  countryFlags: true, customCountries: ["US", "MX"],
  notifPref: true, notifDefault: "both", attendees: true, attendeeFields: ["first", "last", "email"],
  /* `readOnly` is the stored preference behind the Coming soon "Fill and lock
     details for signed-in customers" control. It is never true by default: no
     customer sign-in exists, and `detailsLocked()` ignores it regardless. */
  fileUpload: true, maxAttachments: 5, requiredDocs: [], readOnly: false,
  showStaff: true, showResource: true, addToCalendar: true, allowReschedule: true, allowCancel: true,
  /* Issue 002: "Manage this appointment" on the appointment confirmation.
     Stored as steps.confirmation.showManage; renders only with a server-issued
     booking.manage_url. */
  showManage: true,
  refundMode: "minusFee",
  showQr: false, takePayment: false, partySize: false, maxParty: 6, allowRescheduleReg: false,
  slug: "advising", embedWidthUnit: "auto", embedWidth: 960, embedHeight: 760,
  embedResize: "fixed",
  brandColor: "#5142C7", radiusScale: 6, logo: "",
  language: "en-US",
};

const SECTION_KEYS = {
  general: ["firstStep", "skipService", "skipLocation", "showHeader", "showFooter", "brandedHeader", "brandedFooter", "showMediaImages", "staffSelect", "reschedule", "errBookStaff", "errOtherServices"],
  progress: ["progressType"],
  service: ["showCategories", "showServiceImages", "serviceDisplay", "showDuration", "showPrice"],
  location: ["nextAvailable", "quickbook", "extraLocationInfo", "loadMore", "initialCount", "radius", "radiusUnit", "showMap", "mapHeight"],
  datetime: ["calendarLayout", "slotGrouping", "showTimezone", "defaultDate", "slotInterval"],
  details: ["askEmail", "emailOptional", "askPhone", "phoneOptional", "askAddress", "addressOptional", "countryFlags", "customCountries", "notifPref", "notifDefault", "attendees", "attendeeFields", "maxParty", "requiredDocs", "fileUpload", "maxAttachments", "readOnly", "takePayment"],
  confirmation: ["showStaff", "showResource", "addToCalendar", "allowReschedule", "showManage", "allowCancel", "refundMode", "showQr"],
  customtext: [],
  launch: ["slug", "embedWidthUnit", "embedWidth", "embedHeight", "embedResize"],
};

const PANELS = {
  general: ["General", ""],
  progress: ["Step indicator", ""],
  scan: ["Scan", ""],
  service: ["Service step", ""],
  location: ["Location step", ""],
  datetime: ["Date & time step", ""],
  details: ["Details step", ""],
  confirmation: ["Confirmation step", ""],
  customtext: ["Custom text", ""],
  launch: ["Launch & embed", ""],
};



const CANCEL_CUTOFF_HRS = 24;
const CANCEL_FEE = "$15";

const ZONES = {
  eastern: { label: "Eastern Time (GMT\u221204:00)", short: "Eastern Time" },
  central: { label: "Central Time (GMT\u221205:00)", short: "Central Time" },
  mountain: { label: "Mountain Time (GMT\u221206:00)", short: "Mountain Time" },
  pacific: { label: "Pacific Time (GMT\u221207:00)", short: "Pacific Time" },
  alaska: { label: "Alaska Time (GMT\u221208:00)", short: "Alaska Time" },
  hawaii: { label: "Hawaii Time (GMT\u221210:00)", short: "Hawaii Time" },
};
const ZONE_ORDER = ["eastern", "central", "mountain", "pacific", "alaska", "hawaii"];
const JOURNEY_ZONE_ALIASES = { alaska: "America/Anchorage", hawaii: "Pacific/Honolulu", pacific: "America/Los_Angeles", mountain: "America/Denver", central: "America/Chicago", eastern: "America/New_York" };
function resolveJourneyZone(zone) { return JOURNEY_ZONE_ALIASES[String(zone || "").toLowerCase()] || String(zone || ""); }
function validJourneyZone(zone) { try { new Intl.DateTimeFormat("en-US", { timeZone: resolveJourneyZone(zone) }); return true; } catch { return false; } }
/* JAI-2135 — which civil day a PUBLIC availability read starts on. Never the
 * visitor's browser date: a Tokyo visitor booking a Chicago store sees Chicago's
 * today. One read serves at most 62 days (the server's
 * PUBLIC_AVAILABILITY_MAX_SPAN_DAYS), so every window below stays inside it.
 *   "store"   a selected store with a valid zone: its civil today + windowDays.
 *   "union"   no store selected: earliest..latest civil today of the PHYSICAL
 *             stores whose zone is valid. Virtual and zoneless rows do not count.
 *   "server"  no usable zone (a virtual or zoneless selection, an unknown id, or
 *             no physical store with a zone): dateFrom is OMITTED so the server's
 *             own venue-aware default anchors the read; dateTo only bounds it.
 *   "invalid" a zone is present but this browser's Intl rejects it: the one case
 *             reported to the visitor. */
const PUBLIC_AVAILABILITY_MAX_SPAN_DAYS = 62;
/* Renamed from civilDateInZone (CC, 2026-10-03): this module also imports
   civilDateInZone(instantMs, timeZone) from events-public-data, and this local
   (zone, now) helper shadowed it, so the public events list passed a timestamp as
   a time zone and crashed (RangeError: Invalid time zone specified). */
function civilTodayInZone(zone, now) {
  const parts = new Intl.DateTimeFormat("en-US", { timeZone: resolveJourneyZone(zone), year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(now);
  const get = (type) => (parts.find((part) => part.type === type) || { value: "00" }).value;
  return get("year") + "-" + get("month") + "-" + get("day");
}
function addCivilDays(iso, n) { const p = String(iso).split("-").map(Number); return new Date(Date.UTC(p[0], p[1] - 1, p[2] + n)).toISOString().slice(0, 10); }
function locationZoneOf(row) { return String((row && (row.tz || row.timezone || row.timeZone)) || "").trim(); }
function publicDateAnchorFor(locations, selectedId, windowDays, now = new Date()) {
  const rows = Array.isArray(locations) ? locations : [];
  const span = Math.min(Math.max(1, Number(windowDays) || 60), PUBLIC_AVAILABILITY_MAX_SPAN_DAYS - 1);
  const lastServable = (from) => addCivilDays(from, PUBLIC_AVAILABILITY_MAX_SPAN_DAYS - 1);
  // The server's own "today" is at most one civil day either side of the UTC
  // date, so ending min(span, 60) days after the UTC date never asks for more
  // than 62 days, whatever zone the server anchors on.
  const server = () => ({ kind: "server", zone: "", dateTo: addCivilDays(now.toISOString().slice(0, 10), Math.min(span, PUBLIC_AVAILABILITY_MAX_SPAN_DAYS - 2)) });
  if (selectedId) {
    const row = rows.filter((location) => location && location.id === selectedId)[0];
    const zone = row && !row.virtual ? locationZoneOf(row) : "";
    if (!zone) return server();
    if (!validJourneyZone(zone)) return { kind: "invalid", zone, dateTo: "" };
    const today = civilTodayInZone(zone, now);
    const to = addCivilDays(today, span);
    return { kind: "store", zone, dateFrom: today, dateTo: to > lastServable(today) ? lastServable(today) : to };
  }
  const present = [...new Set(rows.filter((location) => location && !location.virtual).map(locationZoneOf).filter(Boolean))];
  const valid = present.filter(validJourneyZone);
  if (!valid.length) return present.length ? { kind: "invalid", zone: present[0], dateTo: "" } : server();
  const dates = [...new Set(valid.map((zone) => civilTodayInZone(zone, now)))].sort();
  const from = dates[0];
  const to = addCivilDays(dates[dates.length - 1], span);
  return { kind: "union", zone: "", dateFrom: from, dateTo: to > lastServable(from) ? lastServable(from) : to };
}

/* An empty catalogue is a REAL state, not a failure: a tenant that offers
 * nothing must look like it, and the public mount starts with every binding
 * emptied on purpose. `renderVals` reads the current row's fields
 * unconditionally, so each list has a row-shaped blank to fall back to —
 * otherwise the first render before the catalogue read lands throws on
 * `undefined.name` and the boundary eats the whole screen. */
/** The registration API sends "waitlist" (PublicRegistrationRecord.status is
 *  registered | waitlist); older code tested "waitlisted". Both count (CC, 28 Sept). */
/** "© <this year> <institution>": the institution from the brand kit (the public theme on a
 *  customer page, where the Studio session's tenant is empty), else the tenant name, else
 *  just the year. Never "This workspace" (CC, 29 Sept). */
/** The basic footer's contact line: the brand kit's footer text, or nothing. It was a fixed
 *  "Advising Center" name and phone number on every tenant (CC, 3 Oct). */
function BasicFooterContact({ studio }) {
  const chrome = useTenantChrome(studio);
  const text = (chrome && chrome.footerText) || "";
  return text ? <span>{text}</span> : null;
}
function BasicFooterCopyright({ studio, tenantName }) {
  const chrome = useTenantChrome(studio);
  const name = (chrome && (chrome.legalName || chrome.name)) || tenantName || "";
  return <span>{"© " + new Date().getFullYear() + (name ? " " + name : "")}</span>;
}
/** Two-letter initials for an Avatar, by the tile-initials rule: the first letter of the
 *  first two words that start with a letter (CC 0073 step 3b). jrni-ui's Avatar takes
 *  `initials`, not `name`, so passing a name drew an empty circle. */
function nameInitials(name) {
  return String(name || "").trim().split(/\s+/).filter((w) => /^[A-Za-z\u00C0-\u024F]/.test(w)).slice(0, 2).map((w) => w[0].toUpperCase()).join("");
}
function isWaitlistStatus(s) { const v = String(s || ""); return v === "waitlist" || v === "waitlisted"; }
/* A reservation request waiting for approval (CC, 2026-10-04): the registration POST answers 201 with
   "pending-approval", the public re-read with "pending_approval". It holds no place yet, so the confirmation
   must not read as registered, show a check-in code or offer the event for a calendar. */
function isPendingApprovalStatus(s) { const v = String(s || ""); return v === "pending-approval" || v === "pending_approval"; }

const EMPTY_SERVICE = { id: "", name: "", desc: "", cat: "general", dur: "", price: "", window: 60, locs: [], questions: [] };
const EMPTY_LOCATION = { id: "", name: "", addr: "", dist: "", tz: "eastern", next: "", hours: "", phone: "" };
const EMPTY_STAFF = { id: "", name: "" };
const EMPTY_DURATION = { id: "", label: "", sub: "" };
const EMPTY_EVENT = { id: "", name: "", desc: "", addr: "", date: "", time: "", when: "", venue: "", cat: "", left: 0, cap: 0, maxPer: 1, price: "Free", hue: "" };

/* THE CATALOGUE IS THE TENANT'S OR IT IS NOTHING (2026-09-09, CD item 1).
 *
 * These four lists were higher-ed FIXTURES — six services, nine branches, three
 * advisers, two durations. They were designer-only until the release that serves
 * this screen's bundle on the PUBLIC booking host, at which point the literals
 * compiled into a bundle a customer downloads, and three of the six service names
 * happened to match the test tenant's real ones. That is what made the fixture
 * look like tenant data: the designer preview rendered "Course planning and a
 * degree audit with your assigned adviser" while the live booking page rendered
 * "Meet with an academic adviser to plan your courses" — two sources, not one
 * source twice. Renaming the three obviously-invented rows would have left the
 * other three lying more convincingly.
 *
 * So they start EMPTY, in every mode, and the only thing that fills them is a read
 * of this tenant: `loadPreviewCatalog` in the designer (services · locations ·
 * staff · durations, from the same choke points the tenant's own surfaces read)
 * and `loadPublicCatalog` on the published page. A tenant that offers nothing, or
 * a read that is refused, now draws nothing — the honest state, and the one the
 * EMPTY_* row-blanks above exist to make renderable on the first frame.
 *
 * Do not reintroduce sample rows here. A designer canvas with no catalogue is a
 * tenant-shaped problem to fix upstream, not a gap to fill with invented copy;
 * if the local preview needs something to draw, it belongs in the harness seed
 * layer (which never ships) and not in this module. */
/* `any[]` and not `PreviewService[]` etc: two reads fill these and they disagree
   on row type — `loadPreviewCatalog` returns the typed `Preview*` rows, while
   `loadPublicCatalog` returns `PublicCatalogRead`, whose lists are `Dict[]`. An
   empty literal on its own would infer `never[]` and refuse both. This is the same
   looseness the fixtures had; narrowing it means one row type across both reads,
   which is a seam change and not this pass. */
let SERVICES: any[] = [];
/* Confirmed service images by service id, collected from the raw catalogue rows
   the wizard adopted. buildCatalog() keeps only the fields the steps print, so
   the image is gathered beside it rather than threaded through it. */
let SERVICE_IMAGES: Record<string, string> = {};
/** A storable image address: http(s) or root-relative. Never `blob:` / `data:`. */
const durableImageUrl = (value: unknown): string => {
  const v = typeof value === "string" ? value.trim() : "";
  return v && (/^https?:\/\//i.test(v) || /^\/(?!\/)/.test(v)) ? v : "";
};
function catalogServiceImages(rows: unknown): Record<string, string> {
  const out: Record<string, string> = {};
  (Array.isArray(rows) ? rows : []).forEach((row: any) => {
    if (!row || !row.id) return;
    const hero = row.landing && row.landing.hero;
    const url = durableImageUrl(row.image) || durableImageUrl(hero && hero.image);
    if (url) out[String(row.id)] = url;
  });
  return out;
}
/** Service image precedence: the journey's explicit override, then the catalogue
 *  service's confirmed image, then none. Nothing at all when the journey's
 *  `showServiceImages` is off. */
function resolveServiceImage(show: boolean, override: unknown, catalogImage: unknown): string {
  if (!show) return "";
  return durableImageUrl(override) || durableImageUrl(catalogImage);
}
let DURATIONS: any[] = [];
let LOCATIONS: any[] = [];
let STAFF: any[] = [];


const SLOTS = {
  morning: ["9:00 AM", "9:30 AM", "10:00 AM", "10:30 AM", "11:20 AM"],
  afternoon: ["1:00 PM", "1:30 PM", "2:20 PM", "3:00 PM", "3:40 PM"],
  evening: ["5:00 PM", "5:40 PM"],
};


const CT_GROUPS = [
  { id: "progress", step: "service", labelKey: "shell.allJourneys.ctLabel.progressBar", label: "Progress bar", fields: [
    { id: "svc", labelKey: "shell.allJourneys.ctLabel.serviceStep", label: "Service step", def: "What" },
    { id: "loc", labelKey: "shell.allJourneys.ctLabel.locationStep", label: "Location step", def: "Where" },
    { id: "dt", labelKey: "shell.allJourneys.ctLabel.dateTimeStep", label: "Date & time step", def: "When" },
    { id: "pd", labelKey: "shell.allJourneys.ctLabel.personalDetailsStep", label: "Personal details step", def: "Your info" },
    { id: "conf", labelKey: "shell.allJourneys.ctLabel.confirmationStep", label: "Confirmation step", def: "Confirmation" },
    { id: "cta", labelKey: "shell.allJourneys.ctLabel.continueButton", label: "Continue button", def: "Continue" },
    { id: "sum", labelKey: "shell.allJourneys.ctLabel.summaryHeader", label: "Summary header", def: "Your appointment" },
    { id: "page", labelKey: "shell.allJourneys.ctLabel.pageTitleInTheHeader", label: "Page title in the header", def: "Book an appointment" },
  ] },
  { id: "service", step: "service", labelKey: "shell.allJourneys.ctLabel.serviceStep", label: "Service step", fields: [
    { id: "hdr", labelKey: "shell.allJourneys.ctLabel.serviceHeader", label: "Service header", def: "Select a service" },
    { id: "dur", labelKey: "shell.allJourneys.ctLabel.durationHeader", label: "Duration header", def: "Select a duration" },
  ] },
  { id: "location", step: "location", labelKey: "shell.allJourneys.ctLabel.locationStep", label: "Location step", fields: [
    { id: "hdr", labelKey: "shell.allJourneys.ctLabel.locationSearchHeader", label: "Location search header", def: "Select location" },
  ] },
  { id: "datetime", step: "datetime", labelKey: "shell.allJourneys.ctLabel.dateTimeStep", label: "Date & time step", fields: [
    { id: "date", labelKey: "shell.allJourneys.ctLabel.dateSelectionHeader", label: "Date selection header", def: "Select a date" },
    { id: "time", labelKey: "shell.allJourneys.ctLabel.timeslotSelectionHeader", label: "Timeslot selection header", def: "Select a time" },
  ] },
  { id: "details", step: "details", labelKey: "shell.allJourneys.ctLabel.personalDetailsStep", label: "Personal details step", fields: [
    { id: "hdr", labelKey: "shell.allJourneys.ctLabel.pageHeader", label: "Page header", def: "Enter your details" },
    { id: "upl", labelKey: "shell.allJourneys.ctLabel.documentUploadHeader", label: "Document upload header", def: "Document upload" },
    { id: "uplin", labelKey: "shell.allJourneys.ctLabel.documentUploadInstructions", label: "Document upload instructions", def: "PNG, JPG, DOC and PDF supported. Max file size 20 MB." },
    { id: "phdr", labelKey: "shell.allJourneys.ctLabel.paymentHeader", label: "Payment header", def: "Payment" },
    { id: "pinfo", labelKey: "shell.allJourneys.ctLabel.paymentInfoText", label: "Payment info text", def: "Not shown unless you add text" },
  ] },
  { id: "confirmation", step: "confirmation", labelKey: "shell.allJourneys.ctLabel.confirmationStep", label: "Confirmation step", fields: [
    { id: "msg", labelKey: "shell.allJourneys.ctLabel.confirmationMessage", label: "Confirmation message", def: "Thanks! Your appointment is confirmed" },
    { id: "flaghdr", labelKey: "shell.allJourneys.ctLabel.infoFlagHeader", label: "Info-flag header", def: "Appointment confirmed" },
    { id: "flagtxt", labelKey: "shell.allJourneys.ctLabel.infoFlagText", label: "Info-flag text", def: "We have sent your confirmation. You may close this window.",
        /* CC 0316: the outcome line comes first, always; this sentence shows under it. */
        hint: "A line saying how the confirmation was sent (email, text message, or why nothing was sent) is added automatically above this sentence. Avoid promising a channel here, such as \"an e-mail\"." },
  ] },
  { id: "error", step: "confirmation", labelKey: "shell.allJourneys.ctLabel.linkErrorPage", label: "Link error page", fields: [
    { id: "ttl", labelKey: "shell.allJourneys.ctLabel.heading", label: "Heading", def: "This booking link is no longer available" },
    { id: "body", labelKey: "shell.allJourneys.ctLabel.bodyText", label: "Body text", def: "The adviser or service behind this link is not taking appointments at the moment." },
    { id: "staffttl", labelKey: "shell.allJourneys.ctLabel.anotherAdviserOption", label: "Another-adviser option", def: "Book with another adviser" },
    { id: "staffsub", labelKey: "shell.allJourneys.ctLabel.anotherAdviserDetail", label: "Another-adviser detail", def: "Same service, next available adviser" },
    { id: "svcttl", labelKey: "shell.allJourneys.ctLabel.otherServicesOption", label: "Other-services option", def: "Explore other services" },
    { id: "svcsub", labelKey: "shell.allJourneys.ctLabel.otherServicesDetail", label: "Other-services detail", def: "Services open for booking today" },
    { id: "start", labelKey: "shell.allJourneys.ctLabel.startAgainButton", label: "Start-again button", def: "Start again from the beginning" },
  ] },
  { id: "cancel", step: "confirmation", labelKey: "shell.allJourneys.ctLabel.cancellation", label: "Cancellation", fields: [
    { id: "dlgttl", labelKey: "shell.allJourneys.ctLabel.confirmDialogTitle", label: "Confirm dialog title", def: "Cancel this appointment?" },
    { id: "keep", labelKey: "shell.allJourneys.ctLabel.keepButton", label: "Keep button", def: "Keep appointment" },
    { id: "confirm", labelKey: "shell.allJourneys.ctLabel.confirmButton", label: "Confirm button", def: "Cancel appointment" },
    { id: "msg", labelKey: "shell.allJourneys.ctLabel.cancelledMessage", label: "Cancelled message", def: "This appointment is cancelled" },
    { id: "flaghdr", labelKey: "shell.allJourneys.ctLabel.cancelledFlagHeader", label: "Cancelled flag header", def: "Appointment cancelled" },


    { id: "flagtxt", labelKey: "shell.allJourneys.ctLabel.cancelledFlagText", label: "Cancelled flag text", def: "A cancellation e-mail is on its way. Nothing else is needed from you." },
    { id: "again", labelKey: "shell.allJourneys.ctLabel.bookAgainButton", label: "Book again button", def: "Book a new appointment" },
  ] },
];

const QUEUE_ONLY = {
  queueLocationStep: false,
  capacity: 40, closeWhenFull: true,
  remoteJoin: true, arrivalWindow: 10,
  liveWaitPerService: true,
  showTicketNumber: true, showPosition: true, waitDisplay: "range",
  notifyWhenClose: true, notifyAhead: 2,
  allowLeaveQueue: true, showDesk: true,
  showQr: true, partySize: true, maxParty: 6,
  slug: "walk-in",
};

const QUEUE_DEFAULTS = Object.assign({}, DEFAULTS, {
  showCategories: true, showDuration: false, showPrice: false,
  askEmail: false, askPhone: true, notifDefault: "sms", notifPref: false,
  attendees: false, fileUpload: false, readOnly: false,
  showFooter: false,
}, QUEUE_ONLY);

const ALL_DEFAULTS = Object.assign({}, DEFAULTS, QUEUE_ONLY, {
  eventsWindow: "all",
  eventsPerPage: 6, eventPaging: "pages", eventDisplay: "grid", cardCols: 3,
  eventImages: true, showEventDate: true, showEventTime: true, showEventDesc: true,
  showEventVenue: true, showRemaining: false, showEventPrice: false,
  filterBar: true, filterLocation: true, filterType: true, filterDates: true,
  eventFilters: false, sortOrder: "date", showPastEvents: false,
  skipSingleEventSlot: false,
  showSoldOut: true, waitlist: true,
  ticketQuantity: true, maxTickets: 4, attendeeNames: false,
  showQr: true, allowCancelReg: true, allowRescheduleReg: true,
  takePayment: false, partySize: false, maxParty: 6,
  // Kiosk-only keys — KIOSK_DEFAULTS is declared further down, so the setter list needs them here.
  kioskAppts: true, kioskWalkin: true, kioskEvents: true, kioskCheckin: true,
  kioskWaitTime: true, kioskNextSlot: true, kioskHours: true,
  kioskBigText: true, kioskGreeting: "time", kioskTileSize: "large",
  kioskLocation: "l1",
  ckQr: true, ckWallet: true, ckNfc: true, ckRef: true, ckName: true, ckDefault: "scan",
  ckDoneDwell: 6, kioskResetAfter: 45,
  ckShowQueuePos: true,
});

const QUEUE_SECTION_KEYS = {
  general: ["showHeader", "showFooter", "brandedHeader", "brandedFooter", "showMediaImages"],
  progress: ["progressType"],
  service: ["showCategories", "showServiceImages", "serviceDisplay", "liveWaitPerService", "queueLocationStep"],
  location: ["showDesk"],
  datetime: [],
  details: ["askEmail", "emailOptional", "askPhone", "phoneOptional", "askAddress", "addressOptional", "countryFlags", "customCountries", "partySize", "maxParty", "readOnly", "takePayment"],
  confirmation: ["showTicketNumber", "showPosition", "waitDisplay", "notifyWhenClose", "notifyAhead", "allowLeaveQueue", "showDesk", "showQr"],
  customtext: [],
  launch: ["slug", "embedWidthUnit", "embedWidth", "embedHeight", "embedResize"],
};

const QUEUE_PANELS = {
  general: ["General", ""],
  service: ["What they need", ""],
  location: ["Service point", ""],
  details: ["Details step", ""],
  confirmation: ["Place in line", ""],
};

const QUEUE_CT_GROUPS = [
  { id: "progress", labelKey: "shell.allJourneys.ctLabel.progressBar", label: "Progress bar", fields: [
    { id: "svc", labelKey: "shell.allJourneys.ctLabel.serviceStep", label: "Service step", def: "What" },
    { id: "loc", labelKey: "shell.allJourneys.ctLabel.servicePointStep", label: "Service point step", def: "Where" },
    { id: "pd", labelKey: "shell.allJourneys.ctLabel.personalDetailsStep", label: "Personal details step", def: "Your info" },
    { id: "conf", labelKey: "shell.allJourneys.ctLabel.queueStep", label: "Queue step", def: "Your place in line" },
    { id: "cta", labelKey: "shell.allJourneys.ctLabel.continueButton", label: "Continue button", def: "Continue" },
    /* Read by the page, editable nowhere until now (2026-09-17, CD): the
       header caption beside the progress bar. */
    { id: "page", labelKey: "shell.allJourneys.ctLabel.pageCaption", label: "Page caption", def: "Join the queue" },
  ] },
  { id: "service", labelKey: "shell.allJourneys.ctLabel.serviceStep", label: "Service step", fields: [
    { id: "hdr", labelKey: "shell.allJourneys.ctLabel.serviceHeader", label: "Service header", def: "What do you need help with?" },
    { id: "info", labelKey: "shell.allJourneys.ctLabel.serviceInfoText", label: "Service info text", def: "Not shown unless you add text" },
    { id: "wait", labelKey: "shell.allJourneys.ctLabel.waitTimePrefix", label: "Wait time prefix", def: "Wait about" },
  ] },
  { id: "location", labelKey: "shell.allJourneys.ctLabel.servicePointStep", label: "Service point step", fields: [
    { id: "hdr", labelKey: "shell.allJourneys.ctLabel.servicePointHeader", label: "Service point header", def: "Choose a service point" },
  ] },
  { id: "details", labelKey: "shell.allJourneys.ctLabel.personalDetailsStep", label: "Personal details step", fields: [
    { id: "hdr", labelKey: "shell.allJourneys.ctLabel.pageHeader", label: "Page header", def: "Enter your details" },
    { id: "cta", labelKey: "shell.allJourneys.ctLabel.joinButton", label: "Join button", def: "Join the queue" },
  ] },
  { id: "queue", labelKey: "shell.allJourneys.ctLabel.placeInLine", label: "Place in line", fields: [
    { id: "hdr", labelKey: "shell.allJourneys.ctLabel.queueConfirmationMessage", label: "Queue confirmation message", def: "You're in the queue" },
    { id: "pos", labelKey: "shell.allJourneys.ctLabel.positionLabel", label: "Position label", def: "Position in line" },
    { id: "wait", labelKey: "shell.allJourneys.ctLabel.estimatedWaitLabel", label: "Estimated wait label", def: "Estimated wait" },
    { id: "notify", labelKey: "shell.allJourneys.ctLabel.notificationNote", label: "Notification note", def: "We'll text you when you're next to be seen." },
    { id: "leave", labelKey: "shell.allJourneys.ctLabel.leaveQueueButton", label: "Leave queue button", def: "Leave the queue" },
    { id: "closed", labelKey: "shell.allJourneys.ctLabel.queueFullMessage", label: "Queue full message", def: "The line is full for today. Please come back tomorrow." },
    { id: "appt", labelKey: "shell.allJourneys.ctLabel.bookInsteadButton", label: "Book instead button", def: "Make an appointment instead" },
    { id: "apttl", labelKey: "shell.allJourneys.ctLabel.leaveAndBookDialogTitle", label: "Leave-and-book dialog title", def: "Leave the line and book instead?" },
  ] },
  /* LEFT THE LINE (2026-09-17, CD). Six keys the page and the preview already
     read had no field anywhere, so this whole state was uneditable — the one
     state a visitor reaches by changing their mind, and the one most likely to
     need the tenant's own wording. Defaults are the page's own literals
     verbatim, so adding the group changes nothing until an author types.

     `lvtxt` takes the page's wording, not the designer preview's: the preview
     offers "or book a time instead", which the public page does not render —
     the two had drifted, and the customer-facing one is the truth. */
  { id: "queueLeft", labelKey: "shell.allJourneys.ctLabel.leftTheLine", label: "Left the line", ctGroup: "queue", fields: [
    { id: "lvttl", labelKey: "shell.allJourneys.ctLabel.leaveConfirmationTitle", label: "Leave confirmation title", def: "Leave the line?" },
    { id: "lvkeep", labelKey: "shell.allJourneys.ctLabel.stayInLineButton", label: "Stay in line button", def: "Stay in the line" },
    { id: "lvhdr2", labelKey: "shell.allJourneys.ctLabel.leftTheLineHeader", label: "Left-the-line header", def: "You are no longer in the queue" },
    { id: "lvhdr", labelKey: "shell.allJourneys.ctLabel.leftTheLineBannerTitle", label: "Left-the-line banner title", def: "You have left the line" },
    { id: "lvtxt", labelKey: "shell.allJourneys.ctLabel.leftTheLineBannerText", label: "Left-the-line banner text", def: "Your ticket is no longer valid. You can join again if you still need to be seen." },
    { id: "lvagain", labelKey: "shell.allJourneys.ctLabel.joinAgainButton", label: "Join again button", def: "Join the line again" },
  ] },
];

/* No tenant's name in a default (2026-09-17, CD). This prefilled every new
   walk-in journey with a "welcome to <place>" line — a fixture brand from a seed
   world, sitting in the first line a customer reads on a page belonging to
   somebody else. The header now falls through to the page's own default
   ("You're in the queue"); the CTA prefill stays, because it names the act
   rather than an organisation. */
const QUEUE_CT_PREFILL = {
  "details.cta": "Join the walk-in line",
};

const QUEUE_DESKS_ON_DUTY = 3;
const QUEUE_AVG_MIN = 6;

const LANDING_BLOCKS = [
  { id: "desc", labelKey: "shell.allJourneys.blockLabel.descriptionAndAgenda", label: "Description and agenda" },
  { id: "speakers", labelKey: "shell.allJourneys.blockLabel.speakersAndHosts", label: "Speakers and hosts" },
  { id: "included", labelKey: "shell.allJourneys.blockLabel.whatSIncluded", label: "What's included" },
  { id: "tickets", labelKey: "shell.allJourneys.blockLabel.ticketTypesAndPrices", label: "Ticket types and prices" },
  { id: "spaces", labelKey: "shell.allJourneys.blockLabel.spacesRemaining", label: "Spaces remaining" },
  { id: "map", labelKey: "shell.allJourneys.blockLabel.mapAndDirections", label: "Map and directions" },
  { id: "faq", labelKey: "shell.allJourneys.blockLabel.fAQ", label: "FAQ" },
  { id: "related", labelKey: "shell.allJourneys.blockLabel.relatedEvents", label: "Related events" },
  { id: "sponsors", labelKey: "shell.allJourneys.blockLabel.sponsorLogos", label: "Sponsor logos" },
  { id: "share", labelKey: "shell.allJourneys.blockLabel.shareButtons", label: "Share buttons" },
];












const SPONSOR_TIERS = ["Gold sponsor", "Silver sponsor", "Bronze sponsor", "Supporter"];
/* The tier NAME is the stored value (lpSponsorRows[].tier) and is compared as English, so it
   never changes; only the label shown for it is translated, through this map. */
const SPONSOR_TIER_LABEL_KEYS = {
  "Gold sponsor": "shell.allJourneys.sponsorTier.goldSponsor",
  "Silver sponsor": "shell.allJourneys.sponsorTier.silverSponsor",
  "Bronze sponsor": "shell.allJourneys.sponsorTier.bronzeSponsor",
  "Supporter": "shell.allJourneys.sponsorTier.supporter",
};
const sponsorTierLabel = (tier) => (SPONSOR_TIER_LABEL_KEYS[tier] ? i18nT(SPONSOR_TIER_LABEL_KEYS[tier], tier) : tier);

const SHARE_CHANNELS = [
  { id: "copy", labelKey: "shell.allJourneys.shareChannel.copyLink", label: "Copy link" },
  { id: "email", labelKey: "shell.allJourneys.shareChannel.eMail", label: "E-mail" },
  { id: "linkedin", label: "LinkedIn" }, // ui-config: brand name, not translated
  { id: "x", label: "X" }, // ui-config: brand name, not translated
  { id: "whatsapp", label: "WhatsApp" }, // ui-config: brand name, not translated
];

const LANDING_ONLY = {
  landingTemplate: "banner",
  heroHeight: 280, heroTreatment: "image",
  stickyBar: true, coverImage: "",
  blockOrder: ["desc", "speakers", "included", "tickets", "spaces", "map", "faq", "related", "sponsors", "share"],
  // Authored fixture content removed 2026-09-10; empty until real event data or a tenant author fills it.
  lpDescText: "",
  lpAgendaRows: [],
  lpSpeakerRows: [],
  lpIncludedRows: [],
  lpFaqRows: [],
  lpSponsorRows: [],
  sponsorTiers: true,
  lpShareChannels: ["copy", "email", "linkedin"],
  relatedRule: "category", relatedCount: 3, relatedManual: [],
  blocksOn: ["desc", "speakers", "included", "tickets", "spaces", "map", "faq", "related", "share"],
  soldOutMode: "waitlist",
  /* Custom-layout state. NEITHER key may be added to
     `EVENT_LANDING_PRESENTATION_KEYS`: `EventLandingPresentationSchema` is
     `.strict()` and would reject both, and its `landingTemplate` enum has no
     `"custom"` member — which is why "this page uses the canvas" is carried by
     `customTpl` locally and derived from the saved BLOCKS on load
     (`carriesCustomLayout`), never written as a presentation value. */
  customTpl: false,
  customBlocks: [],
};

// The setter factory is built from ALL_DEFAULTS, so landing keys must be in it.

const SERVICE_WAITS = { advising: "12–18 min", aid: "25 min", career: "8 min", tour: "—", registrar: "5 min", wellness: "20 min" };

const EVENT_ONLY = {
  eventsWindow: "all",
  eventsPerPage: 6, eventPaging: "pages", eventDisplay: "grid", cardCols: 3,
  eventImages: true, showEventDate: true, showEventTime: true, showEventDesc: true,
  showEventVenue: true, showRemaining: false, showEventPrice: false,
  filterBar: true, filterLocation: true, filterType: true, filterDates: true,
  eventFilters: false, sortOrder: "date", showPastEvents: false,
  skipSingleEventSlot: false,
  showSoldOut: true, waitlist: true,
  ticketQuantity: true, maxTickets: 4, attendeeNames: false, attendees: false,
  showQr: true, allowCancelReg: true, showManage: true, allowRescheduleReg: true, showStaff: true,
  fileUpload: false, maxAttachments: 3, showTimezone: true,
  slug: "events", embedHeight: 900,
};

const EVENT_DEFAULTS = Object.assign({}, DEFAULTS, {
  askEmail: true, askPhone: true, phoneOptional: true, readOnly: false,
  attendees: false, fileUpload: false, notifDefault: "email",
  showFooter: true,
}, EVENT_ONLY);

const EVENT_SECTION_KEYS = {
  general: ["sortOrder", "showPastEvents", "skipSingleEventSlot", "showHeader", "showFooter", "brandedHeader", "brandedFooter", "showMediaImages"],
  progress: ["progressType"],
  events: ["eventsWindow", "eventsPerPage", "eventPaging", "eventDisplay", "cardCols", "eventImages", "showEventDesc", "showEventDate", "showEventTime", "showEventVenue", "showRemaining", "showEventPrice", "showSoldOut", "waitlist", "filterBar", "filterLocation", "filterType", "filterDates", "showTimezone", "eventFilters"],
  details: ["askEmail", "emailOptional", "askPhone", "phoneOptional", "askAddress", "addressOptional", "countryFlags", "customCountries", "notifPref", "notifDefault", "ticketQuantity", "readOnly", "fileUpload", "maxAttachments", "takePayment"],
  confirmation: ["showQr", "addToCalendar", "allowCancelReg", "showManage", "showResource", "showStaff"],
  customtext: [],
  launch: ["slug", "embedWidthUnit", "embedWidth", "embedHeight", "embedResize"],
};



Object.assign(ALL_DEFAULTS, LANDING_ONLY);



const LANDING_DEFAULTS = Object.assign({}, DEFAULTS, {
  askEmail: true, askPhone: true, phoneOptional: true, readOnly: false,
  attendees: false, fileUpload: false, notifDefault: "email",
  showFooter: true, allowCancelReg: true, showManage: true, allowRescheduleReg: false, showQr: true,
  slug: "orientation", embedHeight: 1200,
}, EVENT_ONLY, LANDING_ONLY);

const LANDING_SECTION_KEYS = {
  general: ["landingTemplate", "heroHeight", "heroTreatment", "stickyBar", "coverImage", "showHeader", "showFooter", "brandedHeader", "brandedFooter", "showMediaImages"],
  landing: ["blockOrder", "blocksOn", "soldOutMode", "waitlist", "relatedRule", "relatedCount", "relatedManual"],
  details: ["askEmail", "emailOptional", "askPhone", "phoneOptional", "askAddress", "addressOptional", "countryFlags", "customCountries", "ticketQuantity", "readOnly", "takePayment"],
  confirmation: ["showQr", "addToCalendar", "allowCancelReg", "showManage", "showResource", "showStaff"],
  customtext: [],
  launch: ["slug", "embedWidthUnit", "embedWidth", "embedResize", "embedHeight"],
};

const LANDING_PANELS = {
  general: ["Page layout", ""],
  landing: ["Content blocks", ""],
  details: ["Your details", ""],
  confirmation: ["Confirmation", ""],
};

const EVENT_PANELS = {
  general: ["General", ""],
  events: ["Events list", ""],
  details: ["Your details", ""],
  confirmation: ["Confirmation", ""],
};

const EVENT_CT_GROUPS = [
  { id: "progress", labelKey: "shell.allJourneys.ctLabel.progressBar", label: "Progress bar", fields: [
    { id: "svc", labelKey: "shell.allJourneys.ctLabel.eventsStep", label: "Events step", def: "Events" },
    { id: "pd", labelKey: "shell.allJourneys.ctLabel.personalDetailsStep", label: "Personal details step", def: "Your info" },
    { id: "conf", labelKey: "shell.allJourneys.ctLabel.confirmationStep", label: "Confirmation step", def: "Confirmation" },
    { id: "cta", labelKey: "shell.allJourneys.ctLabel.continueButton", label: "Continue button", def: "Continue" },
  ] },
  { id: "events", labelKey: "shell.allJourneys.ctLabel.eventsList", label: "Events list", fields: [
    { id: "hdr", labelKey: "shell.allJourneys.ctLabel.listHeader", label: "List header", def: "Select an event" },
    { id: "info", labelKey: "shell.allJourneys.ctLabel.listInfoText", label: "List info text", def: "Not shown unless you add text" },
    { id: "remaining", labelKey: "shell.allJourneys.ctLabel.spacesRemainingLabel", label: "Spaces remaining label", def: "spaces left" },
    { id: "soldout", labelKey: "shell.allJourneys.ctLabel.fullyBookedLabel", label: "Fully booked label", def: "Fully booked" },
    { id: "waitlist", labelKey: "shell.allJourneys.ctLabel.waitingListButton", label: "Waiting list button", def: "Join the waitlist" },
    { id: "empty", labelKey: "shell.allJourneys.ctLabel.noEventsMessage", label: "No events message", def: "There are no events to show right now." },
    { id: "regclosed", labelKey: "shell.allJourneys.ctLabel.registrationClosedLabel", label: "Registration closed label", def: "Registration closed" },
      { id: "cancelledevt", labelKey: "shell.allJourneys.ctLabel.eventCancelledLabel", label: "Event cancelled label", def: "Cancelled" },
      { id: "cancelledTitle", labelKey: "shell.allJourneys.ctLabel.eventCancelledTitle", label: "Event cancelled heading", def: "This event has been cancelled" },
      { id: "cancelledText", labelKey: "shell.allJourneys.ctLabel.eventCancelledText", label: "Event cancelled sentence", def: "It won't go ahead. If you registered, the organiser will contact you about what happens next." },
    { id: "ended", labelKey: "shell.allJourneys.ctLabel.eventEndedLabel", label: "Event ended label", def: "Event ended" },
    { id: "happening", labelKey: "shell.allJourneys.ctLabel.happeningNowLabel", label: "Happening now label", def: "Happening now" },
  ] },
  { id: "details", labelKey: "shell.allJourneys.ctLabel.personalDetailsStep", label: "Personal details step", fields: [
    { id: "hdr", labelKey: "shell.allJourneys.ctLabel.pageHeader", label: "Page header", def: "Enter your details" },
    { id: "cta", labelKey: "shell.allJourneys.ctLabel.registerButton", label: "Register button", def: "Complete registration" },
    { id: "tickets", labelKey: "shell.allJourneys.ctLabel.ticketQuantityLabel", label: "Ticket quantity label", def: "How many places?" },
    { id: "upl", labelKey: "shell.allJourneys.ctLabel.documentUploadHeader", label: "Document upload header", def: "Document upload" },
    { id: "uplin", labelKey: "shell.allJourneys.ctLabel.documentUploadInstructions", label: "Document upload instructions", def: "PNG, JPG, DOC and PDF supported. Max file size 20 MB." },
    { id: "phdr", labelKey: "shell.allJourneys.ctLabel.paymentHeader", label: "Payment header", def: "Payment" },
    { id: "pinfo", labelKey: "shell.allJourneys.ctLabel.paymentInfoText", label: "Payment info text", def: "Not shown unless you add text" },
  ] },
  { id: "confirmation", labelKey: "shell.allJourneys.ctLabel.confirmationStep", label: "Confirmation step", fields: [
    { id: "msg", labelKey: "shell.allJourneys.ctLabel.confirmationMessage", label: "Confirmation message", def: "Thanks! You're registered" },
    { id: "flaghdr", labelKey: "shell.allJourneys.ctLabel.infoFlagHeader", label: "Info-flag header", def: "Registration confirmed" },
    { id: "flagtxt", labelKey: "shell.allJourneys.ctLabel.infoFlagText", label: "Info-flag text", def: "Your ticket is below and a copy is on its way by email" },
    { id: "cnclreg", labelKey: "shell.allJourneys.ctLabel.cancelButton", label: "Cancel button", def: "Cancel registration" },
    { id: "keepreg", labelKey: "shell.allJourneys.ctLabel.keepMyPlaceButton", label: "Keep-my-place button", def: "Keep my place" },
    { id: "cnclttl", labelKey: "shell.allJourneys.ctLabel.cancelDialogTitle", label: "Cancel dialog title", def: "Cancel this registration?" },
    { id: "cnclmsg", labelKey: "shell.allJourneys.ctLabel.cancelledMessage", label: "Cancelled message", def: "This registration is cancelled" },
    { id: "cnclhdr", labelKey: "shell.allJourneys.ctLabel.cancelledFlagHeader", label: "Cancelled flag header", def: "Registration cancelled" },
    { id: "cncltxt", labelKey: "shell.allJourneys.ctLabel.cancelledFlagText", label: "Cancelled flag text", def: "Your place has been released. A confirmation e-mail is on its way." },
    { id: "backev", labelKey: "shell.allJourneys.ctLabel.backToEventsButton", label: "Back-to-events button", def: "Back to the events list" },
  ] },
];

/* LANDING is its own bucket (2026-09-28, CD): it was falling through to the
   appointment CT_GROUPS (`ctGroupDefs()`'s default arm) even though it seeds
   from EVENT_CT_PREFILL and reads the same `events.*`/`details.*`/
   `confirmation.*` keys as the events list — the editor showed unrelated
   appointment fields (Category header, Duration header…) with none of the
   fields the landing page actually renders. Same keys as EVENT_CT_GROUPS
   minus the list-only ones (hdr/info/empty/waitlist never render on a
   single-event page), with the progress step's own default. */
const LANDING_CT_GROUPS = [
  { id: "progress", labelKey: "shell.allJourneys.ctLabel.progressBar", label: "Progress bar", fields: [
    { id: "svc", labelKey: "shell.allJourneys.ctLabel.eventPageStep", label: "Event page step", def: "Event page" },
    { id: "pd", labelKey: "shell.allJourneys.ctLabel.personalDetailsStep", label: "Personal details step", def: "Your info" },
    { id: "conf", labelKey: "shell.allJourneys.ctLabel.confirmationStep", label: "Confirmation step", def: "Confirmation" },
    { id: "cta", labelKey: "shell.allJourneys.ctLabel.continueButton", label: "Continue button", def: "Continue" },
  ] },
  { id: "events", labelKey: "shell.allJourneys.ctLabel.availabilityStatus", label: "Availability status", fields: [
    { id: "remaining", labelKey: "shell.allJourneys.ctLabel.spacesRemainingLabel", label: "Spaces remaining label", def: "spaces left" },
    { id: "soldout", labelKey: "shell.allJourneys.ctLabel.fullyBookedLabel", label: "Fully booked label", def: "Fully booked" },
    { id: "regclosed", labelKey: "shell.allJourneys.ctLabel.registrationClosedLabel", label: "Registration closed label", def: "Registration closed" },
      { id: "cancelledevt", labelKey: "shell.allJourneys.ctLabel.eventCancelledLabel", label: "Event cancelled label", def: "Cancelled" },
      { id: "cancelledTitle", labelKey: "shell.allJourneys.ctLabel.eventCancelledTitle", label: "Event cancelled heading", def: "This event has been cancelled" },
      { id: "cancelledText", labelKey: "shell.allJourneys.ctLabel.eventCancelledText", label: "Event cancelled sentence", def: "It won't go ahead. If you registered, the organiser will contact you about what happens next." },
    { id: "ended", labelKey: "shell.allJourneys.ctLabel.eventEndedLabel", label: "Event ended label", def: "Event ended" },
    { id: "happening", labelKey: "shell.allJourneys.ctLabel.happeningNowLabel", label: "Happening now label", def: "Happening now" },
  ] },
  { id: "details", labelKey: "shell.allJourneys.ctLabel.personalDetailsStep", label: "Personal details step", fields: [
    { id: "hdr", labelKey: "shell.allJourneys.ctLabel.pageHeader", label: "Page header", def: "Enter your details" },
    { id: "cta", labelKey: "shell.allJourneys.ctLabel.registerButton", label: "Register button", def: "Complete registration" },
    { id: "tickets", labelKey: "shell.allJourneys.ctLabel.ticketQuantityLabel", label: "Ticket quantity label", def: "How many places?" },
    { id: "upl", labelKey: "shell.allJourneys.ctLabel.documentUploadHeader", label: "Document upload header", def: "Document upload" },
    { id: "uplin", labelKey: "shell.allJourneys.ctLabel.documentUploadInstructions", label: "Document upload instructions", def: "PNG, JPG, DOC and PDF supported. Max file size 20 MB." },
    { id: "phdr", labelKey: "shell.allJourneys.ctLabel.paymentHeader", label: "Payment header", def: "Payment" },
    { id: "pinfo", labelKey: "shell.allJourneys.ctLabel.paymentInfoText", label: "Payment info text", def: "Not shown unless you add text" },
  ] },
  { id: "confirmation", labelKey: "shell.allJourneys.ctLabel.confirmationStep", label: "Confirmation step", fields: [
    { id: "msg", labelKey: "shell.allJourneys.ctLabel.confirmationMessage", label: "Confirmation message", def: "Thanks! You're registered" },
    { id: "flaghdr", labelKey: "shell.allJourneys.ctLabel.infoFlagHeader", label: "Info-flag header", def: "Registration confirmed" },
    { id: "flagtxt", labelKey: "shell.allJourneys.ctLabel.infoFlagText", label: "Info-flag text", def: "Your ticket is below and a copy is on its way by email" },
    { id: "cnclreg", labelKey: "shell.allJourneys.ctLabel.cancelButton", label: "Cancel button", def: "Cancel registration" },
    { id: "keepreg", labelKey: "shell.allJourneys.ctLabel.keepMyPlaceButton", label: "Keep-my-place button", def: "Keep my place" },
    { id: "cnclttl", labelKey: "shell.allJourneys.ctLabel.cancelDialogTitle", label: "Cancel dialog title", def: "Cancel this registration?" },
    { id: "cnclmsg", labelKey: "shell.allJourneys.ctLabel.cancelledMessage", label: "Cancelled message", def: "This registration is cancelled" },
    { id: "cnclhdr", labelKey: "shell.allJourneys.ctLabel.cancelledFlagHeader", label: "Cancelled flag header", def: "Registration cancelled" },
    { id: "cncltxt", labelKey: "shell.allJourneys.ctLabel.cancelledFlagText", label: "Cancelled flag text", def: "Your place has been released. A confirmation e-mail is on its way." },
    { id: "backev", labelKey: "shell.allJourneys.ctLabel.backToEventsButton", label: "Back-to-events button", def: "Back to the events list" },
  ] },
];

const EVENT_CT_PREFILL = {
  "events.hdr": "Upcoming events",
  "details.cta": "Reserve my place",
};

/* ═══ Kiosk — lobby entry page on a wall-mounted tablet, landscape ════════════════
   The entry screen IS the resting state: there is no separate attract loop, so anything
   personal has to be cleared on a timer rather than waiting for the next visitor to notice. */
/** The data-protection block every customer journey renders identically
 *  (appointment, walk-in, event) — see `WalkInJourney.tsx` for the same pair. */
export const CONSENT_HEADER = "Data protection policy";
/* Consent is a condition of every public event registration (CC 0434). */
const CONSENT_REQUIRED = "Please tick the box to continue.";
export const CONSENT_TEXT = "I consent to my data being used for the purposes of this booking.";

const KIOSK_DEFAULTS = Object.assign({}, DEFAULTS, {
  showHeader: true, showFooter: false, progressType: "none",
  kioskAppts: true, kioskWalkin: true, kioskEvents: true, kioskCheckin: true,
  kioskWaitTime: true, kioskNextSlot: true, kioskHours: true,
  kioskBigText: true,
  kioskGreeting: "time", kioskTileSize: "large",
  // A kiosk is bolted to one wall, so the location is a property of the unit, not a choice
  // the visitor makes. Everything it hands off to inherits it.
  kioskLocation: "l1",
  ckQr: true, ckWallet: true, ckNfc: true, ckRef: true, ckName: true,
  ckDefault: "scan",
  ckDoneDwell: 6, kioskResetAfter: 45,
  ckShowQueuePos: true,
  slug: "lobby-kiosk", embedWidthUnit: "fixed", embedWidth: 1280, embedHeight: 800,
});

const KIOSK_SECTION_KEYS = {
  general: ["showHeader", "showFooter", "brandedHeader", "brandedFooter", "kioskLocation", "kioskGreeting", "kioskResetAfter", "kioskBigText", "showMediaImages"],
  kioskentry: ["kioskAppts", "kioskWalkin", "kioskEvents", "kioskCheckin", "kioskTileSize", "kioskWaitTime", "kioskNextSlot", "kioskHours"],
  kioskcheckin: ["ckQr", "ckWallet", "ckNfc", "ckRef", "ckName", "ckDefault", "ckShowQueuePos", "ckDoneDwell"],
  customtext: [],
  launch: ["slug", "embedWidthUnit", "embedWidth", "embedHeight", "embedResize"],
};

const KIOSK_PANELS = {
  general: ["General", "How the kiosk behaves between visitors, and who it has to serve."],
  kioskentry: ["Entry screen", "The four destinations and the live information around them."],
  kioskcheckin: ["Check in", "How someone with a booking identifies themselves, and what they see afterwards."],
  customtext: ["Custom text", ""],
  launch: ["Launch & embed", ""],
};

const KIOSK_CT_GROUPS = [
  { id: "entry", labelKey: "shell.allJourneys.ctLabel.entryScreen", label: "Entry screen", fields: [
    { id: "greet", labelKey: "shell.allJourneys.ctLabel.greeting", label: "Greeting", def: "Good morning" },
    { id: "sub", labelKey: "shell.allJourneys.ctLabel.lineUnderTheGreeting", label: "Line under the greeting", def: "What do you need today?" },
    { id: "appts", labelKey: "shell.allJourneys.ctLabel.appointmentsTile", label: "Appointments tile", def: "Book an appointment" },
    { id: "apptsSub", labelKey: "shell.allJourneys.ctLabel.appointmentsTileDescription", label: "Appointments tile description", def: "Advising, financial aid, careers and more" },
    { id: "walkin", labelKey: "shell.allJourneys.ctLabel.walkInTile", label: "Walk-in tile", def: "See someone today" },
    { id: "walkinSub", labelKey: "shell.allJourneys.ctLabel.walkInTileDescription", label: "Walk-in tile description", def: "Join the walk-in line at this campus" },
    { id: "events", labelKey: "shell.allJourneys.ctLabel.eventsTile", label: "Events tile", def: "What's on" },
    { id: "eventsSub", labelKey: "shell.allJourneys.ctLabel.eventsTileDescription", label: "Events tile description", def: "Orientation, fairs and workshops" },
    { id: "ck", labelKey: "shell.allJourneys.ctLabel.checkInTile", label: "Check-in tile", def: "I'm here for my appointment" },
    { id: "ckSub", labelKey: "shell.allJourneys.ctLabel.checkInTileDescription", label: "Check-in tile description", def: "Scan your pass or find your booking" },
    { id: "page", labelKey: "shell.allJourneys.ctLabel.titleInThePurpleHeader", label: "Title in the purple header", def: "Welcome" },
  ] },
  { id: "checkin", labelKey: "shell.allJourneys.ctLabel.checkInScreen", label: "Check-in screen", fields: [
    { id: "hdr", labelKey: "shell.allJourneys.ctLabel.checkInHeading", label: "Check-in heading", def: "Let's find your booking" },
    { id: "scan", labelKey: "shell.allJourneys.ctLabel.scanInstruction", label: "Scan instruction", def: "Hold your QR code or wallet pass up to the reader" },
    { id: "refHdr", labelKey: "shell.allJourneys.ctLabel.bookingReferenceHeading", label: "Booking reference heading", def: "Enter your booking reference" },
    { id: "nameHdr", labelKey: "shell.allJourneys.ctLabel.nameSearchHeading", label: "Name search heading", def: "Find your booking by name" },
    { id: "done", labelKey: "shell.allJourneys.ctLabel.checkedInMessage", label: "Checked-in message", def: "You're checked in" },
    { id: "doneSub", labelKey: "shell.allJourneys.ctLabel.lineUnderTheCheckedInMessage", label: "Line under the checked-in message", def: "Please take a seat — someone will call your name." },
  ] },
  { id: "problems", labelKey: "shell.allJourneys.ctLabel.whenSomethingGoesWrong", label: "When something goes wrong", fields: [
    { id: "noMatch", labelKey: "shell.allJourneys.ctLabel.scanNotRecognised", label: "Scan not recognised", def: "We couldn't read that pass" },
    { id: "noMatchSub", labelKey: "shell.allJourneys.ctLabel.scanNotRecognisedWhatToDo", label: "Scan not recognised — what to do", def: "Try holding it flatter and a little closer, or find your booking by name instead." },
    { id: "already", labelKey: "shell.allJourneys.ctLabel.alreadyCheckedIn", label: "Already checked in", def: "You're already checked in" },
    { id: "cancelled", labelKey: "shell.allJourneys.ctLabel.bookingCancelled", label: "Booking cancelled", def: "This booking was cancelled" },
    { id: "noCamera", labelKey: "shell.allJourneys.ctLabel.readerUnavailable", label: "Reader unavailable", def: "The reader isn't working" },
    { id: "offline", labelKey: "shell.allJourneys.ctLabel.offline", label: "Offline", def: "This kiosk is offline" },
    { id: "offlineSub", labelKey: "shell.allJourneys.ctLabel.offlineWhatToDo", label: "Offline — what to do", def: "Please check in at the welcome desk. Your appointment is still booked." },
  ] },
];

const KIOSK_CT_PREFILL = {
  "entry.sub": "What do you need today?",
};

/* The kiosk shows back only what the check-in lookup returned for a typed code
   (lookupPublicCheckIn, already camelized by the client). There is no sample
   booking: with no lookup result the kiosk stays neutral. */
const KIOSK_ARRIVED_STATUSES = ["checked_in", "arrived", "completed"];
const KIOSK_CANCELLED_STATUSES = ["cancelled", "canceled"];
function kioskBookingFromLookup(code, found) {
  const d = found || {};
  const type = d.resultType || d.result_type || "";
  if (type === "booking_found") {
    const b = d.booking || {};
    const start = b.startUtc ? new Date(String(b.startUtc)) : null;
    return {
      ref: code, kind: "appointment", pos: 0,
      name: String(b.customerName || ""), svc: String(b.serviceName || ""), staff: String(b.staffName || ""),
      when: start && !isNaN(start.getTime()) ? friendlyCalendarWeekday(start) + " " + friendlyTime(start) : "",
      status: String(b.status || ""),
    };
  }
  if (type === "queue_found") {
    const e = d.entry || {};
    // A walk-in has no slot, so the line position is the only thing it can be told.
    return {
      ref: code, kind: "walkin", pos: Number(e.position) || 0,
      name: String(e.customerName || ""), svc: String(e.queueName || ""), staff: "", when: "",
      status: String(e.status || ""),
    };
  }
  return null;
}

/** The landing draft seed (layout defaults + prefilled custom text) for a draft mount outside this file. */
export function landingDraftSeed(): { s: Record<string, unknown>; ct: Record<string, unknown> } {
  return { s: { ...LANDING_DEFAULTS }, ct: { ...EVENT_CT_PREFILL } };
}

/**
 * The list page's header name from its first page (CC 0352, 0365): the company every row names, null
 * when the rows span companies or one names none, undefined for an empty page (nothing to decide).
 */
export function listTenantName(rows: ReadonlyArray<unknown>): string | null | undefined {
  if (!rows.length) return undefined;
  const names = rows.map((row) => { const tn = (row as { tenant?: { name?: unknown } | null } | null)?.tenant?.name; return typeof tn === "string" ? tn.trim() : ""; });
  return names.every((n) => n && n === names[0]) ? names[0] : null;
}

export class Designer extends React.Component<DesignerProps, any> {
  kioskLocationKey = "";
  /** Bumped whenever the kiosk's lookup result stops applying (reference edited, kiosk reset). */
  ckLookupGen = 0;
  dwellTimer: any; qTimer: any; _onResize: any; _onPopState: any; _catalogAbort: any; _gone = false;
  /** Unsubscribe from served public-config changes. */
  _offPublicConfig: any;
  _offTenantChrome: any;
  /** Builder default data: the tenant's newest services + the admin catalogue's
   *  locations/staff/durations, for when the journey's own catalogue is empty. */
  sampleCatalog: any = null;
  /** TRUE when the journey's own (public) catalogue came back empty in the builder. */
  publicCatalogEmpty = false;
  /** The latest availability read. An older read's success OR failure is dropped. */
  _availRequestId = 0;
  /** The availability read currently open, as `{ key, promise }`, so a second
   *  caller for the same key joins it instead of sending the request twice.
   *  Instance-local and cleared on settle — never a cache. */
  _availPending: { key: string; promise: Promise<any> } | null = null;
  /** Which read populated the wizard's catalogue: the public one always wins. */
  catalogSource: "admin" | "public" = "admin";
  /** The authored `events` block config for the current public Events Page
   *  read (or the neutral defaults) — set once per `bootstrapPublicEventsList`. */
  eventsPageBlock: EventsBlockConfig | null = null;
  eventsPagePresentation: unknown = null;
  /** The authored `skipSingleEventSlot` captured straight off the public
   *  page's `eventJourney`, NOT read back off `state.s` at click time: the
   *  selected-event load merges the event's own `EventLandingPresentation`
   *  into `s`, and a landing document is free to carry its own value for a
   *  key of the same name. The list's authored setting is the one that
   *  governs a list entry, so it is held here where nothing can overwrite it. */
  publicListSkipSingleSlot = false;
  /** Whether the CURRENT public event selection actually skipped its Event
   *  page. Reset per `selectPublicEvent` and set only once the authoritative
   *  detail has resolved, so the public rail never claims a step count the
   *  navigation has not settled on yet. */
  publicLandingStepSkipped = false;
  /** Whether the CURRENT public event selection was entered THROUGH the
   *  events list. A directly opened `/{eventId}` URL has no list
   *  behind it, so its rail must be the same three steps the Studio Event
   *  Landing Page tab shows — the `events` step only exists for a selection
   *  that actually came from the list (or a history entry that recorded it). */
  publicEventFromList = false;
  /** The canonical resolver's tenant display zone
   *  (`PortalAnonymousDocumentResponse.timeZone`) — relative `dateRange`/
   *  `pastHorizonDays` horizons resolve in THIS zone server-side
   *  (`workers/portal/src/resolve.ts` `windowFor`/`eventsWindowFor`), never
   *  the visitor's browser zone. */
  eventsPageTimeZone = "UTC";
  /** The current event's resolved landing presentation and configured
   *  registration questions — set once per `bootstrapPublicEventDetail`. */
  landingPresentation: unknown = null;
  landingIsConfigured = false;
  /** Public events/landing only: whether the published events page or landing
   *  document set the branded header/footer at all (CC, 2026-10-03). Un-authored,
   *  the tenant's Branding kit is drawn by default; an authored on/off is honoured. */
  publicBrandedAuthored = { header: false, footer: false };
  /** Public events/landing only: the tenant's public Branding theme, read before the
   *  page bootstraps so rows are projected in the tenant's colour (CC, 2026-10-03).
   *  null = not read, or the read failed (the platform default colour stays). */
  publicChrome: { brandColor?: string } | null = null;
  /** The Studio Branding record, read on mount (settleChrome): the workspace brand a journey with no colour
   *  of its own previews in, as the public pages do (2026-10-08). */
  studioChrome: { brandColor?: string } | null = null;
  /** Whether the stored landing / events documents already carry the branded
   *  chrome keys. Their live schemas are strict, so the keys are written only
   *  once an author turns one on or the document already has them. */
  landingBrandedStored = false;
  eventsBrandedStored = false;
  /** Whether the real attend public config has resolved — gates the
   *  events/landing launch/embed/share actions in STUDIO so they never act
   *  on `publicHost()`'s pre-config fallback host. Irrelevant to the public
   *  mount, which uses `window.location.origin` directly. */
  attendConfigResolved = false;
  landingRegistrationQuestions: unknown[] = [];
  /** `null` = no payment provider configured (gate paid tiers up front); a
   *  refused READ (e.g. 403) is a distinct error, kept separate below. */
  eventPaymentConfig: unknown = null;
  /** JAI-1835: the public event read's `tenant.name` (the company that runs the event), trimmed or
   *  null — never an id. Set when an event's detail loads, and on the list page from its rows when
   *  they all name one company (CC 0352); otherwise the neutral wording. */
  publicTenantName: string | null = null;
  /** The name a visitor reads: the host's prop first, else the event read's tenant name, else "". */
  tenantDisplayName(): string { return this.props.tenantName || this.publicTenantName || ""; }
  _evtListRequestId = 0;
  /** The `includePast` window the admin preview pool was last fetched under,
   *  so a later draft/toggle change can tell whether a re-read is needed. */
  _previewIncludedPast = false;
  _previewGeneration = 0;
  _evtQueryDebounce: ReturnType<typeof setTimeout> | null = null;
  _scrollSentinelObserver: IntersectionObserver | null = null;
  _scrollSentinelEl: Element | null = null;
  _eventRegIdemKey: string | null = null;
  _eventRegIdemFingerprint: string | null = null;
  /** The events catalogue this MOUNT is showing — an instance field, not a
   *  module global, so one Designer instance (e.g. an admin preview) cannot
   *  clobber another's (e.g. a concurrently-mounted public page) rows. Empty
   *  until a real read populates it; never fixture data. */
  eventsRows: DesignerEventRow[] = [];
  /** The Studio Events/Landing tabs' last-loaded baseline documents — kept so
   *  a save can preserve every field this Designer does not author (other
   *  blocks, header, locale, theme, branding, globalCss…) instead of
   *  replacing the whole document with a freshly-guessed one. `null` until
   *  loaded, or when nothing has been authored yet. */
  eventsDocBaseline: EventsDocumentDraft | null = null;
  landingDocBaseline: LandingDraft | null = null;
  eventsTabLoadState: "loading" | "ready" | "error" = "loading";
  landingTabLoadState: "loading" | "ready" | "error" = "loading";
  eventsTabSaving = false;
  landingTabSaving = false;
  set: Record<string, (v: any) => void> = {};
  actions: Record<string, any> = {};
  constructor(props: DesignerProps) {
    super(props);
    const bookingProfile = props.bookingSession && props.bookingSession.profile;
    this.state = {
      journey: "appointment",
      stash: {
        queue: { s: { ...QUEUE_DEFAULTS }, ct: { ...QUEUE_CT_PREFILL } },
        kiosk: { s: { ...KIOSK_DEFAULTS }, ct: { ...KIOSK_CT_PREFILL } },
        events: { s: { ...EVENT_DEFAULTS }, ct: { ...EVENT_CT_PREFILL } },
        landing: { s: { ...LANDING_DEFAULTS }, ct: { ...EVENT_CT_PREFILL } },
      },
      evt: "e1", evtPage: 1, evtCat: "all", tickets: 1, evtQuery: "", evtPicked: false, evtFrom: "", evtTo: "",
      evtDetailState: "idle", evtListState: "idle", evtPaymentState: "idle", selectedTicketId: null, registeredEventId: null, memberPay: null,
      s: { ...DEFAULTS },
      ct: {},
      section: props.initialSection || "general",
      previewStep: "service",
      device: props.device || "desktop",
      // Preview dialog (harness only): whether it is open, and the width it draws at.
      pvOpen: false,
      pvDevice: "desktop",
      // Manual zoom (%) for the in-editor preview pane and the preview dialog,
      // independent so opening the dialog doesn't disturb the pane's zoom.
      // 100 = the device's real CSS size; use when a tall/wide device frame
      // doesn't fit the visible pane at 100%. (2026-09-28, CD)
      paneZoom: 100,
      pvZoom: 100,
      // "wide" | "mid" | "phone" — set by initResize on mount, before first paint.
      layout: "wide",
      formOpen: true,
      ctFilter: "all",
      ckMethod: "scan",
      ckRefInput: "",
      ckNameInput: "",
      ckFound: "", ckBooking: null, ckLooking: false, ckLookedUp: "",
      ckKeys: false,
      dwellLeft: null,
      ckProblem: "",
      pinnedLoc: "",
      bigText: false,
      openGroups: { service: true },
      svc: "aid",
      dur: "d1",
      loc: "l1",
      desk: "", party: 1,
      day: this.iso(this.today()),
      // JAI-2135: false until the visitor picks a day. Until then the public
      // landing day is the anchor's civil today (initDay), never this browser date.
      dayChosen: false,
      calMonth: { y: this.today().getFullYear(), m: this.today().getMonth() },
      slot: "",
      staff: "priya",
      cat: "all",
      consent: false,
      /* A submit or Join was refused for the consent box; its red message shows until ticked. */
      consentTried: false,
      // Appointment reminder choice: "" until the customer changes a checkbox, so
      // the authored notifDefault applies until then (appointment-reminder-preference).
      notifChoice: "", notifChoiceRefused: false,
      // The eligibility key (slug|service|horizon) the loaded window answers.
      availFor: "",
      openHours: {},
      geoState: "idle", locQuery: "", locShown: 0, stripWeek: 0, custTz: "",
      email: String((bookingProfile && bookingProfile.email) || ""), emailTouched: false,
      phone: String((bookingProfile && bookingProfile.phone) || ""), phoneTouched: false, phoneCountry: "US", countryMax: false,
      first: String((bookingProfile && bookingProfile.firstName) || ""), firstTouched: false,
      last: String((bookingProfile && bookingProfile.lastName) || ""), lastTouched: false,
      answers: {}, answersTouched: false,
      addr: "",
      attRows: [], attSeq: 0,
      upFiles: [], upError: "", calMenu: false, calPicked: "",
      cancelAsking: false, cancelled: false, cancelRegError: "",
      qPos: 4, qFree: 1, qServed: 0, leaveAsking: false, leftQueue: false, soldOutPreview: false, blockOpen: {}, faqOpen: {}, wlDone: false, wlName: "", wlEmail: "",
      // Custom-layout canvas: what is selected, the open rail tab, and the
      // undo/redo stacks (layout history only — never `ct` or settings). There
      // is deliberately NO `clDevice`: the canvas authors whichever device the
      // toolbar's existing DevicePreviewToggle (`state.device`) is set to, so
      // one control drives both the preview width and the grid.
      clSel: "", clPast: [], clFuture: [], clTab: "add",
      dirty: false, saved: {}, published: {}, saveNote: "", previewOnly: false,
      // Appointments seam (drop 1): the admin journey this tab writes to, and the
      // server's typed refusal when it says no. Never cleared by a later local edit
      // — only by the next save attempt, so the refusal cannot go quiet on its own.
      apptJourneyId: null, apptJourney: null, apptBaseline: null, refusedFields: [], saveNotices: [],
      /* Scan policy per journey type (CD scan docket, C) — the MANAGE SETTINGS
         document, read and written on its own, keyed by journey type so switching
         tabs does not show one type's setting under another's name. */
      scanByType: {},
      // Walk-Ins seam: the queue journey this tab writes to, the author's own
      // queue selection, the tenant's real queues to pick from, and the
      // canonical public read of the selected ones (what the preview draws).
      // Every one of these starts EMPTY — the preview shows an empty state
      // until a real read lands, never a fixture desk.
      wiJourneyId: null, wiJourney: null, wiBaseline: null,
      wiQueueIds: [], wiQueueOptions: [], wiQueues: [], wiQueuesLoading: false, wiPreviewStep: "service",
      // Builder default data (2026-09-28): TRUE while the builder is drawing the
      // tenant's newest records because the journey has none of its own.
      apptSample: false, wiSample: false,
    };

    // A ?preview=<journey> tab is a customer view, so it is resolved before the first render.
    let previewJourney = "";
    // Kiosk location — a property of the physical unit, arriving as /kiosk?location={external_key}
    // (operator, 2026-09-03: the record holds no location). Never a seed id.
    try { this.kioskLocationKey = new URLSearchParams(window.location.search).get("location") || ""; } catch (err) { this.kioskLocationKey = ""; }
    try { previewJourney = new URLSearchParams(window.location.search).get("preview") || ""; } catch (err) {}
    if (previewJourney && ["appointment", "queue", "events", "landing"].indexOf(previewJourney) > -1) {
      const seed = previewJourney === "queue" ? { s: { ...QUEUE_DEFAULTS }, ct: { ...QUEUE_CT_PREFILL } }
        : previewJourney === "events" ? { s: { ...EVENT_DEFAULTS }, ct: { ...EVENT_CT_PREFILL } }
        : previewJourney === "landing" ? { s: { ...LANDING_DEFAULTS }, ct: { ...EVENT_CT_PREFILL } }
        : { s: { ...DEFAULTS }, ct: {} };
      this.state = Object.assign({}, this.state, {
        previewOnly: true, formOpen: false, journey: previewJourney,
        s: seed.s, ct: seed.ct,
        previewStep: previewJourney === "landing" ? "landing" : previewJourney === "events" ? "events" : "service",
      });
    }
    /* Draft mount (Preview dialog). Deliberately BEFORE the public branch and
       mutually exclusive with it: the caller hands over the page it is already
       showing, so there is nothing to fetch and nothing that can fail to
       settle. `mode` is not "public" on this path, so no public bootstrap runs. */
    if (this.props.previewJourney && this.props.draft) {
      const pj = this.props.previewJourney;
      if (Array.isArray(this.props.draftRows)) this.eventsRows = this.props.draftRows as typeof this.eventsRows;
      this.state = Object.assign({}, this.state, {
        previewOnly: true, formOpen: false, journey: pj,
        // The preview dialog's draft mount runs no reads of its own, so the
        // queues the editor is drawing (attached or sample) are handed in.
        ...(this.props.draftWalkIn ? { wiQueues: this.props.draftWalkIn.queues || [], wiQueueIds: this.props.draftWalkIn.queueIds || [], wiSample: !!this.props.draftWalkIn.sample } : {}),
        s: { ...this.props.draft.s }, ct: { ...this.props.draft.ct },
        ...(this.props.draftEvent ? { evt: this.props.draftEvent } : {}),
        previewStep: this.props.draftStep || (pj === "landing" ? "landing" : pj === "events" ? "events" : "service"),
      });
    }
    if (this.props.mode === "public") {
      // Before the published config arrives there is nothing true to draw, and
      // the prototype's arrays are what would otherwise be drawn. A customer
      // must never see another tenant's services, even for one frame.
      SERVICES = []; SERVICE_IMAGES = {}; LOCATIONS = []; STAFF = []; DURATIONS = []; this.eventsRows = [];
      // Default preserved exactly: an omitted `journey` prop still renders the
      // appointment page, unchanged from before this prop existed.
      const publicJourney = this.props.journey || "appointment";
      this.state = Object.assign({}, this.state, {
        previewOnly: true, formOpen: false, journey: publicJourney,
        previewStep: publicJourney === "landing" ? "landing" : publicJourney === "events" ? "events" : "service",
      });
    }
    /* Open on the first journey the tenant composes BEFORE the first render (2026-10-20): the
       same transition setJourney makes, so the opening tab's defaults, section and preview step
       come with it. componentDidMount used to draw Appointments first and switch, and its
       bootstrap reads still saw the Appointments tab. */
    if (!this.isPublic() && !this.props.previewJourney && !this.journeyEnabled(this.state.journey)) {
      const opening = this.journeyTransition(this.state, Designer.firstJourney(this.props.modules));
      if (opening) this.state = Object.assign({}, this.state, opening);
    }
    this.set = {};
    Object.keys(ALL_DEFAULTS).forEach((k) => {
      this.set[k] = (v) => {
        let val = v;
        if (val && typeof val === "object" && val.target) val = val.target.value;
        if (typeof ALL_DEFAULTS[k] === "number") {
          const n = parseInt(val, 10);
          val = isNaN(n) ? 0 : n;
          // Bounded keys are clamped HERE rather than trusted from the input:
          // `min`/`max` attributes do not stop a typed or pasted value.
          const bound = NUMBER_BOUNDS[k];
          if (bound) val = Math.max(bound[0], Math.min(bound[1], val));
        }
        this.setState((st) => {
          const next = { s: { ...st.s, [k]: val }, dirty: true };
          if (k === "initialCount" || k === "loadMore") next.locShown = 0;
          return next;
        });
      };
    });
    this.actions = {
      noop: () => {},
      stripPrev: () => this.setState((prev) => ({ stripWeek: Math.max(0, prev.stripWeek - 1) })),
      stripNext: () => this.setState((prev) => ({ stripWeek: prev.stripWeek + 1 })),
      moreLocations: () => this.setState((prev) => {
        const batch = Math.max(1, prev.s.initialCount || 1);
        const total = this.locsForService(prev.svc).length;
        return { locShown: Math.min(total, Math.max(1, prev.locShown || batch) + batch) };
      }),
      setLocQuery: (v) => this.setState({ locQuery: v && v.target ? v.target.value : v }),
      useMyLocation: () => {
        // The events/landing static host's Permissions-Policy sets
        // `geolocation=()` — the browser refuses this call outright there, so
        // the affordance is disabled rather than offered and left to always
        // fail as "denied".
        if (this.state.journey === "events" || this.state.journey === "landing") { this.setState({ geoState: "unsupported" }); return; }
        if (!navigator.geolocation) { this.setState({ geoState: "unsupported" }); return; }
        this.setState({ geoState: "locating" });
        navigator.geolocation.getCurrentPosition(
          () => this.setState({ geoState: "done", locQuery: "Current location" }),
          () => this.setState({ geoState: "denied" }),
          { timeout: 8000 }
        );
      },
      setJourney: (j, pinFrom) => this.setState((st) => this.journeyTransition(st, j, pinFrom)),
      copyEmbed: () => {
        // Events/landing embed/share/launch must not act on `publicHost()`'s
        // pre-config fallback host — suppressed until the real attend
        // config has actually resolved.

        if (!this.isPublic() && (this.state.journey === "events" || this.state.journey === "landing") && !this.attendConfigResolved) return;

        const cs = this.state.s;
        const code = this.embedSnippet(cs, this.embedTitle());
        if (!code) return; // No public address yet: nothing to copy, no "Copied" flash.
        try { if (navigator.clipboard) navigator.clipboard.writeText(code); } catch (err) {}
        this.setState({ copiedEmbed: true });
        setTimeout(() => this.setState({ copiedEmbed: false }), 1600);
      },
      evList: () => this.set.eventDisplay("list"),
      evGrid: () => this.set.eventDisplay("grid"),
      pagePages: () => this.set.eventPaging("pages"),
      pageLoadMore: () => this.set.eventPaging("loadmore"),
      pageScroll: () => this.set.eventPaging("scroll"),
      setEvtPage: (p) => { if (this.isPublic()) this.goToPublicEventsPage(p); else this.setState({ evtPage: p }); },
      setEvtFrom: (v) => {
        const value = v || "";
        if (this.isPublic()) this.refreshPublicEventsListFromFilters({ evtFrom: value });
        else this.setState({ evtFrom: value, evtPage: 1 });
      },
      setEvtTo: (v) => {
        const value = v || "";
        if (this.isPublic()) this.refreshPublicEventsListFromFilters({ evtTo: value });
        else this.setState({ evtTo: value, evtPage: 1 });
      },
      setEvtQuery: (v) => {
        const value = v && v.target ? v.target.value : v;
        if (this.isPublic()) {
          this.setState({ evtQuery: value, evtPicked: false });
          this.debounceEvtQuery(value);
          return;
        }
        this.setState({ evtQuery: value, evtPicked: false, evtPage: 1 });
      },
      pickEvent: (id, name) => {
        this.setState({ evtQuery: name, evtPicked: true, evtPage: 1 });
        if (this.isPublic()) this.selectPublicEvent(id, { fromList: true });
        else {
          // Same landing/details routing as a card click, off the row's own
          // raw record; with no matching row there is nothing to decide on,
          // so the selection alone stands.
          const found = this.eventsRows.filter((row) => row.id === id)[0];
          this.setState(found ? this.eventsRailStepForSelection(found) : { evt: id });
        }
      },
      setEvtCat: (c) => {
        if (this.isPublic()) this.refreshPublicEventsListFromFilters({ evtCat: c });
        else this.setState({ evtCat: c, evtPage: 1 });
      },
      setEvtLocationFilter: (locationId) => {
        if (this.isPublic()) this.refreshPublicEventsListFromFilters({ evtLocationFilter: locationId });
        else this.setState({ evtLocationFilter: locationId, evtPage: 1 });
      },
      // The category chips filter the tenant's own service catalogue, so the
      // selection has to survive a filter change: keep the current service when it
      // still matches, otherwise fall to the first service the new category offers,
      // and re-derive the location (and clear the slot) from that service.
      setServiceCategory: (category) => this.setState((prev) => {
        const selected = serviceFilterId(category);
        const current = SERVICES.filter((service) => service.id === prev.svc)[0];
        const nextService = current && (selected === "all" || serviceMatchesCategory(current, selected))
          ? current
          : SERVICES.filter((service) => serviceMatchesCategory(service, selected))[0];
        if (!nextService) return { cat: selected || "all", svc: "", slot: "" };
        const offeredLocations = this.locsForService(nextService.id);
        const locationStillOffered = offeredLocations.some((location) => location.id === prev.loc);
        return {
          cat: selected || "all",
          svc: nextService.id,
          loc: locationStillOffered ? prev.loc : (offeredLocations.length === 1 ? offeredLocations[0].id : ""),
          slot: "",
        };
      }),
      moreEvents: () => { if (this.isPublic()) this.loadMorePublicEvents(); else this.setState((st) => ({ evtPage: st.evtPage + 1 })); },
      // Authoring preview only; the customer page's Reschedule opens the manage page (CC 0010).
      reschedule: () => { if (this.isPublic()) return; this.setState({ previewStep: "datetime", section: "datetime", slot: "", calMenu: false }); },
      errStaff: () => this.setState({ previewStep: "datetime", section: "datetime", slot: "" }),
      errServices: () => this.setState({ previewStep: "service", section: "service" }),
      errStart: () => {
        /* Unknown address: the workspace's booking start, the host's root. */
        if (this.state.linkNotFound && this.isPublic()) { try { window.location.assign("/"); } catch (err) { /* no DOM */ } return; }
        /* A personal link that cannot book: the workspace's standard journey, offered only on an open link. */
        const pOff = this.state.personal && this.state.personal.off ? this.state.personal : null;
        if (pOff && this.isPublic()) { if (pOff.otherUrl) { try { window.location.assign(pOff.otherUrl); } catch (err) { /* no DOM */ } } return; }
        this.setState((prev) => ({ previewStep: this.steps()[0], section: this.sectionForStep(this.steps()[0]) }));
      },
      lpRegister: () => this.setState({ previewStep: "details", section: "details" }),
      // Feeds the SAME shared first/last/email the registration submit reads
      // (`joinWaitlist` → `submitEventRegistration`), not a second, separate
      // set of fields the submit never looks at. A one-word name sets only
      // `first`; `last` stays blank rather than being invented as a copy of
      // it, so the shared "first/last/email required" validation asks for
      // the rest instead of silently duplicating a name.
      setWlName: (v) => {
        const value = v && v.target ? v.target.value : v;
        const trimmed = String(value || "").trim();
        const spaceAt = trimmed.indexOf(" ");
        this.setState({
          wlName: value,
          first: spaceAt > -1 ? trimmed.slice(0, spaceAt) : trimmed,
          last: spaceAt > -1 ? trimmed.slice(spaceAt + 1).trim() : "",
        });
      },
      setWlEmail: (v) => {
        const value = v && v.target ? v.target.value : v;
        this.setState({ wlEmail: value, email: value });
      },
      joinWaitlist: () => this.joinWaitlist(),
      toggleFaq: (i) => this.setState((prev) => ({ faqOpen: { ...prev.faqOpen, [i]: !prev.faqOpen[i] } })),
      removeRow: (key, i) => this.setState((prev) => {
        const rows = (prev.s[key] || []).slice();
        rows.splice(i, 1);
        return { s: { ...prev.s, [key]: rows }, dirty: true };
      }),
      addRow: (key, blank) => this.setState((prev) => ({ s: { ...prev.s, [key]: (prev.s[key] || []).concat([blank]) }, dirty: true })),
      addDoc: () => this.actions.addRow("requiredDocs", ""),
      setDoc: (i, v) => this.setState((prev) => {
        const rows = (prev.s.requiredDocs || []).slice();
        rows[i] = v;
        return { s: { ...prev.s, requiredDocs: rows }, dirty: true };
      }),
      removeDoc: (i) => this.setState((prev) => ({
        s: { ...prev.s, requiredDocs: (prev.s.requiredDocs || []).filter((_, k) => k !== i) }, dirty: true,
      })),
      addAgenda: () => this.actions.addRow("lpAgendaRows", { time: "", what: "" }),
      addSpeaker: () => this.actions.addRow("lpSpeakerRows", { name: "", role: "", photo: "" }),
      addIncluded: () => this.actions.addRow("lpIncludedRows", ""),
      addFaq: () => this.actions.addRow("lpFaqRows", { q: "", a: "" }),
      addSponsor: () => this.actions.addRow("lpSponsorRows", { name: "", tier: "Supporter", logo: "" }),
      setKey: (k, v) => this.setState((prev) => ({ s: { ...prev.s, [k]: v }, dirty: true })),
      tplBanner: () => this.pickTemplate("banner"),
      tplHero: () => this.pickTemplate("hero"),
      tplSplit: () => this.pickTemplate("split"),
      tplLong: () => this.pickTemplate("long"),
      /* The canvas keeps `landingTemplate` at whatever built-in was last
         chosen — see the note on `customTpl` — and turns the canvas on. A
         first visit seeds the blank page: title and register locked in. */
      tplCustom: () => this.setState((prev) => ({
        s: {
          ...prev.s,
          customTpl: true,
          customBlocks: (prev.s.customBlocks || []).length ? prev.s.customBlocks : blankCustomLayout(),
        },
        clSel: "", clPast: [], clFuture: [],
        dirty: true,
      })),
      clSetTab: (t: string) => this.setState({ clTab: t }),
      clAddTabPick: () => this.setState({ clTab: "add" }),
      clBlockTabPick: () => this.setState({ clTab: "block" }),
      /* Clicking a block on the canvas opens its settings: the rail follows the
         selection rather than making you find the tab yourself. Clearing the
         selection (a click on empty grid) goes back to Add, which is the only
         tab with anything to show. */
      clSelect: (uid: string) => this.setState({ clSel: uid, clTab: uid ? "block" : "add" }),
      clChange: (blocks: CustomBlock[], note: string) => this.setState((prev) => ({
        s: { ...prev.s, customBlocks: blocks },
        clPast: (prev.clPast || []).concat([{ blocks: prev.s.customBlocks || [], note }]).slice(-60),
        clFuture: [],
        dirty: true,
      })),
      clAdd: (type: string) => this.setState((prev) => {
        const device: DeviceId = (prev.device || "desktop") as DeviceId;
        const current: CustomBlock[] = prev.s.customBlocks || [];
        const block = makeBlock(type, { c: 0, r: nextFreeRow(current, device) }, DEVICE_COLS[device]);
        const next = placeWithPush(current.concat([block]), device, block.uid, {});
        return {
          s: { ...prev.s, customBlocks: next },
          clPast: (prev.clPast || []).concat([{ blocks: current, note: "Added a block" }]).slice(-60),
          clFuture: [], clSel: block.uid, dirty: true,
        };
      }),
      clUndo: () => {
        if (!(this.state.clPast || []).length) return;
        this.setState((prev) => {
          const past = prev.clPast || [];
          const last = past[past.length - 1];
          return {
            s: { ...prev.s, customBlocks: last.blocks },
            clPast: past.slice(0, -1),
            clFuture: [{ blocks: prev.s.customBlocks || [], note: last.note }].concat(prev.clFuture || []),
            dirty: true,
          };
        });
      },
      clRedo: () => {
        if (!(this.state.clFuture || []).length) return;
        this.setState((prev) => {
          const future = prev.clFuture || [];
          const next = future[0];
          return {
            s: { ...prev.s, customBlocks: next.blocks },
            clPast: (prev.clPast || []).concat([{ blocks: prev.s.customBlocks || [], note: next.note }]).slice(-60),
            clFuture: future.slice(1),
            dirty: true,
          };
        });
      },
      setHeroHeight: (v) => this.actions.setKey("heroHeight", v),
      setHeroTreatment: (v) => this.actions.setKey("heroTreatment", v),
      setStickyBar: (v) => this.actions.setKey("stickyBar", !!v),
      setCoverImage: (v) => this.actions.setKey("coverImage", v),
      soldWaitlist: () => this.actions.setKey("soldOutMode", "waitlist"),
      soldRelated: () => this.actions.setKey("soldOutMode", "related"),
      soldBoth: () => this.actions.setKey("soldOutMode", "both"),
      setSoldOutPreview: (on) => this.setState({ soldOutPreview: !!on }),
      // "Keep past events on the list" changes the WINDOW the rows are read
      // under, not a client-side filter — so it has to re-read, exactly
      // like the other window-changing controls do.
      setShowPastEvents: (on) => this.setState(
        (prev) => ({ s: { ...prev.s, showPastEvents: !!on }, dirty: true }),
        () => {
          if (!this.isPublic()) { this.refreshPublicEventsPreview(); return; }
          // Public reads take the window off the resolved block, not `state.s`
          // — keep the two in step so the re-read is a genuine one.
          this.eventsPageBlock = { ...(this.eventsPageBlock || NEUTRAL_EVENTS_BLOCK_CONFIG), includePast: !!on };
          this.refreshPublicEventsList(1);
        },
      ),
      togglePane: () => this.setState((prev) => ({ formOpen: !prev.formOpen })),
      setDescText: (v) => this.actions.setKey("lpDescText", v && v.target ? v.target.value : v),
      setShareChannels: (vals) => this.actions.setKey("lpShareChannels", vals || []),
      setSponsorTiers: (on) => this.actions.setKey("sponsorTiers", !!on),
      setRelatedRule: (v) => this.actions.setKey("relatedRule", v),
      setRelatedCount: (v) => this.actions.setKey("relatedCount", v),
      setRelatedManual: (vals) => this.actions.setKey("relatedManual", vals || []),
      moveBlock: (id, dir) => this.setState((prev) => {
        const order = moveVisibleLandingBlock(prev.s.blockOrder || [], id, dir);
        if (!order) return null;
        return { s: { ...prev.s, blockOrder: order }, dirty: true };
      }),
      toggleBlock: (id) => this.setState((prev) => {
        const on = (prev.s.blocksOn || []).slice();
        const i = on.indexOf(id);
        if (i > -1) on.splice(i, 1); else on.push(id);
        return { s: { ...prev.s, blocksOn: on }, dirty: true };
      }),
      // Walk-Ins preview step nav. Controlled here so the designer's own step
      // list stays in charge of what is on screen; the customer page owns its
      // own step in the public mount.
      /* The component reporting its own move updates BOTH sides (2026-09-17):
         `wiPreviewStep` is what the page reads, `previewStep` is what the rail
         highlight and the toolbar caption read. Updating only the first left
         the caption saying "Your info" while the page showed the service step
         after the details card's "Change" — the mirror of the bug where the
         rail moved the caption and not the page. `ticket` is the walk-in name
         for the rail's `confirmation`. */
      setWalkInStep: (step) => this.setState({
        wiPreviewStep: step,
        previewStep: step === "ticket" ? "confirmation" : step,
        section: this.sectionForStep(step === "ticket" ? "confirmation" : step),
        wiLeft: false,
        leftQueue: false,
      }),
      toggleWalkInQueue: (id) => this.toggleWalkInQueue(id),
      askLeave: () => this.setState({ leaveAsking: true }),
      dismissLeave: () => this.setState({ leaveAsking: false }),
      doLeave: () => this.setState({ leaveAsking: false, leftQueue: true }),
      rejoinQueue: () => this.setState({ leftQueue: false, qPos: 6, qFree: 1, previewStep: this.steps()[0] }),
      bookInstead: () => this.setState((prev) => ({
        leftQueue: false, journey: "appointment",
        stash: { ...prev.stash, queue: { s: prev.s, ct: prev.ct } },
        s: (prev.stash.appointment && prev.stash.appointment.s) || { ...DEFAULTS },
        ct: (prev.stash.appointment && prev.stash.appointment.ct) || {},
        section: "general", previewStep: "service",
        notifChoice: "", notifChoiceRefused: false,
      })),
      backToEvents: () => {
        // Invalidate any in-flight detail/related load: returning to the list
        // must not be overwritten later by a stale detail response (or its
        // history marker) for the event we just left.
        this._evtDetailRequestId = (this._evtDetailRequestId || 0) + 1;
        this.publicLandingStepSkipped = false;
        this.publicEventFromList = false;
        if (this.isPublic()) {
          this.pushPublicEventsListHistory();
          // Direct-detail entry mounted with `journey: "landing"` — this
          // must actually SWITCH to the list journey (not just flip
          // `previewStep`) and re-bootstrap it, or `isEvents()` stays false,
          // `usingServerEvents` in `renderVals` stays false, and the render
          // falls back to client-side filtering over whatever detail/
          // related rows are still sitting in `eventsRows`.
          this.setState({ cancelled: false, journey: "events", previewStep: "events", section: "events", evtQuery: "", evtPicked: false }, () => this.bootstrapPublicEventsList());
          return;
        }
        this.setState({ cancelled: false, previewStep: "events", section: "events", evtQuery: "", evtPicked: false });
      },
      // The control reads "Show event details step"; the wire field it
      // persists is still `skipSingleEventSlot` (SKIP), so the value is
      // inverted here rather than renaming the stored contract. Turning the
      // step off while it is the previewed step would leave the Studio
      // sidebar selection pointing at a step that no longer exists, so the
      // preview moves on to Your info in the same update.
      setShowEventDetailsStep: (v) => {
        let val: any = v;
        if (val && typeof val === "object" && val.target) val = val.target.checked;
        const show = Boolean(val);
        this.setState((st) => {
          const next: any = { s: { ...st.s, skipSingleEventSlot: !show }, dirty: true };
          if (!show && st.previewStep === "landing" && this.isEvents()) { next.previewStep = "details"; next.section = "details"; }
          return next;
        });
      },
      askCancel: () => this.setState({ cancelAsking: true, calMenu: false }),
      dismissCancel: () => this.setState({ cancelAsking: false }),
      // Appointments cancel through the real appointment cancellation path
      // this dialog already wires elsewhere. Events/Landing have NO public
      // cancellation operation (the JSON registration contract declares
      // none) — this must not locally fabricate a cancelled state for those.
      doCancel: () => {
        if (this.state.journey === "events" || this.state.journey === "landing") {
          /* The public cancel op (KP, 2026-09-17): POST
             /v1/public/events/{eventId}/registrations/{ref}/cancel with THIS
             registration's own reference. `cancelled` is set only from the
             server's answer; a refusal stays on the page. The designer preview
             has no registration to cancel and shows the cancelled state as before. */
          /* WHICH REFERENCE THE ROUTE TAKES (docket 2026-09-18 item 8). The
             backend matches `{ref}` against the REGISTRATION'S OWN ID — the
             value returned as `registration.id` / `registration_ref` — and not
             against the displayed "JRNI-XXXXXXXX" order reference, which
             answers 404 "Registration not found". `orderRef` was tried FIRST,
             so on a real tenant this called the route with the one value it
             refuses. The display reference stays last, as a value better than
             nothing when the response carried no id at all. */
          const b = this.state.booking;
          const ref = String(
            (b && ((b.registration && b.registration.id) || b.registration_ref || b.orderRef)) || "",
          );
          const eventId = String(this.state.registeredEventId || "");
          /* The DESIGNER PREVIEW draws the cancelled state locally, as it draws
             every other state: nothing is registered there to cancel. */
          if (!this.isPublic()) { this.setState({ cancelAsking: false, cancelled: true }); return; }
          if (!ref || !eventId) {
            /* ⛔ NO LOCAL "cancelled" ON A PUBLIC PAGE (docket 2026-09-18b item
               7a). A missing reference or event id used to flip the card to
               cancelled with no server call — a real registration left standing
               behind a screen that says it is gone, which is the fake success
               the mandate forbids. It now says the cancellation did not
               happen. */
            /* i18n: suggested key `journeys.cancelRegUnavailable` — left literal;
               catalog updates arrive from the platform (docket). */
            this.setState({ cancelAsking: false, cancelRegError: "This registration could not be cancelled here. Contact the organiser to cancel it." });
            return;
          }
          this.setState({ cancelAsking: false, submitting: true, cancelRegError: "" });
          cancelEventRegistration(eventId, ref).then(() => {
            if (this._gone) return;
            this.setState({ submitting: false, cancelled: true });
          }).catch((err) => {
            if (this._gone) return;
            this.setState({ submitting: false, cancelRegError: String((err && err.message) || err) });
          });
          return;
        }
        // Customer page: no local-only cancel (CC 0010). The manage page does the real one.
        if (this.isPublic()) { this.setState({ cancelAsking: false }); return; }
        this.setState({ cancelAsking: false, cancelled: true });
      },
      bookAgain: () => this.setState((prev) => ({
        cancelled: false, previewStep: this.steps()[0], slot: prev.slot, calPicked: "",
        notifChoice: "", notifChoiceRefused: false,
      })),
      toggleCal: () => this.setState((prev) => ({ calMenu: !prev.calMenu })),
      pickCal: (id) => this.setState({ calMenu: false, calPicked: id }),
      addFiles: (list) => this.setState((prev) => {
        const max = prev.s.maxAttachments || 5;
        const room = Math.max(0, max - (prev.upFiles || []).length);
        const OK = /\.(png|jpe?g|docx?|pdf|gdoc)$/i;
        const incoming = Array.from(list || []);
        const good = incoming.filter((f) => OK.test(f.name) || f.type === "application/vnd.google-apps.document");
        const rejected = incoming.length - good.length;
        const taken = good.slice(0, room);
        const over = good.length - taken.length;
        const notes = [];
        if (rejected > 0) notes.push(rejected + (rejected === 1 ? " file was" : " files were") + " not a supported type.");
        if (over > 0) notes.push("Only " + max + " files can be attached.");
        return {
          upFiles: (prev.upFiles || []).concat(taken.map((f) => ({ id: f.name + f.size + f.lastModified, file: f, progress: 1 }))),
          upError: notes.join(" "),
        };
      }),
      removeFile: (id) => this.setState((prev) => ({ upFiles: (prev.upFiles || []).filter((f) => f.id !== id), upError: "" })),
      addAttendee: () => this.setState((prev) => {
        const id = (prev.attSeq || 0) + 1;
        /* `company` is part of the attendee object the registration routes
           accept (CD docket 2026-09-15 item 1.4) and was missing from this
           shape; optional on the wire, so it starts empty rather than absent. */
        return { attSeq: id, attRows: (prev.attRows || []).concat([{ id, first: "", last: "", email: "", phone: "", country: "US", company: "", touched: {} }]) };
      }),
      attSet: (id, key, v) => this.setState((prev) => ({
        attRows: (prev.attRows || []).map((r) => (r.id === id ? Object.assign({}, r, { [key]: v && v.target ? v.target.value : v }) : r)),
      })),
      attBlur: (id, key) => this.setState((prev) => ({
        attRows: (prev.attRows || []).map((r) => (r.id === id ? Object.assign({}, r, { touched: Object.assign({}, r.touched, { [key]: true }) }) : r)),
      })),
      attRemove: (id) => this.setState((prev) => ({ attRows: (prev.attRows || []).filter((r) => r.id !== id) })),
      setParty: (v) => {
        const raw = v && typeof v === "object" && v.target ? v.target.value : v;
        const n = parseInt(raw, 10);
        this.setState((st) => ({ party: Math.max(1, Math.min(st.s.maxParty || 1, isNaN(n) ? 1 : n)) }));
      },
      setTickets: (v) => {
        // Multiple places is Coming soon on the event journeys — pin the count
        // at one there, whatever the input or a stale saved setting says.
        if (this.isEvents() || this.isLanding()) { this.setState({ tickets: 1 }); return; }
        const raw = v && typeof v === "object" && v.target ? v.target.value : v;
        const n = parseInt(raw, 10);
        this.setState((st) => {
          const e = this.eventsRows.filter((x) => x.id === st.evt)[0] || this.eventsRows[0] || EMPTY_EVENT;
          const cap = e.availability && e.availability.soldOut ? 0 : (e.maxPer || 1);
          return { tickets: Math.max(1, Math.min(cap, isNaN(n) ? 1 : n)) };
        });
      },
      setSelectedTicketId: (v) => {
        const id = v && v.target ? v.target.value : v;
        if (id && this.tierMemberLocked(id)) { this.setState({ bookingError: "This ticket is for members only." }); return; }
        /* Signed out, a member ticket can't be picked (CC 0264). */
        { const ev0 = this.currentEventRow(); const tt = (((ev0 && ev0.raw && ev0.raw.ticketTiers) || []) as { id: string; audience?: string }[]).find((x) => x.id === id);
          if (tt && this.tierNeedsSignIn(tt)) { this.setState({ bookingError: "Sign in to use a member ticket." }); return; } }
        /* A member ticket is for the member alone (CC 0259): guests added before are dropped, and said so. */
        const dropGuests = this.isMemberLaneTicket(id) && (this.state.attRows || []).length > 0;
        this.setState({ selectedTicketId: id, bookingError: dropGuests ? "Member tickets are for you only; guests need their own ticket." : "", ...(dropGuests ? { attRows: [] } : {}) });
      },
      waitExact: () => this.set.waitDisplay("exact"),
      waitRange: () => this.set.waitDisplay("range"),
      waitPosition: () => this.set.waitDisplay("position"),
      goSection: (id) => this.goSection(id),
      save: () => {
        const st = this.state;
        if (PD_STUB_ONLY[st.journey]) {
          this.setState({ saveNote: "error", saveError: PD_STUB_REASON });
          return;
        }
        // The Appointments tab writes through the CONTRACT (whitelist-only patch to
        // updateAdminJourney). Events and Landing now write through their own
        // real contracts (below); Walk-Ins and Kiosk stay on the page-designer
        // stub until their contracts land.
        if (st.journey === "appointment") { this.saveAppointments(false); return; }
        if (st.journey === "queue") { this.saveWalkIns(false); return; }
        if (st.journey === "events") { this.saveEventsTab(false); return; }
        if (st.journey === "landing") { this.saveLandingTab(false); return; }
        putPageDesigner(this.props.tenant, st.journey, { s: st.s, ct: st.ct, slug: String(st.s.slug || st.journey) })
          .then(() => this.setState((prev) => ({
            dirty: false,
            saved: { ...prev.saved, [prev.journey]: { s: prev.s, ct: prev.ct, at: new Date() } },
            saveNote: "saved", saveError: "",
          })))
          .then(() => this.saveToast(true, false))
          .catch((err) => this.failSave(err, false));
      },
      publish: () => {
        const st = this.state;
        const j = st.journey;
        if (PD_STUB_ONLY[j]) {
          this.setState({ saveNote: "error", saveError: PD_STUB_REASON });
          return;
        }
        if (j === "appointment") { this.saveAppointments(true); return; }
        if (j === "queue") { this.saveWalkIns(true); return; }
        if (j === "events") { this.saveEventsTab(true); return; }
        if (j === "landing") { this.saveLandingTab(true); return; }
        putPageDesigner(this.props.tenant, j, { s: st.s, ct: st.ct, slug: String(st.s.slug || j) })
          .then(() => publishPageDesigner(this.props.tenant, j))
          .then(() => this.setState((prev) => ({
            dirty: false,
            saved: { ...prev.saved, [prev.journey]: { s: prev.s, ct: prev.ct, at: new Date() } },
            published: { ...prev.published, [prev.journey]: true },
            saveNote: "published", saveError: "",
          })))
          .then(() => this.saveToast(true, true))
          .catch((err) => this.failSave(err, true));
      },
      openPreview: () => {
        // In the harness the public host is unreachable, so the same page opens
        // in a dialog. Everywhere else Preview is a new tab, unchanged.
        if (previewInDialog()) { this.setState({ pvOpen: true }); return; }
        if ((this.state.journey === "events" || this.state.journey === "landing") && !this.attendConfigResolved) return;
        try {
          // The one journey you are editing, at its own public link.
          const url = this.publicLink();
          if (url) window.open(url, "_blank");
        } catch (err) {}
      },
      closePreviewDialog: () => this.setState({ pvOpen: false }),
      setPvDevice: (d) => this.setState({ pvDevice: d }),
      setPvZoom: (z: number) => this.setState({ pvZoom: Math.max(50, Math.min(150, z)) }),
      setPaneZoom: (z: number) => this.setState({ paneZoom: Math.max(50, Math.min(150, z)) }),
      openPreviewTab: () => {
        if ((this.state.journey === "events" || this.state.journey === "landing") && !this.attendConfigResolved) return;
        try { const url = this.publicLink(); if (url) window.open(url, "_blank"); } catch (err) {}
      },
      toggleForm: () => this.setState((st) => ({ formOpen: !st.formOpen })),
      /* The phone layout's collapsed rail. One picker, two id spaces; the
         handler map is rebuilt every render in renderVals. */
      goNav: (v) => { const f = this._navHandlers && this._navHandlers[v]; if (f) f(); },
      /* Settings / Preview switch for the mid and phone layouts, where only one
         of the two panes is on screen at a time. */
      setPane: (v) => this.setState({ formOpen: v !== "preview" }),
      setDevice: (d) => this.setState({ device: d }),
      setCtFilter: (v) => this.setState({ ctFilter: v }),
      setCkMethod: (v) => this.setState({ ckMethod: v, ckProblem: "" }),
      setCkRef: (e) => this.setState({ ckRefInput: (e && e.target ? e.target.value : e || "").toUpperCase(), ...this.clearedCkLookup() }),
      setCkName: (e) => this.setState({ ckNameInput: e && e.target ? e.target.value : e || "" }),
      openKeys: () => this.setState({ ckKeys: true }),
      closeKeys: () => this.setState({ ckKeys: false }),
      keyPress: (ch) => this.setState((st) => (st.ckMethod === "name"
        ? { ckNameInput: (st.ckNameInput || "") + ch }
        : { ckRefInput: ((st.ckRefInput || "") + ch).toUpperCase(), ...this.clearedCkLookup() })),
      keyBack: () => this.setState((st) => (st.ckMethod === "name"
        ? { ckNameInput: (st.ckNameInput || "").slice(0, -1) }
        : { ckRefInput: (st.ckRefInput || "").slice(0, -1), ...this.clearedCkLookup() })),
      keyClear: () => this.setState((st) => (st.ckMethod === "name" ? { ckNameInput: "" } : { ckRefInput: "", ...this.clearedCkLookup() })),
      keySpace: () => this.setState((st) => (st.ckMethod === "name" ? { ckNameInput: (st.ckNameInput || "") + " " } : null)),
      setBigText: (v) => this.setState({ bigText: !!v }),
      kioskGo: (dest) => {
        if (dest === "checkin") { this.setState({ journey: "kiosk", previewStep: "kioskCheckin", section: "kioskcheckin", ckProblem: "", formOpen: true }); return; }
        // The three booking destinations hand off to the journey that owns them.
        const j = dest === "walkin" ? "queue" : dest === "events" ? "events" : "appointment";
        const byKey = this.kioskLocationKey ? (LOCATIONS.filter((l) => (l.external_key || l.externalCode || "") === this.kioskLocationKey)[0] || null) : null;
        this.actions.setJourney(j, (byKey && byKey.id) || this.state.s.kioskLocation || "l1");
      },
      kioskLookupRef: () => {
        // Read-only: the lookup never changes a booking. It shows back exactly what the
        // check-in service returned for the typed code, and nothing when it returned nothing.
        const code = String(this.state.ckRefInput || "").trim().toUpperCase();
        if (!code) return;
        // A response is applied only while it is still the latest lookup AND the field still
        // holds the code it was made for; editing the reference or resetting drops it.
        const gen = (this.ckLookupGen += 1);
        const current = () => !this._gone && gen === this.ckLookupGen
          && String(this.state.ckRefInput || "").trim().toUpperCase() === code;
        this.setState({ ckLooking: true, ckLookedUp: "", ckBooking: null, ckFound: "", ckProblem: "" });
        lookupPublicCheckIn(code)
          .then((found) => {
            if (!current()) return;
            const booking = kioskBookingFromLookup(code, found);
            this.setState({ ckLooking: false, ckLookedUp: code, ckBooking: booking, ckFound: booking ? code : "" });
          })
          .catch((err) => {
            if (!current()) return;
            const errCode = String(err && (err.code || (err.body && err.body.error && err.body.error.code) || "") || "");
            const msg = String(err && err.message || "");
            const status = err && (err.status || (err.response && err.response.status)) || 0;
            // No seam at all (network down / route unbound) is the kiosk's OFFLINE state.
            const offline = /network|failed to fetch|offline|route_unbound/i.test(errCode + msg) || status === 501;
            // Anything else — an invalid or unknown code included — is simply no match.
            this.setState({ ckLooking: false, ckLookedUp: code, ckBooking: null, ckFound: "", ckProblem: offline ? "offline" : "" });
          });
      },
      kioskCheckInNow: () => {
        // This renderer is only ever the designer's preview (nothing mounts a live kiosk
        // from it), so it NEVER performs the check-in transition: an arrival posted from a
        // preview would be indistinguishable from a real one in the tenant's data. The
        // outcome shown follows the looked-up booking's own status, when there is one.
        const b = this.state.ckBooking;
        if (b && KIOSK_CANCELLED_STATUSES.indexOf(b.status) > -1) { this.setState({ ckProblem: "cancelled" }); return; }
        if (b && KIOSK_ARRIVED_STATUSES.indexOf(b.status) > -1) { this.setState({ ckProblem: "already" }); return; }
        this.setState({ previewStep: "kioskDone", ckProblem: "", ckKeys: false, dwellLeft: this.state.s.ckDoneDwell || 6 });
      },
      kioskPick: (ref) => this.setState({ ckFound: ref, ckProblem: "" }),
      kioskProblem: (p) => {
        // Each failure state previews on its own. A booking is shown back under it only when
        // a real lookup found one — never a sample customer.
        this.setState({ previewStep: "kioskCheckin", section: "kioskcheckin", ckProblem: p, formOpen: true });
      },
      kioskReset: () => this.setState({ previewStep: "kioskEntry", section: "kioskentry", ckProblem: "", ckRefInput: "", ckNameInput: "", ...this.clearedCkLookup(), ckMethod: this.state.s.ckDefault || "scan", formOpen: true }),
      toggleBigText: () => this.setState((st) => ({ bigText: !st.bigText })),
      kioskScanFail: () => this.setState({ ckProblem: "noMatch" }),
      kioskTryName: () => this.setState({ ckProblem: "", ckMethod: this.state.s.ckName ? "name" : "ref" }),
      prevStep: () => this.moveStep(-1),
      nextStep: () => {
        // The single gate: the step you are ON must be fulfilled before the
        // journey may leave it. Was `invalidAppointmentServiceSelection()`
        // alone, which let an unanswered details form reach Confirmation.
        if (this.continueBlocked()) {
          /* A refused Continue on the details step says why (CC, 2026-10-05). It returned silently, so an
             empty form showed nothing: no field was touched, so no field message rendered, and the
             submit's own "required" message was never reached. */
          if (this.state.previewStep === "details") this.flagDetailsGaps();
          return;
        }
        const steps = this.steps();
        const leavingDetails = this.state.previewStep === "details"
          && steps[Math.min(steps.length - 1, steps.indexOf("details") + 1)] === "confirmation";
        // Preview NEVER posts. Its confirmation step renders the settings under
        // edit; a booking created from it would be indistinguishable from a
        // customer's in the tenant's own data.
        if (leavingDetails && this.isPublic()) {
          if (this.state.journey === "events" || this.state.journey === "landing") { this.submitEventRegistration(); return; }
          this.submitAppointment();
          return;
        }
        this.moveStep(1);
      },
      goStepIndex: (i) => {
        const l = this.steps();
        const target = l[Math.max(0, Math.min(l.length - 1, i))];
        if (!this.canReachStep(target)) return;
        this.setState({ previewStep: target });
      },
      serviceFirst: () => this.setState((st) => ({ s: { ...st.s, firstStep: "service" }, dirty: true })),
      locationFirst: () => this.setState((st) => ({ s: { ...st.s, firstStep: "location" }, dirty: true })),
      mapFirst: () => this.setState((st) => ({
        s: { ...st.s, firstStep: "map", showMap: true, mapHeight: st.s.mapHeight === 190 ? 340 : st.s.mapHeight },
        previewStep: "location", section: "general", dirty: true,
      })),
      startMapResize: (e) => {
        if (e.preventDefault) e.preventDefault();
        const startY = e.clientY;
        const startH = this.state.s.mapHeight;
        const move = (ev) => {
          const h = Math.max(120, Math.min(560, startH + (ev.clientY - startY)));
          this.setState((st) => ({ s: { ...st.s, mapHeight: h }, dirty: true }));
        };
        const up = () => {
          window.removeEventListener("pointermove", move);
          window.removeEventListener("pointerup", up);
          this.setState({ resizingMap: false });
        };

        window.addEventListener("pointermove", move);
        window.addEventListener("pointerup", up);
        this.setState({ resizingMap: true });
      },
      pTitles: () => this.set.progressType("titles"),
      pNumbers: () => this.set.progressType("numbers"),
      pBar: () => this.set.progressType("bar"),
      pNone: () => this.set.progressType("none"),
      dayStrip: () => this.set.calendarLayout("daystrip"),
      monthCal: () => this.set.calendarLayout("month"),
      dateToday: () => this.setState((st) => ({ s: { ...st.s, defaultDate: "today" }, day: this.iso(this.today()), calMonth: { y: this.today().getFullYear(), m: this.today().getMonth() }, dirty: true })),
      dateFirstAvail: () => {
        const iso = this.firstAvailable(this.bookWindow());
        if (!iso) { this.setState({ bookingError: i18nT("shell.allJourneys.locationTimeZoneInvalid", "This location's time zone isn't recognised. Choose another location.") }); return; }
        const d = this.fromIso(iso);
        this.setState((st) => ({ s: { ...st.s, defaultDate: "firstAvailable" }, day: iso, calMonth: { y: d.getFullYear(), m: d.getMonth() }, dirty: true, bookingError: "" }));
      },
      calPrev: () => this.setState((st) => {
        const m = st.calMonth.m - 1;
        return { calMonth: m < 0 ? { y: st.calMonth.y - 1, m: 11 } : { y: st.calMonth.y, m } };
      }),
      calNext: () => this.setState((st) => {
        const m = st.calMonth.m + 1;
        return { calMonth: m > 11 ? { y: st.calMonth.y + 1, m: 0 } : { y: st.calMonth.y, m } };
      }),
      setGrouped: (on) => this.set.slotGrouping(on ? "partofday" : "flat"),
      setDay: (d) => this.setState({ day: d, dayChosen: true, slot: this.firstSlotFor(d), urlSlotGone: false }),
      setCustTz: (z) => this.setState({ custTz: z }),
      setEmail: (v) => this.setState({ email: v && v.target ? v.target.value : v }),
      blurEmail: () => this.setState({ emailTouched: true }),
      setAddr: (v) => this.setState({ addr: v && v.target ? v.target.value : v }),
      setFirst: (v) => this.setState({ first: v && v.target ? v.target.value : v }),
      setAnswer: (qid, v) => this.setState((prev) => ({ answers: { ...(prev.answers || {}), [qid]: v && v.target ? (v.target.type === "checkbox" ? v.target.checked : v.target.value) : v } })),
      blurFirst: () => this.setState({ firstTouched: true }),
      setLast: (v) => this.setState({ last: v && v.target ? v.target.value : v }),
      blurLast: () => this.setState({ lastTouched: true }),
      setCountries: (vals) => {
        const list = vals || [];
        if (list.length > 10) { this.setState({ countryMax: true }); return; }
        this.setState({ countryMax: false });
        this.set.customCountries(list);
      },
      setPhoneCountry: (iso) => this.setState({ phoneCountry: iso }),
      setPhone: (v) => this.setState({ phone: v && v.target ? v.target.value : v, phoneServerError: "" }),
      blurPhone: () => this.setState({ phoneTouched: true }),
      // The picker's value is the slot's selection key (see slotEntriesIn).
      setSlot: (key) => this.setState({ slot: key, reconfirmFrom: null, urlSlotGone: false }),
      setDateValue: () => {},
      setConsent: (v) => this.setState({ consent: v }),
      // Reminder checkboxes. Only notifChoice changes; the authored s.notifDefault
      // is read, never written. Unchecking the last channel is refused.
      setNotifEmail: (checked) => this.setState((prev) => {
        const r = toggleReminderChannel(effectiveReminderPreference(prev.notifChoice, this.reminderDefault(prev)), "email", checked);
        /* The phone refusal stays while the choice is still text only (a refused untick, CC 0333). */
        return { notifChoice: r.preference, notifChoiceRefused: r.refused, notifServerError: "", ...(r.preference !== "text" ? { phoneServerError: "" } : {}) };
      }),
      setNotifText: (checked) => this.setState((prev) => {
        const r = toggleReminderChannel(effectiveReminderPreference(prev.notifChoice, this.reminderDefault(prev)), "text", checked);
        return { notifChoice: r.preference, notifChoiceRefused: r.refused, notifServerError: "", ...(r.preference !== "text" ? { phoneServerError: "" } : {}), ...(checked ? {} : { smsPhoneNeeded: false }) };
      }),
    };
  }

  componentDidMount() {
    // StrictMode (and any mount/unmount/remount cycle) reuses this SAME
    // instance: `componentWillUnmount` set `_gone = true` on the first
    // unmount, and without resetting it here every async completion from
    // the real, currently-mounted instance would be discarded as if it were
    // stale. Reset it, and bump the request-generation counters so any
    // completion still in flight from the FIRST mount (which raced past this
    // reset) is still recognized as stale against the new generation, rather
    // than racing the fresh requests this mount is about to issue.
    this._gone = false;
    this._evtListRequestId = (this._evtListRequestId || 0) + 1;
    this.syncTemplateController();
    /* Open on the first journey the tenant composes (CC, 2026-10-03), through setJourney so its
       defaults, section and preview step come with it. */
    if (!this.isPublic() && !this.props.previewJourney && !this.journeyEnabled(this.state.journey)) this.actions.setJourney(Designer.firstJourney(this.props.modules));
    if (this.isPublic()) {
      if ((this.state.journey === "events" || this.state.journey === "landing") && !this.props.brandColor) {
        /* No host-supplied colour (the standalone attend page): take it from the tenant's
           Branding theme before the rows are projected (CC, 2026-10-03). One cached read; a
           failure resolves to null, the default colour stays, and the page still loads. The
           mount generation guards a StrictMode remount from bootstrapping twice. */
        const journey = this.state.journey;
        const generation = this._evtListRequestId;
        loadTenantChrome(false).then((c) => { this.publicChrome = c; }).finally(() => {
          if (this._gone || generation !== this._evtListRequestId) return;
          if (journey === "events") this.bootstrapPublicEventsList(); else this.bootstrapPublicEventDetail();
        });
      } else if (this.state.journey === "events") this.bootstrapPublicEventsList();
      else if (this.state.journey === "landing") this.bootstrapPublicEventDetail();
      else this.bootstrapPublic();
      if (this.state.journey === "events" || this.state.journey === "landing") {
        this._onPopState = () => this.handlePublicEventsPopState();
        try { window.addEventListener("popstate", this._onPopState); } catch {}
      }
      this.initResize();
      return;
    }
    /* A DRAFT PREVIEW MOUNT BOOTSTRAPS NOTHING (platform review, 2026-09-19). It
       is not `mode="public"`, so it used to fall through to the EDITOR bootstrap
       below: a second full set of admin reads per open, the module-level preview
       catalogue reassigned under the outer editor, and — a round trip later —
       the handed-in draft overwritten by the last SAVED document, under a badge
       that says "What's on the page now". It draws what it was handed. */
    if (this.props.previewJourney && this.props.draft) {
      this.initResize();
      return;
    }
    /* The capability flags arrive with the tenant's served public config, after
       first paint; re-render when they land so a declared surface is not
       withheld until the next interaction. */
    this._offPublicConfig = onPublicConfigChange(() => { if (!this._gone) this.forceUpdate(); });
    /* Branded chrome: settle the Branding read once so a failure can fall back
       to the basic chrome; a Branding save re-reads. */
    const settleChrome = () => loadTenantChrome(!this.isPublic()).then((c) => { if (!this.isPublic()) this.studioChrome = c; if (!this._gone) this.forceUpdate(); });
    settleChrome();
    this._offTenantChrome = onTenantChromeChange(settleChrome);
    // Records — one per journey per tenant. The saved draft seeds each journey's stash so
    // switching tabs shows what was last saved; published flags light the status pill.
    listPageDesigner(this.props.tenant).then((env) => {
      this.setState((prev) => {
        const stash = { ...prev.stash };
        const published = { ...prev.published };
        // Baseline for the discard-on-leave rule: what the server last saved for each journey.
        const saved = { ...prev.saved };
        let s = prev.s, ct = prev.ct;
        for (const j of Object.keys(env.records)) {

          const r = env.records[j];
          if (!r) continue;

          // The appointment record is a localStorage stub; the server's journey is the
          // truth for this tab and hydrates it below. Never seed the form from the stub.
          // Events and Landing are ALSO real contracts now (`loadEventsTabDraft`/
          // `loadLandingTabDraft`, called separately below) — this stub read/write
          // loop must not race them or overwrite their config/publication flags.
          //
          // WALK-INS joins them (2026-09-11). `queue` now writes through the typed
          // admin journey door (`hydrateWalkIns` -> `adoptWalkInJourney`), and both
          // reads land asynchronously: whichever resolved LAST won, so a stale
          // browser-local stub from an earlier session could overwrite the settings
          // the server had just returned — an initial-load race with a visibly
          // wrong form. The stub is no longer read for it, and no longer written
          // (see `save`/`publish`), so the server row is the only source.
          if (j === "appointment" || j === "queue" || j === "events" || j === "landing") continue;
          if (j === prev.journey) { s = { ...s, ...r.s }; ct = { ...ct, ...r.ct }; }
          else stash[j] = { s: { ...(this.journeyDefaults(j).s), ...r.s }, ct: { ...(this.journeyDefaults(j).ct), ...r.ct } };
          saved[j] = { s: { ...(this.journeyDefaults(j).s), ...r.s }, ct: { ...(this.journeyDefaults(j).ct), ...r.ct }, at: r.updated_at ? new Date(r.updated_at) : new Date() };
          if (r.published_at && !r.active_to) published[j] = true;
        }
        return { stash, published, saved, s, ct };
      });
    }).catch(() => { /* first run — nothing saved yet */ });
    // The preview describes THIS tenant: services, locations and staff come from
    // the same choke points their own surfaces read. Whatever a read does not
    // return keeps its placeholder, so the preview always draws.
    this._catalogAbort = typeof AbortController === "function" ? new AbortController() : null;
    loadPreviewCatalog(this._catalogAbort ? this._catalogAbort.signal : undefined).then((cat) => {
      if (this._gone) return;
      // The public catalogue is what a customer is offered, so once it has been
      // adopted the admin read must not overwrite it -- otherwise the preview
      // shows rows the customer page does not, decided by whichever request
      // happened to finish last.
      if (this.catalogSource === "public" && !this.publicCatalogEmpty) return;
      /* Builder default data (2026-09-28): the admin read is the whole tenant
         catalogue, never this journey's own, so the builder draws only the 3
         newest services from it, labelled as sample. Kept aside so an empty
         journey catalogue that lands later can fall back to it. */
      const sampleServices = this.isPublic() ? cat.services : (cat.sampleServices || newestFirst(cat.services, SAMPLE_COUNT));
      this.sampleCatalog = { services: sampleServices, locations: cat.locations, staff: cat.staff, durations: cat.durations };
      // Assigned WHOLESALE, including empty. These used to be applied only when
      // non-empty, so a refused or unauthenticated read left the prototype's
      // demo services, branches and advisers on screen looking like this
      // tenant's own data.
      LOCATIONS = cat.locations;
      SERVICES = sampleServices;
      // The admin preview rows carry no image; a later public read supplies them.
      SERVICE_IMAGES = catalogServiceImages(sampleServices);
      STAFF = cat.staff;
      DURATIONS = cat.durations;
      // A selection naming a row this tenant does not have would render blank.
      this.setState((prev) => ({
        apptSample: !this.isPublic() && sampleServices.length > 0,
        svc: SERVICES.filter((x) => x.id === prev.svc)[0] ? prev.svc : (SERVICES[0] && SERVICES[0].id) || prev.svc,
        loc: this.catalogLocation(prev),
        staff: STAFF.filter((x) => x.id === prev.staff)[0] ? prev.staff : (STAFF[0] && STAFF[0].id) || prev.staff,
        dur: DURATIONS.filter((x) => x.id === prev.dur)[0] ? prev.dur : (DURATIONS[0] && DURATIONS[0].id) || prev.dur,
        // A category filter naming a category this catalogue no longer returns would hide every service.
        cat: prev.journey === "appointment" ? (serviceCategoryOptions(SERVICES).some((option) => option.id === serviceFilterId(prev.cat)) ? serviceFilterId(prev.cat) : "all") : prev.cat,
      }));
    });

    // Which admin journey the Appointments tab patches. Resolved from the list op,
    // never a baked id; a failure here only means the tab reports it on first save.
    // ONE read path, list first. The remembered pin is passed to the resolve
    // as a preference rather than read on its own: a pin whose journey is gone
    // is then simply not matched, and the row that IS matched always carries
    // the address fields `adoptJourney` fills the detail read's gaps from.
    // Not asked when the tenant does not compose booking: the Appointments tab is hidden there,
    // and its reads only answer 403/503.
    if (this.journeyEnabled("appointment")) {
      resolveAppointmentJourney(String(this.state.s.slug || ""), this.rememberedJourneyId())
        .then((row) => (row && this.ownsJourney(row)
          ? loadAppointmentJourney(String(row.id)).then((journey) => ({ row, journey }))
          : null))
        .then((got) => { if (got) this.adoptJourney(got.journey, true, got.row); })
        .catch((err) => this.failSave(err, false));
    }
    // Which admin journey the Walk-Ins tab patches, and the tenant's real
    // queues to attach. Same shape as the appointment resolve above: from the
    // list op, never a baked id, and a failure only means the tab reports it
    // on first save.
    this.hydrateWalkIns();
    /* The scan block for the tab that is open. Keyed by journey type and asked
       for once — a refusal is terminal until the author retries. The opening tab
       is already a composed one (constructor); the check keeps it that way. */
    if (this.journeyEnabled(this.state.journey)) this.hydrateScan();
    // WALK-INS NO LONGER RUNS A TIMER. The old preview decremented the queue
    // position on a four-second interval with `Math.random()`, which is a
    // designer preview inventing a live queue: the number moved while nobody
    // was served. The Walk-Ins preview now draws a clearly-labelled sample
    // ticket that does not move, and a real customer's position comes from
    // `getPublicQueuePosition`.
    // The checked-in screen returns to the entry screen on its own, so the count is real:
    // it ticks down and actually navigates, rather than describing a delay that never happens.
    this.dwellTimer = setInterval(() => {
      if (!this.isKiosk() || this.state.previewStep !== "kioskDone") return;
      this.setState((prev) => {
        const left = (prev.dwellLeft == null ? (prev.s.ckDoneDwell || 6) : prev.dwellLeft) - 1;
        if (left <= 0) {
          return { previewStep: "kioskEntry", section: "kioskentry", dwellLeft: null,
            ckProblem: "", ckRefInput: "", ckNameInput: "", ckKeys: false };
        }
        return { dwellLeft: left };
      });
    }, 1000);
    // Event List / Event Landing documents and their preview rows: only for a tenant that
    // composes events (both tabs are hidden otherwise).
    if (this.journeyEnabled("events")) {
      this.loadEventsTabDraft();
      this.loadLandingTabDraft();
      this.refreshPublicEventsPreview();
    }
    ensurePublicConfig().then((cfg) => { if (!this._gone) { this.attendConfigResolved = Boolean(cfg); this.setState({}); } });
    this.initResize();
    this.initDay();
  }

  /**
   * Admin-preview rows for the Events List/Landing tabs — the tenant's OWN
   * real, live catalogue (`getPublicEvents`), never the removed fixtures and
   * never left empty. Loaded once on mount (like `loadPreviewCatalog` for the
   * Appointments tab) and used by both tabs' preview panes regardless of
   * which one is currently active, so switching tabs never re-shows stale or
   * fixture rows.
   */
  refreshPublicEventsPreview() {
    if (this.isPublic()) return;
    // "Keep past events on the list" (`showPastEvents` ↔ the block's
    // `includePast`) decides whether the preview pool may contain completed
    // events at all — `upcoming: true` strips them server-side, so a
    // hard-coded `true` here meant the toggle could never show anything.
    const includePast = Boolean((this.state.journey === "events" ? this.state.s : this.state.stash?.events?.s)?.showPastEvents);
    // Request identity: a later refresh (past-events toggled, a filter
    // changed, a draft hydrated) always wins. An older in-flight walk that
    // resolves afterwards is dropped whole rather than overwriting the newer
    // rows/facets — and `_previewIncludedPast` is only committed by the walk
    // that actually lands, so the flag can never describe rows it did not
    // produce.
    const generation = (this._previewGeneration = (this._previewGeneration || 0) + 1);
    const stale = () => this._gone || generation !== this._previewGeneration;
    // The preview's own pickers, Location facet, related-events pool and
    // "N events are live" count all read `eventsRows`, so an arbitrary first
    // page is not a catalogue — walk every page the response says exists.
    // `getPublicEvents` returns `pagination: { page, limit, total, total_pages,
    // hasMore }`, the same shape `refreshPublicEventsList` already paginates
    // on, so the terminator is the server's own fact, not a guess.
    const PREVIEW_PAGE_LIMIT = 100;
    const PREVIEW_MAX_PAGES = 20;
    const walk = (page, rowsSoFar, firstFacets) => loadPublicEventsList({
      page, limit: PREVIEW_PAGE_LIMIT, upcoming: !includePast, includeSoldOut: true, includeNonBookable: true,
    }).then((result) => {
      if (stale()) return null;
      const rows = rowsSoFar.concat(result.data.map((row) => projectPublicEventToDesignerRow(row, this.publicBrandColor())));
      const facets = firstFacets || result.facets;
      const pg = result.pagination;
      const totalPages = pg && pg.total_pages ? Math.max(1, pg.total_pages) : page;
      const more = pg ? (pg.hasMore === true || page < totalPages) : false;
      if (more && result.data.length > 0 && page < PREVIEW_MAX_PAGES) return walk(page + 1, rows, facets);
      return { rows, facets };
    });

    walk(1, [], null)
      .then((loaded) => {
        if (!loaded || stale()) return;
        this._previewIncludedPast = includePast;
        this.eventsRows = loaded.rows;
        // Same read door, same facet payload: the preview's Location filter is
        // populated from the server's real {id,name} catalogue, exactly as the
        // public list is, and the Category chips now read the same facets.
        // Landing default (2026-09-28): with no event picked, the newest-created
        // event that has a landing page, else the newest event.
        const withLanding = this.eventsRows.filter((e) => e.raw && e.raw.landing_page_id);
        const newestEvt = newestFirst(withLanding.length ? withLanding : this.eventsRows, 1, (e) => createdTime(e.raw))[0];
        this.setState((prev) => ({ evt: this.eventsRows.find((e) => e.id === prev.evt) ? prev.evt : (newestEvt?.id || prev.evt), evtFacets: loaded.facets }));
      })
      .catch(() => { /* the tab's own empty-state renders honestly; no fixture fallback */ });
  }

  componentWillUnmount() {
    this._gone = true;
    if (this._offTplCtrl) { this._offTplCtrl(); this._offTplCtrl = null; }
    // `_gone` is reset on a StrictMode remount of this same instance, so an
    // availability read still in flight from before the unmount is also made
    // stale by id — it can never land on the remounted page.
    this._availRequestId = (this._availRequestId || 0) + 1;
    if (this._catalogAbort) this._catalogAbort.abort();
    if (this.qTimer) clearInterval(this.qTimer);
    if (this.dwellTimer) clearInterval(this.dwellTimer);
    if (this._onResize) window.removeEventListener("resize", this._onResize);
    if (this._onPopState) { try { window.removeEventListener("popstate", this._onPopState); } catch {} }
    if (this._evtQueryDebounce) clearTimeout(this._evtQueryDebounce);
    if (this._scrollSentinelObserver) { this._scrollSentinelObserver.disconnect(); this._scrollSentinelObserver = null; }
    if (this._offPublicConfig) { this._offPublicConfig(); this._offPublicConfig = null; }
    if (this._offTenantChrome) { this._offTenantChrome(); this._offTenantChrome = null; }
  }

  /** The port carried TWO componentDidMount / componentWillUnmount / initDay trios;
   *  the later definitions shadowed the earlier ones, so the resize listener was never
   *  attached and the intervals were never cleared. One of each now, and the two
   *  distinct initDay bodies live under their own names. */
  initResize() {
    this._onResize = () => {
      const w = window.innerWidth;
      /* THREE layouts, not an on/off. The old rule was a single `narrow` flag at
         1100px that CLOSED the settings pane and did nothing else, so between
         roughly 700px and 1100px the designer was a preview with no settings,
         and below the three panes' own minimums (236 + 340 + 440) the row just
         scrolled sideways — the whole screen shifted under the operator instead
         of reflowing (operator, 2026-09-14). */
      const layout = designerLayoutFor(w);
      const automaticDevice = this.isPublic() && !this.props.device
        ? (w < 640 ? "mobile" : w < 1024 ? "tablet" : "desktop")
        : null;
      this.setState((st) => {
        const patch = {};
        if (st.layout !== layout) {
          Object.assign(patch, { layout, narrow: layout !== "wide" });
          // Coming back to the wide layout re-opens the settings pane, because
          // there is room for both. Narrowing keeps whichever pane the operator
          // was reading rather than snapping them to one of them.
          if (layout === "wide") patch.formOpen = true;
          else if (st.layout === "wide") patch.formOpen = true;
        }
        if (automaticDevice && st.device !== automaticDevice) Object.assign(patch, { device: automaticDevice });
        return Object.keys(patch).length ? patch : null;
      });
    };
    this._onResize();
    window.addEventListener("resize", this._onResize);
  }

  isPublic() { return this.props.mode === "public"; }

  /** Builder default data for Walk-ins (2026-09-28, KP): when the page's queues
   *  offer no services, the same 3 sample services Appointments uses, served
   *  through one sample desk so the walk-in page renders them exactly as a
   *  queue's own. Builder/preview only; never on the customer page. */
  walkInServiceSample(): any[] | null {
    if (this.isPublic()) return null;
    const reads = this.state.wiQueues || [];
    if (reads.some((r) => r && r.id === "__sample__")) return null;
    if (reads.some((r) => serviceOptionsOf(r && r.view).length)) return null;
    const svcs = (this.sampleCatalog && this.sampleCatalog.services) || [];
    return svcs.length ? svcs : null;
  }

  walkInPreviewQueues(): any[] {
    const reads = this.state.wiQueues || [];
    const svcs = this.walkInServiceSample();
    if (!svcs) return reads;
    const minutes = (d) => { const m = /(\d+)/.exec(String(d || "")); return m ? Number(m[1]) : 0; };
    return [{
      id: "__sample__",
      error: "",
      status: { canAcceptEntries: true, storeOpen: true, nextOpenTime: "", nextOpenAt: "", timezone: "", waiting: 0, estWaitMins: 0 },
      view: { queue: {
        id: "__sample__", name: i18nT("shell.allJourneys.sampleLine", "Sample line"), status: "open",
        serviceOptions: svcs.map((x) => ({ id: x.id, name: x.name, category: x.cat || "", image: SERVICE_IMAGES[x.id] || x.image || "", durationMinutes: minutes(x.dur), description: x.desc || "", questions: [] })),
      } },
    }];
  }

  /** The builder's label for default sample data; "" on the customer page or when the journey shows its own. */
  sampleNote(): string {
    if (this.isPublic()) return "";
    const j = this.state.journey;
    if (j === "appointment" && this.state.apptSample) return SERVICES.some((x) => x.draft) ? i18nT("shell.allJourneys.sampleNoteServicesWithDrafts", "Sample: your 3 newest services, drafts included. Customers only see published services.") : i18nT("shell.allJourneys.sampleNoteServices", "Sample: your 3 newest services. This page has none of its own yet.");
    if (j === "queue" && this.walkInServiceSample()) return i18nT("shell.allJourneys.sampleNoteWalkInServices", "Sample: your 3 newest services, drafts included. Customers see the services your queues offer.");
    if (j === "queue" && this.state.wiSample) return i18nT("shell.allJourneys.sampleNoteQueues", "Sample: your 3 newest queues. Attach one in General.");
    if (j === "events") return i18nT("shell.allJourneys.sampleNoteEvents", "Showing your 3 newest events.");
    return "";
  }

  /** Clears the kiosk's lookup result and invalidates any lookup still in flight, so a booking
   *  found for one reference can never be checked in under another. */
  clearedCkLookup() {
    this.ckLookupGen += 1;
    return { ckBooking: null, ckFound: "", ckLookedUp: "", ckLooking: false };
  }

  /** Reservation workflow (2026-10-02): the chosen (or only) tier of the current event needs staff approval. */
  selectedTierNeedsApproval(): boolean {
    const evt = this.currentEventRow();
    const tiers = ((evt && evt.raw && evt.raw.ticketTiers) || []) as { id: string; requiresApproval?: boolean }[];
    const id = this.state.selectedTicketId || (tiers.length === 1 ? tiers[0].id : "");
    return !!tiers.find((x) => x.id === id && x.requiresApproval);
  }
  /** Whether one tier of an event has no seat left, from the same read the seat limit uses
   *  (the snake row's sold_out / spots_remaining first, then the camel tier's soldOut /
   *  capacity - sold). Unknown capacity is not full. */
  tierIsFull(evt, tierId): boolean {
    const raw = (evt && evt.raw) || null;
    if (!raw || !tierId) return false;
    const snake = (raw.ticket_tiers || []).filter((t) => t.id === tierId)[0];
    if (snake) return snake.sold_out === true || (snake.spots_remaining != null && Number(snake.spots_remaining) <= 0);
    const tier = (raw.ticketTiers || []).filter((t) => t.id === tierId)[0];
    if (!tier) return false;
    if (tier.soldOut === true) return true;
    const cap = Number(tier.capacity);
    return Number.isFinite(cap) && cap > 0 && cap - Number(tier.sold || 0) <= 0;
  }
  /** Does a full ticket take its waitlist now (CC 0342)? With availability read, the ticket's own
   *  waitlistOpen decides; an older server that does not send it falls back to the event's flag.
   *  Without availability (the designer's preview), the designer setting. */
  tierWaitlistOpen(evt, tierId): boolean {
    const av = (evt && evt.availability) || null;
    if (!av) return Boolean(this.state.s.waitlist);
    const tier = ((evt && evt.raw && evt.raw.ticketTiers) || []).filter((t) => t.id === tierId)[0];
    if (tier && typeof tier.waitlistOpen === "boolean") return tier.waitlistOpen === true;
    return av.waitlistOpen === true;
  }
  /** The full tickets whose own waitlist takes a guest now (CC 0346). Read from the projection only. */
  waitlistTiers(evt) {
    const tiers = (evt && evt.raw && Array.isArray(evt.raw.ticketTiers)) ? evt.raw.ticketTiers : [];
    return tiers.filter((t) => t && t.waitlistOpen === true && this.tierIsFull(evt, t.id));
  }
  /** A sold-out event offers its waitlist when the event's own flag is open OR any ticket's is (CC 0346). */
  eventWaitlistOpen(evt) {
    const av = (evt && evt.availability) || null;
    if (!av) return true;
    return av.waitlistOpen === true || this.waitlistTiers(evt).length > 0;
  }
  /** The chosen (or only) tier is full and its waitlist takes a guest: submitting joins it (CC 0235 §4, 0342). */
  selectedTierJoinsWaitlist(): boolean {
    const evt = this.currentEventRow();
    const tiers = ((evt && evt.raw && evt.raw.ticketTiers) || []) as { id: string }[];
    const id = this.state.selectedTicketId || (tiers.length === 1 ? tiers[0].id : "");
    if (!id || !this.tierIsFull(evt, id)) return false;
    return this.tierWaitlistOpen(evt, id);
  }
  /** The price beside the event page's Reserve button and in its sticky bar (CC 0272). A selected
   *  (or only) ticket shows its own price, "Free" only when it is free. With none selected:
   *  "Free" when every ticket is free, else "From" the event's lowest price. An unknown price
   *  hides the line. */
  eventPriceLine(evt): string {
    const tiers = ((evt && evt.raw && evt.raw.ticketTiers) || []) as { id: string; priceMinor?: number; currency?: string | null }[];
    const id = this.state.selectedTicketId || (tiers.length === 1 ? tiers[0].id : "");
    const chosen = id ? tiers.find((x) => x.id === id) : undefined;
    if (chosen) {
      if (typeof chosen.priceMinor !== "number" || !Number.isFinite(chosen.priceMinor)) return "";
      const own = formatEventPrice({ amountMinor: chosen.priceMinor, currency: chosen.currency ?? null });
      return own === "Price unavailable" ? "" : own;
    }
    if (tiers.length && tiers.every((x) => x.priceMinor === 0)) return "Free";
    /* Free and paid tickets, none chosen (CC 0284): "Free – <lowest paid>", never "From Free". Paid tiers
       in more than one currency have no single price to name, so just "Free" then. */
    if (tiers.some((x) => x.priceMinor === 0)) {
      const paid = tiers.filter((x) => typeof x.priceMinor === "number" && x.priceMinor > 0);
      if (!paid.length || new Set(paid.map((x) => x.currency ?? null)).size > 1) return "Free";
      const low = paid.reduce((a, b) => ((b.priceMinor as number) < (a.priceMinor as number) ? b : a));
      const lowText = formatEventPrice({ amountMinor: low.priceMinor as number, currency: low.currency ?? null });
      return lowText === "Price unavailable" ? "Free" : "Free – " + lowText;
    }
    const lowest = evt ? evt.price : "";
    if (!lowest || lowest === "Price unavailable") return "";
    return tiers.length ? "From " + lowest : lowest === "Free" ? "Free" : "From " + lowest;
  }
  /* Signed in (CC 0236): the session's own read says which member tickets this customer
     may buy. Keyed by event; null until it answers, so the public read's rows stand meanwhile. */
  _sessionTiers: { eventId: string; byId: Record<string, { audience?: string; eligible: boolean; memberTierLabels: string[] }> } | null = null;
  _sessionTiersFor = "";
  maybeLoadSessionTiers() {
    const slug = this.props.bookingSessionSlug || "";
    const eventId = String(this.state.evt || "");
    if (!this.props.bookingSession || !slug || !eventId) return;
    const key = slug + "|" + eventId;
    if (this._sessionTiersFor === key) return;
    this._sessionTiersFor = key;
    loadPortalEventSessionTicketTiers(slug).then((r) => {
      if (this._gone || this._sessionTiersFor !== key) return;
      const byId: Record<string, { audience?: string; eligible: boolean; memberTierLabels: string[] }> = {};
      for (const t of (r && r.tiers) || []) byId[t.id] = { audience: t.audience, eligible: t.eligible === true, memberTierLabels: t.memberTierLabels || [] };
      this._sessionTiers = { eventId: (r && r.eventId) || eventId, byId };
      this.forceUpdate();
    }).catch(() => { if (this._sessionTiersFor === key) this._sessionTiersFor = ""; /* the public read's rows stand */ });
  }
  /** A signed-in registration answered: show the confirmation. */
  finishSessionRegistration(result: unknown, eventId: string) {
    if (this._gone) return;
    this._eventRegIdemKey = null;
    this._eventRegIdemFingerprint = null;
    const data = (result && typeof result === "object" && (result as { data?: unknown }).data) || result;
    this.setState({ submitting: false, memberPay: null, booking: data as any, bookingError: "", previewStep: "confirmation", registeredEventId: eventId });
  }
  /** A signed-in registration (or its checkout) was refused: say why, never silently. */
  failSessionRegistration(err: unknown) {
    if (this._gone) return;
    const e = (err || {}) as { code?: string; status?: number; message?: string };
    const message = String(e.message || err);
    if (e.code === "PAY_STEP_OPENED") return; // the pay step is on screen
    // A lost session lifts the lock instead of erroring over read-only
    // fields — the same rule as `finishAppointmentFailure`. The signed-in
    // door answers 404 for an expired or cleared session, and clears it.
    if ((e.code === "PORTAL_BOOKING_SESSION_REQUIRED" || e.status === 404) && this.props.onBookingSessionEnded) {
      /* After a captured payment the reference stays visible (CC 0259). */
      this.setState((prev) => ({ submitting: false, bookingError: "", memberPay: prev.memberPay && prev.memberPay.refused ? prev.memberPay : null }));
      this.props.onBookingSessionEnded(message || "Your secure session has ended.");
      return;
    }
    if (e.code === "PAY_UNAVAILABLE_SIGNED_IN") {
      this.setState({ submitting: false, bookingError: "Paying for member tickets online isn't available yet. Please contact the organiser." });
      return;
    }
    if (e.code === "CHECKOUT_CUSTOMER_FORBIDDEN") {
      this.setState({ submitting: false, bookingError: "Only the person who started this checkout can complete it. Sign in as them, or start again." });
      return;
    }
    this.setState({ submitting: false, bookingError: audienceRefusalCopy(i18nT, err) ?? message });
  }
  /** Paid (CC 0242): register the held seat with its payment. Awaited by the pay component. */
  async completeMemberPayment(payment: { paymentIntentId: string; orderAccessToken: string; provider: "paypal" | "adyen" }, openedFor?: any) {
    /* The pay step this payment was taken in (CC 0261): if it was cleared while the provider
       was finishing, the reference must still reach the screen. */
    const mp = this.state.memberPay && !this.state.memberPay.refused ? this.state.memberPay : openedFor;
    const session = this.props.bookingSession;
    if (!mp) throw new Error("pay-step-gone");
    if (!session) {
      if (!this._gone) this.setState({ submitting: false, memberPay: { ...mp, refused: { reference: payment.paymentIntentId, message: "Your secure session ended before the registration was completed." } } });
      throw new Error("session-ended");
    }
    try {
      const result = await registerPortalEventSessionOp(mp.slug, {
        ...mp.body, checkout_id: mp.checkoutId,
        /* Reached only past submitEventRegistration's consent check (CC 0434); the signed-in door now requires it (CC 0453). */
        agreedToTerms: true,
        paymentIntentId: payment.paymentIntentId, orderAccessToken: payment.orderAccessToken, paymentCredentialProvider: payment.provider,
      } as never, session.csrf);
      this.finishSessionRegistration(result, mp.eventId);
    } catch (err) {
      /* Paid, then refused (CC 0259): the pay buttons go, so a second click can't start a second
         charge, and the payment reference stays on screen for the visitor to quote. */
      const why = audienceRefusalCopy(i18nT, err) ?? String((err as { message?: string } | null)?.message || err);
      if (!this._gone) this.setState({ memberPay: { ...mp, refused: { reference: payment.paymentIntentId, message: why } } });
      this.failSessionRegistration(err);
      throw err;
    }
  }
  /** A refused paid registration locks this event's registration (CC 0261) until the visitor
   *  deliberately starts a new one, so a second press can't replay a released hold. */
  memberPayLocked(eventId?: string): boolean {
    const mp = this.state.memberPay;
    return !!(mp && mp.refused && String(mp.eventId) === String(eventId ?? this.state.evt ?? ""));
  }
  /** "Start a new registration": clears the refusal and the idempotency key, so the next
   *  submit starts a NEW hold rather than replaying the released one. */
  startNewMemberRegistration() {
    this._eventRegIdemKey = null;
    this._eventRegIdemFingerprint = null;
    clearTimeout(this._holdTimer);
    this.setState({ memberPay: null, bookingError: "", submitting: false });
  }
  /** The pay step for a paid member ticket, drawn above the submit row. */
  memberPayElement() {
    const mp = this.state.memberPay;
    if (!mp) return null;
    const box = { marginTop: "12px", padding: "12px", border: "1px solid var(--jrni-color-surface-border, #E6E8EC)", borderRadius: "10px" };
    if (mp.refused) {
      return React.createElement("div", { "data-testid": "member-pay-refused", role: "alert", style: box }, [
        React.createElement("div", { key: "h", style: { fontSize: "13px", fontWeight: 600, marginBottom: "6px" } }, "Your payment went through, but the registration wasn't completed"),
        React.createElement("div", { key: "m", style: { fontSize: "12.5px", marginBottom: "6px" } }, mp.refused.message),
        React.createElement("div", { key: "r", style: { fontSize: "12.5px" } }, "Payment reference: ", React.createElement("strong", { key: "ref", style: { fontFamily: "ui-monospace, Menlo, monospace" } }, mp.refused.reference)),
        React.createElement("div", { key: "n", style: { fontSize: "12px", color: "var(--jrni-color-text-secondary, #697180)", marginTop: "8px" } }, "Please contact the organiser and quote this reference. Don't pay again."),
        React.createElement("button", { key: "again", type: "button", "data-testid": "member-pay-start-new", onClick: () => this.startNewMemberRegistration(),
          style: { marginTop: "10px", border: "1px solid var(--jrni-color-surface-border, #E6E8EC)", background: "transparent", borderRadius: "8px", padding: "6px 12px", fontSize: "12.5px", fontWeight: 600, cursor: "pointer", fontFamily: "inherit" } }, "Start a new registration"),
      ]);
    }
    const cfg = (this.eventPaymentConfig || {}) as { paypal?: unknown };
    const head = React.createElement("div", { key: "h", style: { fontSize: "13px", fontWeight: 600, marginBottom: "8px" } }, "Pay for your member ticket");
    /* The hold's own expiry (CC 0259), in the visitor's clock. */
    const holdAt = mp.holdExpiresAt ? new Date(mp.holdExpiresAt) : null;
    /* Past the hold, no payment may start (CC 0261): it would be captured, then refused. */
    if (holdAt && !isNaN(+holdAt)) {
      const left = +holdAt - Date.now();
      if (left <= 0) {
        return React.createElement("div", { "data-testid": "member-pay-expired", role: "status", style: box }, [
          React.createElement("div", { key: "m", style: { fontSize: "12.5px" } }, "Your held seat was released. Start again to hold a new one."),
          React.createElement("button", { key: "again", type: "button", onClick: () => this.startNewMemberRegistration(),
            style: { marginTop: "10px", border: "1px solid var(--jrni-color-surface-border, #E6E8EC)", background: "transparent", borderRadius: "8px", padding: "6px 12px", fontSize: "12.5px", fontWeight: 600, cursor: "pointer", fontFamily: "inherit" } }, "Start again"),
        ]);
      }
      if (this._holdTimerFor !== mp.checkoutId) {
        this._holdTimerFor = mp.checkoutId;
        clearTimeout(this._holdTimer);
        this._holdTimer = setTimeout(() => { if (!this._gone) this.forceUpdate(); }, Math.min(left + 250, 2147483000));
      }
    }
    const holdText = holdAt && !isNaN(+holdAt)
      ? "Your seat is held for you until " + new Intl.DateTimeFormat(undefined, { hour: "numeric", minute: "2-digit" }).format(holdAt) + " while you pay."
      : "Your seat is held for you while you pay.";
    const note = React.createElement("div", { key: "n", style: { fontSize: "12px", color: "var(--jrni-color-text-secondary, #697180)", marginTop: "8px" } }, holdText);
    const pay = mp.provider === "paypal"
      ? React.createElement(PayPalCheckout, { key: "p", eventId: mp.eventId, ticketId: mp.ticketId, quantity: mp.quantity, sdkConfig: cfg.paypal as never,
          onPaid: (o: { orderId: string; orderAccessToken: string }) => this.completeMemberPayment({ paymentIntentId: o.orderId, orderAccessToken: o.orderAccessToken, provider: "paypal" }, mp) })
      : React.createElement(AdyenCheckout, { key: "a", eventId: mp.eventId, ticketId: mp.ticketId, quantity: mp.quantity,
          onPaid: (x: { sessionId: string; orderAccessToken: string }) => this.completeMemberPayment({ paymentIntentId: x.sessionId, orderAccessToken: x.orderAccessToken, provider: "adyen" }, mp) });
    return React.createElement("div", { "data-testid": "member-pay", style: box }, [head, pay, note]);
  }
  /** The signed-in member lane (CC 0259): a member ticket bought through the signed-in
   *  checkout. That lane is ONE seat, the member alone; the server refuses any other hold. */
  isMemberLaneTicket(tierId?: string | null): boolean {
    if (!tierId || !this.props.bookingSession || !this.props.bookingSessionSlug || !this.detailsLocked()) return false;
    const evRow = this.currentEventRow();
    const raw = (((evRow && evRow.raw && evRow.raw.ticketTiers) || []) as { id: string; audience?: string }[]).find((x) => x.id === tierId);
    return this.sessionTier(tierId)?.audience === "member" || raw?.audience === "member";
  }
  /** The ticket the registration will use: the picked one, or the only one. */
  memberLaneSelected(): boolean {
    const evRow = this.currentEventRow();
    const tiers = ((evRow && evRow.raw && evRow.raw.ticketTiers) || []) as { id: string }[];
    return this.isMemberLaneTicket(this.state.selectedTicketId || (tiers.length === 1 ? tiers[0].id : ""));
  }
  /** The session's view of one tier, when it has answered for this event. */
  sessionTier(tierId) {
    const st = this._sessionTiers;
    return st && st.eventId === String(this.state.evt || "") ? st.byId[tierId] || null : null;
  }
  /** A member ticket this signed-in customer doesn't qualify for: shown, not buyable. */
  tierMemberLocked(tierId): boolean {
    const t = this.sessionTier(tierId);
    return !!t && t.audience === "member" && !t.eligible;
  }
  /** A member ticket a signed-OUT visitor can't use until they sign in (CC 0264). */
  tierNeedsSignIn(t): boolean {
    return !!t && t.audience === "member" && !this.props.bookingSession;
  }
  /** The member tiers a ticket is for, from the session read or the public tier's own labels. */
  memberLabelsFor(t): string[] {
    const s = this.sessionTier(t.id)?.memberTierLabels;
    if (s && s.length) return s;
    const own = (t && (t.memberTierLabels || t.member_tier_labels)) as string[] | undefined;
    return Array.isArray(own) ? own.filter(Boolean) : [];
  }
  /** The selected event's own ticket tiers from its read — never a sample list. */
  eventTicketRows(evt) {
    /* Each full ticket follows its own waitlistOpen (CC 0339, 0342; see tierWaitlistOpen). */
    return ((evt && evt.raw && evt.raw.ticketTiers) || []).map((t) => ({
      key: t.id, name: t.name,
      /* A full tier says so before submit (CC 0235 §4, CC 0280), e.g. "Free · Full · join the waitlist". */
      price: formatEventPrice({ amountMinor: t.priceMinor, currency: t.currency })
        + (this.tierMemberLocked(t.id) || this.tierNeedsSignIn(t) ? " · Members only" + (this.memberLabelsFor(t).length ? " · " + this.memberLabelsFor(t).join(", ") : "") : "")
        + (this.tierIsFull(evt, t.id) ? (this.tierWaitlistOpen(evt, t.id) ? " · Full · join the waitlist" : " · Full") : ""),
      disabled: this.tierMemberLocked(t.id) || this.tierNeedsSignIn(t),
      /* Signed out: a member ticket can't be picked; the row says how to use it. */
      note: this.tierNeedsSignIn(t) ? (t.desc ? t.desc + " " : "") + "Sign in to use a member ticket." : t.desc || "",
      // The row the guest will register with: the picked tier, or the only one.
      selected: this.state.selectedTicketId ? this.state.selectedTicketId === t.id : ((evt.raw.ticketTiers as unknown[]).length === 1),
    }));
  }

  /**
   * The preview draws the catalogue a CUSTOMER would be offered, from the same
   * public read the standalone page uses.
   *
   * The tab still loads the tenant's full admin catalogue for its own pickers;
   * what changed is that the wizard no longer renders from it. Two surfaces
   * reading the same journey from two different routes is how they drift while
   * both look right -- the operator's whole complaint. `catalogSource` makes the
   * outcome independent of which read resolves first: once the public list has
   * been adopted, the admin one never overwrites it.
   */
  refreshPublicCatalog() {
    const slug = this.bookingSlug();
    if (!slug || this.state.journey !== "appointment") return;
    loadPublicCatalog(slug, this.isPublic() ? "public" : "preview").then((catalog) => {
      if (this._gone) return;
      // null means there was nothing to read. An EMPTY catalogue is a real
      // answer and is adopted: a tenant that offers nothing must look like it.
      if (!catalog) return;
      // The window is service-wide, so a location change never needs a re-read;
      // a service or horizon change is picked up by componentDidUpdate's key check.
      this.adoptCatalog(buildCatalog(this.state.apptJourney, catalog.services, catalog.locations, (this.state.avail || EMPTY_AVAILABILITY).staff), catalog.services);
    }).catch(() => { /* the admin catalogue already drew; a refused public read changes nothing */ });
  }

  /**
   * Availability belongs to a (service, staff) pair, so a change to either
   * invalidates the loaded window. Reloading here rather than at each call site
   * is what keeps the preview and the public page identical.
   */
  componentDidUpdate(prevProps, prevState) {
    if (prevState && prevState.journey !== this.state.journey) this.syncTemplateController();
    /* The pay step belongs to one checkout (CC 0259): a change of event, ticket or party while it
       is open clears it, so the pay buttons never stay bound to an old hold. A refusal after a
       captured payment stays on screen with its reference until the event changes. */
    const mp = this.state.memberPay;
    if (mp && prevState && (prevState.evt !== this.state.evt
      || (!mp.refused && (prevState.selectedTicketId !== this.state.selectedTicketId || (prevState.attRows || []).length !== (this.state.attRows || []).length)))) {
      this.setState({ memberPay: null });
    }
    /* A draft mount follows its host (2026-10-05): new rows redraw, a new step moves the page. */
    if (this.props.previewJourney && this.props.draft) {
      if (prevProps.draftRows !== this.props.draftRows && Array.isArray(this.props.draftRows)) { this.eventsRows = this.props.draftRows as typeof this.eventsRows; this.forceUpdate(); }
      const ds = this.props.draftStep;
      if (ds && prevProps.draftStep !== ds && ds !== this.state.previewStep) this.setState({ previewStep: ds, section: this.sectionForStep(ds) });
    }
    /* The module list can arrive after mount (CC, 2026-10-03): leave a journey the tenant does not compose. */
    if (this.props.mode !== "public" && !this.props.previewJourney && prevProps.modules !== this.props.modules && !this.journeyEnabled(this.state.journey)) {
      this.actions.setJourney(Designer.firstJourney(this.props.modules));
    }
    // An online-only service takes its location without the step (CC 0039).
    if (this.state.svc && !this.state.loc && this.serviceMethod(this.state.svc) === "virtual") {
      const onlineLoc = this.onlineLocationFor(this.state.svc);
      if (onlineLoc) this.setState({ loc: onlineLoc });
    }
    /* If the location step was on screen when it dropped out (its availability
       landed after Continue), move on to the next step now instead of leaving
       "Choose a location" showing for a step the bar no longer lists (CC 0039 bounce). */
    if (this.state.previewStep === "location" && !this.isQueue() && this.steps().indexOf("location") < 0) {
      const l = this.steps();
      const next = l[this.stepIndexAround(l, "location", 1)];
      if (next && next !== "location") this.setState({ previewStep: next, section: this.sectionForStep(next) });
    }
    // The pass for the booking just made (CC 0054 item 1): read once per booking, public page only.
    const bk = this.state.booking && this.state.booking.booking;
    const bkId = bk && (bk.bookingId || bk.id);
    if (this.isPublic() && !this.isEvents() && !this.isLanding() && typeof bkId === "string" && bkId && this._walletFor !== bkId) {
      this._walletFor = bkId;
      getPublicBookingConfirmationPage(bkId)
        .then((res) => {
          if (this._gone || this._walletFor !== bkId) return;
          const a = res && res.data && res.data.actions;
          this.setState({ apptWalletUrl: a && a.walletPassEnabled === true && a.walletPassUrl ? String(a.walletPassUrl) : "" });
        })
        .catch(() => { if (!this._gone && this._walletFor === bkId) this.setState({ apptWalletUrl: "" }); });
    }
    /* The session arrives AFTER the first render on every real mount: the public
       page renders this component immediately and fetches `/session/context` in
       an effect. Seeding the account's details only in the constructor therefore
       left a signed-in visitor with identity fields that `detailsLocked()` had
       made read-only while they still held empty strings — and the event
       register guard below refuses on an empty first/last/email, so the journey
       could not be completed at all. Exactly the defect the walk-in screen had;
       this is the same fix on the surface that shares this component. */
    this.maybeLoadSessionTiers();
    if (this.props.bookingSession && prevProps.bookingSession !== this.props.bookingSession) {
      const p = this.props.bookingSession.profile || {};
      this.setState({
        first: String(p.firstName || ""),
        last: String(p.lastName || ""),
        email: String(p.email || ""),
        phone: String(p.phone || ""),
      });
    }
    /* THE ONE STOP RETURN STRIP asks whether the booking is complete before it
       lets the student leave (KP, 2026-09-17). Public mode only: the
       confirmation step with a real booking behind it is "complete"; every other
       step is not. */
    if (this.isPublic() && (prevState.previewStep !== this.state.previewStep || prevState.booking !== this.state.booking)) {
      setJourneyComplete(this.state.previewStep === "confirmation" && !!this.state.booking);
    }
    /* A different tab is a different journey TYPE, and the scan block belongs to
       the type — so it is read for the tab that is now open (once per type). */
    if (prevState.journey !== this.state.journey) this.hydrateScan();
    // The dialog re-renders one mount at three widths, so the device the operator
    // picked arrives as a prop and the page's own layout state follows it.
    if (this.props.device && prevProps.device !== this.props.device && this.state.device !== this.props.device) {
      this.setState({ device: this.props.device });
    }
    // A different authored reminder default (a journey load, or the author editing
    // it) starts the customer back on that default. Ordinary renders and step
    // back/forward leave the customer's choice alone.
    if ((this.state.notifChoice || this.state.notifChoiceRefused)
      && normalizeReminderPreference(prevState.s && prevState.s.notifDefault) !== normalizeReminderPreference(this.state.s && this.state.s.notifDefault)) {
      this.setState({ notifChoice: "", notifChoiceRefused: false });
    }
    if (this.state.journey !== "appointment") return;
    // A selected store supplies the civil-date authority for the service-wide
    // union. A store change therefore re-reads the same union with a new local
    // date anchor; the staff choice still does not narrow the window.
    if (this.availabilityKeyFor(this.state.svc) !== this._availKeyRequested) this.refreshAvailability();
  }

  /**
   * Create the booking the customer actually chose.
   *
   * The slot object is carried through whole — its own staffId, startUtc and
   * endUtc — so the instant booked is the instant offered. Nothing here parses a
   * displayed time back into a moment.
   *
   * Confirmation is shown only once the backend has confirmed. A failure stays
   * on the details step with the real message, because a wizard that advances
   * on a refusal has told the customer they have an appointment they do not.
   */
  /* The reminder default the screen starts from (CC 0312). Text message is never ticked at load while the
     phone field is empty: an authored "both" starts as Email, and an authored "sms" starts as Email when
     the journey asks for an email. Once a number is typed, the authored default applies again. The
     customer's own ticks always win (notifChoice). */
  reminderDefault(st) {
    /* No text can reach this booking (CC 0331): email only, whatever was authored. */
    if (st && st.textOffered === false) return "email";
    const d = st && st.s && st.s.notifDefault;
    const phoneEmpty = !String((st && st.phone) || "").trim();
    if (!phoneEmpty) return d;
    if (d === "both") return "email";
    if (d === "sms" && st.s.askEmail !== false) return "email";
    return d;
  }

  submitAppointment() {
    const st = this.state;
    if (st.submitting) return;
    /* Consent is required on the appointment details step (CC 2026-10-16 item 1). */
    if (!st.consent) { this.setState({ consentTried: true }, () => this.focusConsent()); return; }
    /* Text message needs a number (CC 0312). The choice governs every message of the booking, the
       confirmation included, so text without a number would send nothing. Refused here, at the phone field. */
    if (st.s && st.s.notifPref && !this.detailsLocked() && !String(st.phone || "").trim()
        && reminderChannels(effectiveReminderPreference(st.notifChoice, this.reminderDefault(st))).text) {
      this.setState({ smsPhoneNeeded: true, bookingError: "" }, () => {
        try { const el = document.getElementById("jrn-phone"); if (el) el.focus(); } catch (err) { /* no DOM */ }
      });
      return;
    }
    const slot = this.slotObjFor(st.day, st.slot);
    if (!slot) {
      this.setState({ bookingError: "That time is no longer available. Please choose another." });
      // Forced: a window requested before the slot went would still offer it.
      this.refreshAvailability(true);
      return;
    }
    const signedIn = this.detailsLocked();
    const email = String(st.email || "").trim();
    if (!signedIn && !email) { this.setState({ bookingError: "An email address is required to confirm a booking." }); return; }
    // "Answered" = something will be sent (so an unreadable counter is not an answer).
    const missingQ = this.serviceQuestions().filter((q) => q.required && this.answerForSubmit(q, (st.answers || {})[q.id]) === undefined);
    if (missingQ.length) { this.setState({ answersTouched: true, bookingError: "Please answer: " + missingQ.map((q) => q.label).join(", ") + "." }); return; }
    this.setState({ submitting: true, bookingError: "" });
    submitBooking({
      slug: this.bookingSlug(),
      serviceId: st.svc,
      /* The SLOT's store when the producer named one, else the store the
         visitor chose. Store-level availability makes the service-wide window a
         union across stores, so two stores can offer the same instant under the
         same label; `st.loc` alone books whichever the step was pointing at.
         (Build report §2, 2026-09-14.) */
      /* The SLOT's store, for every booking, online-only included (CC 0071): the
         booking door picks the adviser and counts capacity by it, and answers 409
         SLOT_UNAVAILABLE without it. Only the page's wording drops the branch for
         an online-only service (details line and "Where"). */
      locationId: slot.locationId || st.loc || undefined,
      // A multi-method service records how it is held (CC 0077 d): a virtual branch
      // means video, any other branch in person. A single-method service leaves it out.
      method: this.bookingMethodFor(st.svc, slot.locationId || st.loc),
      slot,
      guest: signedIn ? undefined : {
        firstName: String(st.first || "").trim() || undefined,
        lastName: String(st.last || "").trim() || undefined,
        email,
        phone: String(st.phone || "").trim() || undefined,
        phoneCountry: this.phoneCountryForSubmit(st.phone, st.phoneCountry),
      },
      bookingSessionCsrf: signedIn ? this.props.bookingSession.csrf : undefined,
      // api-contracts' notification-channel-policy: a RECORDED preference outranks
      // the tenant's configured channels, and absence fails open to that
      // configuration. So nothing is sent unless the reminder choice is SHOWN. When
      // it is shown, the customer submits the selection they are looking at --
      // their own change, or the authored default they left checked -- normalised
      // to email | text | both (never the legacy `sms`).
      notificationPreference: reminderPreferenceForSubmit(!!st.s.notifPref, st.notifChoice, this.reminderDefault(st)),
      answers: this.appointmentAnswersForSubmit(),
      agreedToTerms: st.consent === true,
    }).then((response) => {
      if (this._gone) return;
      const payload = (response && (response.data || response)) || {};
      this.setState({ submitting: false, booking: payload, bookingError: "", previewStep: "confirmation" });
    }).catch((err) => this.finishAppointmentFailure(err, signedIn));
  }

  /** Session loss is recoverable only by returning to One Stop and signing in
   *  again. Do not leave a stale, locked form behind or refresh availability as
   *  though a slot alone failed. */
  finishAppointmentFailure(err, signedIn) {
    if (this._gone) return;
    const message = String((err && (err.message || err.detail)) || err);
    if (signedIn && err && err.code === "PORTAL_BOOKING_SESSION_REQUIRED" && this.props.onBookingSessionEnded) {
      this.setState({ submitting: false, bookingError: "" });
      this.props.onBookingSessionEnded(message || "Your secure booking session has ended.");
      return;
    }
    /* 422 APPOINTMENT_QUESTIONS_INVALID (CC 0041): each field names
       `answers.<questionId>`, so its message is shown beside that question. */
    const answerErrors = {};
    if (err && err.code === "APPOINTMENT_QUESTIONS_INVALID" && Array.isArray(err.fields)) {
      err.fields.forEach((x) => { const m = /^answers\.(.+)$/.exec(String((x && x.path) || "")); if (m) answerErrors[m[1]] = String(x.message || ""); });
    }
    /* 400 APPOINTMENT_REQUEST_INVALID for a text-only choice that cannot deliver (CC 0331): the
       message goes to the field it names, guest.phone (back on the details step) or
       notificationPreference (under the update choice). No availability re-read: the slot is fine. */
    if (err && err.code === "APPOINTMENT_REQUEST_INVALID" && Array.isArray(err.fields)) {
      const f = err.fields.find((x) => x && (x.path === "guest.phone" || x.path === "notificationPreference"));
      if (f) {
        const msg = String(f.message || message);
        const toDetails = f.path === "guest.phone" && this.steps().indexOf("details") > -1;
        this.setState({
          submitting: false, bookingError: "", answerErrors,
          ...(f.path === "guest.phone" ? { phoneServerError: msg } : { notifServerError: msg }),
          ...(toDetails ? { previewStep: "details", section: this.sectionForStep("details") } : {}),
        });
        return;
      }
    }
    /* JAI-2020: 403 AUDIENCE_FORBIDDEN — the approved refusal sentence, and no
       availability re-read: the slot was not the problem, the audience was. */
    const refusal = audienceRefusalCopy(i18nT, err);
    if (refusal) { this.setState({ submitting: false, bookingError: refusal, answerErrors }); return; }
    this.setState({ submitting: false, bookingError: message, answerErrors });
    if (Object.keys(answerErrors).length) return;
    // A refused slot is usually a slot someone else took between the read and
    // the submit, so re-read rather than leaving a stale calendar on screen.
    // Forced, for the same reason the stale-slot guard above forces it.
    this.refreshAvailability(true);
  }

  /**
   * The adviser the BOOKING was assigned — read from the successful booking
   * response only, never from the wizard's own staff selection.
   *
   * The server assigns the adviser atomically when it books (store-level
   * availability: any worker on the store's roster can take the instant), so
   * the visitor's `state.staff` — or `STAFF[0]` when they chose nobody — says
   * nothing about who was assigned. Showing that was how a booking assigned to
   * Luis Herrera confirmed "Aaron Feld", the first row of the roster.
   *
   * The id is the booking's own `staffId` (the registration's `JourneyStaffId`
   * as its twin). A name is taken only when it belongs to THAT id: the
   * response's `staff` row, the registration's `JourneyStaff`, or the real
   * catalogue row for the id. No name for the assigned id → "" and the
   * confirmation hides the adviser; there is no default person.
   */
  confirmedAdviserName(booking) {
    if (!booking || typeof booking !== "object") return "";
    const row = booking.booking || {};
    const reg = booking.registration || {};
    const fields = reg.customFields || reg.custom_fields || {};
    const staffRow = booking.staff || {};
    const id = String(row.staffId || row.staff_id || fields.JourneyStaffId || staffRow.id || "").trim();
    if (!id) return "";
    const named = (rowId, name) => String(rowId || "").trim() === id && String(name || "").trim() !== "";
    if (named(staffRow.id, staffRow.name)) return String(staffRow.name).trim();
    if (named(fields.JourneyStaffId, fields.JourneyStaff)) return String(fields.JourneyStaff).trim();
    const known = STAFF.filter((x) => x.id === id)[0];
    return known && String(known.name || "").trim() ? String(known.name).trim() : "";
  }

  /** The journey whose slug this page or preview is showing. */
  bookingSlug() {
    if (this.isPublic()) return String(this.props.slug || "");
    const journey = this.state.apptJourney || {};
    return String(journey.slug || this.state.s.slug || "");
  }

  /**
   * The published journey, its settings and its catalogue — one read, no admin
   * route, and the same shapes the designer hydrates from. A slug that resolves
   * to nothing lands on the existing link-error step, which names no tenant.
   */
  bootstrapPublic() {
    const slug = this.bookingSlug();
    // Two reads, both public and both existing: the published config, and the
    // service catalogue. The config alone is not enough — the public surface
    // normalises away the hydrated `custom` block the admin row carries, which
    // is where a first attempt at this wrongly looked for services.
    Promise.all([
      loadPublishedJourney(slug, "public"),
      loadPublicCatalog(slug, "public"),
    ]).then((results) => {
      if (this._gone) return;
      const surface = results[0];
      const catalog = results[1];
      const config = surface && (surface.configJson || surface.config_json);
      /* No published journey at this address (CC 0312): its own state, not the switched-off link's. */
      if (!surface || !config) { this.setState({ previewStep: "linkError", linkNotFound: true }); return; }
      const hydrated = designerFromJourney(config);
      /* Whether a text can reach this booking at all (CC 0331). Only an explicit false hides the choice. */
      if (surface.textNotificationsOffered === false) this.setState({ textOffered: false });
      /* A personal booking link (?staff=, CC 0308 / 0330 / 0333). The journey read answers with
         personalLink. staff is a roster entry ({ id, name, … }). locked = only this member (the
         journey's allowOtherOnPBL false); unavailable = the link is switched off; locationIds and
         unbookableReason say where the member can be booked (absent when the roster could not be
         read: then the Where step is as today). */
      const pl = surface.personalLink && typeof surface.personalLink === "object" ? surface.personalLink : null;
      const personal = pl ? {
        name: String((pl.staff && pl.staff.name) || "").trim(),
        staffId: String((pl.staff && pl.staff.id) || ""),
        locked: pl.locked === true,
        otherUrl: pl.locked === true ? "" : String(pl.standardPublicUrl || ""),
        locIds: Array.isArray(pl.locationIds) ? pl.locationIds.map(String) : null,
        off: pl.unavailable === true ? "unavailable" : (pl.unbookableReason === "no_location" ? "no_location" : ""),
      } : null;
      // A published journey whose catalogue read came back missing is an empty
      // wizard, not an invented one.
      this.adoptCatalog(buildCatalog(config, (catalog && catalog.services) || [], (catalog && catalog.locations) || [], []), (catalog && catalog.services) || []);
      const order = Array.isArray(config.order) ? config.order : [];
      // URL PRESELECT (2026-09-16). A One-Stop service card links here as
      // `/<journey>?service=<serviceId>`. The id is NAVIGATIONAL ONLY: it is kept
      // only when it names a service in the catalogue this journey published (the
      // server narrows that catalogue to the journey's own allow-list), so an
      // unknown, unpublished or out-of-list id is ignored and the visitor sees the
      // service step exactly as with no parameter. Never written back to the URL.
      let urlService = "";
      try { urlService = new URLSearchParams(window.location.search).get("service") || ""; } catch (err) { urlService = ""; }
      const preSvc = urlService && SERVICES.some((x) => x.id === urlService) ? urlService : "";
      // EXACT SLOT (2026-09-25). With a valid service, `?date&time&tz[&staff]`
      // names one opening. It is checked against the first availability window
      // this service receives (refreshAvailability), never trusted on its own.
      let urlSlot = null;
      try { urlSlot = preSvc ? readSlotDeepLink(window.location.search) : null; } catch (err) { urlSlot = null; }
      this._urlSlot = urlSlot;
      this.setState((prev) => ({
        catalogRead: !!catalog,
        s: { ...DEFAULTS, ...hydrated.s },
        ct: { ...hydrated.ct },
        apptJourney: config,
        personal,
        ...(preSvc ? { svc: preSvc } : {}),
        preselectedService: !!preSvc,
        previewStep: (order[0] === "location" || config.flowOrder === "location-first") ? "location" : "service",
      }), () => {
        /* Not taking bookings, or no location can offer the member: say so, and ask for no times. */
        if (personal && personal.off) { this.setState({ previewStep: "linkError", section: "general" }); return; }
        this.startPublicAppointment();
      });
    }).catch((err) => {
      if (this._gone) return;
      const status = err && (err.status ?? (err.response && err.response.status));
      this.setState({ previewStep: "linkError", linkNotFound: status === 404 });
    });
  }

  /** Start on the first step that remains after the published singleton rules
   * have been applied to the now-hydrated settings and public catalog. */
  startPublicAppointment() {
    const first = this.steps()[0] || "service";
    this.setState({ previewStep: first, section: this.sectionForStep(first) }, () => this.refreshAvailability());
  }

  /**
   * The standalone Event List Page (Attend `/`). Reads the live Events Page
   * settings (presentation + the authored `events` block, or the neutral
   * platform defaults when the tenant has authored nothing) and the first
   * real page of the catalogue with those settings mapped onto the wire
   * query. Filtering/sorting/paging beyond the first page happen through
   * `refreshPublicEventsList` below, never by re-slicing this page client-side.
   */
  /**
   * Whether the authored `events` block's catalogue-scoping (`scope.events`
   * — curated ids; `scope.orgNodes`; more than one `scope.categories`) can
   * actually be expressed through `getPublicEvents`'s query params (a single
   * `category` + `locationId`, no curated-id list, no org-node scope). When
   * it cannot, the read must not silently fall back to the tenant's WHOLE
   * catalogue and pretend that was the configured scope — the caller shows
   * an explicit unsupported-config state instead.
   */
  publicListScopeIsFulfillable(block: EventsBlockConfig): boolean {
    const scope = (block.scope || {}) as { events?: string[]; categories?: string[]; orgNodes?: string[] };
    if ((scope.events || []).length > 0) return false;
    if ((scope.orgNodes || []).length > 0) return false;
    if ((scope.categories || []).length > 1) return false;
    // The read door's `sort` has no companion `order` param — a `desc`
    // authored order cannot be expressed and rendering `asc` results under
    // it would silently misrepresent the configured order.
    if (block.order === "desc") return false;
    return true;
  }

  /** The single authored scope category, when the block scopes to exactly
   *  one — the one scope dimension the read door CAN express (`category`).
   *  ALWAYS wins over a visitor's own category chip: a fixed authored scope
   *  cannot be broadened or overridden from the public side. */
  publicListScopeCategory(block: EventsBlockConfig): string | undefined {
    const scope = (block.scope || {}) as { categories?: string[] };
    return (scope.categories || [])[0];
  }

  /** The authored forward window (`dateRange`) and lookback
   *  (`includePast`+`pastHorizonDays`) as `{from, to}` civil dates — the
   *  bounds `getPublicEvents`'s typed `from`/`to` can actually express.
   *  Mirrors `workers/portal/src/resolve.ts`'s `windowFor`/`eventsWindowFor`
   *  exactly, rather than guessed semantics:
   *    - an explicit `dateRange: {from, to}` is left ALONE — "an author who
   *      named both edges of the window has already said where their past
   *      ends"; `includePast`/`pastHorizonDays` never touch it.
   *    - otherwise the window is relative: `[now, now + horizonDays]`
   *      (default 365 here, CC 2026-10-05: with no authored events page, a tenant
   *      whose portal serves no page document always lands on the neutral block,
   *      and a 7-day default hid every event published more than a week out;
   *      an authored horizon is kept as-is. The portal resolver still defaults
   *      to 7), widened backward to `now - pastHorizonDays` (default
   *      30) only when `includePast` is on. "Now" and the resulting civil
   *      day both resolve in the canonical resolver's OWN zone
   *      (`this.eventsPageTimeZone`, threaded from `page.timeZone`), never
   *      the visitor's browser zone. */
  publicListScopeDateBounds(block: EventsBlockConfig): { from?: string; to?: string } {
    const dateRange = block.dateRange as { from?: string; to?: string; horizonDays?: number } | undefined;
    if (typeof dateRange?.from === "string" && typeof dateRange?.to === "string") {
      return { from: dateRange.from, to: dateRange.to };
    }
    const timeZone = this.eventsPageTimeZone || "UTC";
    const nowMs = Date.now();
    const days = typeof dateRange?.horizonDays === "number" ? dateRange.horizonDays : 365;
    let fromMs = nowMs;
    const toMs = nowMs + days * 86_400_000;
    if (block.includePast) {
      const pastDays = typeof block.pastHorizonDays === "number" ? block.pastHorizonDays : 30;
      fromMs = nowMs - pastDays * 86_400_000;
    }
    return { from: civilDateInZone(fromMs, timeZone), to: civilDateInZone(toMs, timeZone) };
  }

  /** Intersects the authored bound with the visitor's own chosen bound —
   *  never the wider of the two (that would let a visitor filter reach
   *  outside the authored window). `undefined` means "no bound on this
   *  side"; the intersection keeps whichever bound is actually present and
   *  more restrictive. */
  intersectDateBound(authored: string | undefined, visitor: string | undefined, keepLater: boolean): string | undefined {
    if (!authored) return visitor || undefined;
    if (!visitor) return authored;
    return keepLater ? (visitor > authored ? visitor : authored) : (visitor < authored ? visitor : authored);
  }

  bootstrapPublicEventsList() {
    loadPublicEventsPage().then((page) => {
      if (this._gone) return;
      this.eventsPageBlock = page.block;
      this.eventsPagePresentation = page.eventJourney;
      this.publicListSkipSingleSlot = (page.eventJourney as { skipSingleEventSlot?: boolean }).skipSingleEventSlot === true;
      this.eventsPageTimeZone = page.timeZone || "UTC";
      this.publicBrandedAuthored = { header: !!page.brandedHeaderStored, footer: !!page.brandedFooterStored };
      // Hydrate the ACTUAL renderer state from the live config — the
      // header/footer chrome and every visual EventListPresentation field
      // this render tree already reads off `state.s` (showHeader/showFooter
      // are page.presentation's own authoritative fields, not part of
      // eventJourney, so they are set explicitly alongside it).
      this.setState((prev) => ({
        s: { ...prev.s, ...page.eventJourney, ...designerFieldsFromEventsBlockConfig(page.block), showHeader: page.showHeader, showFooter: page.showFooter, brandedHeader: !!page.brandedHeader, brandedFooter: !!page.brandedFooter },
        ct: { ...prev.ct, ...(page.eventJourney.customText || {}) },
        evtPage: 1,
        evtScopeUnsupported: !this.publicListScopeIsFulfillable(page.block),
      }), () => this.refreshPublicEventsList(1));
    }).catch(() => { if (!this._gone) this.setState({ previewStep: "linkError" }); });
  }

  /**
   * Load one server page of the public events catalogue using the authored
   * `events` block's semantic settings (sort/order/includePast/includeSoldOut)
   * plus whatever filters the visitor has chosen (category/location/date
   * range), and adopt it wholesale. Never pages or re-sorts the array the
   * server already paged — a new page replaces `this.eventsRows`, it does not append
   * to or re-slice it, except in "loadmore"/"scroll" presentation modes,
   * which the caller is responsible for concatenating explicitly.
   */
  refreshPublicEventsList(page: number, opts: { append?: boolean } = {}) {
    const st = this.state;
    const block: EventsBlockConfig = this.eventsPageBlock || NEUTRAL_EVENTS_BLOCK_CONFIG;
    const presentation = this.eventsPagePresentation;
    // "loadmore"/"scroll" accumulate pages; "pages" (and the default, when
    // config is unresolved yet) always replaces with exactly the requested
    // page — never both paged AND re-sliced client-side.
    const append = Boolean(opts.append) && presentation?.eventPaging !== "pages";
    // "curated"/"name" have no server sort=curated lane; curated has no
    // manual-order source on this read door, so it degrades to the door's
    // own default (date) rather than silently reinterpreting "curated".
    const sort = block.sort === "popularity" ? "popularity" : block.sort === "name" ? "name" : "date";
    this.setState({ evtListState: "loading", evtListError: "" });
    const requestId = (this._evtListRequestId = (this._evtListRequestId || 0) + 1);
    const authoredBounds = this.publicListScopeDateBounds(block);
    // Authored scope wins: a fixed category cannot be broadened by a
    // visitor's chip, and the effective date window is the INTERSECTION of
    // the authored bounds and whatever the visitor picked, never the wider
    // of the two.
    const scopeCategory = this.publicListScopeCategory(block);
    const hasExplicitDateRange = (() => {
      const dr = block.dateRange as { from?: string; to?: string } | undefined;
      return typeof dr?.from === "string" && typeof dr?.to === "string";
    })();
    // An explicit authored range already names its own edges — `upcoming`
    // (which filters to events that have not yet started) would otherwise
    // have the read door strip out exactly the history the range asked for,
    // even when `includePast` itself is off.
    const upcoming = hasExplicitDateRange ? false : !block.includePast;
    loadPublicEventsList({
      page,
      limit: block.pageSize,
      sort,
      includeNonBookable: true,
      includeSoldOut: block.includeSoldOut,
      upcoming,
      locationId: st.evtLocationFilter || undefined,
      category: scopeCategory || ((st.evtCat && st.evtCat !== "all") ? st.evtCat : undefined),
      from: this.intersectDateBound(authoredBounds.from, st.evtFrom || undefined, true),
      to: this.intersectDateBound(authoredBounds.to, st.evtTo || undefined, false),
      query: (st.evtQuery || "").trim() || undefined,
    }).then((result) => {
      if (this._gone || requestId !== this._evtListRequestId) return;
      const rows = result.data.map((row) => projectPublicEventToDesignerRow(row, this.publicBrandColor()));
      this.eventsRows = append ? this.eventsRows.concat(rows) : rows;
      /* The list page's header name (CC 0352): the events' tenant.name, only when every row on the
         first page names the same company. A list that spans companies keeps the neutral wording. */
      /* The first page decides every time (CC 0365): a list that spans companies clears a name an
         event page set earlier. An empty first page (a search with no match) keeps the current one. */
      if (!append) {
        const decided = listTenantName(result.data);
        if (decided !== undefined) this.publicTenantName = decided;
      }
      this.setState({
        evtPage: page,
        evtPagination: result.pagination,
        evtFacets: result.facets,
        evtListState: this.eventsRows.length === 0 ? "empty" : "ready",
        previewStep: "events",
      });
    }).catch((err) => {
      if (this._gone || requestId !== this._evtListRequestId) return;
      this.setState({ evtListState: "error", evtListError: String((err && err.message) || err) });
    });
  }

  /** Reset paging and re-read page 1 whenever a list filter changes (category,
   *  location, date range, search query) — never re-filters the page already
   *  on screen. Every filter-changing control must call this, not mutate
   *  `evtPage`/`evtCat`/etc. and leave the old server page on screen. */
  refreshPublicEventsListFromFilters(patch: Record<string, unknown>) {
    this.setState({ ...patch, evtPage: 1 }, () => this.refreshPublicEventsList(1));
  }

  /** Load the next server page. For "pages" presentation this REPLACES the
   *  current page (a genuine page-2 read, never an empty re-slice of page 1);
   *  for "loadmore"/"scroll" it appends. */
  loadMorePublicEvents() {
    const pagination = this.state.evtPagination;
    if (!pagination || !pagination.hasMore) return;
    this.refreshPublicEventsList((this.state.evtPage || 1) + 1, { append: true });
  }

  /** Jump directly to an explicit page ("pages" presentation's pager). */
  goToPublicEventsPage(page: number) {
    if (page < 1) return;
    this.refreshPublicEventsList(page, { append: false });
  }

  /** "scroll" presentation's actual sentinel: observes the mounted element
   *  and fetches the next real server page on intersection — pending/end
   *  guarded by `evtListState`/`evtPagination.hasMore`, same as the manual
   *  "Load more" button. Re-attaches on remount (the sentinel unmounts once
   *  `hasMore` goes false) and disconnects any prior observer first, so a
   *  re-render never accumulates duplicate observers on the same instance. */
  attachScrollSentinel(el: Element | null) {
    if (this._scrollSentinelObserver) { this._scrollSentinelObserver.disconnect(); this._scrollSentinelObserver = null; }
    this._scrollSentinelEl = el;
    if (!el || typeof IntersectionObserver !== "function") return;
    this._scrollSentinelObserver = new IntersectionObserver((entries) => {
      if (this._gone) return;
      const pagination = this.state.evtPagination;
      if (!entries.some((e) => e.isIntersecting)) return;
      if (this.state.evtListState === "loading" || !pagination?.hasMore) return;
      this.loadMorePublicEvents();
    }, { rootMargin: "200px" });
    this._scrollSentinelObserver.observe(el);
  }

  /**
   * One event's Landing Page (`/{eventId}`). Reads the single event's
   * real projection, including its resolved `landingDesign` (per-event, else
   * tenant default, else `null` — resolved server-side, never guessed here)
   * and its actual configured `registrationQuestions`.
   */
  bootstrapPublicEventDetail() {
    // Direct-load, no dependency on the list ever having run — this is the
    // whole reason `/{eventId}` works without visiting the list at `/`.
    const eventId = String(this.props.eventId || "");
    if (!eventId) { this.setState({ previewStep: "linkError" }); return; }
    this.selectPublicEvent(eventId, { isRoute: true });
  }

  /**
   * THE ONE selected-event loader — every path that puts a visitor on one
   * event's landing page (the direct `/{eventId}` route, a card click

   * from the list, or a related-event pick) goes through this, so none of
   * them can land on stale detail/questions/design/payment from whatever
   * event was previously selected.

   */
  selectPublicEvent(eventId: string, opts: { isRoute?: boolean; fromPopstate?: boolean; fromList?: boolean } = {}) {
    if (!eventId) { this.setState({ previewStep: "linkError" }); return; }
    // The direct-route mount and a popstate restore both reflect a URL that
    // is ALREADY correct — pushing again here would create a duplicate
    // history entry for the same navigation. A user-initiated selection
    // (card/related click) is the only case that adds one.
    // Origin is resolved BEFORE the push, so the entry this navigation
    // creates records the origin it actually has.
    const freshFromList = Boolean(opts.fromList) && !opts.isRoute && !opts.fromPopstate;
    // Restored only from the entry the browser navigated to — never from a
    // stale instance field, which would describe the previous selection.
    let restoredSkipped = false;
    if (opts.isRoute) {
      // A directly opened URL has no list behind it, whatever any history
      // marker says.
      this.publicEventFromList = false;
    } else if (opts.fromPopstate) {
      let marker: { fromList?: unknown; skipped?: unknown } | null = null;
      try { marker = ((window.history.state || {}) as { attendEventJourney?: { fromList?: unknown; skipped?: unknown } }).attendEventJourney || null; } catch {}
      this.publicEventFromList = Boolean(marker) && marker!.fromList === true;
      restoredSkipped = this.publicEventFromList && Boolean(marker) && marker!.skipped === true;
    } else if (freshFromList) {
      this.publicEventFromList = true;
    }
    // else: a related-event pick keeps the origin of the journey it is made
    // from — related-from-direct stays direct.
    const originFromList = this.publicEventFromList;
    if (this.isPublic() && !opts.isRoute && !opts.fromPopstate) this.pushPublicEventsHistory(eventId);
    this.publicLandingStepSkipped = false;
    this._eventRegIdemKey = null;
    this._eventRegIdemFingerprint = null;
    this.eventPaymentConfig = null;
    this.landingPresentation = null;
    this.landingRegistrationQuestions = [];
    // A generation guard, not just an id comparison: a stale completion from
    // an earlier mount (StrictMode's discarded first mount, or a previous
    // selection of this SAME event id) must not land after a newer
    // selection has started, even though `this.state.evt` would still equal
    // this `eventId` by the time it resolves.
    const requestId = (this._evtDetailRequestId = (this._evtDetailRequestId || 0) + 1);
    // Read the authored list setting NOW, at click time, and hold it in a
    // local: `this.publicListSkipSingleSlot` is mutable instance state that a
    // concurrent list re-bootstrap is free to rewrite while the detail
    // request is still in flight, which would settle this navigation on a
    // setting that was not the one in force when the visitor tapped the card.
    // The `fromList` intent is captured alongside it for the same reason.
    const fromList = freshFromList;
    const listSkipSingleSlot = this.publicListSkipSingleSlot;
    this.setState({
      evt: eventId,
      // Consistent with `backToEvents`/popstate: a real journey switch, not
      // just a `previewStep` flip, whichever direction the navigation is.
      journey: "landing",
      evtDetailState: "loading",
      evtPaymentState: "loading",
      // Event-specific form/registration state must not leak from whichever
      // event was selected before.
      selectedTicketId: null,
      answers: {},
      answersTouched: false,
      bookingError: "",
      booking: null,
      submitting: false,
      previewStep: opts.isRoute ? "landing" : "landing",
      section: "general",
    });
    loadPublicEventDetail(eventId).then((detail) => {
      if (this._gone || requestId !== this._evtDetailRequestId) return;
      this.eventsRows = [projectPublicEventToDesignerRow(detail, this.publicBrandColor()), ...this.eventsRows.slice(1).filter((e) => e.id !== detail.id)];
      const presentation = detail.landingDesign ? detail.landingDesign.presentation : neutralLandingPresentationDefaults();
      this.landingPresentation = presentation;
      /* Draft preview: the author's unpublished layout wins over the published
         presentation, and counts as CONFIGURED — they have authored it, it just
         has not shipped. Every event FACT below still comes from the read. */
      const draft = this.props.draft;
      const layout = draft ? draft.s : presentation;
      const layoutText = draft ? draft.ct : ((presentation as { customText?: Record<string, unknown> }).customText || {});
      if (draft) this.landingPresentation = draft.s;
      {
        // The landing document's own choice wins; when it is silent, the events
        // page's choice (if the visitor came through the list) still stands.
        const lp = (layout || {}) as Record<string, unknown>;
        this.publicBrandedAuthored = {
          header: typeof lp.brandedHeader === "boolean" || this.publicBrandedAuthored.header,
          footer: typeof lp.brandedFooter === "boolean" || this.publicBrandedAuthored.footer,
        };
      }
      // Distinguishes "a landing document exists, and this field's value IS
      // the empty string the author explicitly left it" from "no document
      // exists at all, so there is nothing authored to show" — only the
      // second case may fall back to the event's own real facts.
      this.landingIsConfigured = Boolean(this.props.draft) || Boolean(detail.landingDesign);
      this.landingRegistrationQuestions = detail.registrationQuestions || [];
      // "Skip event details for a single date/time" — an authored shortcut for
      // list entries ONLY, and only once the authoritative detail has both
      // loaded and confirmed the event is bookable with exactly one valid
      // one-off schedule. It moves the step AFTER every hydration above
      // (presentation, questions, related, payment config all still run), so
      // ticket tiers, paid-provider gating and validation are untouched; a
      // direct URL, a popstate restore or a related-event pick never carries
      // the intent and so always lands on Event details.
      const gate = detail as unknown as { bookable?: unknown; registrationOpen?: unknown; soldOut?: unknown };
      const skipToRegistration = (
        (fromList && listSkipSingleSlot)
        // A back/forward restore replays the shortcut the recorded entry
        // already took — still only if the event is genuinely eligible now.
        || restoredSkipped
      )
        && gate.bookable === true
        // The server's own registration gate and sold-out flag, read as the
        // literal booleans the contract declares — never `!soldOut` (which
        // would let an undisclosed `null` count as "places left") and never
        // derived from `remaining`.
        && gate.registrationOpen === true
        && gate.soldOut === false
        && hasSingleOneOffSchedule(detail);
      // Before the state flip, so the very render that lands on `details`
      // already computes the matching (landing-less) public rail.
      this.publicLandingStepSkipped = skipToRegistration;
      // Record the settled shape of THIS navigation on its own existing
      // history entry (no new entry, no URL change), so a later back/forward
      // returning here restores the same 3- or 4-step journey.
      try {
        const prevState = (window.history.state || {}) as Record<string, unknown>;
        window.history.replaceState({ ...prevState, attendEventJourney: { fromList: originFromList, skipped: skipToRegistration } }, "");
      } catch {}
      {
        const tn = (detail as { tenant?: { name?: unknown } | null }).tenant?.name;
        this.publicTenantName = typeof tn === "string" && tn.trim() ? tn.trim() : null;
      }
      this.setState((prev) => ({
        evt: detail.id,
        s: { ...prev.s, ...layout },
        ct: { ...prev.ct, ...layoutText },
        // `landingDesign.blocks`/`.branding` are NOT rendered — this renderer
        // only understands the declarative `blockOrder`/`blocksOn` + `lp*`
        // row arrays already hydrated above from `presentation`. Freeform
        // block/branding data has no existing renderer here, so it is
        // deliberately not stored as state implying it does something.
        previewStep: skipToRegistration ? "details" : "landing",
        section: skipToRegistration ? "details" : prev.section,
        evtDetailState: "ready",
      }));
      this.loadRelatedPublicEvents(detail, requestId);
      // A refused READ (e.g. the known 403 on this endpoint) is a distinct
      // error — NOT the same fact as "no provider configured" — so paid
      // tiers stay gated (disabled) on either outcome, but the reason shown
      // to an author/operator differs. Never let this failure retroactively
      // block the free-tier path already rendering above.
      // By the CANONICAL id the detail read resolved (`detail.id`, the same id
      // `evt` is set to above), never the route/list `eventId`: that can be a
      // public slug, and the payment-config route binds the event id only, so
      // a slug 404s ("Event not found") and paid tiers would read as a refusal.
      loadEventPaymentConfig(detail.id).then((cfg) => {
        if (this._gone || requestId !== this._evtDetailRequestId) return;
        this.eventPaymentConfig = cfg;
        this.setState({ evtPaymentState: "ready" });
      }).catch((err) => {
        if (this._gone || requestId !== this._evtDetailRequestId) return;
        this.eventPaymentConfig = null;
        this.setState({ evtPaymentState: "error", evtPaymentError: String((err && err.message) || err) });
      });
    }).catch(() => { if (!this._gone && requestId === this._evtDetailRequestId) this.setState({ previewStep: "linkError", evtDetailState: "error" }); });
  }

  /** `{provider: null}` is a REAL, truthy response object meaning "no
   *  provider configured" — checking the object's own truthiness (as opposed
   *  to its `provider` field) would treat every resolved config, including
   *  this one, as usable. A config read that errored is NOT "no provider"
   *  either; both cases return false here, and the caller distinguishes them
   *  via `evtPaymentState` for the message it shows. */
  hasUsablePaymentProvider(): boolean {
    const cfg = this.eventPaymentConfig as { provider?: string | null; verified?: boolean } | null;
    return Boolean(cfg && cfg.provider && cfg.verified);
  }

  /** Debounced live search — mints one server request per pause in typing,
   *  not one per keystroke, still through the same server-filtered path. */
  debounceEvtQuery(value: string) {
    if (this._evtQueryDebounce) clearTimeout(this._evtQueryDebounce);
    this._evtQueryDebounce = setTimeout(() => {
      if (this._gone) return;
      this.refreshPublicEventsListFromFilters({ evtQuery: value });
    }, 300);
  }

  /** Real related-event choices for the landing page's "you might also like"
   *  picker — the server's own category catalogue, never the synthetic
   *  `e4`/`e5`/`e7` ids the old fixtures used. Falls back to the upcoming
   *  catalogue (no category filter) when the event has none. */
  loadRelatedPublicEvents(detail: PublicEventDetail, requestId: number) {
    loadPublicEventsList({ category: detail.category || undefined, limit: 5, upcoming: true })
      .then((result) => {
        if (this._gone || requestId !== this._evtDetailRequestId) return;
        const related = result.data
          .filter((row) => row.id !== detail.id)
          .map((row) => projectPublicEventToDesignerRow(row, this.publicBrandColor()));
        this.eventsRows = [this.eventsRows[0], ...related].filter(Boolean);
        this.setState({ evtRelatedLoaded: true });
      })
      .catch(() => { /* related choices staying empty is honest; the primary event already loaded */ });
  }

  /**
   * Submit a public event registration. First/last/email are mandatory in the
   * current JSON contract regardless of the authored optional toggle, so this
   * refuses locally with the same message a server refusal would carry rather
   * than sending a request the contract will reject anyway. Ticket/payment
   * fields the current registration API does not accept (quantity-as-count,
   * address, notification preference) are never sent.
   */
  /** The "join the waitlist" CTA is the SAME details-form submission, with
   *  `waitlist: true` — never a second, bypassing API call. It reads the
   *  same first/last/email/answers/consent the visitor already entered in
   *  this event's own details form; a single-word name is never invented
   *  into both first and last (the shared validation below refuses it
   *  honestly, the same way it refuses any other incomplete name). */
  joinWaitlist() {
    /* No consent: say so at the box, in red, and send nothing (one behaviour with the walk-in, 2026-10-18). */
    if (!this.state.consent) { this.setState({ consentTried: true }, () => this.focusConsent()); return; }
    /* The box offers only tickets whose own waitlist takes a guest (CC 0346): exactly one is taken
       without asking; with several the guest's choice must be one of them. When no ticket has its
       own waitlist (the event's waitlist), the submit chooses as before. */
    const evt = this.currentEventRow();
    const wl = evt ? this.waitlistTiers(evt) : [];
    if (wl.length === 1 && this.state.selectedTicketId !== wl[0].id) {
      this.setState({ selectedTicketId: wl[0].id }, () => this.submitEventRegistration({ waitlist: true }));
      return;
    }
    if (wl.length > 1 && !wl.some((t) => t.id === this.state.selectedTicketId)) {
      /* bookingError is what the box's banner shows (pv.wlError); picking a ticket clears it (CC 0348). */
      this.setState({ bookingError: "Choose the ticket you'd like to wait for." });
      return;
    }
    this.submitEventRegistration({ waitlist: true });
  }

  submitEventRegistration(intent: { waitlist?: boolean } = {}) {
    // Preview NEVER posts — enforced HERE, at the single shared submission

    // method, so EVERY entrypoint is covered: the details-step submit from
    // `nextStep`, the sold-out landing's "join the waiting list" CTA
    // (`actions.joinWaitlist` → `joinWaitlist`), and any later caller. Real
    // contact details typed into a Studio preview must not create a
    // registration that would be indistinguishable from a customer's in the
    // tenant's own data. A no-op is the honest outcome: nothing is written
    // into `booking`/`registeredEventId` to imply a registration happened.
    if (!this.isPublic()) return;
    const st = this.state;
    const eventId = String(st.evt || "");
    // Disable-concurrent-clicks AND the post-success no-retry guard, the
    // latter keyed to the EVENT the successful result belongs to (not
    // `previewStep`, which back/details navigation can change without
    // undoing the registration) — navigating away and back must not be able
    // to silently create a second registration for the same event.
    if (st.submitting || st.registeredEventId === eventId) return;
    /* A refused paid member registration stays put (CC 0261): only "Start a new registration" lifts it. */
    if (this.memberPayLocked(eventId)) return;
    /* Nothing on sale (CC 0436 B / 0437): an event that HAS tickets, none on sale now, isn't sent; the server refuses
       it. No tickets is a capacity-only event and goes through. */
    { const ev2 = this.currentEventRow(); const sale = eventSaleState(((ev2 && ev2.raw && ev2.raw.ticketTiers) as SaleTier[] | undefined) ?? undefined, String((ev2 && ev2.raw && (ev2.raw.startsAt || ev2.raw.start)) || "") || null);
      if (sale.state !== "on-sale") { this.setState({ bookingError: sale.state === "closed" ? "Registration is closed for this event." : "Registration for this event isn't open yet." }); return; } }
    /* Consent first (CC 0434): no registration, waitlist included, is sent without it. */
    if (!st.consent) { this.setState({ bookingError: CONSENT_REQUIRED }); return; }
    /* Signed out, a member ticket isn't sent at all (CC 0264): the visitor is told to sign in. */
    { const ev1 = this.currentEventRow(); const tiers1 = (((ev1 && ev1.raw && ev1.raw.ticketTiers) || []) as { id: string; audience?: string }[]);
      const pick1 = st.selectedTicketId ? tiers1.find((x) => x.id === st.selectedTicketId) : (tiers1.length === 1 ? tiers1[0] : undefined);
      if (pick1 && this.tierNeedsSignIn(pick1)) { this.setState({ bookingError: "Sign in to use a member ticket." }); return; } }
    const first = String(st.first || "").trim();
    const last = String(st.last || "").trim();
    const email = String(st.email || "").trim();
    /* Signed in, these three are the ACCOUNT's: they are read-only on the page
       and the request carries none of them, because the server reads the
       registrant from the session. Demanding them here would refuse a
       registration over values that are neither editable nor sent. */
    // Each of the three is demanded unless the ACCOUNT supplies it (per-field
    // lock): an account with no e-mail on file must still give one to register.
    if ((!this.lockedField("first") && !first) || (!this.lockedField("last") && !last) || (!this.lockedField("email") && !email)) {
      this.setState({ bookingError: "First name, last name and email are required." });
      return;
    }
    const questions = (this.landingRegistrationQuestions || []) as { id: string; label: string; required?: boolean; type?: string }[];
    const answersMap = st.answers || {};
    if (Object.keys(st.fileUploads || {}).some((k) => st.fileUploads[k] && st.fileUploads[k].state === "uploading")) {
      this.setState({ bookingError: "A file is still uploading. Try again when it has finished." });
      return;
    }
    const missingRequired = questions.filter((q) => q.required && this.answerForSubmit(q, answersMap[q.id]) === undefined);
    if (missingRequired.length) {
      this.setState({ answersTouched: true, bookingError: `Please answer: ${missingRequired.map((q) => q.label).join(", ")}.` });
      return;
    }
    const eventRow = this.eventsRows.find((e) => e.id === eventId);
    const tiers = (eventRow?.raw?.ticketTiers || []) as { id: string; priceMinor: number }[];
    // Never silently pick a tier the visitor did not choose: with more than
    // one real tier, an explicit `selectedTicketId` is required; with
    // exactly one (or none — a tierless free event) the single/absent choice
    // is unambiguous.
    if (tiers.length > 1 && !st.selectedTicketId) {
      this.setState({ bookingError: "Choose a ticket type." });
      return;
    }
    const ticketId = tiers.length > 1 ? st.selectedTicketId : (st.selectedTicketId || tiers[0]?.id);
    const selectedTier = tiers.find((t) => t.id === ticketId);
    // A priced SELECTION — the actual tier's real `priceMinor`, never the
    // event-level priceFrom summary (can understate a specific tier) and
    // never the first tier when a different one was chosen — with no usable
    // payment provider cannot accept a paid registration. A free tier stays
    // registerable even on an otherwise-paid (mixed) event.
    const requiresPayment = Boolean(selectedTier && selectedTier.priceMinor > 0);
    if (requiresPayment && !this.hasUsablePaymentProvider()) {
      // STILL READING is not NOT CONFIGURED. The config fetch leaves
      // `hasUsablePaymentProvider()` false while it is open, so a visitor who
      // pressed Register before it landed was told the event has no payment
      // method — a claim about the tenant's setup made from a pending read.
      // Three distinct answers; entered fields are untouched either way.
      this.setState({ bookingError:
        this.state.evtPaymentState === "loading" ? "Still checking how this event takes payment. Try again in a moment."
          : this.state.evtPaymentState === "error" ? "Payment could not be verified for this event right now. Try again shortly."
          : "This event requires payment, and no payment method is configured for it." });
      return;
    }
    const answers = questions
      .map((q) => ({ questionId: q.id, value: this.answerForSubmit(q, answersMap[q.id]) }))
      .filter((a) => a.value !== undefined);

    // Fresh idempotency key whenever the actual payload (event, ticket,
    // answers, contact) changes since the last attempt — same key across a
    // retry of the IDENTICAL payload, so a duplicate click after a network
    // failure cannot create a second registration. The key itself carries no
    // PII: it is `newIdempotencyKey()`'s random token, keyed off a
    // fingerprint that is compared, never transmitted.
    const waitlist = Boolean(intent.waitlist);
    const attendees = this.attendeePayload();
    /* Attendee content is part of the payload, so it is part of the key:
       adding, removing or editing a person must mint a fresh idempotency key
       (CD docket 2026-09-15 item 1.8), while a retry of an identical payload
       keeps the previous one and cannot create a second registration. */
    const fingerprint = JSON.stringify({ eventId, ticketId, first, last, email, phone: st.phone, phoneCountry: st.phoneCountry, answers, consent: st.consent, waitlist, attendees });
    if (this._eventRegIdemFingerprint !== fingerprint) {
      this._eventRegIdemKey = newIdempotencyKey();
      this._eventRegIdemFingerprint = fingerprint;
    }
    this.setState({ submitting: true, bookingError: "" });
    // Signed in: register through this host's session, where the server takes
    // the registrant from the session rather than from these fields. The page
    // still sends the ticket choice and the event's own answers - everything
    // that is about the VISIT rather than about the person.
    const session = this.detailsLocked() ? this.props.bookingSession : undefined;
    const sessionSlug = this.props.bookingSessionSlug || "";
    if (session && sessionSlug) {
      const phone = String(st.phone || "").trim();
      /* Signed in, a MEMBER ticket starts its checkout as this customer (CC 0236, corrected
         in 0239): the hold records the customer, and the registration below carries its
         checkout_id. A paid ticket that isn't a member ticket keeps today's flow exactly,
         and a free, open ticket registers directly, as before. */
      if (ticketId && this.tierMemberLocked(ticketId)) {
        this.setState({ submitting: false, bookingError: "This ticket is for members only." });
        return;
      }
      const evRow = this.currentEventRow();
      const rawTier = (((evRow && evRow.raw && evRow.raw.ticketTiers) || []) as { id: string; priceMinor?: number; audience?: string }[]).find((x) => x.id === ticketId);
      const needsCheckout = !!ticketId && (this.sessionTier(ticketId)?.audience === "member" || rawTier?.audience === "member");
      /* The registration body, built once: sent straight away, or after the pay step. */
      const regBody: Record<string, unknown> = {
        // The event the visitor CHOSE, verified by the door against the event
        // the session's journey publishes — in-page navigation to another
        // event must never register them for the one the grant was minted for.
        event_id: eventId,
        ...(ticketId ? { ticket_type_id: ticketId } : {}),
        ...(answers && Object.keys(answers).length ? { answers } : {}),
        // Fill-ins: only a field the account LACKS travels, and the server
        // reads the account first, so nothing here can re-name the registrant.
        ...(this.lockedField("first") || !first ? {} : { first_name: first }),
        ...(this.lockedField("last") || !last ? {} : { last_name: last }),
        ...(this.lockedField("email") || !email ? {} : { email }),
        ...(this.lockedField("phone") || !phone ? {} : { phone, ...(this.phoneCountryForSubmit(st.phone, st.phoneCountry) ? { phone_country: this.phoneCountryForSubmit(st.phone, st.phoneCountry) } : {}) }),
      };
      const PAY_UNAVAILABLE = "PAY_UNAVAILABLE_SIGNED_IN";
      const PAY_STEP = "PAY_STEP_OPENED";
      /* The member lane is one seat, the member alone (CC 0259). */
      if (needsCheckout && attendees.length > 0) {
        this.setState({ submitting: false, attRows: [], bookingError: "Member tickets are for you only; guests need their own ticket." });
        return;
      }
      /* A paid member ticket needs a pay step this page has (PayPal or Adyen) BEFORE any seat
         is held (CC 0259): otherwise say so and hold nothing. */
      const provider = this.hasUsablePaymentProvider() ? String((this.eventPaymentConfig as { provider?: string } | null)?.provider || "") : "";
      const paypalReady = provider === "paypal" && !!(this.eventPaymentConfig as { paypal?: unknown } | null)?.paypal;
      const payable = paypalReady || provider === "adyen";
      const paidTier = Number(rawTier?.priceMinor || 0) > 0;
      if (needsCheckout && paidTier && !payable) {
        this.setState({ submitting: false, bookingError: this.state.evtPaymentState === "loading"
          ? "Still checking how this event takes payment. Try again in a moment."
          : "Paying for member tickets online isn't available yet. Please contact the organiser." });
        return;
      }
      const checkoutStep: Promise<string | undefined> = needsCheckout
        ? startPortalEventSessionCheckoutOp(sessionSlug, [{ tierId: ticketId, quantity: 1 }], session.csrf, this._eventRegIdemKey || newIdempotencyKey())
            .then((r) => {
              const co = r && r.checkout;
              /* A paid member ticket (CC 0242): the seat is held for this customer, then the
                 visitor pays with the event's own provider, and the registration carries the
                 payment. Only PayPal and Adyen have a pay step on this page; anything else
                 still stops with the message. */
              if (co && co.totals && Number(co.totals.dueNowMinor) > 0) {
                if (payable && co.checkoutId) {
                  this.setState((prev) => prev.memberPay && prev.memberPay.refused ? { submitting: false } : { submitting: false, bookingError: "", memberPay: { eventId, ticketId, quantity: 1, checkoutId: co.checkoutId, provider, body: regBody, slug: sessionSlug, holdExpiresAt: (co as { holdExpiresAt?: string }).holdExpiresAt ?? null } });
                  throw Object.assign(new Error(PAY_STEP), { code: PAY_STEP });
                }
                throw Object.assign(new Error(PAY_UNAVAILABLE), { code: PAY_UNAVAILABLE });
              }
              return co ? co.checkoutId : undefined;
            })
        : Promise.resolve(undefined);
      checkoutStep.then((checkoutId) => registerPortalEventSessionOp(sessionSlug, {
        ...(checkoutId ? { checkout_id: checkoutId } : {}),
        /* Past the consent check above (CC 0434); the signed-in door requires it (CC 0453). */
        agreedToTerms: true,
        ...regBody,
      } as never, session.csrf)).then((result: unknown) => this.finishSessionRegistration(result, eventId))
        .catch((err: unknown) => this.failSessionRegistration(err));
      return;
    }
    submitEventRegistrationOp({
      eventId,
      ticketId,
      firstName: first,
      lastName: last,
      email,
      phone: String(st.phone || "").trim() || undefined,
      phoneCountry: this.phoneCountryForSubmit(st.phone, st.phoneCountry),
      attendees: attendees.length ? attendees : undefined,
      answers,
      agreedToTerms: true as const,
      waitlist,
    }, { idempotencyKey: this._eventRegIdemKey || undefined }).then((result) => {
      if (this._gone) return;
      this._eventRegIdemKey = null;
      this._eventRegIdemFingerprint = null;
      this.setState({ submitting: false, booking: result, bookingError: "", previewStep: "confirmation", registeredEventId: eventId });
    }).catch((err) => {
      if (this._gone) return;
      this.setState({ submitting: false, bookingError: String((err && err.message) || err) });
    });
  }

  /**
   * Studio Events tab load — the real draft for the reserved `events` slug,
   * or the neutral platform defaults when the tenant has authored nothing
   * yet (never a localStorage fallback). Called once from the admin branch of
   * `componentDidMount`, alongside the appointment/OneStop record loads.
   */
  loadEventsTabDraft() {
    this.eventsTabLoadState = "loading";
    loadEventsDocument().then((doc) => {
      if (this._gone) return;
      this.eventsDocBaseline = doc;
      this.eventsTabLoadState = "ready";
      this.eventsBrandedStored = doc.brandedStored === true;
      const hydratedS = { ...doc.eventJourney, ...designerFieldsFromEventsBlockConfig(doc.block), showHeader: doc.showHeader, showFooter: doc.showFooter, brandedHeader: !!doc.brandedHeader, brandedFooter: !!doc.brandedFooter, slug: EVENTS_PAGE_SLUG };
      const hydratedCt = { ...(doc.eventJourney.customText || {}) };
      this.setState((prev) => ({
        s: prev.journey === "events" ? { ...prev.s, ...hydratedS } : prev.s,
        ct: prev.journey === "events" ? { ...prev.ct, ...hydratedCt } : prev.ct,
        stash: { ...prev.stash, events: { s: { ...prev.stash.events.s, ...hydratedS }, ct: { ...prev.stash.events.ct, ...hydratedCt } } },
        published: { ...prev.published, events: doc.isPublished },
        saved: { ...prev.saved, events: { s: hydratedS, ct: hydratedCt, at: new Date() } },
      }), () => {
        // The preview pool was fetched on mount, before this draft was known.
        // If the authored setting disagrees with the window it was fetched
        // under, re-read it rather than leaving past events unobtainable.
        if (Boolean(hydratedS.showPastEvents) !== Boolean(this._previewIncludedPast)) this.refreshPublicEventsPreview();
      });
    }).catch((err) => {
      if (this._gone) return;
      this.eventsTabLoadState = "error";
      this.setState({ eventsTabLoadError: String((err && err.message) || err) });
    });
  }

  /** Studio Landing tab load — the tenant-default landing document
   *  (`EVENT_LANDING_DEFAULT_KEY`). Per-event overrides are authored from the
   *  event's own record, not from this tenant-wide tab. */
  loadLandingTabDraft() {
    this.landingTabLoadState = "loading";
    loadLandingDocument(EVENT_LANDING_DEFAULT_KEY).then((doc) => {
      if (this._gone) return;
      this.landingDocBaseline = doc;
      this.landingTabLoadState = "ready";
      /* The saved BLOCKS are the only record of whether this page uses the
         canvas (the presentation enum has no `"custom"`), so they are
         hydrated into `s` alongside the presentation and decide `customTpl`.
         A document whose blocks carry no `grid` was authored by something
         else — left alone, canvas off. */
      const usesCanvas = carriesCustomLayout(doc.blocks);
      this.landingBrandedStored = "brandedHeader" in doc.presentation || "brandedFooter" in doc.presentation;
      const hydratedS = {
        brandedHeader: false, brandedFooter: false,
        ...doc.presentation,
        customTpl: usesCanvas,
        customBlocks: usesCanvas ? fromContractBlocks(doc.blocks) : [],
      };
      const hydratedCt = { ...(doc.presentation.customText || {}) };
      this.setState((prev) => ({
        s: prev.journey === "landing" ? { ...prev.s, ...hydratedS } : prev.s,
        ct: prev.journey === "landing" ? { ...prev.ct, ...hydratedCt } : prev.ct,
        stash: { ...prev.stash, landing: { s: { ...prev.stash.landing.s, ...hydratedS }, ct: { ...prev.stash.landing.ct, ...hydratedCt } } },
        published: { ...prev.published, landing: doc.isPublished },
        saved: { ...prev.saved, landing: { s: hydratedS, ct: hydratedCt, at: new Date() } },
      }));
    }).catch((err) => {
      if (this._gone) return;
      this.landingTabLoadState = "error";
      this.setState({ landingTabLoadError: String((err && err.message) || err) });
    });
  }

  /**
   * Save (and optionally publish) the Events tab. PUT preserves the full
   * loaded baseline (title/header/locale/other blocks) and touches only
   * showHeader/showFooter/eventJourney and the `events` block's
   * semantic-mapped fields (`saveEventsDocument` merges the rest). Refuses to
   * save if the initial load errored (a failed read must not let a save
   * overwrite real settings with this tab's local defaults) or if a save is
   * already in flight. `customText` is read from `state.ct`, not `state.s`.
   */
  saveEventsTab(publish: boolean) {
    if (this.eventsTabSaving) return;
    if (this.eventsTabLoadState !== "ready") {
      this.setState({ saveNote: "error", saveError: this.eventsTabLoadState === "loading" ? "The Events page is still loading. Try again in a moment." : "The Events page could not be loaded, so saving now could overwrite real settings. Reload and try again." });
      return;
    }
    const st = this.state;
    const snapshotS = st.s, snapshotCt = st.ct;
    const presentation = { ...pickPresentationFields(snapshotS, EVENT_LIST_PRESENTATION_KEYS), customText: { ...(snapshotCt || {}) } } as unknown as EventListPresentation;
    const blockPatch = eventsBlockConfigFromDesignerFields(snapshotS);
    const baseline: EventsDocumentDraft = this.eventsDocBaseline || {
      raw: null, showHeader: true, showFooter: true, eventJourney: presentation, block: { ...NEUTRAL_EVENTS_BLOCK_CONFIG },
    };
    this.eventsTabSaving = true;
    saveEventsDocument(baseline, {
      showHeader: Boolean(snapshotS.showHeader ?? true),
      showFooter: Boolean(snapshotS.showFooter ?? true),
      ...brandedChromeFields(snapshotS, this.eventsBrandedStored),
      eventJourney: presentation,
      blockPatch,
      timeZone: tzContext().zone,
    }).then((res) => publish ? publishEventsDocument().then(() => res) : Promise.resolve(res))
      .then((res) => {
        this.eventsTabSaving = false;
        this.eventsDocBaseline = { raw: res.data, showHeader: res.data.settings.showHeader, showFooter: res.data.settings.showFooter, eventJourney: presentation, block: { ...NEUTRAL_EVENTS_BLOCK_CONFIG, ...blockPatch, maxItems: null }, isPublished: publish || res.data.published };
        this.setState((prev) => ({
          // Only clear `dirty` if nothing changed while the request was in
          // flight — an edit made mid-save must not be reported as saved.
          dirty: prev.s === snapshotS && prev.ct === snapshotCt ? false : prev.dirty,
          saved: { ...prev.saved, events: { s: snapshotS, ct: snapshotCt, at: new Date() } },
          published: { ...prev.published, events: publish ? true : prev.published.events },
          saveNote: publish ? "published" : "saved", saveError: "",
        }));
      })
      .then(() => this.saveToast(true, publish))
      .catch((err) => { this.eventsTabSaving = false; this.failSave(err, publish); });
  }

  /** Pick one of the four built-in templates. Turns the canvas off without
   *  discarding its blocks, so switching away and back does not lose a layout
   *  — the blocks are only dropped when the author deletes them. */
  pickTemplate(id: string) {
    this.setState((prev) => ({
      s: { ...prev.s, landingTemplate: id, customTpl: false },
      dirty: true,
    }));
  }

  /** Save (and optionally publish) the tenant-default Landing document. Every
   *  authored `lp*` field and explicit empty value round-trips verbatim, read
   *  from `state.s` (visual fields) and `state.ct` (`customText`) — this is a
   *  PRESENTATION-ONLY patch: blocks/theme/branding/globalCss are never sent,
   *  so the server's partial-update semantics leave them exactly as they
   *  were. Refuses to save if the initial load errored, or while a save is
   *  already in flight. */
  saveLandingTab(publish: boolean) {
    if (this.landingTabSaving) return;
    if (this.landingTabLoadState !== "ready") {
      this.setState({ saveNote: "error", saveError: this.landingTabLoadState === "loading" ? "The Landing page is still loading. Try again in a moment." : "The Landing page could not be loaded, so saving now could overwrite real settings. Reload and try again." });
      return;
    }
    const st = this.state;
    const snapshotS = st.s, snapshotCt = st.ct;
    const presentation = { ...pickPresentationFields(snapshotS, EVENT_LANDING_PRESENTATION_KEYS), ...brandedChromeFields(snapshotS, this.landingBrandedStored), customText: { ...(snapshotCt || {}) } } as unknown as EventLandingPresentation;
    const hasExisting = Boolean(this.landingDocBaseline?.raw);
    this.landingTabSaving = true;
    const write = hasExisting
      ? updateLandingDocument(EVENT_LANDING_DEFAULT_KEY, presentation)
      : createLandingDocument(EVENT_LANDING_DEFAULT_KEY, presentation);
    /* The canvas layout is a SECOND, separate patch to the same document:
       `blocks` only, so the presentation patch above keeps its own
       partial-update guarantee and neither call round-trips the other's
       fields. Sent whenever the canvas is on, including when it holds only
       the two locked blocks — that is what makes the choice persist. */
    const canvasBlocks = snapshotS.customTpl ? toContractBlocks((snapshotS.customBlocks || []) as CustomBlock[]) : null;
    write.then(() => (canvasBlocks ? updateLandingBlocks(EVENT_LANDING_DEFAULT_KEY, canvasBlocks) : Promise.resolve()))
      .then(() => publish ? publishLandingDocument(EVENT_LANDING_DEFAULT_KEY) : Promise.resolve())
      .then(() => loadLandingDocument(EVENT_LANDING_DEFAULT_KEY))
      .then((doc) => {
        this.landingTabSaving = false;
        this.landingDocBaseline = doc;
        this.setState((prev) => ({
          dirty: prev.s === snapshotS && prev.ct === snapshotCt ? false : prev.dirty,
          saved: { ...prev.saved, landing: { s: snapshotS, ct: snapshotCt, at: new Date() } },
          published: { ...prev.published, landing: doc.isPublished },
          saveNote: publish ? "published" : "saved", saveError: "",
        }));
      })
      .then(() => this.saveToast(true, publish))
      .catch((err) => { this.landingTabSaving = false; this.failSave(err, publish); });
  }

  /**
   * Point the wizard at a catalogue. Assigning the module bindings keeps every
   * existing reader unchanged; what has gone is the old behaviour of leaving a
   * baked demo row on screen when a read came back empty.
   */
  adoptCatalog(catalog, rawServices?) {
    /* Builder default data: an EMPTY journey catalogue in the builder falls back
       to the tenant's 3 newest services (labelled sample). The customer page
       adopts the empty catalogue exactly as before. */
    const emptyInBuilder = !this.isPublic() && !((catalog && catalog.services) || []).length;
    this.publicCatalogEmpty = emptyInBuilder;
    if (emptyInBuilder && this.sampleCatalog && this.sampleCatalog.services.length) {
      catalog = this.sampleCatalog;
      rawServices = this.sampleCatalog.services;
    }
    SERVICES = catalog.services;
    SERVICE_IMAGES = catalogServiceImages(rawServices);
    LOCATIONS = catalog.locations;
    STAFF = catalog.staff;
    DURATIONS = catalog.durations;
    this.catalogSource = "public";
    this.setState((prev) => ({
      apptSample: emptyInBuilder && SERVICES.length > 0,
      svc: SERVICES.filter((x) => x.id === prev.svc)[0] ? prev.svc : ((SERVICES[0] && SERVICES[0].id) || ""),
      loc: this.catalogLocation(prev),
      staff: STAFF.filter((x) => x.id === prev.staff)[0] ? prev.staff : ((STAFF[0] && STAFF[0].id) || ""),
      dur: DURATIONS.filter((x) => x.id === prev.dur)[0] ? prev.dur : ((DURATIONS[0] && DURATIONS[0].id) || ""),
      cat: prev.journey === "appointment" ? (serviceCategoryOptions(SERVICES).some((option) => option.id === serviceFilterId(prev.cat)) ? serviceFilterId(prev.cat) : "all") : prev.cat,
    }));
  }

  /**
   * Load the real slots for the selected service. Both modes call this: the
   * designer preview used to draw slots generated from the calendar date, which
   * is fabricated availability shown to whoever is configuring the journey.
   */
  /**
   * @param fresh TRUE for a refresh whose premise is that the world moved while
   *   a read was already open — the stale-slot retry after a refused booking.
   *   Reuse there would re-offer the time the server has just refused.
   */
  refreshAvailability(fresh = false) {
    /* Reads overlap: the wider pre-catalogue read is followed by the store read
       once `adoptCatalog` reconciles `loc`, and service/location changes start
       more. Every call — the early idle return included — takes a new id, and
       a response is applied only while it is still the latest, so a slow answer
       for the previous store or service can never replace the current window,
       and its failure can never blank it. */
    const requestId = (this._availRequestId = (this._availRequestId || 0) + 1);
    const current = () => !this._gone && requestId === this._availRequestId;
    const slug = this.bookingSlug();
    const serviceId = this.state.svc;
    const key = this.availabilityKeyFor(serviceId);
    this._availKeyRequested = key;
    /* AUTHORING vs PUBLIC. The designer preview asks the real engine for the
       UNSAVED interval through the admin preview of the selected journey; the
       public page reads the saved, published config and never sends one. */
    const authoring = !this.isPublic();
    if (!serviceId || !key || (!authoring && !slug)) { this.setState({ avail: EMPTY_AVAILABILITY, availState: "idle", availFor: "" }); return; }
    const journeyId = authoring ? String(this.state.apptJourneyId || "") : "";
    if (authoring && !journeyId) {
      // No selected admin journey: say so. Never the public read instead.
      this.setState({ avail: EMPTY_AVAILABILITY, availState: "error", availFor: "", availError: "There is no saved appointment journey to preview yet. Save the journey to see its times." });
      return;
    }
    const anchor = authoring ? null : this.publicDateAnchor(this.state);
    if (anchor && anchor.kind === "invalid") {
      // A zone this browser cannot resolve: say so (calendarError renders it)
      // rather than read a window anchored on the visitor's own date. A virtual
      // or zoneless selection is NOT this case: it reads with the server's date.
      this.setState({ avail: EMPTY_AVAILABILITY, availState: "error", availFor: "", availError: i18nT("shell.allJourneys.locationTimeZoneInvalid", "This location's time zone isn't recognised. Choose another location.") });
      return;
    }
    const slotInterval = slotIntervalMinutes(this.state.s && this.state.s.slotInterval) ?? SLOT_INTERVAL_DEFAULT;
    /* Phase 1 is STORE-LEVEL, so the staff picker cannot narrow a window:
       every worker on a store's roster inherits the store's hours. The picker
       is off until worker-level availability lands (General > Staff carries a
       Coming soon pill), and no staffId is sent even if a saved journey still
       has the flag on — sending one would filter slots by a dimension this
       phase does not model. */
    const staffId = "";
    /* SERVICE-WIDE, deliberately: no `locationId` is sent. The one window is
       the authority for which saved locations are bookable (each offer names
       its store), and the selected store's times are that same window
       filtered to it (slotEntriesIn). A store-scoped read could never show
       another store is bookable, and N store reads is what this avoids. */
    this.setState({ availState: "loading", availError: "" });
    /* ONE REQUEST PER KEY WHILE IT IS OPEN.
       Mounting starts this read twice for the same key: `componentDidUpdate`
       reacts to the key change, and the same `setState`'s callback
       (`startPublicAppointment`, and `adoptJourney`'s timer when authoring)
       starts it again. The generation guard above already decides which
       RESPONSE is applied; this stops the second REQUEST being sent.

       Instance-local and pending-only: the entry lives on this component, is
       matched by key, and is dropped the moment the read settles either way.
       So it cannot outlive this Designer, cannot be reached by another mount,
       and holds nothing to serve after an api-client reconfiguration. A
       rejection clears it too, so a retry is a real request. */
    if (fresh) this._availPending = null;
    const open = this._availPending;
    let read;
    if (open && open.key === key) {
      read = open.promise;
    } else {
      read = authoring
        ? loadAvailabilityPreview({ journeyId, serviceId, slotInterval, staffId, daysAhead: this.bookWindow() })
        : loadAvailability({ slug, serviceId, staffId, daysAhead: this.bookWindow(), ...(anchor.dateFrom ? { dateFrom: anchor.dateFrom } : {}), dateTo: anchor.dateTo });
      const entry = { key, promise: read };
      this._availPending = entry;
      const clear = () => { if (this._availPending === entry) this._availPending = null; };
      read.then(clear, clear);
    }
    read.then((window) => {
      if (!current()) return;
      if (this.isPublic() && window.staff.length && !STAFF.length) STAFF = window.staff;
      // The selection is re-checked against the window that just arrived: a
      // slot identity from the previous window (another store, another
      // service) is never carried onto one that does not offer it. A choice
      // dropped after the time step stays empty — no first-slot re-pick.
      // The location is reconciled here, and only here, against this eligible
      // response: kept when still eligible, the sole eligible store chosen,
      // otherwise cleared — never borrowed from another row.
      let dropped = false;
      let deepLinked = false;
      this.setState((prev) => {
        if (this.availabilityKeyFor(prev.svc, prev) !== key) return null;
        const eligible = this.eligibleFrom(window, prev.svc);
        const loc = reconcileSelectedLocation(prev.loc, eligible, prev.pinnedLoc);
        const slot = this.revalidatedSlot(window, { ...prev, loc });
        dropped = !!prev.slot && !slot && this.pastTimeStep(prev);
        const base = { avail: window, availState: "ready", availError: "", availFor: key, loc, slot };
        // Only the LINKED service's window can apply the link (CC 0030 item 11);
        // another service's window leaves it pending.
        const want = this.isPublic() && this._urlSlot && this._urlSlot.service === prev.svc ? this._urlSlot : null;
        if (!want) return base;
        // One shot, applied to the first window for the linked service. Pure:
        // the link is cleared in the callback, not here.
        deepLinked = true;
        const now = Date.now();
        const hit = findDeepLinkedSlot(window, want, (x) => isBookableOffer(x, now));
        const hitLoc = hit ? String(hit.slot.locationId || "") : "";
        // The slot's store must itself be selectable here (eligible, and the
        // pinned kiosk store when there is one).
        const locOk = !!hitLoc && reconcileSelectedLocation(hitLoc, eligible, prev.pinnedLoc) === hitLoc;
        const entry = hit && locOk ? this.slotEntriesIn(window, hit.day, hitLoc).filter((e) => e.slot === hit.slot)[0] : null;
        if (entry) {
          const d = this.fromIso(hit.day);
          return { ...base, loc: hitLoc, day: hit.day, dayChosen: true, calMonth: { y: d.getFullYear(), m: d.getMonth() }, slot: entry.key, urlSlotGone: false, previewStep: "details", section: this.sectionForStep("details") };
        }
        // Gone: the date picker on the linked date, nothing preselected, and a
        // polite notice. A date outside the window falls back to the usual first day.
        const inWindow = !!(window.byDay && window.byDay[want.date]);
        const landing = prev.dayChosen ? prev.day : (() => { const t = this.validPublicToday({ ...prev, ...base }); return t ? this.iso(t) : ""; })();
        const day = inWindow ? want.date : (prev.s.defaultDate === "firstAvailable" ? this.firstAvailable(this.bookWindow()) : landing);
        if (!day) return { ...base, slot: "", urlSlotGone: true, availError: i18nT("shell.allJourneys.locationTimeZoneInvalid", "This location's time zone isn't recognised. Choose another location.") };
        const d = this.fromIso(day);
        const chosen = inWindow ? { dayChosen: true } : {};
        // No store chosen yet (several eligible): stay on the current first step;
        // the notice shows once they reach the time step.
        if (!loc) return { ...base, ...chosen, day, calMonth: { y: d.getFullYear(), m: d.getMonth() }, slot: "", urlSlotGone: true };
        return { ...base, ...chosen, day, calMonth: { y: d.getFullYear(), m: d.getMonth() }, slot: "", urlSlotGone: true, previewStep: "datetime", section: this.sectionForStep("datetime") };
      }, () => {
        if (deepLinked) { this._urlSlot = null; return; }
        if (!dropped) this.initDay();
      });
    }).catch((err) => {
      if (!current()) return;
      // Never a silent empty calendar: an unreadable window is reported, so a
      // day with no slots cannot be confused with a read that failed.
      this.setState({ avail: EMPTY_AVAILABILITY, availState: "error", availFor: "", availError: String((err && err.message) || err) });
    });
  }

  /** The selected service's booking questions, as the catalogue resolved them
   *  (seed review step 06). Empty when the service has none or the read is old. */
  serviceQuestions() {
    const svc = SERVICES.filter((x) => x.id === this.state.svc)[0];
    return (svc && Array.isArray(svc.questions)) ? svc.questions : [];
  }

  /** The real, configured questions for whichever journey's details step is
   *  actually rendering — the appointment's catalogue questions, or (events/
   *  landing) `landingRegistrationQuestions` from the event's own detail read.
   *  Same renderable shape (`id/label/type/help/required/options`) either
   *  way, so the one existing question renderer below serves both without a
   *  second implementation. */
  activeQuestions() {
    if (this.state.journey === "events" || this.state.journey === "landing") {
      return this.landingRegistrationQuestions || [];
    }
    return this.serviceQuestions();
  }
  /* `false` IS an answer (a required yes/no answered "No"), on every journey —
     CC 0031 item 36. */
  answerEmpty(v) { return v === undefined || v === null || v === "" || (Array.isArray(v) && !v.length); }

  /** One question's stored answer in the shape question-answers.ts fixes for its
   *  type (CC 0030 item 9, 0031 item 39): option types send an array of option
   *  ids (a single choice is a one-item array), yes/no sends true/false, number
   *  a number, datetime its ISO string, file the ids the upload returned.
   *  `undefined` = no answer to send. */
  answerForSubmit(q, v) {
    const type = q && q.type;
    if (v === undefined || v === null) return undefined;
    if (type === "radio" || type === "select" || type === "checks" || type === "multi") {
      const ids = (Array.isArray(v) ? v : [v]).map((x) => String(x)).filter((x) => x !== "");
      return ids.length ? ids : undefined;
    }
    if (type === "yesno") return v === true ? true : v === false ? false : undefined;
    if (type === "number") {
      const s = String(v).trim();
      if (s === "") return undefined;
      const n = Number(s.replace(",", "."));
      return isFinite(n) ? n : undefined;
    }
    if (type === "file") {
      const ids = (Array.isArray(v) ? v : []).filter((x) => typeof x === "string" && x);
      return ids.length ? ids : undefined;
    }
    if (type === "stepper") {
      /* EVENTS: one whole-number count per rendered row, keyed by row id (the
         registration check's map). A required stepper is answered only once
         every rendered row has a number. APPOINTMENTS: the public question has
         only single-row bounds and no row ids, so the count is one whole number,
         the same way a number is sent (CC 0040). Empty = no answer. */
      const rows = this.stepperRowsOf(q);
      if (rows) {
        const src = v && typeof v === "object" && !Array.isArray(v) ? v : {};
        const out = {};
        rows.forEach((r) => { const n = this.stepperCount(src[r.id], r); if (n !== undefined) out[r.id] = n; });
        const got = Object.keys(out).length;
        if (!got) return undefined;
        if (q.required && got < rows.length) return undefined;
        return out;
      }
      return this.stepperCount(typeof v === "object" ? undefined : v, q.stepper || null);
    }
    const s = String(v);
    return s === "" ? undefined : s;
  }

  /** An event stepper's rendered rows (all with allowMultiple, else the first),
   *  mirroring findSingleAnswerError. `null` = no rows served (the appointment
   *  shape, or an event read without row ids). */
  stepperRowsOf(q) {
    const rows = q && q.stepper && Array.isArray(q.stepper.rows) ? q.stepper.rows.filter((r) => r && r.id) : [];
    if (!rows.length) return null;
    return q.stepper.allowMultiple ? rows : rows.slice(0, 1);
  }
  /** Typed text → a whole number held to the bounds (`min`/`max` when set; never
   *  below 0). "" or unreadable → undefined, which is no answer. */
  stepperCount(v, b) {
    if (v === undefined || v === null) return undefined;
    const s = String(v).trim();
    if (s === "" || !/^\d+$/.test(s)) return undefined;
    let n = parseInt(s, 10);
    const lo = b && typeof b.min === "number" ? Math.max(0, b.min) : 0;
    const hi = b && typeof b.max === "number" ? b.max : Infinity;
    if (n < lo) n = lo;
    if (n > hi) n = hi;
    return n;
  }

  /** The appointment booking's answers map, keyed by question id, each value in
   *  its type's shape. */
  appointmentAnswersForSubmit() {
    const all = this.state.answers || {};
    const out = {};
    this.serviceQuestions().forEach((q) => {
      const v = this.answerForSubmit(q, all[q.id]);
      if (v !== undefined) out[q.id] = v;
    });
    return out;
  }

  /** A file answer on an EVENT (CC 0031 item 40): reserve the slot, PUT the
   *  bytes to the returned URL, then store the returned file id as the answer.
   *  Nothing is stored until both awaited calls succeed. APPOINTMENTS (CC 0041,
   *  live since 0043) reserve on the journey door with the chosen service, then
   *  PUT through the same uploadQuestionFileContent. A question that takes several
   *  files (`q.file.allowMultiple`, CC 0047) uploads each in turn; the answer is
   *  every returned id, stored only once all of them are in. */
  uploadQuestionFile(q, picked) {
    const list = Array.isArray(picked) ? picked : picked && picked.target && picked.target.files ? Array.prototype.slice.call(picked.target.files) : [];
    const cfg = this.fileConfigOf(q);
    const files = cfg.multiple ? list : list.slice(0, 1);
    const setUp = (patch) => this.setState((prev) => ({ fileUploads: { ...(prev.fileUploads || {}), [q.id]: patch } }));
    const names = files.map((x) => x.name).join(", ");
    if (!files.length) { this.actions.setAnswer(q.id, undefined); setUp(null); return; }
    const onEvents = this.state.journey === "events" || this.state.journey === "landing";
    const eventId = String(this.state.evt || "");
    const slug = this.bookingSlug();
    const svcId = String(this.state.svc || "");
    if (onEvents && !eventId) { setUp({ state: "error", name: names, files, err: i18nT("journeys.bookingQuestions.chooseEventFirst", "Choose an event first.") }); return; }
    if (!onEvents && (!slug || !svcId)) { setUp({ state: "error", name: names, files, err: i18nT("journeys.bookingQuestions.chooseServiceFirst", "Choose a service first.") }); return; }
    // The size tier is checked before anything is reserved; the server refuses the same file anyway.
    const tooBig = questionFileTooLarge(q, files);
    if (tooBig) { setUp({ state: "error", name: names, files, err: tooBig }); return; }
    setUp({ state: "uploading", name: names, files, err: "" });
    this.actions.setAnswer(q.id, undefined);
    const one = (file) => {
      const input = { questionId: q.id, name: file.name, size: file.size, contentType: file.type || "application/octet-stream" };
      const reserve = onEvents ? createQuestionFileUpload(eventId, input) : createJourneyQuestionFileUpload(slug, { ...input, serviceId: svcId });
      return reserve.then((res) => {
        const slot = (res && (res.data || res)) || {};
        if (!slot.uploadUrl || !slot.fileId) throw new Error(i18nT("journeys.walkIn.uploadNotStarted", "The upload could not be started."));
        return uploadQuestionFileContent(slot.uploadUrl, file).then((put) => (put && put.fileId) || slot.fileId);
      });
    };
    files.reduce((chain, file) => chain.then((ids) => one(file).then((id) => ids.concat([id]))), Promise.resolve([]))
      .then((ids) => {
        if (this._gone) return;
        this.actions.setAnswer(q.id, ids);
        setUp({ state: "done", name: names, files, err: "" });
      })
      .catch((err) => {
        if (this._gone) return;
        setUp({ state: "error", name: names, files, err: String((err && err.message) || i18nT("journeys.walkIn.uploadFailed", "The file could not be uploaded.")) });
      });
  }

  /** The host the shared booking-question renderer reads (BookingQuestionFields): answers from
   *  `state.answers`, the server's per-answer refusal or the required line, and this class's upload. */
  bookingQuestionsHost() {
    const st = this.state;
    return {
      value: (q) => (st.answers || {})[q.id],
      set: (id, v) => this.actions.setAnswer(id, v),
      error: (q) => {
        const up = (st.fileUploads || {})[q.id];
        if (up && up.state === "error" && up.err) return up.err;
        if (st.answerErrors && st.answerErrors[q.id]) return st.answerErrors[q.id];
        return q.required && st.answersTouched && this.answerForSubmit(q, (st.answers || {})[q.id]) === undefined
          ? i18nT("journeys.walkInJourney.text12", "This answer is required.") : undefined;
      },
      file: (q) => { const up = (st.fileUploads || {})[q.id]; return up ? { files: up.files || [], busy: up.state === "uploading", error: up.state === "error" ? up.err : "" } : null; },
      pickFiles: (q, files) => this.uploadQuestionFile(q, files),
    };
  }

  /** A file question's picker settings from the catalog (CC 0047): accept list,
   *  size ceiling, several files or one. Absent settings leave the picker open. */
  fileConfigOf(q) {
    return questionFileConfig(q);
  }


  /** The real slots the backend offered for one day. Never generated. */
  slotsFor(isoStr) {
    return this.slotEntriesFor(isoStr).map((entry) => entry.slot);
  }

  /**
   * Every real slot of one day paired with its SELECTION IDENTITY.
   *
   * `state.slot`, the picker values and the first-slot default all hold this
   * key, never the displayed time. Store-level availability makes the
   * service-wide window a union across stores, so two stores can offer the
   * same `startUtc` under the same label; a label-keyed selection resolved
   * store B's offer to store A's. The producer's `id` is not relied on — it is
   * not guaranteed present or unique across stores — so the key is the offer's
   * own store + instant (+ end + staff), with an occurrence suffix for a
   * byte-identical repeat so the picker's React keys stay unique. The slot
   * objects themselves are returned untouched.
   */
  slotEntriesIn(window, isoStr, loc?: string) {
    // The service-wide window narrowed to the SELECTED store's real, future,
    // open offers. An offer naming no store, another store, or no capacity is
    // not a time this customer can book here.
    const at = loc === undefined ? this.state.loc : loc;
    const now = Date.now();
    const list = ((window && window.byDay && window.byDay[isoStr]) || [])
      .filter((slot) => !!at && slot.locationId === at && isBookableOffer(slot, now));
    const seen = {};
    return list.map((slot) => {
      const base = [slot.locationId || "", slot.startUtc, slot.endUtc, slot.staffId || ""].join("|");
      seen[base] = (seen[base] || 0) + 1;
      return { key: seen[base] > 1 ? base + "#" + seen[base] : base, slot };
    });
  }

  slotEntriesFor(isoStr) {
    return this.slotEntriesIn(this.state.avail || EMPTY_AVAILABILITY, isoStr);
  }

  /** The whole slot object behind a selection key, so submit carries its own
   *  UTC instant, staff and store. */
  slotObjFor(isoStr, key) {
    if (!key) return null;
    const hit = this.slotEntriesFor(isoStr).filter((entry) => entry.key === key)[0];
    return hit ? hit.slot : null;
  }

  /** Picker options for one day: value = selection key, label = the time a
   *  person reads. When the day's offers span more than one store, each option
   *  also names its store (sublabel + full tooltip) so two same-time offers are
   *  two distinguishable choices, not two identical buttons. */
  slotOptionsFor(isoStr) {
    const entries = this.slotEntriesFor(isoStr);
    const stores = {};
    entries.forEach((entry) => { stores[entry.slot.locationId || ""] = true; });
    const multiStore = Object.keys(stores).length > 1;
    return entries.map((entry) => {
      const place = multiStore && entry.slot.locationId
        ? LOCATIONS.filter((x) => x.id === entry.slot.locationId)[0]
        : null;
      const option = { value: entry.key, label: this.clockLabel(entry.slot.time) };
      if (place && place.name) { option.sublabel = place.name; option.title = this.clockLabel(entry.slot.time) + " · " + place.name; }
      return { slot: entry.slot, option };
    });
  }

  /**
   * The selection that survives a freshly loaded window. A key the new window
   * still offers is kept. One it no longer offers is replaced by that day's
   * first slot while the customer is still choosing (the same default a day
   * pick applies) — but once they are past the time step it is CLEARED, never
   * swapped, so a submit reports the time as gone instead of booking another.
   */
  revalidatedSlot(window, prev) {
    if (!prev.slot) return "";
    const entries = this.slotEntriesIn(window, prev.day, prev.loc);
    if (entries.some((entry) => entry.key === prev.slot)) return prev.slot;
    if (this.pastTimeStep(prev)) return "";
    return entries[0] ? entries[0].key : "";
  }

  pastTimeStep(st) {
    return st.previewStep === "details" || st.previewStep === "confirmation";
  }


  initDay() {
    if (!this.state.slot) {
      // JAI-2135: an unchosen public day lands on the anchor's civil today: the
      // selected store's, else the window's first day, else the first day the
      // server served. Never the browser's date. A day the visitor picked stays.
      let iso;
      if (this.state.s.defaultDate === "firstAvailable") iso = this.firstAvailable(this.bookWindow());
      else if (this.isPublic() && !this.state.dayChosen) { const t = this.validPublicToday(); iso = t ? this.iso(t) : ""; }
      else iso = this.state.day;
      if (!iso) { this.setState({ availState: "error", availError: i18nT("shell.allJourneys.locationTimeZoneInvalid", "This location's time zone isn't recognised. Choose another location.") }); return; }
      const d = this.fromIso(iso);
      this.setState({ day: iso, calMonth: { y: d.getFullYear(), m: d.getMonth() }, slot: this.firstSlotFor(iso) });
    }
  }

  /** Appointments tab save/publish — the contract write path (drop 1).
   *  Nothing here is swallowed: a refusal sets the note AND the per-control rows. */
  saveAppointments(alsoPublish) {
    this.setState({ saveNote: "saving", saveError: "", refusedFields: [], saveNotices: [] });
    // Read the live state at call time: the id/create path may re-baseline first.
    const run = (id) => {
      const st = this.state;
      return saveAppointmentSettings(id, st.s, st.ct, { baseline: st.apptBaseline, journey: st.apptJourney }).then((res) => {
        if (!res.ok) { this.reportRefusal(res.refusal); return null; }
        if (!alsoPublish) return res;
        // An empty diff wrote nothing — but Publish was still asked for, and a
        // saved draft with no further edits is exactly the case where a customer
        // is waiting on it. Only an already-published journey has nothing to do.
        if (res.noop && String((res.journey || {}).status || "") === "published") return res;
        return publishAppointmentJourney(id).then((pub) => {
          if (!pub.ok) { this.reportRefusal(pub.refusal); return null; }
          return pub;
        });
      }).then((res) => {
        if (!res) return;
        const noop = !!res.noop;
        const journey = res.journey || st.apptJourney;
        // Re-baseline from the server's answer, so the next save diffs against what
        // was actually stored rather than what this screen believes it sent.
        const h = journey ? designerFromJourney(journey) : { s: {}, ct: {} };
        const base = { s: { ...DEFAULTS, ...h.s }, ct: { ...h.ct } };
        const notices = noop ? [] : this.saveNotices(st);
        this.setState((prev) => ({
          dirty: false,
          apptJourney: journey, apptBaseline: base,
          saved: { ...prev.saved, appointment: { s: prev.s, ct: prev.ct, at: new Date() } },
          published: alsoPublish && !noop ? { ...prev.published, appointment: true } : prev.published,
          saveNote: noop ? "nochange" : alsoPublish ? "published" : "saved",
          saveError: "", refusedFields: [], saveNotices: notices,
        }));
        if (noop) this.noopToast(); else this.saveToast(true, alsoPublish);
      }).catch((err) => this.failSave(err, alsoPublish));
    };
    const id = this.state.apptJourneyId;
    if (id) { run(id); return; }
    resolveAppointmentJourney(String(this.state.s.slug || "")).then((row) => {
      if (row && this.ownsJourney(row)) {
        return loadAppointmentJourney(String(row.id)).then((journey) => {
          this.adoptJourney(journey, false, row);
          return run(String(row.id));
        });
      }
      // No appointment journey on this tenant: make one from the 'classic' starter
      // rather than refusing the save.
      // Never where the tenant is known not to compose booking (CC, 2026-10-03).
      if (!this.journeyEnabled("appointment")) { this.failSave(new Error(i18nT("journeys.allJourneys.appointmentsNotInWorkspace", "Appointments aren't part of this workspace, so there is no appointment journey to save.")), alsoPublish); return; }
      return createAppointmentJourney((this.props.tenantName || "Appointments") + " booking").then((res) => {
        if (!res.ok) { this.reportRefusal(res.refusal); return; }
        this.adoptJourney(res.journey, false);
        return run(String(res.journey.id));
      });
    }).catch((err) => this.failSave(err, alsoPublish));
  }

  /** Adopt a stored journey as this tab's source of truth. `seedForm` also writes
   *  the hydrated values into the open form (mount); a save-time adopt only
   *  re-baselines, so edits made before the save are not thrown away. */
  adoptJourney(journey, seedForm, row) {
    // The detail read can come back without the address fields the list row
    // carries; the row fills those gaps rather than the tab inventing a host.
    if (row) {
      journey = {
        ...journey,
        domain: journey.domain || row.domain,
        slug: journey.slug || row.slug,
        status: journey.status || row.status,
        // The server-declared public address survives adoption: the admin row
        // carries `publicUrl`/`publicPath`, and `publicLinkFor` reads them.
        publicUrl: journey.publicUrl || row.publicUrl,
        publicPath: journey.publicPath || row.publicPath,
      };
    }
    const h = designerFromJourney(journey);
    const s = { ...DEFAULTS, ...h.s };
    const ct = { ...h.ct };
    // The journey carries the slug the availability endpoint is keyed on, so the
    // preview can only load real slots once this has landed. Without this the
    // designer would show an empty calendar rather than the tenant's own.
    if (!this.isPublic()) setTimeout(() => {
      if (this._gone) return;
      this.refreshPublicCatalog();
      this.refreshAvailability();
    }, 0);
    this.setState((prev) => {
      const saved = { ...prev.saved, appointment: { s, ct, at: new Date(String(journey.updatedAt || Date.now())) } };
      const published = { ...prev.published, appointment: journey.status === "published" };
      // The baseline is the MERGED object, not the hydrator's partial output —
      // otherwise every default diffs on the first save.
      this.rememberJourneyId(journey.id);
      const next = { apptJourneyId: String(journey.id), apptJourney: journey, apptBaseline: { s, ct }, saved, published };
      if (!seedForm) return next;
      if (prev.journey === "appointment") return { ...next, s, ct, dirty: false };
      return { ...next, stash: { ...prev.stash, appointment: { s, ct } } };
    });
  }

  /* ── Walk-Ins seam ───────────────────────────────────────────────────
   *
   * The Walk-Ins tab writes through the SAME admin journey door the
   * Appointments tab does — `type: "queue"` instead of `"appointment"`, and a
   * `walkIn` presentation block instead of `steps`. Everything below mirrors
   * the appointment methods above so the two tabs cannot drift about what a
   * save, a publish or a refusal means.
   *
   * Nothing here writes on LOAD. Hydration is three reads (the journey row,
   * the journey, the tenant's queues) and a fourth for the public projection
   * of whichever queues are already attached; a tenant that has never opened
   * this tab ends it with exactly the rows it started with. */
  hydrateWalkIns() {
    if (this.isPublic()) return;
    // No queue module on this tenant: nothing to read, and the queue routes would be refused.
    // Judged on the designer's own module list (the one its tabs follow), not the module cache.
    if (!this.journeyEnabled("queue")) return;
    // The tenant's real queues, for the author's explicit pick. A tenant with
    // none gets an empty picker and a stated reason — never an invented desk.
    listWalkInQueueOptions()
      .then((options) => { if (!this._gone) this.setState({ wiQueueOptions: options }, () => { if (!(this.state.wiQueueIds || []).length) this.refreshWalkInQueues(); }); })
      .catch(() => { /* the picker says "could not be loaded" on its own */ });

    resolveWalkInJourney(String(this.state.s.slug || WALKIN_DEFAULT_SLUG))
      .then((row) => (row && this.ownsJourney(row)
        ? loadWalkInJourney(String(row.id)).then((journey) => ({ row, journey }))
        : null))
      .then((got) => { if (got) this.adoptWalkInJourney(got.journey, true, got.row); })
      .catch((err) => this.failSave(err, false));
  }

  /** Adopt a stored queue journey as this tab's source of truth. `seedForm`
   *  also writes the hydrated values into the open form (mount); a save-time
   *  adopt only re-baselines, so edits made before the save survive. */
  adoptWalkInJourney(journey, seedForm, row) {
    if (row) {
      journey = {
        ...journey,
        domain: journey.domain || row.domain,
        slug: journey.slug || row.slug,
        status: journey.status || row.status,
        // The server-declared public address survives adoption: the admin row
        // carries `publicUrl`/`publicPath`, and `publicLinkFor` reads them.
        publicUrl: journey.publicUrl || row.publicUrl,
        publicPath: journey.publicPath || row.publicPath,
      };
    }
    const h = designerFromWalkInJourney(journey);
    const s = { ...QUEUE_DEFAULTS, ...h.s };
    const ct = { ...h.ct };
    const queueIds = h.queueIds;
    this.setState((prev) => {
      const saved = { ...prev.saved, queue: { s, ct, at: new Date(String(journey.updatedAt || Date.now())) } };
      const published = { ...prev.published, queue: journey.status === "published" };
      const next = {
        wiJourneyId: String(journey.id), wiJourney: journey,
        wiBaseline: { s, ct, queueIds }, wiQueueIds: queueIds, saved, published,
      };
      if (!seedForm) return next;
      if (prev.journey === "queue") return { ...next, s, ct, dirty: false };
      return { ...next, stash: { ...prev.stash, queue: { s, ct } } };
    }, () => this.refreshWalkInQueues());
  }

  /**
   * The canonical public read of every attached queue — the SAME projection
   * the customer page reads, so the preview's service list, service points,
   * status and waits are the tenant's own and cannot disagree with what a
   * visitor is shown.
   */
  refreshWalkInQueues() {
    const attached = this.state.wiQueueIds || [];
    /* Builder default data: a page with no queue attached previews the tenant's
       3 newest queues through the same public read a customer page uses. They
       are never written — save reads `wiQueueIds`, which stays empty. */
    const sample = !attached.length && !this.isPublic();
    const ids = sample ? newestFirst(this.state.wiQueueOptions || [], SAMPLE_COUNT).map((o) => o.id) : attached;
    if (!ids.length) { this.setState({ wiQueues: [], wiQueuesLoading: false, wiSample: false }); return; }
    this.setState({ wiQueuesLoading: true });
    loadWalkInQueues(ids)
      .then((reads) => { if (!this._gone) this.setState({ wiQueues: reads, wiQueuesLoading: false, wiSample: sample }); })
      .catch(() => { if (!this._gone) this.setState({ wiQueuesLoading: false }); });
  }

  /** Attach / detach one of the tenant's real queues. Marks the tab dirty; the
   *  selection only reaches the server on Save, like every other setting. */
  toggleWalkInQueue(id) {
    this.setState((prev) => {
      const list = (prev.wiQueueIds || []).slice();
      const at = list.indexOf(id);
      if (at > -1) list.splice(at, 1); else list.push(id);
      return { wiQueueIds: list, dirty: true };
    }, () => this.refreshWalkInQueues());
  }

  saveWalkIns(alsoPublish) {
    this.setState({ saveNote: "saving", saveError: "", refusedFields: [], saveNotices: [] });
    const run = (id) => {
      const st = this.state;
      return saveWalkInSettings(id, st.s, st.ct, st.wiQueueIds || [], { baseline: st.wiBaseline, journey: st.wiJourney })
        .then((res) => {
          if (!res.ok) { this.reportRefusal(res.refusal); return null; }
          if (!alsoPublish) return res;
          if (res.noop && String((res.journey || {}).status || "") === "published") return res;
          return publishWalkInJourney(id).then((pub) => {
            if (!pub.ok) { this.reportRefusal(pub.refusal); return null; }
            return pub;
          });
        })
        .then((res) => {
          // A refusal already reported itself; the draft stays DIRTY so the
          // operator does not lose what they typed and can retry.
          if (!res) return;
          const noop = !!res.noop;
          const journey = res.journey || this.state.wiJourney;
          const h = journey ? designerFromWalkInJourney(journey) : { s: {}, ct: {}, queueIds: [] };
          const base = { s: { ...QUEUE_DEFAULTS, ...h.s }, ct: { ...h.ct }, queueIds: h.queueIds };
          this.setState((prev) => ({
            dirty: false,
            wiJourney: journey, wiBaseline: base, wiQueueIds: h.queueIds.length ? h.queueIds : prev.wiQueueIds,
            saved: { ...prev.saved, queue: { s: prev.s, ct: prev.ct, at: new Date() } },
            // The pill reflects the SERVER's answer, not the button that was
            // pressed: a publish that no-op'd on an already-published journey
            // is still published, and a save is never reported as one.
            published: { ...prev.published, queue: String((journey || {}).status || "") === "published" },
            saveNote: noop ? "nochange" : alsoPublish ? "published" : "saved",
            saveError: "", refusedFields: [], saveNotices: [],
          }));
          if (noop) this.noopToast(); else this.saveToast(true, alsoPublish);
        })
        .catch((err) => this.failSave(err, alsoPublish));
    };
    const id = this.state.wiJourneyId;
    if (id) { run(id); return; }
    resolveWalkInJourney(String(this.state.s.slug || WALKIN_DEFAULT_SLUG)).then((row) => {
      if (row && this.ownsJourney(row)) {
        return loadWalkInJourney(String(row.id)).then((journey) => {
          this.adoptWalkInJourney(journey, false, row);
          return run(String(row.id));
        });
      }
      return createWalkInJourney((this.props.tenantName || "Walk-ins") + " line").then((res) => {
        if (!res.ok) { this.reportRefusal(res.refusal); return; }
        this.adoptWalkInJourney(res.journey, false);
        return run(String(res.journey.id));
      });
    }).catch((err) => this.failSave(err, alsoPublish));
  }

  /** The admin list op is not tenant-scoped here, so a row belonging to another
   *  tenant must never hydrate or be patched by this tab. Rows carry a short
   *  tenant key; the shell carries a served deployment slug. */
  apptKey() { return "apd.apptJourney." + String(this.props.tenant || "default"); }
  rememberedJourneyId() {
    try { return localStorage.getItem(this.apptKey()) || ""; } catch (e) { return ""; }
  }
  rememberJourneyId(id) {
    try { localStorage.setItem(this.apptKey(), String(id)); } catch (e) {}
  }

  ownsJourney(row) {
    // A journey this tab created under this tenant is ours whatever the row says.
    if (row && this.rememberedJourneyId() && String(row.id) === this.rememberedJourneyId()) return true;
    const theirs = String((row && row.tenant) || "").toLowerCase().replace(/[^a-z]/g, "");
    const mine = String(this.props.tenant || "").toLowerCase().replace(/[^a-z]/g, "");
    if (!theirs || !mine) return true;
    return mine.indexOf(theirs) > -1 || theirs.indexOf(mine) > -1;
  }

  /** What a successful save could NOT put on the visitor's page, said out loud. */
  saveNotices(st) {
    const out = [];
    const unmapped = unmappedKeys(st.s, st.apptBaseline && st.apptBaseline.s);
    const notLive = notYetLiveKeys(st.s);
    const inert = inertTextKeys(st.ct);
    if (unmapped.length) out.push("Not carried by the booking contract, so not stored: " + unmapped.join(", ") + ".");
    if (notLive.length) out.push("Stored, but the public page does not render it yet: " + notLive.join(", ") + ".");
    if (inert.length) out.push("Custom text the public page does not read yet: " + inert.join(", ") + ".");
    return out;
  }

  /** The origin the public link, embed snippet and QR codes point at: the
   *  appointment journey's own domain when the server knows one. */

  /** Attend is its own DOMAIN, so its ROOT is the event list and `/{eventId}`
   *  is one event. Those are the only two addresses this mount generates —
   *  there is no `/events` base to preserve and no alias to stay inside. */
  pushPublicEventsHistory(eventId: string) {
    // A placeholder marker: `skipped` is only authoritative once the detail
    // has resolved, at which point `selectPublicEvent` replaces it in place.
    // (CC, 2026-10-05) Print the event's readable public path when its row
    // declared one (the server resolves it as well as the id), else the id.
    // `evt` stays the id: the detail read sets it from `detail.id` whichever
    // shape the address bar carries.
    const declared = canonicalEventPath((this.eventsRows.filter((row) => row.id === eventId)[0] || { publicPath: "" }).publicPath || "");
    try { window.history.pushState({ attendEventJourney: { fromList: this.publicEventFromList, skipped: false } }, "", declared || `/${encodeURIComponent(eventId)}`); } catch {}
  }

  pushPublicEventsListHistory() {
    try { window.history.pushState({}, "", "/"); } catch {}
  }

  /** Restores state to match a URL the browser already navigated to (back/
   *  forward) — never pushes a new entry itself, that would duplicate the
   *  one the browser just created. Malformed/extra-segment paths land on the
   *  same honest not-found state the initial mount would show. */
  handlePublicEventsPopState() {
    if (this._gone || !this.isPublic()) return;
    let pathname = "";
    try { pathname = window.location.pathname; } catch { return; }
    // The SAME parser the standalone mount used on first load
    // (`parsePublicEventsPath`), so a root-mounted `/{eventId}` resolves
    // identically whether the visitor typed it or reached it with the back
    // button. It read `parts[1]` before, which under the canonical root
    // scheme is never the id: `/{eventId}` would have been mistaken for the
    // list, silently dropping the visitor's back-navigation on the wrong page.
    const route = parsePublicEventsPath(pathname);
    if (route.invalid) { this.setState({ previewStep: "linkError" }); return; }
    if (!route.eventId) {
      // Same fix as `backToEvents`: an actual journey switch, not just a
      // step flip, so `isEvents()`/`usingServerEvents` are true and the
      // real page-1 config + rows load rather than falling back to
      // whatever detail/related rows are still in `eventsRows`.
      // Same invalidation as `backToEvents` — a back-navigation to the list
      // outruns a detail request that would otherwise settle on top of it.
      this._evtDetailRequestId = (this._evtDetailRequestId || 0) + 1;
      this.publicLandingStepSkipped = false;
      this.publicEventFromList = false;
      this.setState({ journey: "events", previewStep: "events", section: "events" }, () => this.bootstrapPublicEventsList());
      return;
    }
    this.setState({ journey: "landing" }, () => this.selectPublicEvent(route.eventId, { fromPopstate: true }));
  }

  publicOrigin() {
    if (this.state.journey === "events" || this.state.journey === "landing") {
      if (this.isPublic()) {
        // The anonymous mount HAS no tenant (`tenant=""`, no config hook) —
        // asking `publicHost()` here just drives an authenticated Studio
        // settings fetch that can only fail for this mount and falls back
        // to an invented `attend.your-workspace`. The browser's own origin
        // IS the canonical one already; this mount is served from it.
        try { return window.location.origin; } catch { return ""; }
      }
      // Studio preview: the real, resolved attend config only. `publicHost`
      // returns a same-shaped fallback host before the config has loaded,
      // so callers that must not act on an invented host gate on
      // `attendConfigResolved` (set once `ensurePublicConfig()` settles)
      // rather than trusting this return value alone.
      //
      // The configured authority is taken WHOLE — port included. `attendHost`
      // is the authority the deployment declared (`localhost:18811` locally,
      // the tenant's real Attend domain live); this composes around it and
      // never rewrites, truncates or substitutes a hostname of its own.
      // If it already declares a scheme, that scheme wins, because a config
      // that says `http://` is stating something Studio's own protocol cannot
      // tell us. Otherwise the SCHEME follows Studio's origin: a local
      // generator maps the canonical attend host to a plain-HTTP local port,
      // and hardcoding `https://` there fails TLS against it.
      const authority = splitPublicAuthority(publicHost("attend", this.props.tenant));
      if (!authority.host) return "";
      let scheme = "https:";
      try { if (window.location.protocol === "http:" || window.location.protocol === "https:") scheme = window.location.protocol; } catch {}
      return (authority.scheme || scheme) + "//" + authority.host;
    }
    // Walk-Ins lives on the JOIN host. Deriving it from `publicLink()` keeps
    // the origin and the link from ever disagreeing, and keeps the `book`
    // fallback below off a walk-in embed snippet or QR.
    if (this.state.journey === "queue") {
      const walkInLink = this.publicLink();
      return walkInLink ? walkInLink.replace(/^([a-z]+:\/\/[^/]+).*$/, "$1") : "";
    }
    const link = this.state.journey === "appointment" ? publicLinkFor(this.state.apptJourney) : "";
    if (link) return link.replace(/^(https?:\/\/[^/]+).*$/, "$1");
    // An anonymous PUBLIC mount is served from its own origin, and that is
    // the answer until the server's declared link hydrates. `publicHost()`
    // below reads the tenant settings through an admin endpoint that requires
    // `settings.read` — every call from this mount was a 403, re-fired on each
    // render (`renderVals` asks for this origin unconditionally).
    if (this.isPublic()) { try { return window.location.origin; } catch { return ""; } }
    /* No vanity-domain literal. `publicHost("book")` resolves the tenant's own
       public config and degrades to a bare `book.<slug>` when it has not
       loaded — never another tenant's real domain (2026-09-08). */
    return "https://" + (this.props.publicHost || publicHost("book", this.props.tenant));
  }

  /** True when the address printed for an appointment journey is the SERVER'S
   *  declaration rather than one this tab composed. The Launch & embed panel
   *  says which, so a composed address is never passed off as confirmed. */
  apptLinkDeclared() {
    return this.state.journey === "appointment" && !!publicLinkFor(this.state.apptJourney);
  }

  /** The deployment's own booking address for this journey, composed from the
   *  tenant public config (`book.<tenant>.<rootDomain>`) and the journey's
   *  slug. Used ONLY when the server has declared none: an operator opening
   *  Launch & embed on an unpublished journey needs to see where it will live,
   *  and the panel labels it as not live yet. Returns "" if either half is
   *  missing — still never an invented host. */
  composedApptLink() {
    // Studio only. A public mount never composes a tenant host: that read is
    // the admin settings endpoint, which an anonymous visitor cannot call.
    if (this.isPublic()) return "";
    const host = String(this.props.publicHost || publicHost("book", this.props.tenant) || "").trim();
    const journeySlug = String((this.state.apptJourney && this.state.apptJourney.slug) || "").trim();
    const slug = journeySlug || String(this.state.s.slug || "").trim();
    if (!host || !slug || host.indexOf(".") < 0) return "";
    return "https://" + host.replace(/\/+$/, "") + "/" + slug;
  }

  /** The journey's public address, or "" when the server has not given it one. */
  publicLink() {
    // The server's declaration always wins (root-path journeys included). Until
    // it hydrates, a public mount's address is the one the visitor is on;
    // Studio composes a provisional one and labels it as such.
    if (this.state.journey === "appointment") {
      return publicLinkFor(this.state.apptJourney) || (this.isPublic() ? this.servedPublicAddress() : this.composedApptLink());
    }
    if (this.state.journey === "landing") {
      // The per-event path the server itself declared (`row.public_path`) —
      // never a hand-composed `/{slug}` guess, and never the appointment host.
      // `canonicalEventPath` accepts only the canonical one-segment `/{eventId}`
      // shape; anything else prints no link rather than a guessed one.
      const row = this.eventsRows.find((e) => e.id === this.state.evt);
      const origin = this.publicOrigin();
      if (!origin) return "";
      const path = canonicalEventPath(row?.publicPath || "");
      if (path) return origin + path;
      return "";
    }
    // Attend's ROOT is the event list — it is a dedicated domain, so there is
    // no path segment to compose.
    if (this.state.journey === "events") {
      const origin = this.publicOrigin();
      return origin ? origin + "/" : "";
    }
    // Walk-Ins is served on the tenant's JOIN host. `publicHost("join")` now
    // returns the SERVED authority (`joinHost`, resolved by the canonical
    // `publicBaseUrlFor(queue)`) or "" — it no longer composes
    // `join.<tenant>.<rootDomain>`, which was a hostname that resolved to
    // nothing while the real surface was on a local `localhost:<port>`.
    //
    // The authority is used WHOLE. There is no shape check on it: an earlier
    // cut required a dot in the hostname to weed out the composed fallback,
    // and that heuristic rejected the canonical local `localhost:18812`
    // outright. With the fallback gone there is nothing to weed out — a
    // non-empty value here IS the configured authority, whatever it looks
    // like, and "" is the only "no link yet".
    //
    // NEVER the `book` host: that is the appointment surface and serves no
    // queue page at all.
    if (this.state.journey === "queue") {
      /* The join host's ROOT is the walk-in page for the DEFAULT journey — same
         rule as attend's root being the event list (operator, 2026-09-15, PP):
         there were two addresses for one surface, `join.<tenant>/walk-in` and
         `join.<tenant>/`, and the root is the correct one.

         Scoped to the default slug on 2026-09-16 (CD's blocking review, and
         they were right). The slug is not decoration on this surface — it is
         the journey's IDENTITY: `resolveWalkInJourney(slug)` is how a walk-in
         journey is looked up, and `public-walkins` resolves a bare root to
         WALKIN_DEFAULT_SLUG. So dropping a NON-default slug does not shorten
         the address, it addresses a DIFFERENT journey — a tenant on
         `front-desk` would have been handed a link to whatever journey holds
         the default slug. One surface one address was the right call; applying
         it to custom slugs was overreach.

         `/walk-in` remains SERVED as a compatibility alias; it is simply never
         what we hand anyone, compose, print or embed. */
      const wiSlug = String(this.state.s.slug || WALKIN_DEFAULT_SLUG).trim();
      const atDefaultSlug = !wiSlug || wiSlug === WALKIN_DEFAULT_SLUG;
      const joinPath = atDefaultSlug ? "/" : "/" + wiSlug;
      if (this.isPublic()) { try { return window.location.origin + joinPath; } catch { return ""; } }
      // The server's own `publicUrl`/`publicPath` on the published journey
      // wins; the join-authority composition below is the fallback for a row
      // the server has not yet given an address. Reduced to its origin only at
      // the default slug — a served address that carries a custom slug is kept
      // verbatim, since the server is the authority on where it published it.
      if (this.state.wiJourney) {
        const served = publicLinkFor(this.state.wiJourney);
        if (served) {
          if (!atDefaultSlug) return served;
          try { return new URL(served).origin + "/"; } catch { return served; }
        }
      }
      const joinAuthority = splitPublicAuthority(publicHost("join", this.props.tenant));
      if (!joinAuthority.host) return "";
      // Scheme: what the config declared, else Studio's own — so a local
      // plain-HTTP join surface is not linked over https and failed on TLS.
      let joinScheme = "https:";
      try { if (window.location.protocol === "http:" || window.location.protocol === "https:") joinScheme = window.location.protocol; } catch {}
      return (joinAuthority.scheme || joinScheme) + "//" + joinAuthority.host + joinPath;
    }
    if (this.isPublic()) return this.servedPublicAddress();
    return "https://" + (this.props.publicHost || publicHost("book", this.props.tenant)) + "/" + String(this.state.s.slug || this.state.journey);
  }

  /** The address a PUBLIC mount is actually served at: the browser's own
   *  origin and path, as the visitor reached it — `/` for a booking-root
   *  journey, `/{slug}` otherwise. Nothing composed, no tenant host derived,
   *  and no admin settings read (anonymous visitors cannot make one). Query
   *  and hash are not part of the address; a trailing slash is not either,
   *  except for the root itself. */
  servedPublicAddress() {
    try {
      const path = String(window.location.pathname || "/").replace(/\/+$/, "") || "/";
      return window.location.origin + path;
    } catch { return ""; }
  }

  /** One success/failure announcement for every save path (toast + the inline note). */
  saveToast(ok, published) {
    try {
      toast(published
        ? { tone: "success", title: "Published", subtitle: "This journey is live for customers now.", autoDismissMs: 4000 }
        : { tone: "success", title: "Saved", subtitle: "Your changes are stored.", autoDismissMs: 4000 });
    } catch (e) {}
  }

  noopToast() {
    try {
      toast({ tone: "info", title: "No changes to save", subtitle: "Nothing here differs from what is already live.", autoDismissMs: 4000 });
    } catch (e) {}

  }

  errorToast(message, published) {
    try {
      toast({ tone: "error", title: published ? "Couldn't publish" : "Couldn't save", subtitle: message, autoDismissMs: 9000 });
    } catch (e) {}
  }

  failSave(err, published) {
    const message = String((err && err.message) || err || "Something went wrong.");
    this.setState({ saveNote: "error", saveError: message, refusedFields: [] });
    this.errorToast(message, published);
  }

  /** One place that turns a typed refusal into what the operator reads. */
  reportRefusal(refusal) {
    /* 2026-09-08 (Nick): the server sometimes sends the WHOLE zod issue array as
       one message string, which rendered as a wall of JSON across the header.
       Expand it into one row per issue with the path as the label. */
    const expand = (f) => {
      const raw = String(f.message || "").trim();
      if (raw.charAt(0) === "[") {
        try {
          const issues = JSON.parse(raw);
          if (Array.isArray(issues)) return issues.map((is) => ({
            path: Array.isArray(is.path) ? is.path.join(".") : String(is.path || f.path),
            label: Array.isArray(is.path) ? is.path.filter((p) => typeof p === "string").join(" › ") : f.path,
            panel: (f.control && f.control.panel) || "",
            message: is.message === "Required" ? "This field is required." : String(is.message || "The server refused this value."),
          }));
        } catch (err) { /* fall through to the single row */ }
      }
      return [{
        path: f.path,
        label: (f.control && f.control.label) || f.path,
        panel: (f.control && f.control.panel) || "",
        message: f.message || "The server refused this value.",
      }];
    };
    const rows = (refusal.fields || []).flatMap(expand);
    const headline = refusal.kind === "config-invalid"
      ? (rows.length ? "The server refused " + rows.length + (rows.length === 1 ? " setting." : " settings.") : refusal.message)
      : refusal.kind === "envelope-invalid"
        ? "This save was refused as malformed: " + refusal.message
        : refusal.kind === "not-found"
          ? "That journey no longer exists on this tenant."
          : refusal.message;
    this.setState({ saveNote: "error", saveError: headline, refusedFields: rows });
    this.errorToast(headline, false);
  }

  today() { const n = new Date(); return new Date(n.getFullYear(), n.getMonth(), n.getDate()); }

  /** The public page's civil "today" (local-midnight Date), from the anchor:
   *  the store's or window's first day; for a server-anchored read, the first
   *  day the server served (the UTC date until it lands). Invalid Date only for
   *  an "invalid" zone. Authoring keeps the browser date. */
  publicToday(st = this.state) {
    if (!this.isPublic()) return this.today();
    const anchor = this.publicDateAnchor(st);
    if (anchor.kind === "invalid") return new Date(NaN);
    if (anchor.dateFrom) return this.fromIso(anchor.dateFrom);
    const served = st && st.avail && st.availFor && st.availFor === this.availabilityKeyFor(st.svc, st)
      ? Object.keys(st.avail.byDay || {}).sort() : [];
    return this.fromIso(served[0] || new Date().toISOString().slice(0, 10));
  }

  validPublicToday(st = this.state) {
    const d = this.publicToday(st);
    return Number.isFinite(d.getTime()) ? d : null;
  }

  iso(d) { return d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0") + "-" + String(d.getDate()).padStart(2, "0"); }

  fromIso(s) { const p = (s || "").split("-"); return new Date(+p[0], +p[1] - 1, +p[2]); }

  parsePhone(raw, pickedIso) {
    const typed = String(raw || "");
    const digits = typed.replace(/\D/g, "");
    const intl = typed.trim().charAt(0) === "+";
    const picked = DIAL.filter((c) => c.iso === pickedIso)[0];
    let country = picked || DIAL[0], national = digits;
    if (intl) {
      const hit = DIAL.slice().sort((a, b) => b.dial.length - a.dial.length)
        .filter((c) => digits.indexOf(c.dial) === 0)[0];
      if (hit) { country = hit; national = digits.slice(hit.dial.length); }
    }
    const len = national.length;
    return { country, national, len, intl, valid: len >= country.min && len <= country.max };
  }

  /**
   * The region to send ALONGSIDE a phone number (CD docket 2026-09-15 item 4).
   *
   * The selector's country used to live only in state and in validation, so a
   * national-format number left the browser with no region attached: the
   * backend had nothing to resolve "775340945" against, stored no usable
   * number, and sent no message — silently, in both directions.
   *
   * ISO 3166-1 alpha-2, uppercase, `undefined` when there is no number to
   * qualify. `parsePhone` resolves a typed `+41…` to its real country, so a
   * number that carries its own dial code wins and this only fills the gap.
   * The number itself is sent exactly as the visitor typed it — formatting and
   * validation stay ours, normalisation to E.164 is the backend's.
   */
  phoneCountryForSubmit(raw, pickedIso) {
    if (!String(raw || "").trim()) return undefined;
    const iso = this.parsePhone(raw, pickedIso).country.iso;
    return iso ? String(iso).toUpperCase() : undefined;
  }

  /**
   * Was this registration a REPLAY — the same person submitting the same
   * e-mail again (CD docket 2026-09-15 item 5)?
   *
   * The backend answers 200, returns the ORIGINAL registration's reference and
   * sends nothing. Presenting that as a fresh registration leaves the visitor
   * waiting for a confirmation e-mail that will never arrive.
   *
   * Keyed on the `reason`, never on `status` alone: "skipped" is overloaded — a
   * replay, a designed-lineage handover, an opted-out channel and a swallowed
   * error all carry it, and only the reason separates them. Any one channel
   * saying `already_registered` is enough; a replay skips them all.
   *
   * Returns `{ reference }` (the original registration's, which is the one the
   * visitor will be asked for at the door) or null. Reads the response already
   * on the confirmation step — no new request, no new field.
   */
  /* The appointment confirmation sentence from data.notifications (PublicNotificationOutcomes,
     public-notification-outcome.ts). Only status "scheduled" means a message is coming
     (notificationIsOnItsWay); skipped and failed both mean nothing was sent, and the reason says why.
     Returns null when the answer has no outcomes, so the authored or neutral line shows instead. */
  appointmentConfirmationLine() {
    const b = this.state.booking;
    const n = b && b.notifications;
    if (!n || typeof n !== "object" || !n.email || !n.sms) return null;
    const on = (o) => !!o && o.status === "scheduled";
    const email = on(n.email), sms = on(n.sms);
    /* A repeat of the same booking (already_registered: the first answer handled the confirmation)
       or a message already on its way (already_scheduled) is not "nothing was sent" (CC 0314).
       Null hands over to the neutral line, or the workspace's own sentence. */
    if (!email && !sms && [n.email, n.sms].some((o) => o && (o.reason === "already_registered" || o.reason === "already_scheduled"))) return null;
    if (email && sms) return "Your confirmation is on its way by email and text message. You may close this window.";
    if (email) return "Your confirmation is on its way by email. You may close this window.";
    if (sms) return "Your confirmation is on its way by text message. You may close this window.";
    const outcomes = [n.email, n.sms];
    const has = (r) => outcomes.some((o) => o && o.reason === r);
    const why = outcomes.some((o) => o && o.status === "failed")
      ? "the message could not be sent"
      : has("no_delivery_path") ? "this workspace can't send messages on the channel you chose"
      : has("no_recipient") ? "we have no email address or mobile number to send it to"
      : (outcomes.every((o) => o && o.reason === "channel_not_selected")) ? "you chose not to receive messages"
      : null;
    return "Your appointment is booked, but no confirmation was sent" + (why ? ", because " + why : "")
      + ". Keep the details on this page.";
  }

  eventRegistrationReplay() {
    const b = this.state.booking;
    /* LOCAL PATCH (platform side, 2026-10-04 JAI-2279): the neutral duplicate answer (alreadyRegistered: true) names no registration, so it is a replay with no reference whatever its notifications say. CD to absorb. */
    if (b && b.alreadyRegistered === true) return { reference: "" };
    const chans = b && b.notifications;
    if (!chans || typeof chans !== "object") return null;
    const replayed = Object.keys(chans).some((k) => {
      const c = chans[k];
      return !!c && typeof c === "object"
        && String(c.status || "") === "skipped"
        && String(c.reason || "") === "already_registered";
    });
    if (!replayed) return null;
    return { reference: String((b && (b.orderRef || b.registration_ref)) || "") };
  }

  /* ── additional attendees (CD docket 2026-09-15 item 1) ─────────────────
   *
   * The event and landing journeys used to suppress this section outright,
   * because `submitEventRegistration` carried no people list and collecting
   * people the wire cannot carry is worse than not offering it. The contract,
   * the typed client and the mocks now carry `attendees` end to end, so the
   * capability exists — and the authority for how many people may come is the
   * SERVER's public attendee policy, not the authored journey switch and not
   * the appointment side's `s.maxParty`.
   */

  /** Per-attendee limits the backend enforces. Ours to respect, not to invent. */
  attendeeLimits() {
    return { first: 120, last: 120, email: 254, phone: 32, company: 200, rows: 9 };
  }

  /** Custom HTML templates, authoring only: ONE controller for the life of this Designer. The
   *  template panel mounts only in the General settings, so the view, event and unapplied pick
   *  live here instead, where a rail section change or the phone Settings/Preview switch cannot
   *  drop them. Null on every customer mount (public, draft preview, ?preview= tab). */
  templateController() {
    if (this.props.mode || this.props.previewJourney || this.state.previewOnly) return null;
    if (!this._tplCtrl) this._tplCtrl = new TemplatePreviewController();
    return this._tplCtrl;
  }

  /** The template tab this journey is ("events" or "landing"), or null. */
  templateTab() {
    return this.state.journey === "events" ? "events" : this.state.journey === "landing" ? "landing" : null;
  }

  /** Follow the journey tab and redraw when the template controller changes. On mount and on a tab change. */
  syncTemplateController() {
    const c = this.templateController();
    if (!c) return;
    if (!this._offTplCtrl) this._offTplCtrl = c.subscribe(() => { if (!this._gone) this.forceUpdate(); });
    c.activate(this.templateTab(), this.state.evt || null);
  }

  /** Put the right-hand preview on screen (the mid and phone layouts show one pane at a time). */
  showTemplatePreview() {
    if (this.state.layout !== "wide" && this.state.formOpen) this.setState({ formOpen: false });
  }

  /** Open the tab's General settings, where a template is chosen and applied. */
  goTemplateSettings() {
    this.goSection("general");
  }

  /** The customer address of the custom template on screen, or why there is none. Only an APPLIED
   *  template that visitors are served is linked: a preview of an unapplied choice is never
   *  presented as live. The event comes from the template catalogue (ended events included) and
   *  its server-declared path, never from this tab's upcoming-only event pick. */
  templateLiveLink(tplView) {
    const sel = tplView && tplView.selection;
    const unavailable = i18nT("journeys.customTemplates.liveUnavailable", "The live address isn't available here.");
    if (!sel) return { url: "", reason: unavailable };
    if (sel.candidate) return { url: "", reason: i18nT("journeys.customTemplates.liveNotApplied", "Not live: this choice isn't applied.") };
    if (tplView.notPublished) return { url: "", reason: i18nT("journeys.customTemplates.notLiveTitle", "This template isn't published right now") };
    if (sel.eventRole === "exemplar" && sel.exemplarOverride) return { url: "", reason: i18nT("journeys.customTemplates.liveExampleOwn", "This example event uses its own template, so its live page doesn't show this one.") };
    if (previewInDialog() || !this.attendConfigResolved) return { url: "", reason: unavailable };
    const origin = this.publicOrigin();
    let url = "";
    if (sel.tab === "events") url = origin ? origin + "/" : "";
    else if (sel.event) { const path = canonicalEventPath(sel.event.publicPath || ""); url = origin && path ? origin + path : ""; }
    return url ? { url, reason: "" } : { url: "", reason: unavailable };
  }

  /** The event row a registration is being built for, or null. */
  currentEventRow() {
    const st = this.state;
    return this.eventsRows.filter((e) => e.id === st.evt)[0] || this.eventsRows[0] || null;
  }

  /** Server attendee policy for the current event. Absent policy means no
   *  guests — the contract defaults `allowGuests` to false, and reading
   *  permission out of silence would offer seats the submit is then refused
   *  for. The authored `s.attendees` switch can only narrow this. */
  eventAttendeePolicy() {
    const evt = this.currentEventRow();
    const pol = evt && evt.attendeePolicy;
    if (!pol) return { allowGuests: false, maxGuests: 0, maxTotal: 1 };
    return pol;
  }

  /** Seats left on the SELECTED tier, or null for unbounded/unknown. Reads the
   *  contract's explicit `ticket_tiers[].spots_remaining` (nullable = no tier
   *  bound) and only falls back to capacity-minus-sold when that is absent. */
  selectedTierSeatsLeft() {

    const st = this.state;
    const evt = this.currentEventRow();
    const raw = (evt && evt.raw) || null;
    if (!raw) return null;
    const camel = raw.ticketTiers || [];

    const chosen = st.selectedTicketId || (camel.length === 1 ? camel[0].id : "");
    if (!chosen) return null;
    const snake = (raw.ticket_tiers || []).filter((t) => t.id === chosen)[0];
    if (snake) {
      if (snake.sold_out === true) return 0;
      return snake.spots_remaining == null ? null : Math.max(0, Number(snake.spots_remaining));
    }
    const tier = camel.filter((t) => t.id === chosen)[0];
    if (!tier) return null;
    if (tier.soldOut === true) return 0;
    const cap = Number(tier.capacity);
    if (!Number.isFinite(cap) || cap <= 0) return null;
    return Math.max(0, cap - Number(tier.sold || 0));
  }

  /**
   * How many seats this registration may actually claim, and how many of those
   * may be additional people. The lowest of: the server policy, the event's
   * remaining capacity, and the selected tier's remaining capacity — because
   * the backend derives requested seats as `1 + attendees.length` and rejects
   * the whole registration atomically when any of the three cannot cover it.
   */
  attendeeSeatLimit() {
    const pol = this.eventAttendeePolicy();
    const evt = this.currentEventRow();
    const av = (evt && evt.availability) || null;
    if (av && av.soldOut === true) return { maxTotal: 0, maxGuests: 0, policy: pol };
    const bounds = [pol.maxTotal];
    if (av && av.spotsRemaining != null) bounds.push(Math.max(0, Number(av.spotsRemaining)));
    const tierLeft = this.selectedTierSeatsLeft();
    if (tierLeft != null) bounds.push(tierLeft);
    const maxTotal = Math.max(0, Math.min.apply(null, bounds));
    return { maxTotal, maxGuests: Math.max(0, maxTotal - 1), policy: pol };
  }

  /** Field-level errors for one attendee row.
   *
   *  TWO contracts, deliberately not merged:
   *
   *  EVENT / LANDING — the registration contract mandates attendee identity:
   *  first, last and a valid e-mail always required, phone and company always
   *  optional, and the author's field settings neither relax nor tighten it.
   *  Unchanged.
   *
   *  APPOINTMENT — the details step renders attendee fields from the author's
   *  own `attendeeFields` list (attFirst/attLast/attEmail/attPhone), so a field
   *  the author omitted has no input at all; requiring one demanded a value the
   *  visitor could not supply and left Continue permanently disabled. Only
   *  CONFIGURED VISIBLE fields are tested here. A visible field may legitimately
   *  require input: a shown name is required and goes through nameValid, and
   *  e-mail/phone follow the saved `emailOptional` / `phoneOptional` flags —
   *  the same flags the host's own fields honour in detailsFulfilled, and the
   *  same ones the required labels are derived from. Hidden fields impose
   *  nothing. */
  attendeeRowErrors(r) {
    const lim = this.attendeeLimits();
    const trim = (v) => String(v || "").trim();
    const first = trim(r.first), last = trim(r.last), email = trim(r.email);
    const phone = trim(r.phone), company = trim(r.company);
    const out: Record<string, string> = {};
    const eventRow = this.isEvents() || this.isLanding();
    const s = (this.state && this.state.s) || {};
    /* Event rows show every identity field by contract; an appointment row
       shows exactly what the author configured. */
    const shows = (f: string) => eventRow || (s.attendeeFields || []).indexOf(f) > -1;
    if (shows("first")) {
      if (!first) out.first = "A first name is required.";
      else if (first.length > lim.first) out.first = lim.first + " characters maximum.";
      else if (!eventRow && !this.nameValid(first)) out.first = "Enter a first name using letters, spaces, hyphens or apostrophes.";
    }
    if (shows("last")) {
      if (!last) out.last = "A last name is required.";
      else if (last.length > lim.last) out.last = lim.last + " characters maximum.";
      else if (!eventRow && !this.nameValid(last)) out.last = "Enter a last name using letters, spaces, hyphens or apostrophes.";
    }
    if (shows("email")) {
      /* Exact inverse of the `attendeeRequired` label prop. Optional means
         "valid if given", not "unchecked". Never optional on an event row. */
      const emailOptional = !eventRow && !!s.emailOptional;
      if (!email) { if (!emailOptional) out.email = "An e-mail address is required."; }
      else if (!this.emailValid(email)) out.email = "Enter a valid e-mail address, like you@example.com.";
      else if (email.length > lim.email) out.email = lim.email + " characters maximum.";
    }
    if (shows("phone")) {
      /* Exact inverse of the `attendeePhoneRequired` label prop: optional on an
         event registration by contract, and on an appointment exactly what the
         author saved — a configured-visible phone can legitimately be required. */
      const phoneRequired = !eventRow && !s.phoneOptional;
      if (!phone) { if (phoneRequired) out.phone = "A phone number is required."; }
      else if (phone.length > lim.phone) out.phone = lim.phone + " characters maximum.";
      else {
        const rp = this.parsePhone(phone, r.country);
        if (!rp.valid) out.phone = "That is not a valid " + rp.country.name + " number — "
          + (rp.country.min === rp.country.max ? rp.country.min + " digits" : rp.country.min + "–" + rp.country.max + " digits") + " expected.";
      }
    }
    /* Company is the last field that was still checked unconditionally. It is
       optional everywhere, so it could never demand input — but a row carried
       over from an event journey can hold a >200-char company, and on an
       appointment that hides the field the over-length error had no input to
       clear it and blocked Continue. Same rule as the four above: a field the
       author did not configure imposes nothing. */
    if (shows("company") && company && company.length > lim.company) out.company = lim.company + " characters maximum.";
    return out;
  }

  /** Are the attendees, as a set, submittable? Row validity plus the seat
   *  ceiling — an over-capacity party is refused here rather than by a
   *  rejected registration. A section the author switched off imposes nothing. */
  attendeesFulfilled() {
    const st = this.state;
    const rows = st.attRows || [];
    /* `s.attendees` is what puts the attendee section on an appointment page —
       the same flag `canAddAttendee` reads. With it off the rows are never
       rendered, so validating any stale row would gate Continue on inputs the
       visitor cannot see. Event/landing always carry the section. */
    if (!(this.isEvents() || this.isLanding()) && !((st.s || {}).attendees)) return true;
    if (!rows.length) return true;
    const lim = this.attendeeLimits();
    if (rows.length > lim.rows) return false;
    if ((this.isEvents() || this.isLanding()) && rows.length > this.attendeeSeatLimit().maxGuests) return false;
    return rows.every((r) => Object.keys(this.attendeeRowErrors(r)).length === 0);
  }

  /** The `attendees` array for the wire. Each person carries their OWN phone
   *  country, never the host's. Optional fields are omitted rather than sent
   *  empty. */
  attendeePayload() {
    const trim = (v) => String(v || "").trim();
    return (this.state.attRows || []).map((r) => {
      const person: Record<string, string> = {
        firstName: trim(r.first),
        lastName: trim(r.last),
        email: trim(r.email),
      };
      const phone = trim(r.phone);
      if (phone) {
        person.phone = phone;
        const iso = this.phoneCountryForSubmit(phone, r.country);
        if (iso) person.phoneCountry = iso;
      }
      const company = trim(r.company);
      if (company) person.company = company;
      return person;
    });
  }

  /* buildCatalog gives each location its IANA zone (CC 0061), so a valid IANA
     name passes through unchanged; only the six legacy keys are mapped. */
  ianaFor(zone) {
    const z = String(zone || "");
    if (z.indexOf("/") > 0 || z === "UTC") {
      try { new Intl.DateTimeFormat("en-US", { timeZone: z }); return z; } catch (e) { /* not a zone: fall through */ }
    }
    return ({ eastern: "America/New_York", central: "America/Chicago", mountain: "America/Denver", pacific: "America/Los_Angeles", alaska: "America/Anchorage", hawaii: "Pacific/Honolulu" })[z] || "America/Chicago";
  }

  /** The zones the locations are in: legacy keys in ZONE_ORDER first, then IANA names in location order. */
  liveZones() {
    const seen = {};
    const iana = [];
    LOCATIONS.forEach((x) => {
      if (!x.tz || seen[x.tz]) return;
      seen[x.tz] = true;
      if (ZONE_ORDER.indexOf(x.tz) < 0 && this.ianaFor(x.tz) === x.tz) iana.push(x.tz);
    });
    return ZONE_ORDER.filter((z) => seen[z]).concat(iana);
  }

  zoneFor(locId) {
    const l = LOCATIONS.filter((x) => x.id === locId)[0];
    return (l && l.tz) || this.liveZones()[0] || "central";
  }

  hoursUntilAppt() {
    // The picked offer's own instant when it resolves; the displayed time is
    // only a fallback. `state.slot` is a selection key, never parsed.
    const picked = this.slotObjFor(this.state.day, this.state.slot);
    const startMs = picked ? Date.parse(picked.startUtc) : NaN;
    if (!Number.isNaN(startMs)) return (startMs - Date.now()) / 3600000;
    const d = this.fromIso(this.state.day);
    const m = /(\d+):(\d+)\s*(AM|PM)/i.exec((picked && picked.time) || "");
    if (m) {
      let h = parseInt(m[1], 10) % 12;
      if (/pm/i.test(m[3])) h += 12;
      d.setHours(h, parseInt(m[2], 10), 0, 0);
    }
    return (d - new Date()) / 3600000;
  }

  ordinal(n) {
    const s2 = ["th", "st", "nd", "rd"], v = n % 100;
    return n + (s2[(v - 20) % 10] || s2[v] || s2[0]);
  }

  bookWindow() {
    const svc = SERVICES.filter((x) => x.id === this.state.svc)[0];
    return (svc && svc.window) || 60;
  }

  /**
   * How many real slots the backend offered for a day.
   *
   * This used to be arithmetic on the calendar date — closed Sundays, alternate
   * Saturdays, one "fully booked" day a week, and 3 to 11 slots from
   * `3 + ((date * 7) % 9)`. It looked plausible and matched nothing, on the
   * designer preview as much as on the customer page.
   */
  slotCount(d, daysAhead) {
    return this.slotsFor(this.iso(d)).length;
  }

  durMinutes() {
    const st = this.state;
    const cur = SERVICES.filter((x) => x.id === st.svc)[0] || SERVICES[0] || EMPTY_SERVICE;
    const src = cur.dur === "multi"
      ? (DURATIONS.filter((d) => d.id === st.dur)[0] || DURATIONS[0] || EMPTY_DURATION).label
      : cur.dur;
    let mins = 0;
    const hr = /(\d+)\s*(hour|hr)/i.exec(src);
    const mn = /(\d+)\s*(minute|min)/i.exec(src);
    if (hr) mins += parseInt(hr[1], 10) * 60;
    if (mn) mins += parseInt(mn[1], 10);
    return mins || 30;
  }

  /** "Ends at" for a displayed start label plus a duration, always in the
   *  summary's 12-hour style ("10:30am"). The availability endpoint labels
   *  slots on the store's 24-hour clock ("09:00"); a meridiem label
   *  ("9:00 AM") is read exactly as before. Labels are read, never rewritten.
   *  09:00 + 90 -> 10:30am; 23:30 + 60 -> 12:30am (wraps past midnight). */
  endTime(slot, mins) {
    const m = /(\d{1,2}):(\d{2})(?:\s*(AM|PM))?/i.exec(slot || "");
    if (!m) return "";
    let h = parseInt(m[1], 10);
    if (m[3]) {
      h = h % 12;
      if (/pm/i.test(m[3])) h += 12;
    } else if (h > 23 || parseInt(m[2], 10) > 59) {
      return "";
    }
    const total = h * 60 + parseInt(m[2], 10) + (Number(mins) || 0);
    const eh = Math.floor(total / 60) % 24;
    const em = total % 60;
    // The workspace's clock (item 8), not a hard-coded "4:50pm".
    return friendlyClock(eh, em);
  }

  /** A slot's time label ("4:00 PM", "16:00") re-printed in the workspace's clock,
   *  for display only; selection keys keep the producer's label. */
  clockLabel(label) {
    const m = /(\d{1,2}):(\d{2})(?:\s*(AM|PM))?/i.exec(label || "");
    if (!m) return label || "";
    let h = parseInt(m[1], 10);
    if (m[3]) { h = h % 12; if (/pm/i.test(m[3])) h += 12; }
    if (h > 23 || parseInt(m[2], 10) > 59) return label;
    return friendlyClock(h, parseInt(m[2], 10));
  }

  /** The first real slot's selection key (not its label) for a day. */
  firstSlotFor(isoStr) {
    const first = this.slotEntriesFor(isoStr)[0];
    return first ? first.key : "";
  }

  /** The selection key for a displayed time AT one store (Quickbook's
   *  next-available label). Another store's same-time offer is never taken. */
  slotKeyForTime(isoStr, time, locId) {
    const same = this.slotEntriesFor(isoStr).filter((entry) => entry.slot.time === time);
    const hit = same.filter((entry) => entry.slot.locationId === locId)[0]
      || same.filter((entry) => !entry.slot.locationId)[0];
    return hit ? hit.key : "";
  }

  firstAvailable(daysAhead) {
    const t = this.validPublicToday();
    if (!t) return "";
    for (let i = 0; i <= (daysAhead || 60); i++) {
      const d = new Date(t.getFullYear(), t.getMonth(), t.getDate() + i);
      if (this.slotsFor(this.iso(d)).length > 0) return this.iso(d);
    }
    return this.iso(t);
  }

  /** A journey day ("YYYY-MM-DD", a civil date) for display (item 8): long = the
   *  workspace's date pattern with the weekday, short = weekday, day and month in
   *  the page's language. Never hand-built English month names. */
  fmtDay(isoStr, long) {
    if (!isoStr) return "";
    const noon = civilNoon(isoStr);
    return long ? friendlyDate(noon) : friendlyDateNoYear(noon);
  }

  /** Whether a catalogue row may be opened from the search shortcut at all.
   *  Same authoritative fields the cards use (`bookable`/`registrationOpen`/
   *  `soldOut`/`waitlistOpen`, and `bookable_reason: "completed"` for a past
   *  event) — so a shortcut can never reach registration or the event
   *  detail step for something the list itself renders as closed. Rows with
   *  no projection yet keep the previous permissive behaviour. */
  eventRowSelectable(e) {
    const av = e && e.availability;
    if (!av) return true;
    if (av.bookableReason === "completed") return false;
    if (av.bookable === true && av.registrationOpen === true && av.soldOut === false) return true;
    // Authoritative in BOTH the Studio preview and the public page: the
    // preview's rows come from the same `getPublicEvents` read, so a row that
    // carries a projection is judged on `waitlistOpen`, never on the author's
    // switch alone. The switch is the fallback only when no projection exists.
    return Boolean(this.state.s.waitlist) && av.soldOut === true && av.happeningNow !== true && this.eventWaitlistOpen(e);
  }

  ctv(group, field, def) {
    const v = this.state.ct[group + "." + field];
    return v && v.trim() ? v : def;
  }

  isQueue() { return this.state.journey === "queue"; }
  /** Does the walk-in journey actually ASK where? (2026-09-17, CD.)
   *
   *  One rule, read by the rail, the preview toolbar and the customer page
   *  alike. The rail used to list "2 Where" whenever the setting was on, while
   *  `WalkInJourney.steps()` adds the step only when more than one desk could
   *  take the visit — so a single-desk journey showed a rail step the page
   *  never rendered, and Back/Next walked onto a step that was not there.
   *
   *  The page's own test is finer (it re-filters by the chosen service, which
   *  only it knows); this is the journey-level half of the same rule, and the
   *  two agree on the case that was wrong: one desk, no question. */
  walkInAsksLocation() {
    return !!this.state.s.queueLocationStep && (this.state.wiQueues || []).length > 1;
  }
  isKiosk() { return this.state.journey === "kiosk"; }
  /* Journey tabs follow the tenant's modules (CC, 2026-10-03): booking -> Appointments; queue ->
     Walk-Ins and Kiosk; events -> Event List Page and Event Landing Page. OneStop has no module of
     its own and always shows. A module KNOWN absent hides its tabs; unknown modules show all six. */
  static journeyModule(j: string) { return j === "appointment" ? "booking" : j === "queue" || j === "kiosk" ? "queue" : j === "events" || j === "landing" ? "events" : null; }
  static journeyEnabled(j: string, modules?: string[] | null) { const m = Designer.journeyModule(j); return !m || !Array.isArray(modules) || modules.includes(m); }
  /** True only when a KNOWN list composes at least one journey module (booking, queue or events).
   *  OneStop has no module of its own, so it is not a reason to open the designer on its own. */
  static composesAJourney(modules: string[] | null | undefined): boolean {
    return Array.isArray(modules) && ["appointment", "queue", "events", "landing", "kiosk"].some((j) => modules.includes(Designer.journeyModule(j) as string));
  }
  static firstJourney(modules?: string[] | null) { return ["appointment", "queue", "events", "landing", "kiosk", "onestop"].find((j) => Designer.journeyEnabled(j, modules)) || "appointment"; }
  journeyEnabled(j: string) { return Designer.journeyEnabled(j, this.props.modules); }
  journeyOptions() { return [{ value: "appointment", label: "Appointments" }, { value: "queue", label: "Walk-Ins" }, { value: "events", label: "Event List Page" }, { value: "landing", label: "Event Landing Page" }, { value: "kiosk", label: "Kiosk" }, { value: "onestop", label: "OneStop" }].filter((o) => this.journeyEnabled(o.value)); }
  isEvents() { return this.state.journey === "events"; }
  isLanding() { return this.state.journey === "landing"; }
  defaults() { return this.isQueue() ? QUEUE_DEFAULTS : this.isKiosk() ? KIOSK_DEFAULTS : this.isEvents() ? EVENT_DEFAULTS : this.isLanding() ? LANDING_DEFAULTS : DEFAULTS; }
  sectionKeys() { return this.isQueue() ? QUEUE_SECTION_KEYS : this.isKiosk() ? KIOSK_SECTION_KEYS : this.isEvents() ? EVENT_SECTION_KEYS : this.isLanding() ? LANDING_SECTION_KEYS : SECTION_KEYS; }
  /* ── Scan policy per journey type (CD scan docket, C) ─────────────────────
   *
   * The `scan` block lives in the MANAGE SETTINGS document for a journey type,
   * not in the page-designer record this tab otherwise edits — two different
   * documents behind one screen, so it reads and writes on its own and does not
   * ride the tab's Save. `updateManageSettings` validates the whole document, so
   * the read is kept verbatim and only `scan` is replaced (the same shape
   * SettingsManageLinks uses for the shortlink fields).
   *
   * A document without the block reads as the documented default —
   * `{action: 'open', id_check: false}` — never as "check in directly", which is
   * the answer that would write to a customer's record by surprise. */
  scanJourneyType() {
    const j = this.state.journey;
    if (j === "appointment") return "appointment";
    if (j === "queue") return "queue";
    if (j === "events" || j === "landing") return "event";
    return null;
  }
  scanBlockOf(settings) {
    const raw = (settings && settings.scan) || {};
    return { action: raw.action === "check_in" ? "check_in" : "open", id_check: raw.id_check === true };
  }
  hydrateScan(force) {
    /* Authoring only. The scan block feeds the setup rail's Scan panel, which
       the public page never renders, and the read is an ADMIN settings route —
       on the public page it can only 401. The public events -> landing switch
       re-enters here via componentDidUpdate, so the guard lives in the reader
       itself, not at one caller. */
    if (this.isPublic()) return;
    const type = this.scanJourneyType();
    if (!type) return;
    const have = this.state.scanByType[type];
    if (have && !force && have.state !== "error") return;
    this.setState((st) => ({ scanByType: Object.assign({}, st.scanByType, { [type]: { state: "loading", cfg: null, raw: null, err: null } }) }));
    manageSurface.getManageSettings(type).then((res) => {
      const settings = (res && res.data && res.data.settings) || {};
      this.setState((st) => ({ scanByType: Object.assign({}, st.scanByType,
        { [type]: { state: "ready", cfg: this.scanBlockOf(settings), raw: settings, err: null } }) }));
    }).catch((err) => {
      /* One request, and the refusal is the answer: a member without
         `settings.read` must not turn this panel into a retry loop. */
      this.setState((st) => ({ scanByType: Object.assign({}, st.scanByType,
        { [type]: { state: "error", cfg: null, raw: null, err: (err && err.message) || "the settings could not be read" } }) }));
    });
  }
  patchScan(patch) {
    const type = this.scanJourneyType();
    if (!type) return;
    this.setState((st) => {
      const cur = st.scanByType[type];
      if (!cur || !cur.cfg) return {};
      return { scanByType: Object.assign({}, st.scanByType,
        { [type]: Object.assign({}, cur, { cfg: Object.assign({}, cur.cfg, patch), dirty: true }) }) };
    });
  }
  saveScan() {
    const type = this.scanJourneyType();
    if (!type) return;
    const cur = this.state.scanByType[type];
    if (!cur || !cur.cfg || cur.saving) return;
    this.setState((st) => ({ scanByType: Object.assign({}, st.scanByType, { [type]: Object.assign({}, cur, { saving: true }) }) }));
    const next = Object.assign({}, cur.raw || {}, { scan: { action: cur.cfg.action, id_check: !!cur.cfg.id_check } });
    manageSurface.updateManageSettings(type, { settings: next }).then((res) => {
      const settings = (res && res.data && res.data.settings) || next;
      this.setState((st) => ({ scanByType: Object.assign({}, st.scanByType,
        { [type]: { state: "ready", cfg: this.scanBlockOf(settings), raw: settings, err: null, saving: false, dirty: false } }) }));
      toast({ tone: "success", title: "Scan settings saved" });
    }).catch((err) => {
      this.setState((st) => ({ scanByType: Object.assign({}, st.scanByType, { [type]: Object.assign({}, cur, { saving: false }) }) }));
      toast({ tone: "danger", title: "Not saved",
        description: ((err && err.message) || "the server refused the write") + ". The setting is unchanged." });
    });
  }
  panels() {
    if (this.isQueue()) return Object.assign({}, PANELS, QUEUE_PANELS);
    if (this.isKiosk()) return Object.assign({}, PANELS, KIOSK_PANELS);
    if (this.isEvents()) return Object.assign({}, PANELS, EVENT_PANELS);
    if (this.isLanding()) return Object.assign({}, PANELS, LANDING_PANELS);
    return PANELS;
  }
  ctGroupDefs() { return this.isQueue() ? QUEUE_CT_GROUPS : this.isKiosk() ? KIOSK_CT_GROUPS : this.isEvents() ? EVENT_CT_GROUPS : this.isLanding() ? LANDING_CT_GROUPS : CT_GROUPS; }

  /** The identity of ONE availability read, for a service the catalogue carries, else "".
   *  public:    public|slug|service|horizon — no interval: the public page reads the saved config.
   *  authoring: authoring|journeyId|interval|authoring|service|horizon — the selected admin
   *             journey and the UNSAVED interval, so changing either starts a new read
   *             (componentDidUpdate) and a late answer for the old one is discarded. */
  availabilityKeyFor(svcId, st = this.state) {
    const svc = SERVICES.filter((x) => x.id === svcId)[0];
    if (!svc) return "";
    if (this.isPublic()) {
      const base = eligibilityKey(this.bookingSlug(), svcId, svc.window || 60);
      const anchor = this.publicDateAnchor(st);
      return base ? ["public", base, anchor.kind, anchor.zone, anchor.dateFrom || "", anchor.dateTo].join("|") : "";
    }
    const interval = slotIntervalMinutes(st && st.s && st.s.slotInterval) ?? SLOT_INTERVAL_DEFAULT;
    return ["authoring", String((st && st.apptJourneyId) || "nojourney"), String(interval), eligibilityKey("authoring", svcId, svc.window || 60)].join("|");
  }

  /** The civil-date anchor of the public read for `st` (publicDateAnchorFor);
   *  null off the public page, where the authoring preview keeps its own window. */
  publicDateAnchor(st = this.state) {
    if (!this.isPublic()) return null;
    const svc = SERVICES.filter((x) => x.id === (st && st.svc))[0];
    return publicDateAnchorFor(LOCATIONS, st && st.loc, (svc && svc.window) || 60);
  }

  /** Catalogue rows with a bookable offer in `window`, narrowed by an explicit service restriction. */
  eligibleFrom(window, svcId) {
    const svc = SERVICES.filter((x) => x.id === svcId)[0];
    const rows = eligibleLocations(LOCATIONS, offeredLocationIds(window, Date.now()), svc && Array.isArray(svc.locs) ? svc.locs : null);
    /* A personal link offers only the stores that have its member on the roster (CC 0330). */
    const only = this.state.personal && this.state.personal.locIds;
    return only ? rows.filter((r) => only.indexOf(String(r.id)) > -1) : rows;
  }

  /** The ONE eligible location set every Where surface reads. Empty unless the
   *  window for the current key has actually arrived. */
  locationEligibility() {
    const st = this.state;
    const status = eligibilityStatus(st, this.availabilityKeyFor(st.svc, st));
    return { status, rows: status === "ready" ? this.eligibleFrom(st.avail, st.svc) : [] };
  }

  /** The selection a catalogue adoption leaves: reconciled against a ready
   *  window, otherwise kept only when the catalogue carries it — never row 0. */
  catalogLocation(prev) {
    if (prev.pinnedLoc && prev.loc === prev.pinnedLoc) return prev.loc;
    const svc = SERVICES.filter((x) => x.id === prev.svc)[0] ? prev.svc : ((SERVICES[0] && SERVICES[0].id) || "");
    if (eligibilityStatus(prev, this.availabilityKeyFor(svc, prev)) === "ready") {
      return reconcileSelectedLocation(prev.loc, this.eligibleFrom(prev.avail, svc), prev.pinnedLoc);
    }
    return LOCATIONS.some((x) => x.id === prev.loc) ? prev.loc : "";
  }

  /** The catalog's delivery method for a service, lower-cased ("virtual", "in-person", …), or "". */
  serviceMethod(svcId) {
    const sv = SERVICES.filter((x) => x.id === svcId)[0];
    return String((sv && sv.method) || "").trim().toLowerCase();
  }
  /** The location an online-only service is booked against without asking: the
   *  eligible virtual branch, else the only eligible branch. "" when neither is
   *  known, and then the location step stays. Never an arbitrary pick among several. */
  onlineLocationFor(svcId) {
    const rows = this.locsForService(svcId);
    const virt = rows.filter((x) => { const l = locationById(LOCATIONS, x.id); return !!(l && l.virtual); });
    if (virt.length) return virt[0].id;
    return rows.length === 1 ? rows[0].id : "";
  }
  /** "Method" in the summary: from the service, never from the location's distance (CC 0039).
   *  A service with several methods names the one the chosen branch serves it by. */
  /** The booking's `method` for a service offered more than one way; undefined when
   *  the service has a single method (the door records that one) or no branch is known. */
  bookingMethodFor(svcId, locId) {
    const m = this.serviceMethod(svcId);
    if (["virtual", "video", "online", "phone", "in-person", "inperson", "in person"].includes(m)) return undefined;
    const l = locationById(LOCATIONS, locId);
    return l ? (l.virtual ? "video" : "inPerson") : undefined;
  }
  /** Is the booked appointment held remotely? The booking's own `method` when the
   *  response carries one (CC 0077 d), else today's rule: an online-only service not
   *  booked at a virtual branch. */
  heldRemotely(st, bookedLoc) {
    const bm = st.booking && st.booking.booking ? st.booking.booking.method : undefined;
    if (bm !== undefined && bm !== null) return bm === "video" || bm === "phone";
    return this.serviceMethod(st.svc) === "virtual" && !(bookedLoc && bookedLoc.virtual);
  }
  /** The method's words, preferring the booking's own method. */
  bookedMethodLabel(st) {
    const bm = st.booking && st.booking.booking ? st.booking.booking.method : undefined;
    if (bm === "video") return "Video appointment";
    if (bm === "phone") return "Phone appointment";
    return this.methodLabel(st.svc, st.loc);
  }
  methodLabel(svcId, locId) {
    const m = this.serviceMethod(svcId);
    if (m === "virtual" || m === "video" || m === "online") return "Video appointment";
    if (m === "phone") return "Phone appointment";
    if (m === "in-person" || m === "inperson" || m === "in person") return "In-person";
    const l = locationById(LOCATIONS, locId);
    return l ? (l.virtual ? "Video appointment" : "In-person") : "";
  }

  /** Eligibility is known only for the selected service's window; any other service is unknown, so empty. */
  locsForService(svcId) {
    if (svcId !== this.state.svc) return [];
    return this.locationEligibility().rows;
  }

  steps() {
    const s = this.state.s;
    const pinned = !!this.state.pinnedLoc;
    if (this.isKiosk()) return ["kioskEntry", "kioskCheckin", "kioskDone"];
    // The landing step is a REAL step of the events rail, not decoration: the
    // Studio sidebar, the preview progress row and the public list all read
    // this one list, so "Show event details step" changes the count in every
    // one of them (4 on, 3 off).
    if (this.isLanding()) {
      // A direct event link (or a related pick/popstate) always keeps its
      // Event page; only the list shortcut, once the authoritative detail
      // confirmed it, drops the step for THIS selection. The non-public
      // Event Landing Page tab keeps its own three-step rail unchanged.
      // Only a list-origin selection owns the `events` step; a direct URL
      // (or a related pick made from one) is the plain three-step rail.
      if (this.isPublic() && this.publicEventFromList) return ["events", ...(this.publicLandingStepSkipped ? [] : ["landing"]), "details", "confirmation"];
      return ["landing", "details", "confirmation"];
    }
    if (this.isEvents()) {
      // Off = 3 steps, on = 4. The one exception: a preview selection that
      // was NOT eligible for the shortcut still shows the Event page, so
      // while that is the previewed step the rail keeps it rather than
      // leaving the active pane pointing at a step no longer in the list.
      const keepLanding = !s.skipSingleEventSlot || this.state.previewStep === "landing";
      return keepLanding
        ? ["events", "landing", "details", "confirmation"]
        : ["events", "details", "confirmation"];
    }
    if (this.isQueue()) {
      const q = ["service"];
      if (this.walkInAsksLocation() && !pinned) q.push("location");
      q.push("details", "confirmation");
      return q;
    }
    // The kiosk already decided where you are, so there is nothing to choose and no map to open.
    if (pinned) {
      let k = ["service"];
      if (s.skipService && SERVICES.length === 1) k = [];
      return k.concat(["datetime", "details", "confirmation"]);
    }
    let arr = s.firstStep === "service" ? ["service", "location"] : ["location", "service"];
    if (s.firstStep === "map") arr = ["location", "service"];
    // A step with only one thing to choose is auto-selected and dropped, whichever
    // order the journey runs in — so location-first skips the service step too.
    if (s.skipService && SERVICES.length === 1) arr = arr.filter((x) => x !== "service");
    // A service chosen on the way in (`?service=`, validated in bootstrapPublic)
    // is already selected, so its step is dropped the same way.
    if (this.isPublic() && this.state.preselectedService) arr = arr.filter((x) => x !== "service");
    // A service offered at exactly one location needs no location step.
    // Either order: the same eligible set, and only once it has arrived.
    const oneLoc = this.locsForService(this.state.svc).length === 1;
    if (s.skipLocation && oneLoc && s.firstStep !== "map") arr = arr.filter((x) => x !== "location");
    /* A personal link whose member is at exactly one store skips Where, whatever the journey's setting (CC 0330). */
    else if (this.isPublic() && this.state.personal && this.state.personal.locIds && oneLoc) arr = arr.filter((x) => x !== "location");
    // An online-only service (catalog method "virtual", CC 0039) has no place to choose,
    // once a location to book it against is known (see onlineLocationFor).
    if (this.serviceMethod(this.state.svc) === "virtual" && this.onlineLocationFor(this.state.svc)) arr = arr.filter((x) => x !== "location");
    return arr.concat(["datetime", "details", "confirmation"]);
  }

  /** Where a Studio (non-public) events-rail selection lands: the Event page
   *  when the step is shown, and otherwise `details` only for a row whose own
   *  raw record is genuinely bookable with exactly one valid one-off
   *  schedule. Never submits — it only moves the previewed step. */
  eventsRailStepForSelection(e: any) {
    const raw = (e as { raw?: any }).raw;
    const canSkip = this.state.s.skipSingleEventSlot
      && !e.full
      && raw?.bookable === true
      && raw?.registrationOpen === true
      && raw?.soldOut === false
      && hasSingleOneOffSchedule(raw);
    const step = canSkip ? "details" : "landing";
    return { evt: e.id, previewStep: step, section: this.sectionForStep(step) };
  }

  stepLabel(id) {
    const q = this.isQueue();
    switch (id) {
      case "kioskEntry": return "Entry screen";
      case "kioskCheckin": return "Check in";
      case "kioskDone": return "Checked in";
      case "landing": return this.ctv("progress", "svc", "Event page");
      case "events": return this.ctv("progress", "svc", "Events");
      case "service": return this.ctv("progress", "svc", "What");
      case "location": return this.ctv("progress", "loc", "Where");
      case "datetime": return this.ctv("progress", "dt", "When");
      case "booking": return "Your booking";
      case "details": return this.ctv("progress", "pd", "Your info");
      default: return q ? this.ctv("progress", "conf", "Your place in line") : this.ctv("progress", "conf", "Confirmation");
    }
  }

  sectionForStep(id) {
    if (id === "kioskEntry") return "kioskentry";
    if (id === "kioskCheckin" || id === "kioskDone") return "kioskcheckin";
    // The events rail's Event page step is previewed under the Events
    // settings section — that journey has no `landing` panel of its own.
    if (id === "landing" && this.isEvents()) return "events";
    return id === "booking" ? "details" : id;
  }

  /** The categories this tenant's catalogue actually returns, for the toggle's
   *  description — a fixed list would contradict the chips the step renders. */
  categoryHint() {
    const labels = serviceCategoryOptions(SERVICES).filter((c) => c.id !== "all").map((c) => c.label);
    if (!labels.length) return "No service categories are set on this catalogue yet.";
    const shown = labels.slice(0, 3).join(", ");
    return labels.length > 3 ? shown + " and " + (labels.length - 3) + " more." : shown + ".";
  }

  invalidAppointmentServiceSelection() {
    return this.state.journey === "appointment"
      && this.state.previewStep === "service"
      && !SERVICES.some((service) => service.id === this.state.svc
        && (!this.state.s.showCategories || serviceMatchesCategory(service, this.state.cat)));
  }

  /* ── Step gating ─────────────────────────────────────────────────────
   *
   * ONE definition of "this step is fulfilled", read by the progress bar's
   * tabs, the toolbar's Next, the customer page's Continue and the summary's
   * change links. Before this, the tabs set `previewStep` directly, so a
   * customer could click Confirmation from What and land on a confirmation
   * built from no service, no location and no slot — the flow broke rather
   * than refusing (operator, 2026-09-14).
   *
   * The rule the operator asked for: FORWARD only out of a fulfilled step,
   * BACKWARD always. Going back is how a customer corrects a choice, and the
   * step behind them is by definition one they already satisfied.
   */

  /** Letters (including accents), spaces, hyphens and apostrophes; ≥2 chars. */
  nameValid(v) { return /^[\p{L}][\p{L}\u2019' -]{1,}$/u.test(String(v || "").trim()); }
  emailValid(v) { return /^[^\s@]+@[^\s@]+\.[A-Za-z]{2,}$/.test(String(v || "").trim()); }

  /**
   * Whether the details step's primary person (first, last, e-mail, phone,
   * address) comes filled and LOCKED from a signed-in customer account.
   *
   * This is true only when the standalone public appointment mount received a
   * verified same-origin booking session. Studio preview and anonymous public
   * visits never receive one, whatever the stored `s.readOnly` preference says.
   * The backend still resolves the customer from its signed principal and
   * ignores browser identity fields; the values here are display-only profile
   * context, not an ownership assertion.
   */
  /**
   * The details step belongs to the ACCOUNT, not to the form.
   *
   * True for any public journey opened inside a verified portal session - an
   * appointment, and now an event registration, which this same component
   * renders. The desk's own screen (`WalkInJourney`) makes the same decision in
   * its own render. Signed out, every journey keeps its ordinary form.
   */
  detailsLocked() {
    return this.isPublic()
      && (this.state.journey === "appointment" || this.state.journey === "events" || this.state.journey === "landing")
      && !!(this.props.bookingSession && this.props.bookingSession.csrf);
  }

  /**
   * PER FIELD: an identity input is locked only when the account SUPPLIES it.
   * A signed-in registrant with no phone on file gets an ordinary phone input,
   * and what they type is sent as a fill-in the server reads only for a field
   * the account lacks. Signed out, nothing is locked. The appointment journey's
   * session write carries no fill-ins, so its lock stays whole.
   */
  lockedField(field: "first" | "last" | "email" | "phone") {
    if (!this.detailsLocked()) return false;
    if (this.state.journey === "appointment") return true;
    const p = this.props.bookingSession.profile;
    const value = field === "first" ? p.firstName : field === "last" ? p.lastName : field === "email" ? p.email : p.phone;
    return !!(value && String(value).trim());
  }

  /** The details step's own test, ignoring `*Touched` — a field the customer
   *  has never focused is still unfulfilled, it just is not shown in error. */
  detailsFulfilled() {
    const st = this.state, s = st.s;
    // A real signed-in session supplies the primary person's profile. Guests'
    // own entries are tested; booking questions and attendee rows remain gated
    // for everyone.
    if (!this.lockedField("first") && !this.nameValid(st.first)) return false;
    if (!this.lockedField("last") && !this.nameValid(st.last)) return false;
    if (s.askEmail && !this.lockedField("email")) {
      const email = String(st.email || "").trim();
      if (s.emailOptional ? (email && !this.emailValid(email)) : !this.emailValid(email)) return false;
    }
    if (s.askPhone && !this.lockedField("phone")) {
      const ph = this.parsePhone(st.phone, st.phoneCountry);
      if (s.phoneOptional ? (ph.len > 0 && !ph.valid) : !ph.valid) return false;
    }
    const missing = this.serviceQuestions().filter((q) => q.required && this.answerForSubmit(q, (st.answers || {})[q.id]) === undefined);
    if (missing.length) return false;
    /* Additional attendees are part of the details step's own contract now
       (CD docket 2026-09-15 item 1.4): a half-filled companion cannot be
       carried past this step, because the backend rejects the whole
       registration atomically rather than dropping the bad row. */
    if (!this.attendeesFulfilled()) return false;
    /* An event registration needs the data-protection consent (CC 0434): the submit refuses without it
       (submitEventRegistration), and so does this step. An appointment booking needs it too
       (CC 2026-10-16 item 1): the box is shown on its details step and its value is sent as is. */
    if ((st.journey === "events" || st.journey === "landing" || st.journey === "appointment") && !st.consent) return false;
    return true;
  }

  /** May the journey advance PAST `id`? A step with nothing to choose (or one
   *  this journey does not run) is vacuously fulfilled. */
  stepFulfilled(id) {
    const st = this.state;
    switch (id) {
      case "service": return !this.invalidAppointmentServiceSelection();
      case "location": return st.journey === "appointment"
        ? (!!st.loc && (st.loc === st.pinnedLoc || this.locsForService(st.svc).some((x) => x.id === st.loc)))
        : !!st.loc;
      case "datetime": return !!st.day && !!this.slotObjFor(st.day, st.slot);
      case "details": return this.detailsFulfilled();
      case "events": return !!st.evt;
      // Terminal, or a step that carries no customer input of its own.
      default: return true;
    }
  }

  /** May the customer jump to `target` (a step id) from where they are?
   *  Backwards and sideways: yes. Forwards: only if every step between here
   *  and there is fulfilled — landing on step 4 with step 2 unanswered is the
   *  same broken flow, one click further along. */
  canReachStep(target) {
    const list = this.steps();
    const from = list.indexOf(this.state.previewStep);
    const to = list.indexOf(target);
    if (to < 0 || from < 0 || to <= from) return true;
    for (let i = from; i < to; i += 1) if (!this.stepFulfilled(list[i])) return false;
    return true;
  }

  /** What the step the customer is ON still needs, in their words. Used by the
   *  Next button (title + the visible note under the progress bar) because a
   *  `title` tooltip does not exist on touch, and this designer now goes down
   *  to 375px — a hover-only explanation there is no explanation. */
  stepNeeds(id) {
    switch (id) {
      case "service": return "Choose a service to continue.";
      case "location": return "Choose a location to continue.";
      case "datetime": return "Pick a date and a time to continue.";
      case "details": return "";
      case "events": return "Choose an event to continue.";
      default: return "";
    }
  }

  /** The reason a locked tab is locked, for its title/aria — never a silent
   *  dead control. */
  stepLockReason(target) {
    const list = this.steps();
    const from = list.indexOf(this.state.previewStep);
    const to = list.indexOf(target);
    for (let i = from; i < to; i += 1) {
      if (!this.stepFulfilled(list[i])) return "Finish " + this.stepLabel(list[i]) + " first.";
    }
    return "";
  }

  continueBlocked() {
    return !this.stepFulfilled(this.state.previewStep);
  }

  /** Shows what keeps the details step from continuing: every field the guest fills is marked touched, so
   *  each one renders its own message, and the alert above the button repeats the submit's wording for a
   *  missing name or email, or for unanswered required questions (CC, 2026-10-05). */
  flagDetailsGaps() {
    const st = this.state, s = st.s;
    const blank = (v) => !String(v || "").trim();
    const nameOrEmailMissing = (!this.lockedField("first") && blank(st.first))
      || (!this.lockedField("last") && blank(st.last))
      || (s.askEmail && !s.emailOptional && !this.lockedField("email") && blank(st.email));
    const missingQ = this.serviceQuestions().filter((q) => q.required && this.answerForSubmit(q, (st.answers || {})[q.id]) === undefined);
    const bookingError = nameOrEmailMissing ? "First name, last name and email are required."
      : missingQ.length ? "Please answer: " + missingQ.map((q) => q.label).join(", ") + "."
      : "";
    this.setState({ firstTouched: true, lastTouched: true, emailTouched: true, phoneTouched: true, answersTouched: true, consentTried: true, bookingError },
      () => { if (!this.focusFirstInvalidField() && !bookingError && !this.state.consent) this.focusConsent(); });
  }

  /* The consent box, so a refusal for it lands the keyboard and screen reader there (CC 2026-10-18 item 9). */
  consentRef = React.createRef<HTMLSpanElement>();
  focusConsent() { try { this.consentRef.current?.focus(); } catch { /* detached */ } }
  /* The first invalid field in visual order gets focus, ahead of the consent box below them
     (CC 2026-10-19 walk 3 item 10). True when one was focused. */
  focusFirstInvalidField() {
    try {
      for (const id of ["jrn-first", "jrn-last", "jrn-email", "jrn-phone"]) {
        const el = document.getElementById(id);
        if (el && el.getAttribute("aria-invalid") === "true") { el.focus(); return true; }
      }
      const att = document.querySelector('[id^="jrn-att-"][aria-invalid="true"]');
      if (att instanceof HTMLElement) { att.focus(); return true; }
    } catch { /* no DOM */ }
    return false;
  }

  summaryGo(stepId) {
    const order = this.steps();
    const target = order.indexOf(stepId);
    const dt = order.indexOf("datetime");
    const next = { previewStep: stepId };
    if (dt > -1 && target <= dt) {
      // anything chosen after this step can no longer be trusted — the customer re-picks a time
      next.slot = "";

      next.reconfirmFrom = stepId === "service" ? "service" : stepId === "location" ? "location" : "date";
    }
    this.setState(next);
  }

  /** The first three start times a given interval produces from a 9:00 open,

   *  so the hint shows the rule instead of describing it. */
  slotIntervalExample(mins) {
    const step = Math.max(1, Math.min(1140, Number(mins) || 60));
    const out = [];
    for (let i = 0, t = 9 * 60; i < 3 && t < 24 * 60; i += 1, t += step) {
      const h = Math.floor(t / 60), m = t % 60;
      const h12 = h % 12 === 0 ? 12 : h % 12;
      out.push(h12 + ":" + String(m).padStart(2, "0") + (h < 12 ? " AM" : " PM"));
    }
    return out.join(", ") + ", …";
  }

  longNext(next) {    const FULL = { Sun: "Sunday", Mon: "Monday", Tue: "Tuesday", Wed: "Wednesday", Thu: "Thursday", Fri: "Friday", Sat: "Saturday" };
    return String(next || "").replace(/^([A-Za-z]{3}),/, (m, a) => (FULL[a] || a) + ",");
  }

  weekFor(hours) {
    const ABBR = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const FULL = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
    const m = String(hours || "").match(/^([A-Za-z]{3})[\u2013-]([A-Za-z]{3})\s+(.+?)\s+[\u2013-]\s+(.+)$/);
    if (!m) return FULL.map((day) => ({ key: day, day, label: hours || "Closed" }));
    const a = ABBR.indexOf(m[1]), b = ABBR.indexOf(m[2]);
    return FULL.map((day, i) => ({ key: day, day, label: i >= a && i <= b ? m[3] + " \u2013 " + m[4] : "Closed" }));
  }

  embedTitle() {
    const j = this.state.journey;
    if (j === "landing") return "Event details";
    if (j === "events") return this.tenantDisplayName() ? this.tenantDisplayName() + " events" : "Events";
    if (j === "queue") return "Join the walk-in line";
    return "Book an appointment";
  }

  /* The platform's journey loader (JAI-1670, CC 2026-09-26-0004). A hand-written
     iframe was outside the loader's contract, `/embed/resize.js` never existed and
     `data-jrni-autoresize` was read by nothing. The loader must be served from the
     journey's OWN origin (it refuses any other), creates and sizes its own frame,
     and preflights the journey itself. Fixed height wraps the container. */
  embedSnippet(s, title) {
    const url = this.publicLink();
    const origin = this.publicOrigin();
    if (!url || !origin) return "";
    const esc = (v) => String(v).replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    /* Events and Landing publish the attend root, which the loader refuses
       (CC 0030 item 10), so they keep the plain iframe. */
    if (this.state.journey === "events" || this.state.journey === "landing") {
      return '<iframe src="' + esc(url) + '" title="' + esc(title || "Events") + '" loading="lazy" style="width:100%;height:' + (Number(s.embedHeight) || 760) + 'px;border:0"></iframe>';
    }
    const slug = String(s.slug || "journey").replace(/[^a-z0-9-]/gi, "-");
    // The loader sizes its own frame, so no fixed-height wrapper.
    const loader = '<script src="' + esc(origin) + '/embed/v1.js" data-mode="inline" data-target="jrni-journey-' + slug + '" data-journey-url="' + esc(url) + '" defer><\/script>';
    const box = '<div id="jrni-journey-' + slug + '"></div>';
    return box + "\n" + loader;
  }


  /** The state change of opening journey `j` from state `st`, or null when it is already open or
   *  the tenant does not compose it. setJourney applies it; the constructor applies it once, before
   *  the first render, so the designer never opens on a tab the tenant does not offer. */
  journeyTransition(st: any, j: string, pinFrom?: unknown): any {
    if (j === st.journey || !this.journeyEnabled(j)) return null;
    // OneStop (2026-09-04) edits the tenant's One-Stop experience document through its own
    // tab (onestop-tab.tsx), not a page-designer record — so it has no s/ct to stash, and
    // leaving it must not stash the previous journey's settings under its name.
    // Unsaved changes are session-scoped to the journey you are on: leaving this tab
    // discards them (product decision 2026-09-08 — no draft, no version to manage), so what
    // gets stashed is the last SAVED copy, never the edited one.
    const kept = st.dirty ? (st.saved[st.journey] || this.journeyDefaults(st.journey)) : { s: st.s, ct: st.ct };
    const keep = { s: kept.s, ct: kept.ct };
    const stash = st.journey === "onestop" ? st.stash : { ...st.stash, [st.journey]: keep };
    if (j === "onestop") return { journey: j, cat: "all", stash, dirty: false, saveNote: "", saveError: "" };
    const next = stash[j] || this.journeyDefaults(j);
    const first = j === "kiosk" ? "kioskEntry" : j === "landing" ? "landing" : j === "events" ? "events" : (j === "appointment" && next.s.firstStep === "location" ? "location" : "service");
    const sec = j === "kiosk" ? "kioskentry" : "general";
    // A pin only exists when a kiosk tile handed us here. Guarded to a location id, because
    // the tab bar's SegmentedControl may pass an event object as its second argument.
    const pinned = typeof pinFrom === "string" && /^l\d+$/.test(pinFrom) ? pinFrom : "";
    const loc = pinned || st.loc;
    return { journey: j, cat: "all", stash, s: next.s, ct: next.ct, section: sec, previewStep: first, wiPreviewStep: "service", cancelled: false, leftQueue: false, ckProblem: "", pinnedLoc: pinned, loc, dirty: false, saveNote: "", saveError: "", refusedFields: [], notifChoice: "", notifChoiceRefused: false };
  }

  journeyDefaults(j) {
    if (j === "kiosk") return { s: { ...KIOSK_DEFAULTS }, ct: { ...KIOSK_CT_PREFILL } };
    if (j === "landing") return { s: { ...LANDING_DEFAULTS }, ct: { ...EVENT_CT_PREFILL } };
    if (j === "queue") return { s: { ...QUEUE_DEFAULTS }, ct: { ...QUEUE_CT_PREFILL } };
    if (j === "events") return { s: { ...EVENT_DEFAULTS }, ct: { ...EVENT_CT_PREFILL } };
    return { s: { ...DEFAULTS }, ct: {} };
  }

  /** The walk-in step a rail id shows, or "" when this journey does not ask it.
   *
   *  The rail's "confirmation" is the walk-in page's "ticket" — the ticket IS
   *  the confirmation here — and a step the journey does not ask for is never
   *  jumped to (2026-09-17, CD). */
  walkInStepFor(id) {
    if (!this.isQueue()) return "";
    const wanted = id === "confirmation" ? "ticket" : id;
    const allowed = ["service"]
      .concat(this.walkInAsksLocation() ? ["location"] : [])
      .concat(["details", "ticket"]);
    return allowed.indexOf(wanted) > -1 ? wanted : "";
  }

  goSection(id) {
    const next = { section: id, formOpen: true };
    if (["service", "location", "datetime", "details", "confirmation"].indexOf(id) > -1) next.previewStep = id;
    /* Walk-Ins renders through WalkInJourney, which keeps its OWN step in
       `wiPreviewStep` (2026-09-17, CD). Setting `previewStep` alone moved a
       value the walk-in preview does not read, so clicking a Customer-journey
       step in the rail changed the panel and left the preview where it was —
       only the toolbar's Back/Next moved it. Same mapping the toolbar uses:
       the rail's "confirmation" is the walk-in page's "ticket" (the ticket IS
       the confirmation here), and a step this journey does not ask for is not
       jumped to at all. */
    if (this.isQueue()) {
      const wanted = this.walkInStepFor(id);
      if (wanted) next.wiPreviewStep = wanted;
    }
    this.setState(next);
  }

  /** For a step no longer in `l`: the index of the next (dir > 0) or previous
   *  (dir < 0) step of `l` in the canonical step order. Falls back to the ends. */
  stepIndexAround(l, gone, dir) {
    const ORDER = ["events", "landing", "service", "location", "map", "datetime", "details", "confirmation"];
    const at = ORDER.indexOf(gone);
    if (at < 0) return dir > 0 ? 0 : l.length - 1;
    if (dir > 0) {
      for (let k = 0; k < l.length; k++) if (ORDER.indexOf(l[k]) > at) return k;
      return l.length - 1;
    }
    for (let k = l.length - 1; k >= 0; k--) { const o = ORDER.indexOf(l[k]); if (o > -1 && o < at) return k; }
    return 0;
  }

  moveStep(dir) {
    // Walk-Ins renders through WalkInJourney, which keeps its own step. The
    // preview toolbar's Back/Next drive THAT step, not the appointment
    // wizard's `previewStep` — otherwise the buttons moved a value nothing on
    // screen was reading.
    if (this.isQueue()) {
      const wl = ["service"]
        .concat(this.walkInAsksLocation() ? ["location"] : [])
        .concat(["details", "ticket"]);
      const wi = Math.max(0, Math.min(wl.length - 1, wl.indexOf(this.state.wiPreviewStep || "service") + dir));
      // Stepping anywhere leaves the "left the line" outcome behind — it is a
      // rail destination, not a step Back/Next passes through (CD, 2026-09-17).
      this.setState({ wiPreviewStep: wl[wi], wiLeft: false, leftQueue: false });
      return;
    }
    const l = this.steps();
    /* The shown step can drop out of steps() while it is on screen (an online-only
       service's location step, once its availability names the location: CC 0039
       bounce). Step from where it WOULD sit, never from -1, which restarted at the first step. */
    const cur = l.indexOf(this.state.previewStep);
    const i = cur > -1
      ? Math.max(0, Math.min(l.length - 1, cur + dir))
      : this.stepIndexAround(l, this.state.previewStep, dir);
    // On the public rail the events step belongs to the LIST journey, so
    // stepping back onto it has to switch journeys exactly like the progress
    // row's own Events tab — flipping `previewStep` alone would leave
    // `isEvents()` false and render the list off stale detail rows.
    if (l[i] === "events" && this.isPublic() && this.isLanding()) { this.actions.backToEvents(); return; }
    this.setState({ previewStep: l[i] });
  }


  icon(name, size) {
    const C = NS()[name];
    return C ? React.createElement(C, { size: size || 16 }) : null;
  }

  cardCover(name, hue, aspect, src) {
    const H = NS().HeroTile;
    if (!H) return null;
    const initials = name.split(" ").filter((w) => /^[A-Za-z]/.test(w)).slice(0, 2).map((w) => w[0].toUpperCase()).join("");
    // `src` is the resolved service image; HeroTile draws it and falls back to the initials tile when absent or broken.
    return React.createElement(H, { src: src || undefined, initials, hue, aspect: aspect || 1.9, radius: 0, alt: name, style: { width: "100%" } });
  }

  /** A square service thumbnail for list rows — the image when one resolves, else the existing icon tile. */
  serviceThumb(name, src, queue) {
    const H = NS().HeroTile;
    if (!src || !H) return this.tile(queue ? "IconUserFriends" : "IconCalendar", "primary");
    return React.createElement(H, { src, size: 36, radius: 8, alt: name });
  }

  /** Public events/landing pages draw the tenant's Branding header/footer unless
   *  the published document says otherwise (CC, 2026-10-03). Studio and the other
   *  journeys are unchanged. */
  /** The host-supplied brand colour, else (public events/landing pages) the tenant's
   *  Branding theme colour (CC, 2026-10-03). An authored journey colour still wins
   *  where it is consulted first. In Studio it is the workspace Branding colour (2026-10-08). */
  publicBrandColor(): string {
    return this.props.brandColor || (this.isPublic() ? this.publicChrome && this.publicChrome.brandColor : this.studioChrome && this.studioChrome.brandColor) || "";
  }

  publicBrandedDefault(which: "header" | "footer") {
    const j = this.state.journey;
    return this.isPublic() && (j === "events" || j === "landing") && !this.publicBrandedAuthored[which];
  }

  hero(e) {
    const H = NS().HeroTile;
    if (!H) return null;
    const initials = e.name.split(" ").filter((w) => /^[A-Za-z]/.test(w)).slice(0, 2).map((w) => w[0].toUpperCase()).join("");
    // The event's own image (CC, 2026-10-03); HeroTile falls back to the initials if it fails to load.
    return React.createElement(H, { src: e.cover || undefined, initials, hue: e.hue, size: 56, radius: 8, alt: e.name });
  }


  heroWide(e) {
    const H = NS().HeroTile;
    if (!H) return null;
    const initials = e.name.split(" ").filter((w) => /^[A-Za-z]/.test(w)).slice(0, 2).map((w) => w[0].toUpperCase()).join("");
    return React.createElement(H, { src: e.cover || undefined, initials, hue: e.hue, aspect: 1.9, radius: 8, alt: e.name, style: { width: "100%" } });
  }

  tile(iconName, tone) {
    const T = NS().IconTile;
    return T ? React.createElement(T, { icon: this.icon(iconName, 14), tone: tone || "primary", size: "md" }) : null;
  }

  renderVals() {
    const st = this.state;
    const persistenceUnavailable = !!PD_STUB_ONLY[st.journey];
    const isPublished = !persistenceUnavailable && (st.journey === "events" || st.journey === "landing"
      ? Boolean(st.published?.[st.journey])
      : st.apptJourney?.status === "published");
    // The events rail's Event page step previews the tenant's REAL saved
    // landing settings (the Event Landing Page tab's draft, already stashed
    // by the journey loader) layered over the list's own `s`, so the
    // existing landing renderer and templates work unchanged. This is a
    // render-time view only — `V.s`, which every settings control writes
    // through, stays the saved list settings and is never overwritten.
    const landingPreviewOnEventsRail = this.isEvents() && st.previewStep === "landing";
    const s = landingPreviewOnEventsRail
      ? { ...st.s, ...((st.stash.landing && st.stash.landing.s) || LANDING_DEFAULTS) }
      : st.s;
    const W: TenantWords = (this.props.vocab as TenantWords) || VOCAB_DEFAULTS;
    const stepList = this.steps();
    const stepIndex = Math.max(0, stepList.indexOf(st.previewStep));
    // The record's own brand colour wins; the prototype's indigo default gives way to the tenant's brand (2026-09-04).
    const brand = (s.brandColor && s.brandColor !== DEFAULTS.brandColor ? s.brandColor : null) || this.publicBrandColor() || DEFAULTS.brandColor;
    const radius = (s.radiusScale || 0) + "px";
    const landing = this.isLanding();
    const kiosk = this.isKiosk();
    const setupIds = kiosk ? ["general", "kioskentry", "kioskcheckin", "customtext", "launch"]
      : landing ? ["general", "customtext", "launch"] : ["general", "progress", "customtext", "launch"];
    /* SCAN (CD scan docket, C). The policy is per journey TYPE, so the section
       shows on the tabs that map to one — Appointments, Walk-Ins, and the two
       event tabs (both `event`). Kiosk has its own check-in screen and no manage
       journey type, so it does not get one. */
    const scanType = this.scanJourneyType();
    if (scanType) setupIds.splice(setupIds.indexOf("customtext"), 0, "scan");
    const setupIcons = { general: "IconSettings", progress: "IconLayers", scan: "IconQr", kioskentry: "IconBrowser", kioskcheckin: "IconQr", customtext: "IconMessage", launch: "IconLink" };
    const queue = this.isQueue();
    const appointment = st.journey === "appointment";
    const events = this.isEvents();
    const P = this.panels();
    const setupItems = setupIds.map((id) => {
      return { id, label: P[id][0], icon: this.icon(setupIcons[id], 15) };
    });

    /* DELIBERATELY UNGATED, unlike the customer progress bar above.
       `stepFulfilled`/`canReachStep` govern the CUSTOMER's control — the
       progress bar on the booking page — because skipping ahead there breaks
       the flow. This is the AUTHOR's rail: an author has to be able to open
       the Confirmation step to edit its settings without first filling in a
       fake booking, and it is also their escape hatch out of a step the
       customer gate would hold them in. Do not "fix" this to match. */
    const journeyItems = stepList.map((id, i) => {
      const sec = this.sectionForStep(id);
      const selected = st.previewStep === id && !st.cancelled && !st.leftQueue && st.previewStep !== "linkError";
      /* Walk-Ins read `wiPreviewStep`, not `previewStep` (2026-09-17, CD): the
         rail used to set only the latter, so clicking a Customer-journey step
         moved the panel and the toolbar caption while the preview stayed put.
         Empty means this journey does not ask that step — then the preview is
         left alone rather than jumped somewhere the page cannot show. */
      const wiStep = this.walkInStepFor(id);
      return {
        id, num: String(i + 1), label: this.stepLabel(id), selected,
        onClick: () => this.setState({ previewStep: id, section: sec, formOpen: true, cancelled: false, leftQueue: false, wiLeft: false,
          ...(wiStep ? { wiPreviewStep: wiStep } : {}),
          dwellLeft: id === "kioskDone" ? (this.state.s.ckDoneDwell || 6) : null }),
        /* SELECTED READS PURPLE (KP, 2026-09-17). This was a white card
           (`surface-card` + a hairline + shadowSm) sitting on a white rail, so
           the current step was all but invisible — while the Setup nav directly
           above it marks its selection with the house purple tint
           (EditorSectionNav's `p3`/`p1`). Two selection languages in one rail,
           one of them illegible. Both now use the same tint. */
        bg: selected ? "var(--jrni-color-primary-3)" : "transparent",
        border: selected ? "var(--jrni-color-primary-3)" : "transparent",
        shadow: "none",
        fg: selected ? "var(--jrni-color-primary-1)" : "var(--jrni-color-text-body)",
        opacity: 1,
        numBg: selected ? "var(--jrni-color-primary-1)" : "var(--jrni-color-neutral-6)",
        numFg: selected ? "#FFFFFF" : "var(--jrni-color-text-soft)",
      };
    });

    // Outcome screens hang off the end of the rail so they can be previewed directly.
    const railExtras = [];
    if (kiosk) {
      // Five failure states, each reachable directly — they are the reason the design holds up.
      const probs = [
        ["noMatch", "Not recognised", "var(--jrni-color-semantic-orange-1)"],
        ["already", "Already checked in", "var(--jrni-color-semantic-orange-1)"],
        ["cancelled", "Cancelled booking", "var(--jrni-color-semantic-red-1)"],
        ["noCamera", "Reader down", "var(--jrni-color-semantic-red-1)"],
        ["offline", "Offline", "var(--jrni-color-semantic-red-1)"],
      ];
      probs.forEach((p) => {
        const on = st.previewStep === "kioskCheckin" && st.ckProblem === p[0];
        railExtras.push({
          id: p[0], num: "!", label: p[1], selected: on,
          onClick: () => this.actions.kioskProblem(p[0]),
          bg: on ? "var(--jrni-color-primary-3)" : "transparent",
          border: on ? "var(--jrni-color-primary-3)" : "transparent",
          shadow: "none",
          fg: on ? "var(--jrni-color-primary-1)" : "var(--jrni-color-text-body)",
          opacity: 1,
          numBg: on ? p[2] : "var(--jrni-color-neutral-6)",
          numFg: on ? "#FFFFFF" : "var(--jrni-color-text-soft)",
        });
      });
    }
    if (!queue && !events && !landing && !kiosk && s.allowCancel) {
      const on = st.previewStep === "confirmation" && st.cancelled;
      railExtras.push({
        id: "cancelled", num: "\u00d7", label: "Cancelled", selected: on,
        onClick: () => this.setState({ previewStep: "confirmation", section: "confirmation", cancelled: true, formOpen: true }),
        bg: on ? "var(--jrni-color-primary-3)" : "transparent",
        border: on ? "var(--jrni-color-primary-3)" : "transparent",
        shadow: "none",
        fg: on ? "var(--jrni-color-primary-1)" : "var(--jrni-color-text-body)",
        opacity: 1,
        numBg: on ? "var(--jrni-color-semantic-red-1)" : "var(--jrni-color-neutral-6)",
        numFg: on ? "#FFFFFF" : "var(--jrni-color-text-soft)",
      });
    }
    if (!queue && !events && !landing && !kiosk) {
      const on = st.previewStep === "linkError";
      railExtras.push({
        id: "linkError", num: "!", label: "Link error", selected: on,
        onClick: () => this.setState({ previewStep: "linkError", section: "general", formOpen: true, cancelled: false }),
        bg: on ? "var(--jrni-color-primary-3)" : "transparent",
        border: on ? "var(--jrni-color-primary-3)" : "transparent",
        shadow: "none",
        fg: on ? "var(--jrni-color-primary-1)" : "var(--jrni-color-text-body)",
        opacity: 1,
        numBg: on ? "var(--jrni-color-semantic-orange-1)" : "var(--jrni-color-neutral-6)",
        numFg: on ? "#FFFFFF" : "var(--jrni-color-text-soft)",
      });
    }
    if ((events || landing) && s.allowCancelReg) {
      const on = st.previewStep === "confirmation" && st.cancelled;
      railExtras.push({
        id: "regCancelled", num: "\u00d7", label: "Cancelled", selected: on,
        onClick: () => this.setState({ previewStep: "confirmation", section: "confirmation", cancelled: true, formOpen: true }),
        bg: on ? "var(--jrni-color-primary-3)" : "transparent",
        border: on ? "var(--jrni-color-primary-3)" : "transparent",
        shadow: "none",
        fg: on ? "var(--jrni-color-primary-1)" : "var(--jrni-color-text-body)",
        opacity: 1,
        numBg: on ? "var(--jrni-color-semantic-red-1)" : "var(--jrni-color-neutral-6)",
        numFg: on ? "#FFFFFF" : "var(--jrni-color-text-soft)",
      });
    }
    if (queue && s.allowLeaveQueue) {
      const on = st.previewStep === "confirmation" && st.leftQueue;
      railExtras.push({
        id: "leftQueue", num: "\u00d7", label: "Left the line", selected: on,
        /* `wiPreviewStep: "ticket"` and `wiLeft` (2026-09-17, CD): this rail
           entry set only the appointment-side `previewStep`, so the walk-in
           preview showed no left state at all — the one screen the entry
           exists to show. `wiLeft` drives WalkInJourney's `left` prop. */
        onClick: () => this.setState({ previewStep: "confirmation", section: "confirmation", leftQueue: true, formOpen: true, wiPreviewStep: "ticket", wiLeft: true }),
        bg: on ? "var(--jrni-color-primary-3)" : "transparent",
        border: on ? "var(--jrni-color-primary-3)" : "transparent",
        shadow: "none",
        fg: on ? "var(--jrni-color-primary-1)" : "var(--jrni-color-text-body)",
        opacity: 1,
        numBg: on ? "var(--jrni-color-semantic-red-1)" : "var(--jrni-color-neutral-6)",
        numFg: on ? "#FFFFFF" : "var(--jrni-color-text-soft)",
      });
    }

    const panelMeta = P[st.section] || P.general;

    /* Where the collapsed phone picker's options dispatch to. Rebuilt every
       render alongside `navOptions` so the two cannot disagree; read by the
       stable `actions.goNav` below. */
    this._navHandlers = {};
    setupItems.forEach((i) => { this._navHandlers["s:" + i.id] = () => this.goSection(i.id); });
    journeyItems.concat(railExtras).forEach((j) => { this._navHandlers["j:" + j.id] = j.onClick; });

    const svcOf = (id) => SERVICES.filter((x) => x.id === id)[0] || SERVICES[0] || EMPTY_SERVICE;
    // Exact id or nothing: an unknown or stale id never borrows another row's identity.
    const locOf = (id) => locationById(LOCATIONS, id) || EMPTY_LOCATION;
    const cur = svcOf(st.svc);
    const curLoc = locOf(st.loc);
    const curStaff = ((STAFF.filter((x) => x.id === st.staff)[0] || STAFF[0] || EMPTY_STAFF) || {}).name || "";

    const priceOf = (x) => {
      if (x.dur === "multi") return "Multiple durations";
      const bits = [];
      if (s.showDuration) bits.push(x.dur);
      if (s.showPrice && x.price) bits.push(x.price);
      return bits.join(" · ");
    };

    const waitPrefix = this.ctv("service", "wait", "Wait about");
    const SVC_HUES = { advising: "#5142C7", aid: "#2D60EB", career: "#C18FE8", tour: "#75ADFA", registrar: "#5AC8AD", wellness: "#87D23B" };
    const visibleServices = SERVICES.filter((x) => appointment
      ? (!s.showCategories || serviceMatchesCategory(x, st.cat))
      : (st.cat === "all" || !s.showCategories || x.cat === st.cat))
      // A kiosk shows only what you can actually get in the building you are standing in.
      .filter((x) => !st.pinnedLoc || !x.locs || x.locs.indexOf(st.pinnedLoc) > -1)
      .map((x) => {
      const on = st.svc === x.id;
      /* No journey-level per-service override is persisted yet (steps.service
         carries showImages and serviceIds only), so the override slot is empty
         and the catalogue's confirmed image decides. */
      const img = resolveServiceImage(!!s.showServiceImages && !!s.showMediaImages, null, SERVICE_IMAGES[x.id]);
      const meta = queue
        ? (s.liveWaitPerService ? waitPrefix + " " + (SERVICE_WAITS[x.id] || "—") : "")
        : priceOf(x);
      return {
        name: x.name,
        sub: s.serviceDisplay === "compact"
          ? (queue ? "" : priceOf(x))
          : (queue ? x.desc : [priceOf(x), x.desc].filter(Boolean).join(" — ")),
        desc: x.desc, meta, hasMeta: !!meta,
        cover: this.cardCover(x.name, SVC_HUES[x.id] || brand, 2.1, img),
        /* Selected must READ as selected (KP, 2026-09-16): a 1px brand line
           beside a brand-coloured cover image was invisible. 2px border, a
           brand ring outside it, and a filled check badge on the card. */
        border: on ? brand : "var(--jrni-color-surface-border)",
        borderWidth: on ? "2px" : "1px",
        shadow: on ? `0 0 0 3px ${brand}33, var(--jrni-shadow-md)` : "var(--jrni-shadow-chrome)",
        checkBg: brand,
        hasImage: !!img,
        metaLine: [meta, queue && s.liveWaitPerService ? waitPrefix + " " + (SERVICE_WAITS[x.id] || "—") : ""].filter(Boolean).join(" · "),
        leading: s.showServiceImages && s.showMediaImages ? this.serviceThumb(x.name, img, queue) : null,
        trailing: queue && s.liveWaitPerService ? waitPrefix + " " + (SERVICE_WAITS[x.id] || "—") : null,
        selected: on,
        onSelect: () => {
          const only = this.locsForService(x.id);
          this.setState(only.length === 1 ? { svc: x.id, loc: only[0].id } : { svc: x.id });
        },
      };
    });

    // Walk-Ins render through WalkInJourney (real queue reads), so the wizard has no desk list of its own.
    // Cards, suggestions, pins, caption, counts and next-available all read this one eligible set.
    const locElig = this.locationEligibility();
    const locSource = queue ? [] : locElig.rows;
    const locCount = !queue && s.loadMore ? Math.max(1, st.locShown || s.initialCount || 1) : locSource.length;
    // Typeahead over name, street, town and ZIP \u2014 whatever the customer types.
    const q = (st.locQuery || "").trim().toLowerCase();
    const locSuggestions = (!queue && q.length > 1 && q !== "current location")
      ? locSource.filter((x) => (x.name + " " + x.addr).toLowerCase().indexOf(q) > -1).slice(0, 6).map((x) => ({
          key: x.id, name: x.name, meta: x.addr + (x.dist === "\u2014" ? "" : " \u00b7 " + x.dist),
          selected: st.loc === x.id,
          bg: st.loc === x.id ? "var(--jrni-color-primary-4)" : "transparent",
          onPick: () => this.setState({ loc: x.id, locQuery: x.name }),
        }))
      : [];
    const nearFirst = !queue && st.geoState === "done";
    const ordered = nearFirst
      ? locSource.slice().sort((a, b) => (parseFloat(a.dist) || 999) - (parseFloat(b.dist) || 999))
      : locSource;
    const visibleLocations = ordered.slice(0, locCount).map((x) => {
      const row = (key, kids) => React.createElement("span", { key, style: { display: "block" } }, kids);
      const kids = [row("a", x.addr)];
      /* Only when there IS one. `x.next` is "" for every row the catalogue
         returns (alljourneys-catalog.ts / appointment-data.ts both set it
         empty), because no read carries a next-available slot PER LOCATION —
         the availability op is scoped by slug, service and staff, not venue.
         Printed unguarded it rendered the bare label "Next available:" with
         nothing after it on all six campuses (operator, 2026-09-14). */
      if (!queue && s.nextAvailable && x.next) {
        kids.push(row("n", [React.createElement("strong", { key: "b", style: { fontWeight: 700 } }, "Next available"), " " + x.next]));
      }
      if (!queue && s.extraLocationInfo) kids.push(row("h", x.hours + " · " + x.phone));
      // Quickbook removed from the customer flow (KP, 2026-09-16): never rendered
      // regardless of the authored toggle. The setting stays in the designer for now.
      const qb = false;
      const on = (queue ? st.desk : st.loc) === x.id;
      return {
        name: x.name, addr: x.addr, phone: x.phone, dist: x.dist,
        sub: React.createElement("span", { style: { display: "flex", flexDirection: "column", gap: "2px" } }, kids),
        leading: this.tile(queue ? "IconConciergeBell" : "IconPin", "neutral"),
        trailing: x.dist,
        showPhone: !queue && s.extraLocationInfo,
        // Same guard as the inline row above: no value, no label.
        showNext: !queue && s.nextAvailable && !!x.next,
        nextLabel: this.longNext(x.next),
        showHours: !queue && s.extraLocationInfo,
        hoursOpen: !!st.openHours[x.id],
        hoursCaret: st.openHours[x.id] ? "rotate(-90deg)" : "rotate(90deg)",
        onToggleHours: (e) => { if (e && e.stopPropagation) e.stopPropagation(); this.setState((prev) => ({ openHours: { ...prev.openHours, [x.id]: !prev.openHours[x.id] } })); },
        week: this.weekFor(x.hours),
        tzLabel: "Local time \u2014 " + (ZONES[x.tz] || ZONES.central).label,
        border: on ? brand : "var(--jrni-color-surface-border)",
        shadow: on ? "var(--jrni-shadow-md)" : "var(--jrni-shadow-chrome)",
        quickbook: qb, quickbookLabel: "Quickbook",
        selected: on,
        onSelect: () => this.setState(queue ? { desk: x.id } : { loc: x.id }),
        onQuickbook: () => {
          const parts = String(x.next || "").split(", ");
          const time = parts[parts.length - 1];
          const datePart = parts.length > 1 ? parts.slice(0, -1).join(", ") : "";
          const t = this.validPublicToday({ ...this.state, loc: x.id });
          if (!t) { this.setState({ bookingError: i18nT("shell.allJourneys.locationTimeZoneInvalid", "This location's time zone isn't recognised. Choose another location.") }); return; }
          let d = t;
          if (datePart && !/today/i.test(datePart)) {
            const md = datePart.replace(/^[A-Za-z]{3},?\s*/, "");
            const parsed = new Date(md + " " + t.getFullYear() + " 00:00:00");
            if (!isNaN(parsed.getTime())) d = parsed;
          }
          const iso = this.iso(d);
          this.setState({
            loc: x.id, day: iso, dayChosen: true, calMonth: { y: d.getFullYear(), m: d.getMonth() },
            slot: this.slotKeyForTime(iso, time, x.id), previewStep: "details", reconfirmFrom: null,
          });
        },
      };
    });

    const slotOpts = (arr) => arr.map((t) => ({ value: t, label: t }));
    const groupDefs = [["Morning", SLOTS.morning], ["Afternoon", SLOTS.afternoon], ["Evening", SLOTS.evening]];

    const dayStart = new Date(this.today().getFullYear(), this.today().getMonth(), this.today().getDate());
    /* An event's day is its own civil date key (dayKey, "YYYY-MM-DD" in the event's zone), never the
       display label: e.date is printed in the workspace's date pattern (item 8), which Date cannot parse
       for every pattern ("29/09/2026"). A row with no key counts as today, as the old fallback did. */
    const todayKey = this.iso(dayStart);
    const evtKey = (e) => e.dayKey || todayKey;
    const eq = (st.evtQuery || "").trim().toLowerCase();
    const evtHay = (e) => (e.name + " " + e.desc + " " + e.venue + " " + e.addr + " " + e.date + " " + e.cat).toLowerCase();
    const remainingLabel = this.ctv("events", "remaining", "spaces left");
    const soldOutLabel = this.ctv("events", "soldout", "Fully booked");
    // "Fully booked" is only true of a sold-out/full event. An event that is
    // simply not open for registration (registrationOpen false, or bookable
    // false for a non-capacity reason) is closed, not full — saying "Fully
    // booked" there invents a capacity fact the server never stated.
    const regClosedLabel = this.ctv("events", "regclosed", "Registration closed");
    // An event in progress (the server's status "live", CC 0362): registration closed because it
    // started, so the card and the page say that rather than the generic closed wording.
    const happeningNowLabel = this.ctv("events", "happening", "Happening now");
    // Public: the server already filtered, sorted and paged these rows \u2014
    // rendered exactly once, never re-sliced/re-sorted/re-filtered here.
    // Totals/pages/facets come from the same server response
    // (`refreshPublicEventsList`'s `evtPagination`/`evtFacets`), not derived
    // from this (partial, one-page) array.
    const usingServerEvents = this.isPublic() && events;
    let evtShown, evtPages, evtPage, evtMatches, evtFilteredCount;
    if (usingServerEvents) {
      evtShown = this.eventsRows;
      const pagination = st.evtPagination;
      evtPages = pagination ? Math.max(1, pagination.total_pages) : 1;
      evtPage = pagination ? pagination.page : (st.evtPage || 1);
      evtMatches = (!st.evtPicked && eq.length > 1)
        ? this.eventsRows.filter((e) => this.eventRowSelectable(e)).slice(0, 5).map((e) => ({
            key: e.id, name: e.name, meta: [e.when, e.venue].filter(Boolean).join(" \u00b7 "),
            onPick: () => this.actions.pickEvent(e.id, e.name),
          }))
        : [];
    } else {
      const evtSorted = this.eventsRows.slice()
        .filter((e) => s.showPastEvents || evtKey(e) >= todayKey)
        .sort((a, b) => {
          if (s.sortOrder === "name") return a.name.localeCompare(b.name);
          if (s.sortOrder === "popularity") return (b.cap - b.left) - (a.cap - a.left);
          return evtKey(a) < evtKey(b) ? -1 : evtKey(a) > evtKey(b) ? 1 : 0;
        });
      // Events are matched to the kiosk's campus on the venue's street address.
      const pinnedAddr = st.pinnedLoc ? ((LOCATIONS.filter((x) => x.id === st.pinnedLoc)[0] || {}).addr || "").split(",")[0].trim().toLowerCase() : "";
      const evtFiltered = evtSorted.filter((e) => st.evtCat === "all" || !s.eventFilters || e.cat === st.evtCat)
        .filter((e) => !pinnedAddr || (e.addr || "").toLowerCase().indexOf(pinnedAddr) > -1)
        // Preview rows are real catalogue rows, so the picked location id is
        // matched against the row's own authoritative location id.
        .filter((e) => !st.evtLocationFilter || (e.raw && (e.raw.locationId || e.raw.location_id)) === st.evtLocationFilter)
        .filter((e) => s.showSoldOut || e.left > 0)
        .filter((e) => !eq || evtHay(e).indexOf(eq) > -1)
        .filter((e) => {
          if (!st.evtFrom && !st.evtTo) return true;
          // Civil date keys compare as strings. The bounds are the pickers' own "YYYY-MM-DD", read as
          // written: parsing one with Date made it UTC midnight, the previous day west of UTC.
          const day = e.dayKey;
          if (!day) return true;
          const bound = (v) => { const k = String(v).slice(0, 10); return /^\d{4}-\d{2}-\d{2}$/.test(k) ? k : null; };
          const from = st.evtFrom ? bound(st.evtFrom) : null;
          const to = st.evtTo ? bound(st.evtTo) : null;
          if (from && day < from) return false;
          if (to && day > to) return false;
          return true;
        });
      evtMatches = (!st.evtPicked && eq.length > 1)
        ? this.eventsRows.filter((e) => evtHay(e).indexOf(eq) > -1 && this.eventRowSelectable(e)).slice(0, 5).map((e) => ({
            key: e.id, name: e.name, meta: [e.when, e.venue].filter(Boolean).join(" \u00b7 "),
            onPick: () => this.actions.pickEvent(e.id, e.name),
          }))
        : [];
      const perPage = Math.max(1, s.eventsPerPage || 1);
      /* Builder default data (2026-09-28): the builder and its preview show the
         3 most recently created events that pass the page's own filters, in the
         page's own sort order. The customer page (the branch above) is unchanged. */
      const newestIds = new Set(newestFirst(evtFiltered, SAMPLE_COUNT, (e) => createdTime(e.raw)).map((e) => e.id));
      const evtList = this.isPublic() ? evtFiltered : evtFiltered.filter((e) => newestIds.has(e.id));
      evtPages = Math.max(1, Math.ceil(evtList.length / perPage));
      evtPage = Math.min(st.evtPage, evtPages);
      evtShown = s.eventPaging === "pages"
        ? evtList.slice((evtPage - 1) * perPage, evtPage * perPage)
        : evtList.slice(0, perPage * evtPage);
      evtFilteredCount = evtList.length;
    }
    // Unified across both branches: the real server total/hasMore in public
    // mode (never derived by re-measuring the one page already on screen),
    // the locally-filtered count in admin preview.
    const evtTotalCount = usingServerEvents ? (st.evtPagination?.total ?? evtShown.length) : undefined;
    const evtHasMore = usingServerEvents ? Boolean(st.evtPagination?.hasMore) : undefined;
    // Category chips: the real facet catalogue the server returned for this
    // scope — on the public path AND in the Studio preview, which now reads
    // the same `getPublicEvents` facets (`refreshPublicEventsPreview` stores
    // them in `evtFacets`). The invented admissions/academic/careers/wellness
    // list is gone: it never matched the tenant's own categories, so filtering
    // a server-backed preview by it could only ever return nothing. With no
    // facets yet, the categories actually present on the loaded rows are
    // offered instead of a fixture. "All" is always first, so clear/all
    // behaviour is unchanged.
    const evtFacetCategories = (st.evtFacets?.categories || []).filter((c) => typeof c === "string" && c.trim());
    const evtCategoryValues = evtFacetCategories.length
      ? evtFacetCategories
      : Array.from(new Set(this.eventsRows.map((e) => e.cat).filter((c) => typeof c === "string" && c.trim())));
    const evtCategoryOptions = [{ value: "all", label: "All" }]
      .concat(evtCategoryValues.map((c) => ({ value: c, label: c })));
    // Location filter: the server's own location facet ({id, name}) off the
    // same `getPublicEvents` response the rows came from — never a fixture
    // list and never an invented id. With no facet there is nothing honest to
    // offer, so the control renders empty and disabled rather than pretending.
    const evtLocationFacet = (st.evtFacets?.locations || []).filter((l) => l && l.id && l.name);
    const evtLocationOptions = [{ value: "", label: "All locations" }]
      .concat(evtLocationFacet.map((l) => ({ value: l.id, label: l.name })));
    const curEvt = this.eventsRows.filter((e) => e.id === st.evt)[0] || this.eventsRows[0] || EMPTY_EVENT;
    // Party size on the event journeys is derived from the PEOPLE, not from a
    // stepper: the backend reads requested seats as `1 + attendees.length`, so
    // anything else here would be a second, disagreeing authority (CD docket
    // 2026-09-15 item 1.6). Appointments and queue keep their own counts.
    const attRows = st.attRows || [];
    const seatLimit = (events || landing) ? this.attendeeSeatLimit() : null;
    const effectiveTickets = (events || landing) ? 1 + attRows.length : st.tickets;
    // Authoritative event host only: the public projection's staffName /
    // staff_name off this row's raw record. Never the appointment journey's
    // `curStaff`, and never a stand-in — blank hides the section.
    const eventHostName = String((curEvt.raw && (curEvt.raw.staffName || curEvt.raw.staff_name)) || "").trim();
    // The Landing/Event detail's availability, read off the same authoritative
    // projection the cards use (`bookable` / `bookableReason` / `soldOut` /
    // `registrationOpen` / `waitlistOpen`) in preview and in public alike.
    const lpAv = curEvt.availability || null;
    /* A CANCELLED EVENT SAYS SO (CC 0416). Its address still opens this page (the event is public), so the page
       itself must say the event won't go ahead, not just "Registration closed". Read from the row's own status,
       or the availability's bookableReason. Cancelled wins over ended and every other closed reason. */
    const lpCancelledEvent = (() => {
      const st0 = String((curEvt.raw && (curEvt.raw.status || curEvt.raw.state)) || curEvt.status || "").toLowerCase();
      return st0 === "cancelled" || st0 === "canceled" || !!(lpAv && (lpAv.bookableReason === "cancelled" || lpAv.bookable_reason === "cancelled"));
    })();
    const lpEnded = !lpCancelledEvent && (lpAv ? lpAv.bookableReason === "completed" : false);
    const lpHappeningNow = lpAv ? lpAv.happeningNow === true && !lpEnded : false;
    const lpSoldOut = lpAv ? (lpAv.soldOut === true || !!st.soldOutPreview) : !!st.soldOutPreview;
    // Registration is offered only on the contract's literal booleans, never
    // on `!soldOut` — which would re-open a completed or closed event.
    /* NOTHING ON SALE, NO FORM (CC 0436 B / 0437): only an event that HAS tickets, none of them on sale now (not yet
       open, ended, or closed). No tickets is a capacity-only event and keeps its form. The page says why instead of
       offering a form the server will refuse ("not on sale"). */
    const lpSale = this.isPublic() ? eventSaleState(((curEvt.raw && curEvt.raw.ticketTiers) as SaleTier[] | undefined) ?? undefined,
      String((curEvt.raw && (curEvt.raw.startsAt || curEvt.raw.start)) || "") || null) : { state: "on-sale" as const };
    const lpRegOpen = lpEnded || lpCancelledEvent || lpSale.state !== "on-sale" ? false
      : lpAv ? (lpAv.bookable === true && lpAv.registrationOpen === true && lpAv.soldOut === false && !st.soldOutPreview)
      : !st.soldOutPreview;
    const lpNotOnSaleLabel = lpSale.state === "closed" ? i18nT("journeys.allJourneys.registrationClosedOnSale", "Registration is closed")
      : lpSale.state === "not-yet" && lpSale.opensAt ? i18nT("journeys.allJourneys.registrationOpensOn", "Registration opens {date}", { date: new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short", ...(String((curEvt.raw && curEvt.raw.timezone) || "") ? { timeZone: String(curEvt.raw.timezone) } : {}) }).format(new Date(lpSale.opensAt)) })
      : lpSale.state !== "on-sale" ? i18nT("journeys.allJourneys.registrationNotOpenYet", "Registration isn't open yet") : "";
    const relatedEvents = (() => {
      /* Never recommend an event a visitor can't go to (CC 0235 §5): cancelled, or already
         over. Read from the row's own status and availability; a past end time counts too. */
      const goneEvent = (e) => {
        const st0 = String((e.raw && (e.raw.status || e.raw.state)) || e.status || "").toLowerCase();
        if (st0 === "cancelled" || st0 === "canceled" || st0 === "archived") return true;
        const av0 = e.availability || null;
        if (av0 && (av0.bookableReason === "completed" || av0.bookable_reason === "completed" || av0.bookableReason === "cancelled")) return true;
        const endIso = (e.raw && (e.raw.endsAt || e.raw.end_at || e.raw.end)) || e.end;
        const endMs = endIso ? Date.parse(String(endIso)) : NaN;
        return Number.isFinite(endMs) && endMs < Date.now();
      };
      const pool = this.eventsRows.filter((e) => e.id !== curEvt.id && !goneEvent(e));
      if (s.relatedRule === "manual") return (s.relatedManual || []).map((id) => pool.filter((e) => e.id === id)[0]).filter(Boolean);
      const n = Math.max(1, s.relatedCount || 3);
      const rank = (e) => {
        if (s.relatedRule === "venue") return e.venue === curEvt.venue && (!e.raw || e.raw.venue || e.raw.location?.venue) ? 0 : 1; /* synthesized "Online" is not a shared venue (CC, 2026-10-05) */
        if (s.relatedRule === "date") return 0;
        return e.cat === curEvt.cat ? 0 : 1;
      };
      return pool.slice().sort((a, b) => rank(a) - rank(b) || (evtKey(a) < evtKey(b) ? -1 : evtKey(a) > evtKey(b) ? 1 : 0)).slice(0, n);
    })();

    const endedLabel = this.ctv("events", "ended", "Event ended");
    const eventCards = evtShown.map((e) => {
      const full = e.left === 0;
      // Authoritative availability, straight off the real public projection
      // (`projectAvailability`: bookable / bookable_reason / registrationOpen
      // / soldOut / waitlistOpen). Never a client-side date comparison and
      // never capacity alone — "this event has ended" is a server fact
      // (`bookable_reason: "completed"`), not something the browser's clock
      // gets to decide. Rows loaded before a projection exists fall back to
      // the previous capacity-only reading, so nothing regresses.
      const av = e.availability || null;
      const ended = av ? av.bookableReason === "completed" : false;
      /* In progress now (CC 0362): a server fact, never the browser's clock against start/end. */
      const happeningNow = av ? av.happeningNow === true && !ended : false;
      const soldOut = av ? av.soldOut === true : full;
      // Waitlist availability follows the same shape the Landing detail uses
      // (`lpShowWaitlist`): the server's own `waitlistOpen` decides on BOTH
      // paths, with the author's switch as the fallback only for a row that
      // carries no projection at all.
      const waitlistOffered = s.waitlist && soldOut && !ended && !happeningNow
        && this.eventWaitlistOpen(e); /* the event's flag or any ticket's own (CC 0346) */
      // Registration is offered only on the contract's literal booleans —
      // never `!soldOut`, which would re-open a completed or closed event.
      const canRegister = ended ? false
        : av ? (av.bookable === true && av.registrationOpen === true && av.soldOut === false)
        : !full;
      const cardClosed = !canRegister && !waitlistOffered;
      // Ended wins, then in progress (CC 0362), then genuine sold-out; anything
      // else closed is closed for a registration reason and says so.
      const closedStatus = ended ? endedLabel : happeningNow ? happeningNowLabel : soldOut ? soldOutLabel : regClosedLabel;
      const bits = [];
      /* The projected time names the event's zone in brackets; the "Time zone" switch drops it from both lines. */
      const zoneOff = (x) => (s.showTimezone ? x : String(x || "").replace(/\s*\([^()]*\)$/, ""));
      if (s.showEventDate) bits.push(zoneOff(e.when));
      if (s.showEventVenue) bits.push(e.venue);
      if (s.showEventPrice) bits.push(e.price);
      return {
        id: e.id, name: e.name, sub: bits.join(" · "),
        desc: s.showEventDesc ? e.desc : "",
        showDesc: s.showEventDesc,
        /* The card's place line (CC, 2026-10-06): the street address, else the venue. An online event has no
           address and its venue reads "Online", so the line used to show a map pin with blank text. A row with
           neither shows no line at all. */
        addr: e.addr || e.venue || "", date: e.date,
        time: zoneOff(e.time),
        showAddr: s.showEventVenue && !!(e.addr || e.venue), showDate: s.showEventDate, showTime: s.showEventTime,
        priceLine: s.showEventPrice ? e.price : "",
        showPrice: s.showEventPrice,
        when: e.when, venue: e.venue, price: e.price,
        cover: s.eventImages && s.showMediaImages ? this.hero(e) : null,
        wide: s.eventImages && s.showMediaImages ? this.heroWide(e) : null,
        left: e.left, cap: e.cap,
        ended,
        happeningNow,
        /* The closed control's tone: an event in progress is a live status, not a muted "closed". */
        closedTone: cardClosed && happeningNow ? "success" : "neutral",
        spaces: cardClosed ? closedStatus : (soldOut ? soldOutLabel : (e.left == null ? "" : e.left + " " + remainingLabel)),
        meterTone: ended ? "neutral" : soldOut ? "danger" : (e.left == null ? "success" : e.left / e.cap < 0.2 ? "warning" : "success"),
        showMeter: s.showRemaining && !ended,
        full, selected: st.evt === e.id,
        interactive: !cardClosed,
        onSelect: (clickEvent?: { stopPropagation?: () => void }) => {
          // The card's own container and the nested Register/waitlist button
          // share this one handler, so a button click bubbles to the card and
          // would select twice — pushing a duplicate history entry. Stopping
          // propagation here keeps it to exactly one; card, keyboard and
          // direct calls pass no event and are unaffected.
          clickEvent?.stopPropagation?.();
          // A closed card (ended, or full with no waitlist) offers nothing to
          // open — the shortcut must not be a back door into registration
          // or the event detail step.
          if (cardClosed) return;
          if (this.isPublic()) this.selectPublicEvent(e.id, { fromList: true });
          // Studio preview: with the Event page step shown, a card opens it —
          // the same order the public rail runs. With it hidden, the shortcut
          // may only fire for a row that really carries an authoritative
          // one-off schedule and is bookable, the same facts the public
          // journey decides on; anything else still shows the Event page.
          // Nothing is submitted here — this only moves the previewed step.
          else this.setState(this.eventsRailStepForSelection(e));
        },
        cta: waitlistOffered ? this.ctv("events", "waitlist", "Join the waitlist") : "",
        taken: e.left == null ? null : e.cap - e.left,
        dim: ended ? 0.6 : happeningNow ? 1 : cardClosed ? 0.72 : 1,
        bookable: canRegister,
        showWaitlist: waitlistOffered,
        closed: cardClosed,
        spacesPill: cardClosed ? closedStatus : (s.showRemaining ? (soldOut ? soldOutLabel : (e.left == null ? null : e.left + " " + remainingLabel)) : null),
      };
    });

    const ctGroups = this.ctGroupDefs().map((g) => {
      const fields = g.fields.map((f) => {
        /* `ctGroup` overrides the panel-group id when the two differ
           (2026-09-17, CD): the "Left the line" group is its own heading in the

           panel but writes `queue.*` keys, because that is what the page and
           the preview already read. Without it the group id would mint
           `queueLeft.lvttl`, which nothing reads — a second silent field of
           exactly the kind this group was added to remove. */
        const key = (g.ctGroup || g.id) + "." + f.id;
        const value = st.ct[key] || "";
        return {

          label: f.labelKey ? i18nT(f.labelKey, f.label) : f.label, def: f.def, value, changed: !!value.trim(), hint: f.hint || "",
          onChange: (e) => {
            const v = e && e.target ? e.target.value : e;
            this.setState((prev) => ({ ct: { ...prev.ct, [key]: v }, dirty: true }));
          },
          onRevert: () => this.setState((prev) => { const ct = { ...prev.ct }; delete ct[key]; return { ct, dirty: true }; }),
        };
      });
      const custom = fields.filter((f) => f.changed).length;
      const shown = st.ctFilter === "custom" ? fields.filter((f) => f.changed) : fields;
      const open = !!st.openGroups[g.id];
      return {
        id: g.id, label: g.labelKey ? i18nT(g.labelKey, g.label) : g.label, open, fields: shown, hasCustom: custom > 0,
        countLabel: custom ? i18nT("shell.allJourneys.ctGroupCustomisedCount", "{custom} of {total} customised", { custom, total: fields.length }) : i18nT("shell.allJourneys.ctGroupFieldCount", "{total, plural, one {# field} other {# fields}}", { total: fields.length }),
        caret: open ? "rotate(90deg)" : "rotate(0deg)",
        onToggle: () => this.setState((prev) => ({ openGroups: { ...prev.openGroups, [g.id]: !prev.openGroups[g.id] } })),
      };
    }).filter((g) => st.ctFilter !== "custom" || g.hasCustom);

    // Letters (including accents), spaces, hyphens and apostrophes only \u2014 no digits, no symbols.
    const nameOk = (v) => this.nameValid(v);
    const nameMsg = (v) => {
      const s2 = String(v || "").trim();
      if (!s2) return "This field is required.";
      if (s2.length < 2) return "Names need at least two letters.";
      return "Use letters, hyphens and apostrophes only.";
    };
    // One lock gate for the primary person (see detailsLocked): no contracted
    // session, no lock — the guest's own fields are validated as typed.
    const locked = this.detailsLocked();
    const firstBad = !this.lockedField("first") && st.firstTouched && !nameOk(st.first);
    const lastBad = !this.lockedField("last") && st.lastTouched && !nameOk(st.last);
    // The address is free text. The suggestion list this used to show was a fixed
    // set of street addresses in one demo town, not a lookup — and the only
    // address-suggest read (geo) is an admin route an anonymous page cannot call.
    const ph = this.parsePhone(st.phone, st.phoneCountry);
    const phoneBad = !this.lockedField("phone") && st.phoneTouched && !ph.valid && !(s.phoneOptional && ph.len === 0);
    /* A replay is a DIFFERENT SUCCESS, not an error: the original registration
       stands, its ticket and Manage links still work, and the only thing the
       plain copy gets wrong is implying something new happened and that an
       e-mail is coming (CD docket 2026-09-15 item 5). */
    const replay = (events || landing) ? this.eventRegistrationReplay() : null;
    const outsideJourney = st.previewStep === "linkError" || !!st.cancelled || !!st.leftQueue;
    const emailOk = this.emailValid(st.email);
    const emailBad = !this.lockedField("email") && st.emailTouched && !emailOk && !(s.emailOptional && !(st.email || "").trim());
    // Single source of truth: the same widths the toggle + dialog use, so the
    // pane can never disagree with the dialog about how wide "tablet" is.
    const deviceW = resolveDeviceWidths();
    const deviceH = resolveDeviceHeights();
    const frameWidth = st.previewOnly ? "100%" : st.device === "mobile" ? deviceW.mobile + "px" : st.device === "tablet" ? "min(100%, " + deviceW.tablet + "px)" : "100%";
    // Fixed device height so mobile/tablet show the WHOLE screen — scrolling
    // inside the frame — rather than shrinking to the page's own content.
    const frameHeight = st.previewOnly || st.device === "desktop" ? undefined : deviceH[st.device] + "px";

    /* ── Kiosk preview ────────────────────────────────────────────────────────
       Every destination tile, the live strip, the three lookup methods and the
       five failure states. Sizes step up when larger text is on, because a wall
       tablet is read standing up and from further away than a phone. */
    const kioskBooking = st.ckBooking || null;
    const bigT = kiosk && s.kioskBigText && st.bigText;
    const phone = st.device === "mobile";
    const ckMethods = [];
    if (s.ckQr || s.ckWallet || s.ckNfc) ckMethods.push({ value: "scan", label: "Scan or tap" });
    if (s.ckRef) ckMethods.push({ value: "ref", label: "Reference" });
    if (s.ckName) ckMethods.push({ value: "name", label: "Name" });
    const ckMethodActive = ckMethods.filter((m) => m.value === st.ckMethod).length ? st.ckMethod : (ckMethods[0] || {}).value;
    const refTyped = (st.ckRefInput || "").trim().toUpperCase();
    const refMatch = kioskBooking && refTyped && st.ckFound === refTyped ? [kioskBooking] : [];
    const nameTyped = (st.ckNameInput || "").trim().toLowerCase();
    // The check-in service looks up by code only; there is no name search to list results from.
    const nameMatch = [];
    const kioskProblem = st.ckProblem || "";
    // Nouns only — these get joined into "Hold your ___ up to the reader", so a verb like
    // "tap" cannot go in the list. NFC is expressed in ckWalletHint underneath instead.
    const scanLabelBits = [];
    if (s.ckQr) scanLabelBits.push("QR code");
    if (s.ckWallet) scanLabelBits.push("wallet pass");
    const compactTiles = s.kioskTileSize === "compact";
    const tileIcon = compactTiles ? (bigT ? 22 : 19) : (bigT ? 34 : 28);
    const kioskTiles = [];
    if (s.kioskAppts) kioskTiles.push({
      id: "appts", title: this.ctv("entry", "appts", "Book an appointment"),
      desc: this.ctv("entry", "apptsSub", "Advising, financial aid, careers and more"),
      icon: this.icon("IconCalendar", tileIcon), hue: "var(--jrni-color-primary-1)",
      // No availability read backs the tile, so it states nothing it cannot know.
      meta: "",
      onClick: () => this.actions.kioskGo("appts"),
    });
    if (s.kioskWalkin) kioskTiles.push({
      id: "walkin", title: this.ctv("entry", "walkin", "See someone today"),
      desc: this.ctv("entry", "walkinSub", "Join the walk-in line at this campus"),
      icon: this.icon("IconUserFriends", tileIcon), hue: "var(--jrni-color-semantic-green-1)",
      meta: "",
      onClick: () => this.actions.kioskGo("walkin"),
    });
    if (s.kioskEvents) kioskTiles.push({
      id: "events", title: this.ctv("entry", "events", this.tenantDisplayName() ? "What's on at " + this.tenantDisplayName() : "What's on"),
      desc: this.ctv("entry", "eventsSub", "Orientation, fairs and workshops"),
      icon: this.icon("IconTicket", tileIcon), hue: "var(--jrni-color-semantic-orange-1)",
      meta: "",
      onClick: () => this.actions.kioskGo("events"),
    });
    if (s.kioskCheckin) kioskTiles.push({
      id: "checkin", title: this.ctv("entry", "ck", "I'm here for my appointment"),
      desc: this.ctv("entry", "ckSub", "Scan your pass or find your booking"),
      icon: this.icon("IconQr", tileIcon), hue: "var(--jrni-color-primary-2)",
      meta: "", onClick: () => this.actions.kioskGo("checkin"),
    });
    const hour = 9;
    /* The kiosk's own location: the ?location= key first, then the configured
       kiosk location — the same pick kioskGo pins for the tiles. Everything the
       "Open today" strip says comes from this location or its queues, and a line
       with nothing served behind it is not drawn. The strip used to state fixed
       hours, a fixed queue line and a fixed next slot on every tenant's kiosk. */
    const kioskLoc = (this.kioskLocationKey ? LOCATIONS.filter((l) => (l.external_key || l.externalCode || "") === this.kioskLocationKey)[0] : null)
      || locationById(LOCATIONS, s.kioskLocation) || EMPTY_LOCATION;
    const kioskAddr = String(kioskLoc.addr || "").trim();
    // Today's hours from the location's own `hours`, read through the same
    // weekFor() the "Branch hours" list uses. No hours served, no hours line.
    const kioskHoursRaw = String(kioskLoc.hours || "").trim();
    const kioskHoursToday = kioskHoursRaw ? String((this.weekFor(kioskHoursRaw)[new Date().getDay()] || {}).label || "") : "";
    // The walk-in line at this location: the Walk-Ins page's attached queues
    // (their live status read, st.wiQueues) whose own location — from the
    // tenant's queue list — is this kiosk's, and which are taking people now.
    // The shortest estimated wait wins; both numbers come from that one read.
    // No queue here, no status read (e.g. a public mount), no line.
    const kioskQueueLoc = {};
    (st.wiQueueOptions || []).forEach((o) => { kioskQueueLoc[o.id] = o.locationId || ""; });
    const kioskLine = (st.wiQueues || [])
      .filter((r) => r && r.status && r.status.canAcceptEntries && kioskLoc.id && kioskQueueLoc[r.id] === kioskLoc.id)
      .map((r) => r.status)
      .sort((a, b) => (a.estWaitMins - b.estWaitMins) || (a.waiting - b.waiting))[0] || null;
    const kioskWaitLine = kioskLine
      ? [kioskLine.estWaitMins > 0 ? "About " + kioskLine.estWaitMins + " min" : "", kioskLine.waiting + " ahead"].filter(Boolean).join(" · ")
      : "";
    const kioskWaitOn = !!(s.kioskWaitTime && kioskWaitLine);
    const kioskHoursOn = !!(s.kioskHours && (kioskHoursToday || kioskAddr));
    const kioskPv = {
      isKioskEntry: kiosk && st.previewStep === "kioskEntry",
      isKioskCheckin: kiosk && st.previewStep === "kioskCheckin" && !kioskProblem,
      isKioskDone: kiosk && st.previewStep === "kioskDone",
      isKioskProblem: kiosk && st.previewStep === "kioskCheckin" && !!kioskProblem,
      kioskGreeting: s.kioskGreeting === "none" ? "" : s.kioskGreeting === "time"
        ? this.ctv("entry", "greet", hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening")
        : this.ctv("entry", "greet", this.tenantDisplayName() ? "Welcome to " + this.tenantDisplayName() : "Welcome"),
      kioskShowGreeting: s.kioskGreeting !== "none",
      kioskSub: this.ctv("entry", "sub", "What do you need today?"),
      kioskTiles,
      kioskTileCols: st.device === "mobile" ? "minmax(0, 1fr)" : "repeat(2, minmax(0, 1fr))",
      kioskTileCompact: s.kioskTileSize === "compact",
      kioskTileRoomy: s.kioskTileSize !== "compact",
      kioskTileMinH: phone ? "auto" : s.kioskTileSize === "compact" ? "auto" : (bigT ? "184px" : "158px"),
      kioskTilePad: s.kioskTileSize === "compact" ? "14px 16px" : "22px 24px",
      kioskTileGap: s.kioskTileSize === "compact" ? "12px" : "12px",
      kioskTileDir: s.kioskTileSize === "compact" ? "row" : "column",
      kioskTileAlign: s.kioskTileSize === "compact" ? "center" : "flex-start",
      kioskIconBox: s.kioskTileSize === "compact" ? (bigT ? "44px" : "38px") : "56px",
      kioskTitleSize: bigT ? (phone ? "30px" : "40px") : (phone ? "25px" : "32px"),
      kioskSubSize: bigT ? (phone ? "19px" : "24px") : (phone ? "16px" : "19px"),
      kioskTileTitleSize: bigT ? (phone ? "19px" : "24px") : (phone ? "17px" : "20px"),
      kioskTileDescSize: bigT ? (phone ? "15px" : "18px") : (phone ? "14px" : "15px"),
      kioskBodySize: bigT ? (phone ? "17px" : "20px") : (phone ? "15px" : "16px"),
      // Only lines with something served behind them; see kioskLoc above.
      // There is no "next appointment" line: no read gives the soonest slot
      // across every service at a location, so the kiosk does not state one.
      kioskShowStrip: kioskWaitOn || kioskHoursOn,
      kioskWait: kioskWaitOn, kioskWaitLine,
      kioskHours: kioskHoursOn, kioskHoursToday,
      kioskHoursAddr: kioskAddr,
      kioskBigTextToggle: s.kioskBigText,
      kioskBigTextOn: !!st.bigText,
      pinnedLocName: (locationById(LOCATIONS, st.pinnedLoc) || EMPTY_LOCATION).name,
      pinnedStepWord: this.stepLabel("location"),
      ckHeader: this.ctv("checkin", "hdr", "Let's find your booking"),
      ckScanLine: this.ctv("checkin", "scan", scanLabelBits.length
        ? "Hold your " + scanLabelBits.join(" or ") + " up to the reader"
        : "Tap your wallet pass against the reader"),
      ckRefHeader: this.ctv("checkin", "refHdr", "Enter your booking reference"),
      ckNameHeader: this.ctv("checkin", "nameHdr", "Find your booking by name"),
      ckMethods, ckMethodActive,
      ckScanMode: ckMethodActive === "scan", ckRefMode: ckMethodActive === "ref", ckNameMode: ckMethodActive === "name",
      ckAnyMethod: ckMethods.length > 0,
      ckShowWalletHint: s.ckWallet || s.ckNfc,
      ckWalletHint: s.ckNfc && s.ckWallet ? "Apple and Google wallet passes work by scan or by tapping the reader."
        : s.ckNfc ? "Tap your pass against the reader." : "Apple and Google wallet passes work here too.",
      ckRefValue: st.ckRefInput, ckNameValue: st.ckNameInput,
      ckRefMatches: refMatch.map((b) => ({ key: b.ref, ref: b.ref, name: b.name, svcWhen: [b.svc, b.when].filter(Boolean).join(" · "), onPick: () => this.actions.kioskPick(b.ref) })),
      ckNameMatches: nameMatch.map((b) => ({ key: b.ref, ref: b.ref, name: b.name, svcWhen: [b.svc, b.when].filter(Boolean).join(" · "), onPick: () => this.actions.kioskPick(b.ref) })),
      ckHasRefMatches: refMatch.length > 0, ckHasNameMatches: nameMatch.length > 0,
      ckNoRefMatch: !!refTyped && st.ckLookedUp === refTyped && !st.ckLooking && refMatch.length === 0,
      ckNoNameMatch: nameTyped.length > 2,
      ckLooking: !!st.ckLooking,
      ckCanLookUp: refTyped.length > 0 && !st.ckLooking,
      // A reference is digits behind a fixed LV- prefix, so it gets a keypad; a name gets QWERTY.
      ckKeysOpen: !!st.ckKeys && !phone,
      ckOwnKeyboard: !phone,
      ckNativeKeyboardNote: phone ? "On a phone or tablet the device's own keyboard opens — the field sets the right layout for it." : "",
      ckQwertyMode: true,
      ckNumberRow: ckMethodActive !== "name",
      ckSpaceKey: ckMethodActive === "name",
      ckKeyboardLabel: "On-screen keyboard",
      ckInputMode: "text",
      ckDigitKeys: "1234567890".split("").concat(["-"]).map((c) => ({ key: c, ch: c, onTap: () => this.actions.keyPress(c) })),
      ckQwertyRows: [
        { key: "q1", keys: "QWERTYUIOP".split("").map((c) => ({ key: c, ch: c, onTap: () => this.actions.keyPress(c) })) },
        { key: "q2", keys: "ASDFGHJKL".split("").map((c) => ({ key: c, ch: c, onTap: () => this.actions.keyPress(c) })) },
        { key: "q3", keys: "ZXCVBNM".split("").map((c) => ({ key: c, ch: c, onTap: () => this.actions.keyPress(c) })) },
      ],
      ckHasBooking: !!kioskBooking,
      ckPickedName: kioskBooking ? kioskBooking.name : "", ckPickedSvc: kioskBooking ? kioskBooking.svc : "",
      ckPickedRef: kioskBooking ? kioskBooking.ref : "",
      ckPickedSvcWhen: kioskBooking ? [kioskBooking.svc, kioskBooking.when].filter(Boolean).join(" · ") : "",
      ckPickedWhenStaff: kioskBooking ? [kioskBooking.when, kioskBooking.staff].filter(Boolean).join(" · ") : "",
      ckDoneHeader: this.ctv("checkin", "done", "You're checked in"),
      ckDoneSub: this.ctv("checkin", "doneSub", "Please take a seat — someone will call your name."),
      // Only a walk-in holds a place in a line; an appointment has a time instead.
      ckShowQueuePos: !!kioskBooking && s.ckShowQueuePos && kioskBooking.kind === "walkin" && kioskBooking.pos > 0,
      ckShowSlot: !kioskBooking || kioskBooking.kind !== "walkin",
      ckQueuePos: !kioskBooking ? "" : kioskBooking.pos === 1 ? "You're next" : String(kioskBooking.pos) + " people ahead of you",
      ckDwellLeft: st.dwellLeft == null ? (s.ckDoneDwell || 6) : st.dwellLeft,
      ckDwellNote: "Back to the entry screen in " + (st.dwellLeft == null ? (s.ckDoneDwell || 6) : st.dwellLeft) + (((st.dwellLeft == null ? (s.ckDoneDwell || 6) : st.dwellLeft) === 1) ? " second" : " seconds") + ".",
      // Failure states — the wording and the way out differ per cause.
      ckProblemIs: kioskProblem,
      ckProbNoMatch: kioskProblem === "noMatch", ckProbAlready: kioskProblem === "already",
      ckProbCancelled: kioskProblem === "cancelled", ckProbNoCamera: kioskProblem === "noCamera",
      ckProbOffline: kioskProblem === "offline",
      ckProbTitle: kioskProblem === "noMatch" ? this.ctv("problems", "noMatch", "We couldn't read that pass")
        : kioskProblem === "already" ? this.ctv("problems", "already", "You're already checked in")
        : kioskProblem === "cancelled" ? this.ctv("problems", "cancelled", "This booking was cancelled")
        : kioskProblem === "noCamera" ? this.ctv("problems", "noCamera", "The reader isn't working")
        : this.ctv("problems", "offline", "This kiosk is offline"),
      ckProbBody: kioskProblem === "noMatch" ? this.ctv("problems", "noMatchSub", "Try holding it flatter and a little closer, or find your booking by name instead.")
        : kioskProblem === "already" ? "This booking is already checked in. " + (s.ckShowQueuePos ? "You're still in the line — please wait to be called." : "Please wait to be called.")
        : kioskProblem === "cancelled" ? "Nothing is booked for this reference any more. Staff at the welcome desk can rebook it for you."
        : kioskProblem === "noCamera" ? "Enter your booking reference or find yourself by name instead — both work without the reader."
        : this.ctv("problems", "offlineSub", "Please check in at the welcome desk. Your appointment is still booked."),
      // Offline and a dead reader are the two the visitor cannot solve on the screen.
      ckProbRecoverable: kioskProblem === "noMatch" || kioskProblem === "noCamera",
      ckNotScanMode: ckMethodActive !== "scan",
      // The booking is only worth showing back when we know which one it was.
      ckProbShowBooking: !!kioskBooking && (kioskProblem === "already" || kioskProblem === "cancelled"),
      bigTextLabel: st.bigText ? "Normal text" : "Larger text",
      ckProbTone: kioskProblem === "cancelled" || kioskProblem === "offline" ? "var(--jrni-color-semantic-red-1)" : "var(--jrni-color-semantic-orange-1)",
      ckProbTint: kioskProblem === "cancelled" || kioskProblem === "offline" ? "var(--jrni-color-semantic-red-2)" : "var(--jrni-color-semantic-orange-2)",
    };
    const dayLabel = this.fmtDay(st.day);

    const publicToday = this.validPublicToday();
    const todayIso = publicToday ? this.iso(publicToday) : "";
    const calendarError = publicToday ? "" : i18nT("shell.allJourneys.locationTimeZoneInvalid", "This location's time zone isn't recognised. Choose another location.");
    const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
    const cm = st.calMonth;
    const firstOfMonth = new Date(cm.y, cm.m, 1);
    const lead = (firstOfMonth.getDay() + 6) % 7;
    const daysInMonth = new Date(cm.y, cm.m + 1, 0).getDate();
    const calCells = [];
    for (let i = 0; i < lead; i++) calCells.push({ key: "b" + i, blank: true, disabled: true, label: "", bg: "transparent", fg: "transparent", border: "transparent", cursor: "default", weight: "400" });
    for (let dNum = 1; dNum <= daysInMonth; dNum++) {
      const d = new Date(cm.y, cm.m, dNum);
      const isoStr = this.iso(d);
      const avail = !!publicToday && this.slotCount(d, this.bookWindow()) > 0;
      const sel = st.day === isoStr && !!publicToday;
      const isToday = isoStr === todayIso;
      calCells.push({
        key: isoStr, blank: false, label: String(dNum), iso: isoStr, avail, selected: sel, isToday,
        bg: sel ? brand : "transparent",
        fg: sel ? "#FFFFFF" : avail ? "var(--jrni-color-text-strong)" : "var(--jrni-color-neutral-3)",
        border: sel ? brand : isToday ? "var(--jrni-color-neutral-3)" : "transparent",
        cursor: avail ? "pointer" : "not-allowed",
        weight: sel || isToday ? "700" : "500",
        disabled: !avail,
        onSelect: avail ? () => this.setState({ day: isoStr, dayChosen: true, slot: this.firstSlotFor(isoStr) }) : () => {},
      });
    }
    const dayStripDays = [];
    const stripOffset = (st.stripWeek || 0) * 7;
    for (let i = stripOffset; publicToday && i < stripOffset + 7; i++) {
      const baseToday = publicToday;
      const d = new Date(baseToday.getFullYear(), baseToday.getMonth(), baseToday.getDate() + i);
      const isoStr = this.iso(d);
      const avail = this.slotCount(d, this.bookWindow()) > 0;
      const sel = st.day === isoStr && avail;
      dayStripDays.push({
        key: isoStr, value: isoStr, avail, selected: sel, disabled: !avail,
        label: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][d.getDay()] + " " + d.getDate(),
        sublabel: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][d.getMonth()],
        bg: sel ? brand : "var(--jrni-color-base-white)",
        fg: sel ? "#FFFFFF" : avail ? "var(--jrni-color-text-strong)" : "var(--jrni-color-neutral-3)",
        subFg: sel ? "#FFFFFF" : "var(--jrni-color-text-soft)",
        border: sel || avail ? brand : "var(--jrni-color-neutral-4)",
        opacity: avail ? "1" : "0.5",
        cursor: avail ? "pointer" : "not-allowed",
        onSelect: () => { if (avail) this.actions.setDay(isoStr); },
      });
    }

    // The day's REAL slots, grouped by the part of day each one falls in. The
    // old sampling decimated a fixed list of twelve invented times to hit a
    // fabricated count.
    const daySlots = this.slotsFor(st.day);
    const dayCount = daySlots.length;
    // One option list for BOTH picker modes, so grouped and flat carry the same
    // selection keys: value is the slot's identity, label the time a person reads.
    const dayOptions = this.slotOptionsFor(st.day);
    const groupOf = (name) => dayOptions
      .filter((entry) => partOfDay(entry.slot) === name)
      .map((entry) => entry.option);
    // The offer behind the selection, for every human-facing line below. The
    // selection key itself is never printed.
    const pickedSlot = this.slotObjFor(st.day, st.slot);
    const pickedTime = pickedSlot ? pickedSlot.time : "";
    // The picked offer's store when the producer named one the catalogue
    // knows, else the chosen store — the same rule submit applies.
    // An offer naming a store the catalogue does not carry resolves to no
    // store, never to the currently selected one.
    const bookedLoc = pickedSlot && pickedSlot.locationId ? (locationById(LOCATIONS, pickedSlot.locationId) || EMPTY_LOCATION) : curLoc;

    // Two hints below state a fact about the tenant's own catalogue while an
    // operator decides whether a rule fires, so they compose from the live
    // arrays the same way the skipService hint does — and correct themselves
    // once loadPreviewCatalog replaces the placeholder rows.
    const singleLocSvcs = SERVICES.filter((x) => (x.locs || []).length === 1);
    const paidEvents = this.eventsRows.filter((x) => x.price && x.price !== "Free");

    return { // ui-config (LOCAL PATCH, platform side, 2026-09-08 full resync: inspector view-model, not data)
      s: st.s, set: this.set, actions: this.actions,
      journey: st.journey,
      ui: { formOpen: st.formOpen, device: st.device, ctFilter: st.ctFilter },
      icons: {
        close: this.icon("IconX", 14), external: this.icon("IconExternal", 13), refresh: this.icon("IconRefresh", 13),
        left: this.icon("IconLeft", 13), right: this.icon("IconRight", 13), panel: this.icon("IconList", 15),
        chevron: this.icon("IconRight", 13), lock: this.icon("IconLock", 14), users: this.icon("IconUsers", 14),
        share: this.icon("IconExternal", 13),
        search: this.icon("IconSearch", 15), pin: this.icon("IconPin", 14), calendar: this.icon("IconCalendar", 14),
        pinSm: this.icon("IconPin", 13), calSm: this.icon("IconCalendar", 13), clockSm: this.icon("IconClock", 13),
        locate: this.icon("IconLocation", 15), embed: this.icon("IconBrowser", 14), copy: this.icon("IconCopy", 13),
        qrLarge: this.icon("IconQr", 40), warnLarge: this.icon("IconWarning", 22), tickLarge: this.icon("IconCheck", 38),
        /* Confirmation actions. Each one carries a glyph that names the verb,
           so the row reads as four distinct actions rather than four grey
           rectangles of text (operator, 2026-09-15). */
        reschedule: this.icon("IconRefresh", 14), manage: this.icon("IconExternal", 14), cancel: this.icon("IconX", 14),
      },
      style: {
        field320: { width: 320, maxWidth: "100%" },
        field240: { width: 240, maxWidth: "100%" },
        field120: { width: "120px" },
        field180: { width: "180px" },
        field140: { width: 140 },
        field100: { width: 100 },
        fieldFull: { width: "100%" },
      },
      hints: {
        skipService: "The single service is selected for the customer and the " + this.stepLabel("service") + " step is dropped \u2014 in location-first journeys too. " + (SERVICES.length === 0
          ? "No services are currently offered, so the customer cannot continue."
          : SERVICES.length === 1
            ? (s.skipService ? "One service is offered, so this step is skipped." : "One service is offered. Enable this option to skip its selection step.")
            : SERVICES.length + " services are offered, so the step still shows."),
        skipLocation: st.pinnedLoc
          ? "No effect while you arrive from the kiosk — the location is fixed by the unit, so there is no " + this.stepLabel("location") + " step to skip. Still applies to web and mobile visitors."
          : "When the chosen service runs at one location only, that location is selected and the " + this.stepLabel("location") + " step is dropped. " + (singleLocSvcs.length === 0
              ? "No service runs at a single location today, so the step still shows."
              : singleLocSvcs.length === 1
                ? singleLocSvcs[0].name + " is the only single-location service today."
                : singleLocSvcs.length + " of " + SERVICES.length + " services run at a single location today."),
        perPage: this.eventsRows.length + " events are live · " + evtPages + (evtPages === 1 ? " page" : " pages"),
        firstAvail: calendarError || ("Skips fully-booked days — today that is " + this.fmtDay(this.firstAvailable(this.bookWindow()), true) + "."),
        map: s.firstStep === "map" ? "Always on while the journey opens on the map." : "A pin per location beside the list.",
        slotInterval: "Counted from each store's opening time, so 9:00 open at "
          + (s.slotInterval || 60) + " minutes offers "
          + this.slotIntervalExample(s.slotInterval || 60)
          + ". A start is only offered when the whole appointment fits before closing. 1 to 1140 minutes.",
        payment: events ? "Card fields appear only for paid events — " + (paidEvents.length === 0
          ? "none of the " + this.eventsRows.length + " live events has a price today."
          : paidEvents.length + " of " + this.eventsRows.length + (paidEvents.length === 1 ? " has" : " have") + " a price today.") : "Card fields appear only when the chosen service has a price.",
      },
      setupItems, setupValue: setupIds.indexOf(st.section) > -1 ? st.section : "",
      /* The scan panel's own inputs — a different document from the rest of this
         tab, so its state, its Save and its refusal are its own. */
      scan: (() => {
        const type = scanType;
        const cur = (type && st.scanByType[type]) || null;
        const cfg = (cur && cur.cfg) || null;
        const label = type === "appointment" ? "appointments" : type === "queue" ? "walk-ins" : "events";
        return {
          typeLabel: label,
          loading: !!(type && (!cur || cur.state === "loading")),
          error: !!(cur && cur.state === "error"), err: (cur && cur.err) || "",
          isOpen: !!cfg && cfg.action === "open", isCheckIn: !!cfg && cfg.action === "check_in",
          idCheck: !!cfg && !!cfg.id_check,
          dirty: !!(cur && cur.dirty), saving: !!(cur && cur.saving),
          foot: cur && cur.dirty ? "Not saved yet — this writes the tenant's " + label + " settings."
            : "Saved for every " + (label === "events" ? "event" : label.replace(/s$/, "")) + " on this tenant.",
          setOpen: () => this.patchScan({ action: "open" }),
          setCheckIn: () => this.patchScan({ action: "check_in" }),
          setIdCheck: (v) => this.patchScan({ id_check: typeof v === "boolean" ? v : !!(v && v.target && v.target.checked) }),
          onSave: () => this.saveScan(),
          onRetry: () => this.hydrateScan(true),
        };
      })(),
      journeyItems: journeyItems.concat(railExtras),
      journeyCountLabel: stepList.length + " steps",
      /* The setup rail collapsed into ONE picker for the phone layout. Both
         halves of the rail are in it, prefixed so the two id spaces cannot
         collide (a setup section and a journey step can share a name), and the
         handlers ride on the instance because `actions` is built once. */
      /* "Which events" (CC 0359). A stored window that is none of these is offered as itself, so it is never lost by opening the page. */
      eventsWindowOptions: (() => {
        const base = [
          { value: "all", label: "All upcoming events" },
          { value: "7", label: "The next 7 days" },
          { value: "30", label: "The next 30 days" },
          { value: "90", label: "The next 90 days" },
          { value: "365", label: "The next 12 months" },
        ];
        const cur = String(s.eventsWindow ?? "all");
        if (cur === "custom") return [...base, { value: "custom", label: "Custom dates (kept as set)" }];
        if (!base.some((o) => o.value === cur)) return [...base, { value: cur, label: "The next " + cur + " days" }];
        return base;
      })(),
      navOptions: setupItems.map((i) => ({ value: "s:" + i.id, label: i.label }))
        .concat(journeyItems.concat(railExtras).map((j) => ({ value: "j:" + j.id, label: j.label }))),
      navValue: setupIds.indexOf(st.section) > -1
        ? "s:" + st.section
        : "j:" + String((journeyItems.concat(railExtras).filter((j) => j.selected)[0] || {}).id || ""),
      panel: {
        title: panelMeta[0], desc: panelMeta[1],
      },
      is: {
        pinnedFromKiosk: !!st.pinnedLoc,
        landingJourney: this.isLanding(), general: st.section === "general", progress: st.section === "progress", service: st.section === "service",
        kiosk, kioskEntry: st.section === "kioskentry", kioskCheckin: st.section === "kioskcheckin",
        location: st.section === "location", datetime: st.section === "datetime", details: st.section === "details",
        hub: st.section === "hub",
        landing: st.section === "landing",
        tplBanner: !s.customTpl && s.landingTemplate === "banner", tplHero: !s.customTpl && s.landingTemplate === "hero",
        tplSplit: !s.customTpl && s.landingTemplate === "split", tplLong: !s.customTpl && s.landingTemplate === "long",
        tplCustom: !!s.customTpl,
        customTplOffered: true,
        clAddTab: (st.clTab || "add") === "add", clBlockTab: st.clTab === "block",
        clCanUndo: (st.clPast || []).length > 0, clCanRedo: (st.clFuture || []).length > 0,
        soldWaitlist: s.soldOutMode === "waitlist", soldRelated: s.soldOutMode === "related", soldBoth: s.soldOutMode === "both",
        confirmation: st.section === "confirmation", customtext: st.section === "customtext",
        scan: st.section === "scan",
        launch: st.section === "launch",
        serviceFirst: s.firstStep === "service", locationFirst: s.firstStep === "location", mapFirst: s.firstStep === "map",
        pTitles: s.progressType === "titles", pNumbers: s.progressType === "numbers",
        pBar: s.progressType === "bar", pNone: s.progressType === "none",
        dayStrip: s.calendarLayout === "daystrip", monthCal: s.calendarLayout === "month",
        dateToday: s.defaultDate === "today", dateFirst: s.defaultDate === "firstAvailable",
        grouped: s.slotGrouping === "partofday", flatSlots: s.slotGrouping === "flat",
        quickbookLocked: !s.nextAvailable,
        appt: !queue && !events && !landing && !kiosk, queue: queue, events: events, docs: !queue && !kiosk,
        // Shared "What you ask for" controls that are authored but not yet
        // delivered on the two event journeys. Appointments, Walk-Ins and
        // Kiosk are untouched — they still author these normally.
        /* Walk-Ins joins this set (2026-09-10): the canonical queue join
           carries name / phone / email / service / party / answers and
           nothing else, so a document checklist, a payment step or
           fill-and-lock for signed-in customers would be a control that
           changes nothing a visitor experiences. Disabled and labelled
           rather than offered and quietly inert. */
        detailsComingSoon: events || landing || queue,
        // Fill-and-lock for signed-in customers is Coming soon on EVERY journey:
        // there is no customer sign-in integration or contracted session, so on
        // Appointments too it would be a control that changes nothing real.
        signedInDetailsComingSoon: true,
        // The Events and Landing journeys save ONE shared presentation record
        // (both `confirmation` section key lists are identical: showQr /
        // addToCalendar / allowCancelReg / showResource / showStaff), so the
        // controls that author it must render on both tabs. Appointments,
        // Walk-Ins and Kiosk keep their own guards untouched.
        eventOrLanding: events || landing,
        eventList: st.section === "events",
        evList: s.eventDisplay === "list", evGrid: s.eventDisplay === "grid",
        cols2: s.cardCols === 2, cols3: s.cardCols === 3, cols4: s.cardCols === 4,
        pagePages: s.eventPaging === "pages", pageLoadMore: s.eventPaging === "loadmore", pageScroll: s.eventPaging === "scroll",
        soldOutHidden: !s.showSoldOut,
        waitExact: s.waitDisplay === "exact", waitRange: s.waitDisplay === "range", waitPosition: s.waitDisplay === "position",
      },
      opts: {
        reschedule: [{ value: "same", label: "Reschedule with the same staff member" }, { value: "any", label: "Reschedule with any available staff" }],
        serviceDisplay: [{ value: "list", label: "List" }, { value: "grid", label: "Cards" }, { value: "compact", label: "Compact list" }],
        radiusUnit: [{ value: "auto", label: "Auto (local)" }, { value: "mi", label: "Miles" }, { value: "km", label: "Kilometres" }],
        notif: [{ value: "both", label: "Both text & email" }, { value: "email", label: "Email only" }, { value: "sms", label: "Text message only" }],
        languages: [{ value: "en-US", label: "English (United States)" }, { value: "es-US", label: "Spanish (United States)" }, { value: "en-GB", label: "English (United Kingdom)" }],
        countries: DIAL_RAW.map(([iso, , name]) => ({ value: iso, label: name })),
        embedResize: [{ value: "fixed", label: "Fixed height" }, { value: "auto", label: "Auto-resize" }],
        dialCodes: (() => {
          const top = (s.customCountries || []);
          const ordered = DIAL.slice().sort((a, b) => {
            const ai = top.indexOf(a.iso), bi = top.indexOf(b.iso);
            if (ai > -1 || bi > -1) return (ai > -1 ? ai : 99) - (bi > -1 ? bi : 99);
            return a.name.localeCompare(b.name);
          });
          /* Flag + dial code only (KP, 2026-09-17); the country name is the menu
             row's supporting line. The ISO code made the trigger too wide to
             read on a narrow phone. */
          return ordered.map((c) => ({ value: c.iso, label: (s.countryFlags ? c.flag + " " : "") + "+" + c.dial, supportingText: c.name }));
        })(),
        shareChannels: SHARE_CHANNELS.map((c) => ({ value: c.id, label: c.labelKey ? i18nT(c.labelKey, c.label) : c.label })),
        sponsorTier: SPONSOR_TIERS.map((x) => ({ value: x, label: sponsorTierLabel(x) })),
        relatedRule: [

          { value: "category", label: "Same type of event" },
          { value: "date", label: "Nearest in date" },
          { value: "venue", label: "Same venue" },
          { value: "manual", label: "Chosen by hand" },
        ],
        relatedCount: [{ value: 2, label: "2" }, { value: 3, label: "3" }, { value: 4, label: "4" }], // ui-config (LOCAL PATCH, platform side, 2026-09-08 full resync: static designer option list, not data)
        heroHeight: [{ value: 200, label: "Short" }, { value: 280, label: "Medium" }, { value: 380, label: "Tall" }], // ui-config (LOCAL PATCH, platform side, 2026-09-08 full resync: static designer option list, not data)
        heroTreatment: [{ value: "image", label: "Photo" }, { value: "tint", label: "Photo with brand tint" }, { value: "solid", label: "Brand colour only" }],
        refund: [
          { value: "full", label: "Refund in full" },
          { value: "minusFee", label: "Refund minus the cancellation fee" },
          { value: "none", label: "No refund" },
        ],
        ctFilter: [{ value: "all", label: "All fields" }, { value: "custom", label: "Customised only" }],
        kioskGreeting: [{ value: "time", label: "By time of day" }, { value: "fixed", label: "Always the same" }, { value: "none", label: "No greeting" }],
        kioskTileSize: [{ value: "large", label: "Large" }, { value: "compact", label: "Compact" }],
        kioskLocations: LOCATIONS.map((l) => ({ value: l.id, label: l.name })),
        ckDefault: [{ value: "scan", label: "Scan" }, { value: "ref", label: "Reference" }, { value: "name", label: "Name" }],
        journeys: this.journeyOptions(),
        sortOrder: [{ value: "date", label: "Soonest first" }, { value: "popularity", label: "Most booked first" }, { value: "name", label: "Alphabetical" }],
        sizeUnit: [{ value: "auto", label: "Auto" }, { value: "px", label: "px" }],
        pane: [{ value: "settings", label: "Settings" }, { value: "preview", label: "Preview" }],
        attendeeFields: [{ value: "first", label: "First name" }, { value: "last", label: "Last name" }, { value: "email", label: "Email" }, { value: "phone", label: "Phone" }],
        cardCols: [{ value: 2, label: "2" }, { value: 3, label: "3" }, { value: 4, label: "4" }], // ui-config (LOCAL PATCH, platform side, 2026-09-08 full resync: static designer option list, not data)
        eventFilterChips: [{ value: "all", label: "All events" }, { value: "admissions", label: "Admissions" }, { value: "academic", label: "Academic" }, { value: "careers", label: "Careers" }, { value: "wellness", label: "Wellness" }],
      },
      ct: { groups: ctGroups },
      pv: {
        brand, radius,
        /* ── Walk-Ins preview inputs ──────────────────────────────────
         * Projected here rather than inside the component so the preview
         * takes the DRAFT under edit, while the standalone page takes the
         * PUBLISHED config for its slug — the one and only difference
         * between the two mounts. `walkInQueues` is the canonical
         * `getPublicQueue` read of the attached queues, so the preview's
         * services, service points, status and waits are the tenant's own. */
        walkInConfig: walkInConfigFromDesigner(s, st.wiQueueIds || []),
        walkInText: st.ct || {},
        walkInPresentation: {
          // The JOURNEY's own persisted brand, through the same resolver the
          // standalone page uses (`walkInBrandFrom`) — not `brand` above and
          // not `props.tenantName`. Those are the Studio shell's tenant colour
          // and tenant name: an authenticated read a visitor cannot make, so
          // preferring them here is what made the preview show the tenant's
          // own name and colour while the published page showed the journey's
          // own. An unbranded journey resolves to "" and the renderer
          // supplies the DS token and the neutral wording, identically.
          ...walkInBrandFrom(st.wiJourney),
          showHeader: s.showHeader !== false,
          showFooter: !!s.showFooter,
          brandedHeader: !!s.brandedHeader && tenantChromeStatus(true) !== "failed",
          brandedFooter: !!s.brandedFooter && tenantChromeStatus(true) !== "failed",
          hideJrni: s.hideJrni ?? ((st.wiJourney && st.wiJourney.advanced && st.wiJourney.advanced.hideJrni) === true),
          footer: (st.wiJourney && st.wiJourney.footer) || {},
          progressType: s.progressType === "none" ? "none" : s.progressType === "bar" ? "bar" : s.progressType === "numbers" ? "numbers" : "titles",
          progressShowCount: !!((st.wiJourney && st.wiJourney.progress && st.wiJourney.progress.showCount)),
        },
        walkInQueues: this.walkInPreviewQueues(),
        walkInQueuesLoading: !!st.wiQueuesLoading,
        // An honest reason, never an empty page that looks configured.
        walkInError: (st.wiQueueIds || []).length || st.wiSample || this.walkInServiceSample()
          ? ""
          : "No service point is attached to this page yet. Pick at least one queue in General before publishing.",
        walkInStep: st.wiPreviewStep || "service",
        walkInLeft: !!st.wiLeft,
        walkInQueueRows: (st.wiSample ? [] : (st.wiQueues || [])).map((read) => {
          const view = (read && read.view) || null;
          const q = (view && view.queue) || null;
          const option = (st.wiQueueOptions || []).filter((o) => o.id === read.id)[0] || null;
          if (!q) {
            return { id: read.id, name: (option && option.name) || read.id, sub: read.error || "Could not be loaded", meta: "" };
          }
          // The resolved catalog NAMES, not the queue's raw refs: a queue that
          // stores service ids listed them as ids in the panel.
          const serviceNames = serviceOptionsOf(read.view).map((o) => o.name);
          // `canAcceptEntries` from the queue's live status read is the
          // authority when it is available — a queue can be `open` and still
          // be outside its hours.
          const live = read.status;
          return {
            id: read.id,
            name: String(q.name || read.id),
            sub: [q.org, serviceNames.join(", ")].filter(Boolean).join(" · "),
            meta: live && !live.canAcceptEntries
              ? (live.storeOpen ? "Not taking anyone" : (live.nextOpenTime ? "Closed · opens " + live.nextOpenTime : "Closed"))
              : q.status === "open"
                ? (q.waiting === 1 ? "1 waiting" : q.waiting + " waiting")
                : q.status === "paused" ? "Paused" : "Closed",
          };
        }),
        walkInQueueOptions: (st.wiQueueOptions || []).map((q) => ({
          id: q.id,
          name: q.name || q.id,
          selected: (st.wiQueueIds || []).indexOf(q.id) > -1,
          // The queue's OWN location and status, as the list op reports them.
          // A field the worker does not enrich stays out of the line rather
          // than being filled with something plausible.
          meta: [q.locationName, q.status === "open" ? "Open" : q.status === "paused" ? "Paused" : "Closed"].filter(Boolean).join(" · "),
        })),
        chrome: !st.previewOnly,
        /* Pane visibility, per layout. Wide shows the rail, the settings pane and
           the preview together; mid drops to the rail plus ONE of the two; phone
           drops the rail as well (it becomes `navOptions` above). */
        layout: st.layout,
        phone: st.layout === "phone",
        onePane: st.layout !== "wide",
        railPane: !st.previewOnly && st.layout !== "phone",
        formPane: st.formOpen && !st.previewOnly,
        previewPane: st.previewOnly || st.layout === "wide" || !st.formOpen,
        paneValue: st.formOpen ? "settings" : "preview",
        saveDisabled: persistenceUnavailable || (!st.dirty && isPublished) || st.saveNote === "saving",
        saveLabel: st.dirty ? "Save" : "Saved",
        publishLabel: st.published[st.journey] ? "Publish changes" : "Publish",
        published: isPublished,
        // One action saves and publishes; the status still comes from the server.
        dirty: !!st.dirty,
        showSavePublish: persistenceUnavailable || st.dirty || !isPublished,
        savePublishLabel: persistenceUnavailable ? "Save unavailable" : st.saveNote === "saving" ? "Saving\u2026" : "Save & publish",
        statusLabel: persistenceUnavailable ? "Save unavailable" : st.dirty ? "Unsaved changes" : isPublished ? "Published" : "Not published",
        statusEdge: isPublished && !st.dirty ? "var(--jrni-color-semantic-green-1)" : "var(--jrni-color-semantic-orange-1)",
        statusFill: isPublished && !st.dirty ? "var(--jrni-color-semantic-green-2)" : "var(--jrni-color-semantic-orange-2)",
        statusInk: isPublished && !st.dirty ? "var(--jrni-color-semantic-green-1)" : "var(--jrni-color-semantic-orange-1)",
        hasSaveNote: persistenceUnavailable || !!st.saveNote,
        // A typed refusal points at controls, not at JSON paths — the same treatment
        // a slug conflict gets (ask 3 of the editor-save entry, 2026-09-04).
        refusedFields: st.refusedFields || [],
        hasRefusals: !!(st.refusedFields && st.refusedFields.length),
        saveDotBg: st.saveNote === "published" ? "var(--jrni-color-semantic-green-1)" : st.saveNote === "error" ? "var(--jrni-color-semantic-red-1)" : "var(--jrni-color-neutral-3)",
        saveNote: persistenceUnavailable ? PD_STUB_REASON : st.saveNote === "error"
          ? (st.saveError || "Couldn't save.")
          : st.saveNote === "nochange"
          ? "No changes to save \u2014 nothing here differs from what is already live."
          : st.saveNote === "published"
          ? "Published \u2014 this journey is live for customers at"
          : "All changes saved. Publish when you want customers to see them.",
        saveNotices: st.saveNotices || [],
        hasSaveNotices: !!(st.saveNotices && st.saveNotices.length),
        liveUrl: this.publicLink(),
        // Walk-Ins steps through WalkInJourney's own list, so the toolbar's
        // bounds are read from THAT list rather than the appointment wizard's.
        atFirstStep: queue ? (st.wiPreviewStep || "service") === "service" : stepIndex <= 0,
        atLastStep: queue ? (st.wiPreviewStep || "service") === "ticket" : stepIndex >= stepList.length - 1,
        /* Phase 1 is store-level: a store's hours are the whole window and
           every worker inherits them, so a staff choice changes no slot. The
           picker is therefore OFF regardless of the saved flag — the value is
           kept and still saved, it is just not honoured yet (operator ruling,
           2026-09-14). Flip this to `s.staffSelect` the day worker-level
           availability lands. */
        staffPicker: false,
        // The toolbar's Next is the PRIMARY forward control, so it carries the
        // same gate as the tabs rather than being a live-looking no-op.
        continueLockReason: this.continueBlocked() ? this.stepNeeds(st.previewStep) : "",
        /* The note's line is kept for the whole step and only hidden when met, so it never moves the
           Continue button between press and release (CC 2026-10-18 item 5). */
        continueLockText: this.stepNeeds(st.previewStep),
        progressPct: Math.round(((stepIndex + 1) / Math.max(1, stepList.length)) * 100) + "%",
        pageTitle: kiosk
          ? this.ctv("entry", "page", "Welcome")
          : this.ctv("progress", "page", queue ? "Join the queue" : landing ? "Event details" : events ? i18nT("journeys.events.publicPageTitle", "Events") : "Book an appointment"),
        showPageTitle: true,
        blockRows: (s.blockOrder || []).filter((id) => !LANDING_BLOCKS_HIDDEN.has(id)).map((id, i, visible) => {
          const def = LANDING_BLOCKS.filter((b) => b.id === id)[0] || { label: id };
          const on = (s.blocksOn || []).indexOf(id) > -1;
          const DERIVED = {
            spaces: i18nT("shell.allJourneys.blockDerived.capacityMeter", "The meter reads the event's capacity and remaining places, so there is nothing to write here."),
            map: i18nT("shell.allJourneys.blockDerived.venueMap", "The pin and address come from the event's venue."),
          };
          const counts = {
            desc: i18nT("shell.allJourneys.blockCount.agendaLines", "{count, plural, one {# agenda line} other {# agenda lines}}", { count: (s.lpAgendaRows || []).length }),
            speakers: i18nT("shell.allJourneys.blockCount.people", "{count, plural, one {# person} other {# people}}", { count: (s.lpSpeakerRows || []).length }),
            included: i18nT("shell.allJourneys.blockCount.lines", "{count, plural, one {# line} other {# lines}}", { count: (s.lpIncludedRows || []).length }),
            tickets: i18nT("shell.allJourneys.blockCount.fromTheEvent", "{count, plural, one {# from the event} other {# from the event}}", { count: this.eventTicketRows(curEvt).length }),
            faq: i18nT("shell.allJourneys.blockCount.questions", "{count, plural, one {# question} other {# questions}}", { count: (s.lpFaqRows || []).length }),
            sponsors: i18nT("shell.allJourneys.blockCount.logos", "{count, plural, one {# logo} other {# logos}}", { count: (s.lpSponsorRows || []).length }),
            share: i18nT("shell.allJourneys.blockCount.channels", "{count, plural, one {# channel} other {# channels}}", { count: (s.lpShareChannels || []).length }),
          };
          const derived = !!DERIVED[id];
          const open = !!st.blockOpen[id];
          return {
            key: id, label: def.labelKey ? i18nT(def.labelKey, def.label) : def.label, on, open,
            meta: counts[id] || "",
            fg: on ? "var(--jrni-color-text-strong)" : "var(--jrni-color-text-soft)",
            caret: open ? "rotate(90deg)" : "rotate(0deg)",
            caretGlyph: "›",
            cursor: "pointer",
            noEditor: false,
            isDesc: id === "desc", isSpeakers: id === "speakers", isIncluded: id === "included",
            isTickets: id === "tickets", isFaq: id === "faq", isSponsors: id === "sponsors",
            isShare: id === "share", isDerived: derived, derivedNote: DERIVED[id] || "",
            first: i === 0, last: i === visible.length - 1,
            onExpand: () => this.actions.expandBlock(id),
            onUp: () => this.actions.moveBlock(id, -1),
            onDown: () => this.actions.moveBlock(id, 1),
            onToggle: () => this.actions.toggleBlock(id),
          };
        }),
        agendaEdit: (s.lpAgendaRows || []).map((r, i) => ({
          key: "ag" + i, time: r.time, what: r.what,
          setTime: (v) => this.actions.editRow("lpAgendaRows", i, "time", v),
          setWhat: (v) => this.actions.editRow("lpAgendaRows", i, "what", v),
          onRemove: () => this.actions.removeRow("lpAgendaRows", i),
        })),
        speakerEdit: (s.lpSpeakerRows || []).map((r, i) => ({
          key: "spe" + i, name: r.name, role: r.role, photo: r.photo || "",
          setName: (v) => this.actions.editRow("lpSpeakerRows", i, "name", v),
          setRole: (v) => this.actions.editRow("lpSpeakerRows", i, "role", v),
          setPhoto: (v) => this.actions.editRow("lpSpeakerRows", i, "photo", v),
          onRemove: () => this.actions.removeRow("lpSpeakerRows", i),
        })),
        includedEdit: (s.lpIncludedRows || []).map((r, i) => ({
          key: "ine" + i, text: r,
          setText: (v) => this.actions.editRow("lpIncludedRows", i, null, v),
          onRemove: () => this.actions.removeRow("lpIncludedRows", i),
        })),
        ticketEdit: this.eventTicketRows(curEvt),
        ticketsEmpty: this.eventTicketRows(curEvt).length === 0,
        ticketsEmptyTitle: curEvt.id ? "No ticket types on this event" : "No event selected",
        ticketsEmptyBody: curEvt.id
          ? "This event sells no ticket types yet. Add them to the event in Studio and they appear here."
          : "Pick an event to see the ticket types it sells.",
        faqEdit: (s.lpFaqRows || []).map((r, i) => ({
          key: "fqe" + i, q: r.q, a: r.a,
          setQ: (v) => this.actions.editRow("lpFaqRows", i, "q", v),
          setA: (v) => this.actions.editRow("lpFaqRows", i, "a", v),
          onRemove: () => this.actions.removeRow("lpFaqRows", i),
        })),
        sponsorEdit: (s.lpSponsorRows || []).map((r, i) => ({
          key: "sre" + i, name: r.name, tier: r.tier || "Supporter", logo: r.logo || "",
          setName: (v) => this.actions.editRow("lpSponsorRows", i, "name", v),
          setTier: (v) => this.actions.editRow("lpSponsorRows", i, "tier", v),
          setLogo: (v) => this.actions.editRow("lpSponsorRows", i, "logo", v),
          onRemove: () => this.actions.removeRow("lpSponsorRows", i),
        })),
        soldOutPreview: !!st.soldOutPreview,
        isLandingPage: (landing || events) && st.previewStep === "landing",
        isLinkError: st.previewStep === "linkError",
        /* An address that names no journey (CC 0312) is not a switched-off link: no adviser wording, no
           recovery cards, no counts; one action to the workspace's booking start. */
        errTitle: st.linkNotFound ? "This page does not exist"
          : (this.isPublic() && st.personal && st.personal.off ? st.personal : null) ? ((this.isPublic() && st.personal && st.personal.off ? st.personal : null).off === "unavailable"
            ? ((this.isPublic() && st.personal && st.personal.off ? st.personal : null).name ? (this.isPublic() && st.personal && st.personal.off ? st.personal : null).name + " isn't taking bookings at the moment" : "This person isn't taking bookings at the moment")
            : ((this.isPublic() && st.personal && st.personal.off ? st.personal : null).name ? (this.isPublic() && st.personal && st.personal.off ? st.personal : null).name + " can't be booked online at the moment" : "This person can't be booked online at the moment"))
          : this.ctv("error", "ttl", "This booking link is no longer available"),
        errBody: st.linkNotFound ? "Check the address, or start from the booking page."
          : (this.isPublic() && st.personal && st.personal.off ? st.personal : null) ? ((this.isPublic() && st.personal && st.personal.off ? st.personal : null).otherUrl
            ? "Nothing has been booked. You can book the same service with someone else instead."
            : "Nothing has been booked. Please try this link again later.")
          : this.ctv("error", "body", "The adviser or service behind this link is not taking appointments at the moment. Nothing has been booked, and nothing has been charged."),
        errHasRoutes: !st.linkNotFound && !(this.isPublic() && st.personal && st.personal.off ? st.personal : null) && (s.errBookStaff || s.errOtherServices),
        errNoRoutes: !st.linkNotFound && !(this.isPublic() && st.personal && st.personal.off ? st.personal : null) && !s.errBookStaff && !s.errOtherServices,
        /* A personal link that cannot book offers the standard journey only when the link is open (CC 0333). */
        errShowStart: !(this.isPublic() && st.personal && st.personal.off ? st.personal : null) || !!(this.isPublic() && st.personal && st.personal.off ? st.personal : null).otherUrl,
        /* The member a personal link names, above every booking step (CC 0308 §3, 0333). The link books
           its member on open and locked links alike (CC 0344), so one sentence for both. */
        personalLine: (this.isPublic() && st.personal && st.personal.name && !st.personal.off && st.previewStep !== "linkError" && st.previewStep !== "confirmation")
          ? "You're booking with " + st.personal.name + "."
          : "",
        /* The confirmation names the member for a personal-link booking, whatever showStaff says (CC 0344). */
        personalStaffName: (this.isPublic() && st.personal && st.personal.name && !st.personal.off) ? st.personal.name : "",
        errNotFound: !!st.linkNotFound,
        errStaffTile: this.tile("IconUsers", "primary"),
        errSvcTile: this.tile("IconCalendar", "primary"),
        errStaffTitle: this.ctv("error", "staffttl", "Book with another adviser"),
        /* A count is a fact only when the catalogue was read (CC 0312); a failed read shows no number. */
        errStaffSub: this.ctv("error", "staffsub", (!this.isPublic() || st.catalogRead) ? "Same service, next available adviser \u2014 " + locSource.length + " locations to choose from." : "Same service, next available adviser"),
        errSvcTitle: this.ctv("error", "svcttl", "Explore other services"),
        errSvcSub: this.ctv("error", "svcsub", (!this.isPublic() || st.catalogRead) ? SERVICES.length + " services are open for booking today." : "Services open for booking today"),

        errStartLabel: st.linkNotFound ? "Go to the booking page" : (this.isPublic() && st.personal && st.personal.off ? st.personal : null) ? "Book with someone else" : this.ctv("error", "start", "Start again from the beginning"),
        tplBannerHero: !s.customTpl && s.landingTemplate === "banner",
        tplHeroHero: !s.customTpl && s.landingTemplate === "hero",
        tplSplitHero: !s.customTpl && s.landingTemplate === "split",
        tplLongHero: !s.customTpl && s.landingTemplate === "long",
        /* Canvas inputs. `clBlocks` is the authored layout, `clDevice` the
           device being laid out, `clSel` the selected block's uid. */
        clBlocks: (s.customBlocks || []) as CustomBlock[],

        /* ONE device control: the toolbar toggle above the preview. */
        clDevice: (st.device || "desktop") as DeviceId,
        clFrameWidth: resolveDeviceWidths()[(st.device || "desktop") as DeviceId] || 0,
        clSel: st.clSel || "",
        clBrand: this.publicBrandColor() || "#5142C7",
        lpShowAside: s.landingTemplate === "banner",
        lpInlineCta: s.landingTemplate === "hero" || s.landingTemplate === "long",
        lpMainW: s.landingTemplate === "banner" ? "420px" : "100%",
        lpHeroH: (s.heroHeight || 280) + "px",
        lpHeroBg: (() => {
          if (s.heroTreatment === "solid") return brand;
          /* The authored cover, else the event's own image (CC, 2026-10-03). The gradient is
             layered underneath, so an image that fails to load leaves the gradient. */
          const coverSrc = s.coverImage || curEvt.cover || "";
          const coverGrad = "linear-gradient(120deg, " + curEvt.hue + " 0%, #2B2350 100%)";
          const img = coverSrc && s.showMediaImages ? 'url("' + coverSrc + '"), ' + coverGrad : coverGrad;
          return s.heroTreatment === "tint" ? "linear-gradient(0deg, " + brand + "66, " + brand + "66), " + img : img;
        })(),
        /* Banner template (the default): the authored cover, else the event's own image,
           above the title bar (CC, 2026-10-03). Empty = no image, the bar alone as before.
           The gradient sits under the image, so a failed load never shows a broken image. */
        lpBannerCover: (() => {
          const src = s.coverImage || curEvt.cover || "";
          return src && s.showMediaImages ? 'url("' + src + '"), linear-gradient(120deg, ' + curEvt.hue + ' 0%, #2B2350 100%)' : "";
        })(),
        lpHeroScrim: s.landingTemplate === "hero" ? "linear-gradient(0deg, rgba(14, 18, 28, 0.72) 0%, rgba(14, 18, 28, 0.15) 70%)" : "transparent",
        lpTitle: curEvt.name,
        lpWhen: curEvt.when, lpVenue: curEvt.venue, lpAddr: curEvt.addr,
        /* The banner layout's band (CC, 2026-10-06): when and where, like the hero and split layouts. It printed
           the date alone, so an event page in that layout never said where, online events included. */
        lpWhenWhere: curEvt.venue ? curEvt.when + " · " + curEvt.venue : curEvt.when,
        /* The landing map uses the same real-coordinate pin contract as the
         * booking Where step. Public event detail supplies venueGeo from the
         * event's geocoded location or its saved venue; the structured location
         * fallback keeps this compatible with older detail payloads. Missing
         * coordinates stay unpinned — never guessed. */
        lpMapLocations: (() => {
          const raw = (curEvt.raw ?? {}) as unknown as {
            venueGeo?: { lat?: unknown; lng?: unknown };
            structuredLocation?: { lat?: unknown; lng?: unknown };
            location?: { lat?: unknown; lng?: unknown };
          };
          const geo = raw.venueGeo || raw.structuredLocation || raw.location;
          const latitude = geo && typeof geo.lat === "number" && Number.isFinite(geo.lat) ? geo.lat : null;
          const longitude = geo && typeof geo.lng === "number" && Number.isFinite(geo.lng) ? geo.lng : null;
          return latitude !== null && longitude !== null
            ? [{ id: curEvt.id, latitude, longitude, label: curEvt.venue || curEvt.addr }]
            : [];
        })(),
        /* Follows the selected ticket (CC 0272); "From Price unavailable" never shows (CC 0260). */
        lpPrice: this.eventPriceLine(curEvt),
        lpCta: this.ctv("details", "cta", "Register"),
        // An explicit authored empty description (a real landing document
        // whose field the author left blank) stays empty; only the ABSENCE
        // of any landing document at all falls back to the event's own
        // description.
        lpDesc: (this.isPublic() && this.landingIsConfigured) ? s.lpDescText : (s.lpDescText || curEvt.desc),
        lpAgenda: (s.lpAgendaRows || []).map((r, i) => ({ key: "a" + i, time: r.time, what: r.what })),
        lpSpeakers: (s.lpSpeakerRows || []).map((x, i) => ({
          key: "sp" + i, name: x.name, role: x.role,
          hasPhoto: !!x.photo, noPhoto: !x.photo,
          photoCss: x.photo ? 'url("' + x.photo + '")' : "none",
        })),
        lpIncluded: (s.lpIncludedRows || []).map((x, i) => ({ key: "in" + i, text: x })),
        // Public and admin alike: the event's OWN ticket tiers from its read.
        // With none (or no event selected) the designer says so; it never
        // invents sample tickets or prices.
        lpTickets: this.eventTicketRows(curEvt),
        lpTaken: Math.max(0, (curEvt.cap || 0) - (st.soldOutPreview ? 0 : curEvt.left || 0)),
        lpCap: curEvt.cap || 0,
        lpMeterTone: st.soldOutPreview ? "danger" : !curEvt.cap ? "success" : (curEvt.left / (curEvt.cap || 1)) < 0.2 ? "warning" : "success",
        // Reads the same resolved availability as the pill above it, so the
        // sticky bar cannot promise places on an event the server closed.
        lpSpacesLabel: lpEnded ? endedLabel : lpHappeningNow ? happeningNowLabel : lpSoldOut ? "No places left" : !curEvt.cap ? "Places available" : curEvt.left + " of " + curEvt.cap + " places left",
        lpFaq: (s.lpFaqRows || []).map((x, i) => ({
          key: x.q, q: x.q, a: x.a, open: !!st.faqOpen[i],
          caret: st.faqOpen[i] ? "rotate(90deg)" : "rotate(0deg)",
          onToggle: () => this.actions.toggleFaq(i),
        })),
        relatedAuto: s.relatedRule !== "manual", relatedManualMode: s.relatedRule === "manual",
        eventOptions: this.eventsRows.filter((e) => e.id !== curEvt.id).map((e) => ({ value: e.id, label: e.name })),
        relatedNote: s.relatedRule === "manual"
          ? "Only the events you pick are shown, in the order you picked them."
          : s.relatedRule === "venue"
            ? "Events at " + curEvt.venue + " come first; the rest fill any gap."
            : s.relatedRule === "date"
              ? "The next events on the calendar, whatever their type."
              : "Events of the same type come first; the rest fill any gap.",
        lpRelated: relatedEvents.map((e) => ({
          key: e.id, name: e.name, meta: [e.when, e.venue].filter(Boolean).join(" \u00b7 "),
          onSelect: () => {
            this.setState({ faqOpen: {}, wlDone: false });
            if (this.isPublic()) this.selectPublicEvent(e.id);
            else this.setState({ evt: e.id });

          },
        })),
        lpSponsorGroups: (() => {
          const rows = (s.lpSponsorRows || []);
          const mk = (r, i) => {
            const big = (r.tier || "").indexOf("Gold") === 0;
            return {
              key: "sr" + i, name: r.name,
              logoCss: r.logo ? 'url("' + r.logo + '")' : "none",
              fallback: r.logo ? "" : r.name,
              border: r.logo ? "1px solid var(--jrni-color-surface-border)" : "1px dashed var(--jrni-color-neutral-4)",
              w: big ? "148px" : "112px", h: big ? "60px" : "44px",
            };
          };
          if (!s.sponsorTiers) return [{ key: "all", tier: "", showTier: false, items: rows.map(mk) }];
          return SPONSOR_TIERS.map((tier) => ({
            key: tier, tier: sponsorTierLabel(tier), showTier: true,
            items: rows.filter((r) => (r.tier || "Supporter") === tier).map(mk),
          })).filter((g) => g.items.length > 0);
        })(),
        lpShare: SHARE_CHANNELS.filter((c) => (s.lpShareChannels || []).indexOf(c.id) > -1).map((c) => ({ key: c.id, label: c.labelKey ? i18nT(c.labelKey, c.label) : c.label })),
        // Authoritative availability facts on BOTH paths. The Studio preview
        // loads its rows from the same `getPublicEvents` read as the public
        // page, so the projection decides here too — there is no isPublic-only
        // branch. `soldOutPreview` remains an authoring aid: it may force the
        // sold-out layout on, but it can never re-open an event the server
        // closed, and it cannot un-end an ended one.
        lpEnded: lpEnded,
        lpHappeningNow: lpHappeningNow,
        lpSoldOut: lpSoldOut,
        lpOpen: lpRegOpen,
        // Closed, but honest about why: ended, genuinely full, or simply not
        // open for registration.
        lpClosed: !lpRegOpen,
        lpClosedLabel: lpCancelledEvent ? this.ctv("events", "cancelledevt", "Cancelled") : lpEnded ? endedLabel : lpHappeningNow ? happeningNowLabel : lpNotOnSaleLabel ? lpNotOnSaleLabel : lpSoldOut ? soldOutLabel : regClosedLabel,
        lpNotOnSale: !!lpNotOnSaleLabel,
        lpClosedTone: lpCancelledEvent ? "danger" : lpHappeningNow ? "success" : "neutral",
      lpEventCancelled: lpCancelledEvent,
      lpCancelledTitle: this.ctv("events", "cancelledTitle", "This event has been cancelled"),
      lpCancelledText: this.ctv("events", "cancelledText", "It won't go ahead. If you registered, the organiser will contact you about what happens next."),
        // An ended event has nothing to wait for, so the waiting list is never
        // offered on it; otherwise the server's own `waitlistOpen` decides,
        // with the author's switch as the fallback when no projection exists.
        lpShowWaitlist: s.soldOutMode !== "related" && lpSoldOut && !lpEnded && !lpHappeningNow
          && this.eventWaitlistOpen(curEvt),
        /* The box's ticket choice (CC 0346): only tickets whose own waitlist takes a guest. Shown
           with two or more; with exactly one the submit takes it (wlTicketId). */
        wlTierChoices: this.waitlistTiers(curEvt).map((t) => ({ value: t.id, label: t.name || "Ticket" })),
        wlShowTierChoice: this.waitlistTiers(curEvt).length > 1,
        wlTierValue: (this.waitlistTiers(curEvt).some((t) => t.id === st.selectedTicketId) ? st.selectedTicketId : "") || "",
        wlOpen: !st.wlDone, wlDone: !!st.wlDone, wlName: st.wlName, wlEmail: st.wlEmail,
        wlNote: isWaitlistStatus(st.booking?.registration?.status)
          ? "You're on the waitlist for " + curEvt.name + "."
          : "",
        wlSubmitting: !!st.submitting, wlError: st.bookingError || "",
        landingBlocks: (s.blockOrder || []).filter((id) => !LANDING_BLOCKS_HIDDEN.has(id))
          .filter((id) => (s.blocksOn || []).indexOf(id) > -1)
          .filter((id) => !(st.soldOutPreview && id === "tickets"))
          .filter((id) => !(st.soldOutPreview && s.soldOutMode === "waitlist" && id === "related"))
          .map((id) => ({
            key: id,
            isDesc: id === "desc", isSpeakers: id === "speakers", isIncluded: id === "included",
            isTickets: id === "tickets", isSpaces: id === "spaces", isMap: id === "map",
            isFaq: id === "faq", isRelated: id === "related", isSponsors: id === "sponsors",
            isShare: id === "share",
          })),
        hasHero: s.landingTemplate === "hero" || s.landingTemplate === "split",
        noHero: !(s.landingTemplate === "hero" || s.landingTemplate === "split"),
        noHeroNote: s.landingTemplate === "banner"
          ? "Banner and sidebar uses a slim colour banner, so there is no cover image to set."
          : "Long-form has no hero. Pick Hero cover or Split panel to set a cover image, height and treatment.",
        frameWidth,
        frameHeight,
        deviceWpx: (this.props.frameSize && this.props.frameSize[st.device]?.w) || deviceW[st.device] || 0,
        deviceHpx: (this.props.frameSize && this.props.frameSize[st.device]?.h) || deviceH[st.device] || 0,
        paneZoom: st.paneZoom || 100,
        brandHeader: s.showHeader,
        /* A failed Branding read falls back to the basic chrome, never a blank bar. */
        brandedHeader: !!(s.showHeader && (s.brandedHeader || this.publicBrandedDefault("header"))) && tenantChromeStatus(!this.isPublic()) !== "failed",
        brandedFooter: !!(s.showFooter && (s.brandedFooter || this.publicBrandedDefault("footer"))) && tenantChromeStatus(!this.isPublic()) !== "failed",
        ...kioskPv,        /* Reservation workflow (2026-10-02): a staff-approved tier asks, it doesn't register. */
        memberPayEl: this.memberPayElement(),
        ctaLabel: queue ? this.ctv("details", "cta", "Join the queue") : (events || landing) ? (this.selectedTierJoinsWaitlist() ? "Join the waitlist" : this.selectedTierNeedsApproval() ? i18nT("journeys.events.requestASpot", "Request a spot") : this.ctv("details", "cta", "Complete registration")) : "Confirm appointment",
        paneLabel: st.formOpen ? "Hide the settings pane" : "Show the settings pane",
        ctaHeight: "44px",
        queueHeader: st.leftQueue ? this.ctv("queue", "lvhdr2", "You are no longer in the queue") : this.ctv("queue", "hdr", "You're in the queue"),
        posLabel: this.ctv("queue", "pos", "Position in line"),
        waitLabel: this.ctv("queue", "wait", "Estimated wait"),
        notifyNote: this.ctv("queue", "notify", "We'll text you when you're next to be seen."),
        leaveLabel: this.ctv("queue", "leave", "Leave the queue"),
        inQueue: !st.leftQueue, leftQueue: !!st.leftQueue,
        leaveAsking: !!st.leaveAsking,
        leaveTitle: this.ctv("queue", "lvttl", "Leave the line?"),
        leaveConsequence: "Your place goes to the next person, and rejoining puts you at the back of the line.",
        leaveConfirmLabel: this.ctv("queue", "leave", "Leave the queue"),
        leaveKeepLabel: this.ctv("queue", "lvkeep", "Stay in the line"),
        leftHeader: this.ctv("queue", "lvhdr", "You have left the line"),
        leftText: this.ctv("queue", "lvtxt", "Your ticket is no longer valid. You can join again or book a time instead."),
        rejoinLabel: this.ctv("queue", "lvagain", "Join the line again"),
        bookInsteadLabel: this.ctv("queue", "appt", "Make an appointment instead"),
        ticketNumber: "A-042",
        position: st.qPos <= 0 ? "Now serving" : st.qPos === 1 ? "You're next" : this.ordinal(st.qPos),
        waitText: (() => {
          const mins = Math.max(0, Math.round((st.qPos * QUEUE_AVG_MIN) / QUEUE_DESKS_ON_DUTY));
          if (st.qPos <= 0) return "Go to the desk";
          if (s.waitDisplay === "exact") return mins + (mins === 1 ? " minute" : " minutes");
          return mins + "\u2013" + (mins + 5) + " minutes";
        })(),
        staffLine: st.qPos <= 0
          ? "An adviser is ready for you now."
          : st.qFree + " of " + QUEUE_DESKS_ON_DUTY + (st.qFree === 1 ? " adviser is" : " advisers are") + " free right now, and the line is moving as they finish.",
        showWait: s.waitDisplay !== "position",
        notifyLine: s.notifyWhenClose ? "You'll get a text when " + s.notifyAhead + (s.notifyAhead === 1 ? " person is" : " people are") + " ahead of you." : "",
        queueLocationHeader: this.ctv("location", "hdr", "Choose a service point"),
        arrivalNote: s.remoteJoin ? "Arrive within " + s.arrivalWindow + " minutes of being called, or you'll be moved to the back of the line." : "",
        hasArrivalNote: queue && s.remoteJoin,
        hasLogo: !!s.logo, noLogo: !s.logo,
        logoCss: s.logo ? 'url("' + s.logo + '")' : "none",
        stepTitle: st.previewStep === "linkError"
          ? "Link error page"
          : st.cancelled
            ? (events || landing ? "Registration cancelled" : "Appointment cancelled")
            : st.leftQueue
              ? "Left the line"
              : this.stepLabel(st.previewStep) + " \u2014 step " + (stepIndex + 1) + " of " + stepList.length,
        stepMeta: st.device === "mobile" ? "390 px · mobile" : st.device === "tablet" ? "Up to 768 px · tablet" : "Full width · desktop",
        atFirst: stepIndex === 0, atLast: stepIndex === stepList.length - 1,
        stepIndex, percent: Math.round(((stepIndex + 1) / stepList.length) * 100),
        percentLabel: "Step " + (stepIndex + 1) + " of " + stepList.length,
        stepperSteps: stepList.map((id) => this.stepLabel(id)),
        steps: stepList.map((id, i) => {
          // Forward out of an unfulfilled step is refused; backward never is.
          // `locked` is what the tab RENDERS as — a disabled control with the
          // reason on it, not a live control that silently does nothing.
          const reachable = i <= stepIndex || this.canReachStep(id);
          const lockReason = reachable ? "" : this.stepLockReason(id);
          return {
          label: this.stepLabel(id),
          current: i === stepIndex,
          locked: !reachable,
          lockReason,
          // The events step is the list journey's own step: on the public
          // rail tapping it is a real journey switch, not a step flip.
          onClick: () => {
            if (!reachable) return;
            if (id === "events" && this.isPublic() && this.isLanding()) { this.actions.backToEvents(); return; }
            this.setState({ previewStep: id });
          },
          underline: i === stepIndex ? brand : "transparent",
          fg: i <= stepIndex || !reachable ? "var(--jrni-color-text-strong)" : "var(--jrni-color-text-soft)",
          // The DS has no disabled-text token, and neutral-4 on white is
          // 1.48:1. `jrni-ui`'s own disabled Button draws text-strong at
          // opacity .5 — the house treatment, so the tabs use it too.
          opacity: !reachable ? "0.5" : "1",
          weight: i === stepIndex ? "700" : "500",
          // Numbered-steps variant only. These were MISSING, so that variant rendered a
          // 22px chip with no glyph and a transparent fill — visually identical to the
          // step-titles variant, which is why picking "Numbered steps" appeared to do
          // nothing. Done steps get the tick the option's description promises.
          num: i < stepIndex ? "\u2713" : String(i + 1),
          numBg: i === stepIndex ? brand : i < stepIndex ? "var(--jrni-color-primary-3)" : "var(--jrni-color-neutral-6)",
          numFg: i === stepIndex ? "#FFFFFF" : i < stepIndex ? brand : "var(--jrni-color-text-soft)",
          connector: i === stepList.length - 1 ? "" : i < stepIndex ? brand : "var(--jrni-color-neutral-5)",
          };
        }),
        /* ONE step rail for every customer journey (KP, 2026-09-16): the titles
           variant, whatever the journey was authored with. One tenant's appointment
           journey was saved as "numbers" and so looked different from the queue
           and event pages. "none" still hides it. */
        progressTitles: s.progressType !== "none" && !outsideJourney, progressNumbers: false, progressBar: false,
        isService: st.previewStep === "service",
        isLocation: st.previewStep === "location" && !(!queue && !events && s.firstStep === "map"),
        isMapOnly: st.previewStep === "location" && !queue && !events && s.firstStep === "map",
        isDatetime: st.previewStep === "datetime",
        slotTakenNotice: st.previewStep === "datetime" && st.urlSlotGone ? i18nT("shell.allJourneys.slotJustTaken", "That time was just taken. Choose another time.") : "",
        isBooking: st.previewStep === "booking", isDetails: st.previewStep === "details",
        isEvents: st.previewStep === "events",
        isConfirmation: st.previewStep === "confirmation" && !queue && !events && !landing,
        isTicket: st.previewStep === "confirmation" && queue,
        isRegistration: st.previewStep === "confirmation" && (events || landing),
        eventsHeader: this.ctv("events", "hdr", "Select an event"),
        eventCards, eventCount: usingServerEvents ? evtTotalCount : evtFilteredCount,
        eventShownLabel: usingServerEvents
          ? "Showing " + evtShown.length + " of " + evtTotalCount + " events"
          : "Showing " + evtShown.length + " of " + evtFilteredCount + " events",
        eventPages: evtPages, eventPage: evtPage,
        showPager: events && s.eventPaging === "pages" && evtPages > 1,
        showLoadMoreEvents: usingServerEvents
          ? (events && s.eventPaging === "loadmore" && evtHasMore)
          : (events && s.eventPaging !== "pages" && evtShown.length < evtFilteredCount),
        // "scroll" gets an actually-mounted sentinel (below) that fetches the
        // next server page on intersection, not the manual button — a
        // config that fetches nothing on scroll is the bug this replaces.
        showScrollSentinel: usingServerEvents && events && s.eventPaging === "scroll" && evtHasMore,
        eventFilterValue: st.evtCat,
        cardGrid: eventCardGridColumns(st.device, s.cardCols || 3),
        showFilterBar: events && s.filterBar,
        evtQuery: st.evtQuery, evtFrom: st.evtFrom, evtTo: st.evtTo,
        evtMatches, hasEvtMatches: evtMatches.length > 0,
        typeOptions: evtCategoryOptions,
        locationOptions: evtLocationOptions,
        hasLocationOptions: evtLocationFacet.length > 0,
        evtLocationValue: st.evtLocationFilter || "",
        eventHostName,
        eventEmpty: usingServerEvents ? evtShown.length === 0 : evtFilteredCount === 0,
        eventEmptyText: this.ctv("events", "empty", "There are no events to show right now."),
        ticketsLabel: this.ctv("details", "tickets", "How many places?"),
        paymentHeader: this.ctv("details", "phdr", "Payment"),
        paymentInfo: this.ctv("details", "pinfo", ""),
        hasPaymentInfo: !!(st.ct["details.pinfo"] || "").trim(),
        totalDue: (() => {
          if (events) {
            const m = /([0-9.]+)/.exec(curEvt.price || "");
            if (!m) return "No payment needed";
            return "$" + (parseFloat(m[1]) * effectiveTickets).toFixed(2);
          }
          return cur.price === "Free" || !cur.price ? "No payment needed" : cur.price;
        })(),
        /* Attendee identity is mandated by the registration contract, not by
           the author: first, last and e-mail are required for every attendee.
           On the event journeys the fields are therefore always rendered —
           hiding one would demand a value the visitor cannot supply. Phone and
           company stay optional, and the appointment journey keeps honouring
           the authored field list exactly as before. */
        attFirst: (events || landing) ? true : (s.attendeeFields || []).indexOf("first") > -1,
        attLast: (events || landing) ? true : (s.attendeeFields || []).indexOf("last") > -1,
        attEmail: (events || landing) ? true : (s.attendeeFields || []).indexOf("email") > -1,
        attPhone: (events || landing) ? true : (s.attendeeFields || []).indexOf("phone") > -1,
        attCompany: (events || landing) ? true : (s.attendeeFields || []).indexOf("company") > -1,
        attendeeRequired: (events || landing) ? true : !s.emailOptional,
        /* Attendee phone: optional on an event registration by contract; on an
           appointment it follows the author's saved flag exactly as the host's
           own phone does in detailsFulfilled. The row markup hardcoded
           `required={false}`, so a configured-required phone rendered with no
           required marker and was never enforced. */
        attendeePhoneRequired: (events || landing) ? false : !s.phoneOptional,
        addAttendeeLabel: attRows.length > 0 ? "Add another attendee" : "Add attendees",
        /* The add affordance stops at the LOWEST of the server policy, the
           event's remaining capacity and the selected tier's remaining
           capacity (CD docket 2026-09-15 item 1.3). */
        canAddAttendee: (events || landing)
          ? (!this.memberLaneSelected() && seatLimit.maxGuests > 0 && attRows.length < Math.min(seatLimit.maxGuests, this.attendeeLimits().rows))
          : (s.attendees && attRows.length < Math.max(0, (s.maxParty || 1) - 1)),
        attendeeLimitNote: (events || landing) && seatLimit.maxGuests > 0 && attRows.length >= seatLimit.maxGuests
          ? (seatLimit.maxTotal === 1
            ? "This event takes one place per registration."
            : "This registration is full at " + seatLimit.maxTotal + " places.")
          : "",
        attendeeList: attRows.map((r, i) => {
          const rp = this.parsePhone(r.phone, r.country);
          const errs = (events || landing) ? this.attendeeRowErrors(r) : null;
          const eOk = /^[^\s@]+@[^\s@]+\.[A-Za-z]{2,}$/.test((r.email || "").trim());
          const fBad = r.touched.first && (errs ? !!errs.first : !nameOk(r.first));
          const lBad = r.touched.last && (errs ? !!errs.last : !nameOk(r.last));
          const eBad = r.touched.email && (errs ? !!errs.email : (!eOk && !(s.emailOptional && !(r.email || "").trim())));
          const pBad = r.touched.phone && (errs ? !!errs.phone : (!rp.valid && !(s.phoneOptional && rp.len === 0)));
          const cBad = r.touched.company && !!(errs && errs.company);
          return {
            key: String(r.id), title: "Attendee " + (i + 1),
            first: r.first, last: r.last, email: r.email, phone: r.phone, company: r.company || "", dialIso: rp.country.iso,
            setFirst: (v) => this.actions.attSet(r.id, "first", v),
            setLast: (v) => this.actions.attSet(r.id, "last", v),
            setEmail: (v) => this.actions.attSet(r.id, "email", v),
            setPhone: (v) => this.actions.attSet(r.id, "phone", v),
            setCompany: (v) => this.actions.attSet(r.id, "company", v),
            setCountry: (iso) => this.actions.attSet(r.id, "country", iso),
            blurFirst: () => this.actions.attBlur(r.id, "first"),
            blurLast: () => this.actions.attBlur(r.id, "last"),
            blurEmail: () => this.actions.attBlur(r.id, "email"),
            blurPhone: () => this.actions.attBlur(r.id, "phone"),
            blurCompany: () => this.actions.attBlur(r.id, "company"),
            firstError: fBad ? (errs ? errs.first : nameMsg(r.first)) : undefined,
            lastError: lBad ? (errs ? errs.last : nameMsg(r.last)) : undefined,
            emailError: eBad ? (errs ? errs.email : ((r.email || "").trim() ? "Enter a valid e-mail address, like you@example.com." : "An e-mail address is required.")) : undefined,
            phoneError: pBad ? (errs ? errs.phone : (rp.len === 0 ? "A phone number is required." : "That is not a valid " + rp.country.name + " number \u2014 " + (rp.country.min === rp.country.max ? rp.country.min + " digits" : rp.country.min + "\u2013" + rp.country.max + " digits") + " expected.")) : undefined,
            companyError: cBad ? errs.company : undefined,
            firstState: fBad ? "error" : "default",
            lastState: lBad ? "error" : "default",
            emailState: eBad ? "error" : "default",
            companyState: cBad ? "error" : "default",
            phoneHint: pBad ? undefined : "Pick the country, then enter the number as it is dialled locally.",
            phoneState: pBad ? "error" : "default",
            onRemove: () => this.actions.attRemove(r.id),
          };
        }),
        /* Everyone on the registration, host first, shown before submit
           (CD docket 2026-09-15 item 1.5). */
        showPartyReview: (events || landing) && attRows.length > 0,
        partyReview: [{
          key: "host",
          role: "Booking contact",
          name: [String(st.first || "").trim(), String(st.last || "").trim()].filter(Boolean).join(" "),
          detail: [String(st.email || "").trim(), String(st.phone || "").trim()].filter(Boolean).join(" \u00b7 "),
        }].concat(attRows.map((r, i) => ({
          key: String(r.id),
          role: "Attendee " + (i + 1),
          name: [String(r.first || "").trim(), String(r.last || "").trim()].filter(Boolean).join(" "),
          detail: [String(r.email || "").trim(), String(r.company || "").trim()].filter(Boolean).join(" \u00b7 "),
        }))),
        showParty: queue && s.partySize,
        party: st.party, partyHint: "Up to " + s.maxParty + " people on one ticket",
        showEventSummary: events || landing,
        // Derived from the people, never a separate stepper: a quantity that
        // could disagree with `1 + attendees.length` is a second authority the
        // server ignores anyway.
        showTickets: (events || landing) ? false : s.ticketQuantity,
        /* The suppression this replaces was honest while it stood: the
           registration op carried no people list, so a journey saved with
           `attendees: true` collected companions and dropped them on the wire.
           `attendees` is now typed end to end (CD docket 2026-09-15 item 1),
           so the section comes back — gated on SERVER policy rather than on
           the authored switch alone, which is not an authority for an event.
           `allowGuests: false` (or no policy at all) still renders nothing. */
        showAttendees: (events || landing)
          ? (seatLimit.maxGuests > 0 || attRows.length > 0)
          : Boolean(s.attendees),
        ticketsHint: seatLimit && seatLimit.maxTotal > 1
          ? "Up to " + seatLimit.maxTotal + " places per registration for this event"
          : "Up to 1 per registration for this event",
        evtSummaryLine: [curEvt.when, curEvt.venue].concat(s.showEventPrice ? [curEvt.price] : []).join(" · "),
        tickets: effectiveTickets, maxTickets: seatLimit ? seatLimit.maxTotal : 1,
        // Real ticket tiers from the event's own detail read — never a
        // fixture list, and never auto-picked when there is more than one:
        // `submitEventRegistration` refuses to submit without an explicit
        // choice in that case.
        ticketChoices: ((curEvt.raw && curEvt.raw.ticketTiers) || []).map((t) => ({
          value: t.id, label: t.name + " — " + formatEventPrice({ amountMinor: t.priceMinor, currency: t.currency })
            + (this.tierIsFull(curEvt, t.id) ? (this.tierWaitlistOpen(curEvt, t.id) ? " · Full · join the waitlist" : " · Full") : ""),
        })),
        /* Said before submit (CC 0280): the chosen ticket is full, so submitting joins its waitlist. */
        ticketWaitlistHint: this.selectedTierJoinsWaitlist() ? "This ticket is full. Submitting puts you on its waitlist." : undefined,
        selectedTicketId: st.selectedTicketId || "",
        ticketChoiceError: st.bookingError === "Choose a ticket type." ? st.bookingError : undefined,
        evtName: curEvt.name, evtWhen: curEvt.when, evtVenue: curEvt.venue, evtPrice: curEvt.price,
        evtCover: this.heroWide(curEvt),
        /* THE REGISTRATION'S RECORD ID (CD, 2026-09-18) — `registration.id`.
           Two corrections, in order: it first encoded `ticketLinks[0].qrUrl`,
           the signed credential URL, whose last segment is not the
           registration id; KP then moved it to `orderRef`, the human booking
           reference, which is what a person reads out. The record id is what it
           carries because it is always present on this response and is a valid
           resolver input — the resolver also accepts check-in code, reference
           and ticket-token forms (CC, 2026-09-21). `orderRef` stays printed
           beside it, and `ticketLinks`
           (wallet / management links) are untouched. Empty until a real
           registration exists; never a sample. */
        qrValue: (() => {
          // A cancelled registration has nothing to check in with (CC 2026-10-16 item 6).
          if (st.cancelled) return "";
          // No check-in code for a waitlisted registration: they have no place yet (CC, 28 Sept).
          if (isWaitlistStatus(st.booking?.registration?.status) || isPendingApprovalStatus(st.booking?.registration?.status)) return "";
          const id = st.booking && st.booking.registration && st.booking.registration.id;
          return typeof id === "string" && id.trim() !== "" ? id : "";
        })(),
        /* Each companion's OWN credentials, off `data.attendees` — never the
           host's token or Manage URL reused for a second person (CD docket
           2026-09-15 item 1.9). A replay returns the stored people and the
           same credentials, so this renders identically without creating or
           rotating anything. Credential issuance can fail on an otherwise
           successful registration, so a missing link renders as no link. */
        attendeeTickets: ((st.booking && st.booking.attendees) || []).map((a, i) => {
          const links = Array.isArray(a.ticketLinks) ? a.ticketLinks : [];
          return {
            key: String(a.registrationId || i),
            name: [a.firstName, a.lastName].map((x) => String(x || "").trim()).filter(Boolean).join(" "),
            email: String(a.email || ""),
            reference: String(a.registrationId || ""),
            status: isPendingApprovalStatus(a.status) ? i18nT("shell.allJourneys.regPending.attendeeStatus", "Awaiting approval") : isWaitlistStatus(a.status)
              ? ("Waitlisted" + (a.waitlistPosition ? " · position " + a.waitlistPosition : ""))
              : "Registered",
            manageUrl: safeManageUrl(links[0] && links[0].managementUrl),
            qrUrl: (links[0] && links[0].qrUrl) || "",
          };
        }),
        showAttendeeTickets: ((st.booking && st.booking.attendees) || []).length > 0,
        /* The booking's own RECORD ID (CD, 2026-09-18) — `booking.bookingId`,
           falling back to `booking.id`. It encoded `booking.checkInCode`, the
           8-character reception code — which the anonymous confirmation lane
           does not carry, so there was nothing to encode. The record id is used
           because it is always present and is a valid resolver input (the
           resolver takes the check-in code, reference and ticket-token forms
           too — CC, 2026-09-21).
           The printed human reference is unchanged beside it. Empty — and the
           QR hidden — when there is no id: never the reference, never a sample. */
        apptQrValue: (() => {
          const b = (st.booking && st.booking.booking) || null;
          const id = b && (b.bookingId || b.id);
          return typeof id === "string" && id.trim() !== "" ? id : "";
        })(),
        bookingReference: (st.booking && st.booking.orderRef) ? String(st.booking.orderRef) : "",
        /* Apple Wallet (CC 0054/0059). Appointments: the confirmation-page read's
           walletPassUrl. Events: the registrant's ticket link's passPkpassUrl, which
           a waitlisted registration does not carry, so nothing is drawn for it. */
        apptWalletUrl: st.cancelled ? "" : (st.apptWalletUrl || ""),
        eventWalletUrl: (() => {
          if (st.cancelled) return "";
          const links = st.booking && Array.isArray(st.booking.ticketLinks) ? st.booking.ticketLinks : [];
          const hit = links.filter((x) => x && typeof x.passPkpassUrl === "string" && x.passPkpassUrl)[0];
          return hit ? hit.passPkpassUrl : "";
        })(),
        bookingRoom: (st.booking && st.booking.booking && st.booking.booking.resourceName)
          ? String(st.booking.booking.resourceName) : "",
        // Issue 002: the server-issued `booking.manage_url`, appointment
        // confirmations only. Never derived from orderRef or a booking id;
        // "" hides the "Manage this appointment" action.
        manageUrl: appointmentManageUrl(st.booking, s.showManage,
          queue ? "queue" : events ? "events" : landing ? "landing" : kiosk ? "kiosk" : "appointment"),
        // The rendered action: enabled only on the customer page with that
        // link; in the authoring preview it is shown disabled with no address.
        manageAction: appointmentManageAction(st.booking, s.showManage,
          queue ? "queue" : events ? "events" : landing ? "landing" : kiosk ? "kiosk" : "appointment",
          this.isPublic() ? "public" : "preview"),
        /* THE CONFIRMATION'S RESCHEDULE AND CANCEL (CC 0010, 29 Sept). On the
           customer page they open the booking's manage page, which runs the real
           move and cancel (step-up and window refusal included). This page has
           no move or cancel of its own: before, Cancel only set a local flag and
           Reschedule placed a second booking. Each is drawn only while
           booking.actions says its window is open. No `actions` = unknown,
           so the button stays (the manage page still refuses a closed window).
           No manage link = nothing honest to do, so the buttons are hidden.
           The authoring preview keeps its local demo. */
        apptChange: (() => {
          const pub = this.isPublic();
          const man = appointmentManageAction(st.booking, s.showManage, "appointment", pub ? "public" : "preview");
          const bk = st.booking && typeof st.booking === "object" ? (st.booking.booking && typeof st.booking.booking === "object" ? st.booking.booking : st.booking) : null;
          const acts = bk && bk.actions && typeof bk.actions === "object" ? bk.actions : null;
          const gate = (k) => { const g = acts && acts[k]; return g && typeof g === "object" ? g : null; };
          const openOf = (k) => { const g = gate(k); return g ? g.allowed === true : true; };
          const when = (iso) => { const d = iso ? new Date(iso) : null; return d && !isNaN(d.getTime()) ? friendlyDateTime(d) : ""; };
          const closedLine = (k) => { const g = gate(k); if (!g || g.allowed === true) return ""; const w = when(g.closes_at); const c = k === "cancel"; if (g.closes_at == null) return c ? i18nT("shell.allJourneys.cancelNotOnline", "This booking can't be cancelled online.") : i18nT("shell.allJourneys.rescheduleNotOnline", "This booking can't be rescheduled online."); if (w) return c ? i18nT("shell.allJourneys.cancellingClosedAt", "Online cancelling closed at {time}.", { time: w }) : i18nT("shell.allJourneys.reschedulingClosedAt", "Online rescheduling closed at {time}.", { time: w }); return c ? i18nT("shell.allJourneys.cancellingClosed", "Online cancelling closed.") : i18nT("shell.allJourneys.reschedulingClosed", "Online rescheduling closed."); };
          const href = man.state === "enabled" ? man.href : "";
          const resOn = !!s.allowReschedule && (!pub || (!!href && openOf("reschedule")));
          const canOn = !!s.allowCancel && (!pub || (!!href && openOf("cancel")));
          const notes = pub && href ? [s.allowReschedule ? closedLine("reschedule") : "", s.allowCancel ? closedLine("cancel") : ""].filter(Boolean) : [];
          return {
            showReschedule: resOn, showCancel: canOn, closedNote: notes.join(" "),
            onReschedule: pub ? () => { openManageInNewTab(href); } : this.actions.reschedule,
            onCancel: pub ? () => { openManageInNewTab(href); } : this.actions.askCancel,
          };
        })(),
        bookingError: st.bookingError || "",
        submitting: !!st.submitting,
        /* A refused paid member registration keeps the button off (CC 0261). */
        submitLocked: this.memberPayLocked(),
        rootMode: this.isPublic() ? "public" : "preview",
        queueQrValue: this.publicOrigin() + "/q/A-042",
        placeCapNote: "Each event carries its own limit and its own remaining places, so the picker never offers more than the event allows \u2014 " + this.eventsRows.map((e) => e.name.split(" \u2014 ")[0] + " allows " + e.maxPer).slice(0, 2).join(", ") + ".",
        /* "Free", the price, or nothing; never "Price unavailable" (CC 0265). */
        placesLine: effectiveTickets + (effectiveTickets === 1 ? " place" : " places") + (!s.showEventPrice || !curEvt.price || curEvt.price === "Price unavailable" ? "" : curEvt.price === "Free" ? " · Free" : " · " + curEvt.price + " each"),
        attendeeRows: effectiveTickets > 1,
        attendeeCount: effectiveTickets - 1,
        serviceHeader: this.ctv("service", "hdr", queue ? "What do you need help with?" : "Select a service"),
        serviceCatalogEmpty: SERVICES.length === 0,
        serviceCatalogEmptyText: "No services are available to book right now.",
        durationHeader: this.ctv("service", "dur", "Select a duration"),
        locationHeader: this.ctv("location", "hdr", queue ? "Choose a service point" : "Select location"),
        dateHeader: this.ctv("datetime", "date", "Select a date"),
        timeHeader: this.ctv("datetime", "time", "Select a time"),
        detailsHeader: this.ctv("details", "hdr", "Enter your details"),
        uploadHeader: this.ctv("details", "upl", "Document upload"),
        uploadHint: this.ctv("details", "uplin", "PNG, JPG, DOC, PDF and Google Docs supported. Max file size 20 MB.")
          + " " + (st.upFiles || []).length + " of " + s.maxAttachments + " added"
          + ((st.upFiles || []).length >= s.maxAttachments ? " \u2014 remove one to add another." : ", one at a time or several at once.")
          + (st.upError ? " " + st.upError : ""),
        uploadAccept: ".png,.jpg,.jpeg,.doc,.docx,.pdf,.gdoc,application/vnd.google-apps.document",
        uploadFull: (st.upFiles || []).length >= s.maxAttachments,
        /* ONE CONSENT SENTENCE ON EVERY JOURNEY (KP, 2026-09-17). The appointment
           record's `steps.details.termsText` mapped onto `details.ctext`, so a
           tenant's authored terms ("This is a … demonstration appointment.")
           replaced the checkbox label while queues and events showed the platform
           sentence. The checkbox is the same on all three now; the authored
           `details.ctext` / `chdr` no longer reach it. */
        consentHeader: CONSENT_HEADER,
      consentMissing: (this.state.journey === "events" || this.state.journey === "landing" || this.state.journey === "appointment") && !this.state.consent,
      /* Shown only after a submit or Join was refused for it, in red, like the walk-in. */
      consentError: (this.state.journey === "events" || this.state.journey === "landing" || this.state.journey === "appointment") && !this.state.consent && !!this.state.consentTried,
      consentRequiredText: CONSENT_REQUIRED,
      consentRef: this.consentRef,
        consentText: (events || landing) ? i18nT("journeys.allJourneys.consentEvent", "I consent to my data being used for the purposes of this registration.") : CONSENT_TEXT,
        confirmMsg: st.cancelled
          ? ((events || landing) ? this.ctv("confirmation", "cnclmsg", "This registration is cancelled") : this.ctv("cancel", "msg", "This appointment is cancelled"))
          : (events || landing)
            ? (replay
              // Not routed through ctv(): there is no authored field for a
              // replay, and an authored "You're registered" must not be able to
              // overwrite the one message that corrects it.
              ? `You're already registered${st.first ? ", " + st.first : ""}`
              /* A waitlisted guest always reads the waitlist wording (CC 0006): like the
                 replay message, it corrects, so saved "You're registered" copy can't overwrite it. */
              : isPendingApprovalStatus(st.booking?.registration?.status)
                ? i18nT("shell.allJourneys.regPending.title", "Request received")
              : isWaitlistStatus(st.booking?.registration?.status)
                ? `Thanks${st.first ? " " + st.first : ""}! You're on the waitlist`
                : this.ctv("confirmation", "msg", `Thanks${st.first ? " " + st.first : ""}! You're registered`))
            // The guest's OWN first name from the form, as events/landing do —
            // never a fixture name greeting every real customer.
            : this.ctv("confirmation", "msg", `Thanks${st.first ? " " + st.first : ""}! Your appointment is confirmed`),
        flagHeader: st.cancelled
          ? ((events || landing) ? this.ctv("confirmation", "cnclhdr", "Registration cancelled") : this.ctv("cancel", "flaghdr", "Appointment cancelled"))
          : (events || landing)
            ? (replay
              ? "We kept your original place"
              : isPendingApprovalStatus(st.booking?.registration?.status) ? i18nT("shell.allJourneys.regPending.flagHeader", "Awaiting approval")
              : (isWaitlistStatus(st.booking?.registration?.status) ? "You're on the waitlist" : this.ctv("confirmation", "flaghdr", "Registration confirmed")))
            : this.ctv("confirmation", "flaghdr", "Appointment confirmed"),
        flagText: st.cancelled
          ? ((events || landing) ? this.ctv("confirmation", "cncltxt", "Your place has been released.") : this.ctv("cancel", "flagtxt", "A cancellation e-mail is on its way. Nothing else is needed from you."))
          : (events || landing)
            ? (replay
              ? (replay.reference
                ? `You're already registered for this event, so no new confirmation was sent. Your reference is still ${replay.reference} \u2014 bring it with you.`
                : "You're already registered for this event, so no new confirmation was sent. Your original registration still stands.")
              : isPendingApprovalStatus(st.booking?.registration?.status) ? i18nT("shell.allJourneys.regPending.body", "We'll email you once it's approved.")
              : (isWaitlistStatus(st.booking?.registration?.status) ? "You'll be notified if a place opens." : this.ctv("confirmation", "flagtxt", "Your ticket is below.")))
            /* What will really happen, from the booking answer's per-channel outcomes (CC 0312 + 0315).
               The neutral line stays the fallback when the answer carries no outcomes. */
            : (this.appointmentConfirmationLine() || this.ctv("confirmation", "flagtxt", "We have sent your confirmation. You may close this window.")),
        /* CC 0316: with an outcome line, the workspace's own sentence still shows, under it. Without one,
           flagText above already is the workspace's sentence (or the neutral default), so nothing extra. */
        flagTextExtra: (!st.cancelled && !(events || landing) && this.appointmentConfirmationLine())
          ? ((st.ct["confirmation.flagtxt"] || "").trim() || null)
          : null,
        services: SERVICES.map((x) => ({ name: x.name, desc: x.desc, meta: x.dur === "multi" ? "2 durations" : x.dur, leading: this.tile("IconCalendar", "neutral") })),
        visibleServices,
        serviceCols: serviceGridColumns(st.device),
        serviceGrid: s.serviceDisplay === "grid",
        serviceRows: s.serviceDisplay !== "grid",
        categories: (appointment ? serviceCategoryOptions(SERVICES) : [{ id: "all", label: "All" }, { id: "academic", label: "Academic" }, { id: "support", label: "Student support" }, { id: "admissions", label: "Admissions" }]).map((c) => ({
          label: c.label, selected: appointment ? serviceFilterId(st.cat) === c.id : st.cat === c.id, onClick: () => appointment ? this.actions.setServiceCategory(c.id) : this.setState({ cat: c.id }),
        })),
        showDurations: cur.dur === "multi",
        durations: DURATIONS.map((d) => ({ label: d.label, sub: s.showPrice ? d.sub : "", selected: st.dur === d.id, onSelect: () => this.setState({ dur: d.id }) })),
        locQuery: st.locQuery,
        suggestions: locSuggestions,
        hasSuggestions: locSuggestions.length > 0,
        locating: st.geoState === "locating",
        geoLabel: st.geoState === "locating" ? "Finding you\u2026" : "Use my location",
        geoDenied: st.geoState === "denied" || st.geoState === "unsupported",
        geoNote: st.geoState === "unsupported"
          ? "This browser cannot share a location. Type a town or ZIP code instead."
          : "Location access was blocked. Type a town or ZIP code instead.",
        visibleLocations, deskList: queue, branchList: !queue,
        // Where-step states for the eligible set. None of them falls back to every store.
        locIdle: !queue && locElig.status === "idle",
        locLoading: !queue && locElig.status === "loading",
        locError: !queue && locElig.status === "error",
        locEmpty: !queue && locElig.status === "ready" && locSource.length === 0,
        locPicked: !queue && locSource.some((x) => x.id === st.loc),
        showLoadMore: !queue && s.loadMore && locCount < locSource.length,
        loadMoreLabel: "Load more locations (" + (locSource.length - locCount) + " more)",
        showMapNow: !queue && s.showMap,
        pinName: curLoc.name, pinMeta: [curLoc.dist, curLoc.next ? "next available " + curLoc.next : ""].filter(Boolean).join(" \u00b7 "),
        /* SELECTION CARD lives once, at the top of `main`, on every step after
           the first (2026-09-28, CD) — not a separate sticky aside beside the
           step. The aside used a column-reverse flip on mobile to fake "card on
           top", which is exactly what broke the 375px When step (a row's flex
           values read as a column's) and left a blank stretch above the card
           on every other step (a lone reversed child sitting at the bottom of
           its box). One column, always; the card renders inline like the
           Walk-Ins and Events "what you're booking" card. */
        bodyDir: "column",
        bodyWrap: "wrap",
        bodyAlign: "stretch",
        mainFlex: "1 1 auto",
        dtDir: st.device !== "mobile" && s.calendarLayout === "month" ? "row" : "column",
        dtWrap: st.device !== "mobile" && s.calendarLayout === "month" ? "wrap" : "nowrap",
        dtAlign: st.device !== "mobile" && s.calendarLayout === "month" ? "flex-start" : "stretch",
        dtCalFlex: st.device !== "mobile" && s.calendarLayout === "month" ? "0 1 316px" : "0 0 auto",
        dtTimeFlex: st.device !== "mobile" && s.calendarLayout === "month" ? "1 1 260px" : "0 0 auto",
        // The workspace's date pattern and clock (item 8), not hand-built English.
        sumWhen: pickedTime ? i18nT("shell.allJourneys.summaryDateTime", "{date}, {time}", { date: st.day ? friendlyDateShort(civilNoon(st.day)) : "", time: this.clockLabel(pickedTime) }) : (st.day ? friendlyDateShort(civilNoon(st.day)) : ""),
        sumEnds: pickedTime ? i18nT("shell.allJourneys.summaryEndsAt", "Ends at {time}", { time: this.endTime(pickedTime, this.durMinutes()) }) : i18nT("shell.allJourneys.summaryNoTimePicked", "No time picked yet"),
        sumShowWhen: stepList.slice(0, stepIndex).indexOf("datetime") > -1,
        sumGoWhen: () => this.summaryGo("datetime"),
        canChange: true,
        reconfirmNote: st.reconfirmFrom
          ? i18nT("shell.allJourneys.reconfirmNote", "{field, select, service {You changed your service, so the times have been re-checked — pick a time again.} location {You changed your location, so the times have been re-checked — pick a time again.} other {You changed your date, so the times have been re-checked — pick a time again.}}", { field: st.reconfirmFrom })
          : "",
        showReconfirm: !!st.reconfirmFrom,
        showTopSummary: !this.isEvents() && !this.isLanding() && !this.isKiosk() && stepIndex > 0 && st.previewStep !== "confirmation",
        summaryRows: (() => {
          const done = stepList.slice(0, stepIndex);
          const rows = [];
          const go = (id) => () => this.summaryGo(id);
          if (done.indexOf("location") > -1) {
            rows.push({ title: curLoc.name, sub: curLoc.addr, onChange: go("location") });
          }
          if (done.indexOf("service") > -1) {
            const dur = cur.dur === "multi"
              ? (DURATIONS.filter((d) => d.id === st.dur)[0] || DURATIONS[0] || EMPTY_DURATION).label
              : cur.dur;
            rows.push({ title: cur.name, sub: s.showDuration ? dur : "", onChange: go("service") });
          }
          if (done.indexOf("datetime") > -1 && false /* staffPicker: see pv.staffPicker */) {
            rows.push({ title: "Adviser", sub: curStaff, onChange: go("datetime") });
          }
          /* A personal link's member (CC 0344): shown from the first summary, with no change control. */
          if (this.isPublic() && st.personal && st.personal.name && !st.personal.off) {
            rows.push({ title: "With", sub: st.personal.name, onChange: undefined });
          }
          const methodSub = this.methodLabel(st.svc, st.loc);
          if (methodSub && (done.indexOf("location") > -1 || this.steps().indexOf("location") < 0)) {
            rows.push({ title: "Method", sub: methodSub, onChange: this.steps().indexOf("location") > -1 ? go("location") : undefined });
          }
          return rows;
        })(),
        showContinue: ["service", "location", "datetime"].indexOf(st.previewStep) > -1 && !(st.previewStep === "location" && !queue && !events && s.firstStep === "map"),
        continueBlocked: this.continueBlocked(),
        continueBg: this.continueBlocked() ? "var(--jrni-color-neutral-4)" : brand,
        continueCursor: this.continueBlocked() ? "not-allowed" : "pointer",
        showCustomerBack: stepIndex > 0,
        continueLabel: this.ctv("progress", "cta", "Continue"),
        publicUrl: this.publicLink(),
        // A composed address, or a declared one on a journey that is not live,
        // is shown WITH the reason it is not yet a working link.
        // Only the Appointments tab can compose an address of its own; for the
        // other tabs a printed link is always the server's, so "declared" is
        // true there and the note is only ever the not-yet-published one.
        publicUrlProvisional: !!this.publicLink()
          && ((st.journey === "appointment" && !this.apptLinkDeclared()) || !isPublished),
        publicUrlNote: !isPublished
          ? "This is where the journey will answer once you publish it. The link does nothing until then."
          : "Composed from this workspace's booking domain \u2014 the server has not confirmed an address for this journey yet.",
        pxWidth: s.embedWidthUnit === "px",
        copyLabel: st.copiedEmbed ? "Copied" : "Copy code",
        embedCode: this.embedSnippet(s, this.embedTitle()),
        autoResize: s.embedResize === "auto", fixedHeight: s.embedResize !== "auto",
        resizeHint: s.embedResize === "auto"
          ? "The frame grows and shrinks with the step. Adds one script tag to your page."
          : "One height for every step, so short steps leave dead space.",
        mapHeight: (s.mapHeight || 190) + "px",
        mapHeightLabel: (s.mapHeight || 190) + " px",
        // Named out loud: an unlocated campus is absent from the map but present
        // in the list, and an author needs to know which of the two they are
        // looking at.
        // Scoped to the eligible PHYSICAL rows actually listed; says nothing about any other record.
        mapCaption: missingPinCaption(locSource),
        resizingMap: !!st.resizingMap,
        // Whether ANY location came back with a next-available slot, so the
        // author is told the truth about what the toggle can do today.
        anyLocationNext: locSource.some((x) => !!x.next),
        selectedAddr: curLoc.addr + " · " + curLoc.dist,
        selectedNext: curLoc.next, showNextLine: s.nextAvailable && !!curLoc.next,
        selectedHours: curLoc.hours + " · " + curLoc.phone,
        // From the catalogue's own virtual flag, and only an eligible virtual row.
        hasVirtual: locSource.some((x) => isVirtualLocation(x)),
        virtualLabel: "Rather meet online? Book a video appointment instead.",
        onVirtual: () => {
          const virtualRow = locSource.filter((x) => isVirtualLocation(x))[0];
          if (virtualRow) this.setState({ loc: virtualRow.id });
        },
        /* REAL coordinates, from the tenant's own location rows (platform
           PR #1888 / map-sync 07c1ae419). This was `mapPins`, which placed
           each campus at a hardcoded percentage picked by its array index —
           `top: [46, 24, 70, …][i % 8]%` — so the London Study Centre sat
           wherever it happened to fall in the list. That is a picture of a map,
           not a map, and it also meant a located campus and an unlocated one
           looked identical. Rows WITHOUT coordinates are simply not pinned
           (LocationsMap drops them); they stay in the list and stay bookable. */
        mapLocations: mapPinsFor(locSource, st.loc),
        days: dayStripDays, day: st.day, slot: st.slot,
        stripPrevDisabled: !dayStripDays.length || (st.stripWeek || 0) === 0,
        stripNextDisabled: !dayStripDays.length || stripOffset + 7 > (this.bookWindow() || 60),
        stripLabel: dayStripDays.length ? this.fmtDay(dayStripDays[0].value, false) + " \u2013 " + this.fmtDay(dayStripDays[dayStripDays.length - 1].value, false) : calendarError,
        calCells, calTitle: MONTHS[cm.m] + " " + cm.y,
        calPrevDisabled: !publicToday || cm.y < publicToday.getFullYear() || (cm.y === publicToday.getFullYear() && cm.m <= publicToday.getMonth()),
        custTz: st.custTz || this.ianaFor(this.zoneFor(st.loc)),
        tzLabelNode: (() => {
          const z = st.custTz || this.ianaFor(this.zoneFor(st.loc));
          let off = "";
          try {
            const parts = new Intl.DateTimeFormat("en-US", { timeZone: z, timeZoneName: "longOffset" }).formatToParts(new Date());
            const found = parts.filter((x) => x.type === "timeZoneName")[0];
            off = found ? found.value : "";
          } catch (err) {}
          return React.createElement("span", { style: { display: "flex", gap: "8px", alignItems: "baseline", flexWrap: "wrap" } }, [
            React.createElement("strong", { key: "l", style: { fontWeight: 700 } }, "Time zone"),
            React.createElement("span", { key: "v", style: { color: "var(--jrni-color-text-soft)", fontSize: "13px" } }, (off ? "(" + off + ") " : "") + z),
          ]);
        })(),
        selectedDayLabel: this.fmtDay(st.day, true),
        /* The window is still being read. Without this, `noSlots` (which is
           just "this day has none yet") renders the empty state while the
           request is open, so waiting looks identical to a day with nothing
           free — and to a failed read. */
        availLoading: st.availState === "loading",
        calendarError,
        noSlotsToday: !!publicToday && this.slotCount(this.fromIso(st.day), this.bookWindow()) === 0,
        // 2026-09-08 (Nick): `pickedSlots` was the fabricated list that `daySlots`
        // replaced above; this one reference survived and threw on every render
        // (ReferenceError). The flat list is the same real day's slots, ungrouped.
        allSlots: dayOptions.map((entry) => entry.option),
        slotGroups: groupDefs.map((g) => ({ label: g[0], slots: groupOf(g[0]) })).filter((g) => g.slots.length > 0),

        hasSlots: dayCount > 0,
        noSlots: dayCount === 0,
        groupedSlots: dayCount > 0 && s.slotGrouping === "partofday",
        flatSlots: dayCount > 0 && s.slotGrouping === "flat",
        noSlotsTitle: "No times left on " + this.fmtDay(st.day),
        noSlotsBody: "Pick another date — the calendar greys out days that are fully booked or outside the booking window.",
        dateValue: dayLabel,
        staff: STAFF.map((p) => ({ name: p.name, leading: null, selected: st.staff === p.id, onClick: () => this.setState({ staff: p.id }) })),
        bookingLeading: this.tile("IconCalendar", "primary"),

        bookingTitle: cur.name, bookingSub: dayLabel + " · " + pickedTime + " · " + (this.heldRemotely(st, bookedLoc) ? this.bookedMethodLabel(st) : bookedLoc.name),
        emailLabel: "E-mail" + (s.emailOptional ? " (optional)" : ""), emailRequired: !s.emailOptional,
        // Events/landing public registration always requires email at the API level, so the
        // authoring controls for it are locked rather than left editable-but-ignored.
        emailApiRequired: this.isEvents() || this.isLanding(),
        /* Bound on Walk-Ins as of 2026-09-17 (CD): the walk-in phone field now
           renders the same dial-code picker the appointment journey does, and
           `walkInConfigFromDesigner` maps these onto the contract's
           `phoneCountryFlags` / `phoneSuggestedCountries`. The flag that used
           to hide them here is gone. */
        phoneCountryControls: true,
        emailSettingTitle: (this.isEvents() || this.isLanding()) ? "Email address (required)" : "Email address",
        emailSettingDescription: (this.isEvents() || this.isLanding())
          ? "Public registration always submits an email address, so it cannot be turned off or made optional."
          : undefined,
        phoneLabel: "Phone" + (s.phoneOptional ? " (optional)" : ""), phoneRequired: !s.phoneOptional,
        dialIso: ph.country.iso,
        countryError: st.countryMax ? "Ten is the maximum \u2014 remove one before adding another." : undefined,
        phoneValue: st.phone,
        phoneError: st.phoneServerError
          ? st.phoneServerError
          : (st.smsPhoneNeeded && !String(st.phone || "").trim())
          ? "Enter a mobile number to get text messages, or untick Text message."
          : phoneBad
          ? (ph.len === 0 ? "A phone number is required." : "That is not a valid " + ph.country.name + " number \u2014 " + (ph.country.min === ph.country.max ? ph.country.min + " digits" : ph.country.min + "\u2013" + ph.country.max + " digits") + " expected.")
          : undefined,
        phoneHint: phoneBad ? undefined : "Pick the country, then enter the number as it is dialled locally.",
        phoneState: (st.phoneServerError || phoneBad || (st.smsPhoneNeeded && !String(st.phone || "").trim())) ? "error" : "default",
        lockState: locked ? "readonly" : "default",
        // Only a verified customer session may name the signed-in person.
        signedIn: locked,
        // Per-field: each input is read-only only when the account SUPPLIES it.
        firstLocked: this.lockedField("first"),
        lastLocked: this.lockedField("last"),
        emailLocked: this.lockedField("email"),
        phoneLocked: this.lockedField("phone"),
        signedInLabel: locked ? "Signed in as " + String(
          this.props.bookingSession.displayName
          || [this.props.bookingSession.profile.firstName, this.props.bookingSession.profile.lastName].filter(Boolean).join(" ")
          || this.props.bookingSession.profile.email
          || "your One Stop account"
        ) : "",
        addressLabel: "Address" + (s.addressOptional ? " (optional)" : ""),
        addressRequired: !s.addressOptional,
        addrValue: st.addr,
        addrState: "default",
        // Booking questions for the chosen service (appointment), or the
        // event's own configured registration questions (events/landing) —
        // `activeQuestions()` picks the real source for whichever journey
        // this is; either way, `false` is a legitimate answer to a yes/no
        // question and must not be treated as "no answer".
        bookingQuestionList: (events || landing ? this.activeQuestions() : this.serviceQuestions()),
        hasBookingQuestions: (events || landing ? this.activeQuestions() : this.serviceQuestions()).length > 0,
        bookingQuestionsHeading: (events || landing
          ? i18nT("journeys.bookingQuestions.headingRegister", "A few questions before you register")
          : (this.serviceQuestions()[0] && this.serviceQuestions()[0].groupName) || i18nT("journeys.bookingQuestions.headingVisit", "A few questions before your visit")),
        // The primary person's fields are the guest's own, on every journey:
        // value from the real form, state from its own validation. The stored
        // `readOnly` preference (a Coming soon capability) never substitutes a
        // fabricated identity or locks a field — only a real customer session
        // could (detailsLocked), and none exists. Events/landing still require
        // first/last/email under their registration contract.
        firstValue: st.first,
        lastValue: st.last,
        firstError: firstBad ? nameMsg(st.first) : undefined,
        lastError: lastBad ? nameMsg(st.last) : undefined,
        firstState: firstBad ? "error" : "default",
        lastState: lastBad ? "error" : "default",
        emailValue: st.email,
        emailError: emailBad ? (st.email.trim() ? "Enter a valid e-mail address, like you@example.com." : "An e-mail address is required.") : undefined,
        emailState: emailBad ? "error" : "default",
        notifEmail: reminderChannels(effectiveReminderPreference(st.notifChoice, this.reminderDefault(st))).email,
        notifSms: reminderChannels(effectiveReminderPreference(st.notifChoice, this.reminderDefault(st))).text,
        notifRefused: !!st.notifChoiceRefused,
        /* CC 0331: the text box only when a text can reach this booking; the server's refusal at the choice. */
        notifTextOffered: st.textOffered !== false,
        notifServerError: st.notifServerError || "",
        notifTextOnly: (() => { const c = reminderChannels(effectiveReminderPreference(st.notifChoice, this.reminderDefault(st))); return c.text && !c.email; })(),
        cancelled: !!st.cancelled, notCancelled: !st.cancelled,
        /* Only a registration that holds a place gets the check mark, the missing-credential note and Add to
           calendar (CC, 2026-10-04); the authoring preview has no booking, so it keeps all three. */
        regPending: isPendingApprovalStatus(st.booking?.registration?.status),
        /* LOCAL PATCH (platform side, 2026-10-04 JAI-2279): a neutral duplicate (alreadyRegistered: true) carries no registration, so its absent status is not a held seat, and it is terminal: no calendar, ticket note or registration actions. CD to absorb. */
        regNeutral: st.booking?.alreadyRegistered === true,
        regHoldsSeat: !st.cancelled && st.booking?.alreadyRegistered !== true && !isPendingApprovalStatus(st.booking?.registration?.status) && !isWaitlistStatus(st.booking?.registration?.status),
        regActionsVis: st.cancelled ? "hidden" : "visible",
        cancelRegLabel: this.ctv("confirmation", "cnclreg", "Cancel registration"),
        cancelRegError: st.cancelRegError || "",
        cancelRegKeepLabel: this.ctv("confirmation", "keepreg", "Keep my place"),
        cancelRegTitle: this.ctv("confirmation", "cnclttl", "Cancel this registration?"),
        cancelRegConsequence: "Your place is released to anyone on the waiting list, and the event may fill before you can register again.",
        backToEventsLabel: this.ctv("confirmation", "backev", "Back to the events list"),
        notQueue: !queue,
        notifSectionTitle: queue ? "Notifications" : "Notifications and attendees",
        cancelAsking: !!st.cancelAsking,
        statusBg: st.cancelled ? "var(--jrni-color-semantic-red-2)" : "var(--jrni-color-semantic-green-2)",
        statusFg: st.cancelled ? "var(--jrni-color-semantic-red-1)" : "var(--jrni-color-semantic-green-1)",
        statusGlyph: st.cancelled ? "\u00d7" : "\u2713",
        flagTone: st.cancelled ? "danger" : (isPendingApprovalStatus(st.booking?.registration?.status) || st.booking?.alreadyRegistered === true) ? "info" : "success", /* LOCAL PATCH (platform side, 2026-10-04 JAI-2279): the neutral duplicate reads as information, not a fresh success. CD to absorb. */
        strike: "none",
        detailFg: st.cancelled ? "var(--jrni-color-text-soft)" : "var(--jrni-color-text-body)",
        againLabel: this.ctv("cancel", "again", "Book a new appointment"),
        cancelTitle: this.ctv("cancel", "dlgttl", "Cancel this appointment?"),
        cancelConsequence: (() => {
          const late = this.hoursUntilAppt() < CANCEL_CUTOFF_HRS;
          if (!late) return "You are outside the " + CANCEL_CUTOFF_HRS + "-hour window, so there is nothing to pay.";
          const money = s.takePayment
            ? (s.refundMode === "full" ? " Your payment is refunded in full." : s.refundMode === "none" ? " Your payment is not refunded." : " Your payment is refunded minus the " + CANCEL_FEE + " fee.")
            : "";
          return "This is inside the " + CANCEL_CUTOFF_HRS + "-hour window, so a " + CANCEL_FEE + " cancellation fee applies." + money;
        })(),
        cancelConfirmLabel: this.ctv("cancel", "confirm", "Cancel appointment"),
        cancelKeepLabel: this.ctv("cancel", "keep", "Keep appointment"),
        calMenuOpen: !!st.calMenu,
        confirmActionsDisplay: st.device === "mobile" ? "grid" : "flex",
        /* AUTO-FIT, NOT A FIXED PAIR. `1fr 1fr` made every confirmation action
           exactly half the card, and the card is narrower than it looks: the
           public frame spends 12px of scroll padding, a 1px border and 24px of
           body padding on EACH side, so the content box is viewport - 74. On a
           390px phone that is 316px and each of the two cells was (316-10)/2 =
           153px — measured on the rendered page, not estimated.
           The widest control needs 230px: "Manage this appointment" measures
           179.84px of glyph at 500/14 DM Sans, plus a 14px icon, the button's
           6px gap, 28px of padding and 2px of border. At 153px it was cut off
           at the card's edge, so the visitor could not read the control they
           were being asked to press.
           This fits AS MANY 240px columns as genuinely fit and drops to a single
           full-width column when even two do not, so no width is hard-coded and
           a phone gets one control per row. `mobile` covers everything under
           640px and the public frame is not capped, so the count really does
           vary across the band; that is the point of auto-fit.
           240 and not the 220 that `manage/theme.tsx` (`.mng-actions`) uses for
           the same job, because 220 is ten pixels short of the widest button:
           two 220px tracks first fit at a 524px viewport, where each track is
           exactly 220px and that 230px control would spill out of it. At 240 the
           first pairing waits until 564px, where the track is already wide
           enough, and every width below it gets one full-width control. */
        confirmActionsCols: st.device === "mobile" ? "repeat(auto-fit, minmax(min(100%, 240px), 1fr))" : "none",
        calTargets: [
          { key: "outlook", label: "Outlook", meta: "Opens the desktop app with an .ics file" },
          { key: "owa", label: "Outlook on the web", meta: "Adds it to outlook.office.com" },
          { key: "google", label: "Google Calendar", meta: "Opens calendar.google.com" },
          { key: "ics", label: "Download .ics file", meta: "Apple Calendar and everything else" },
        ].map((x) => ({ key: x.key, label: x.label, meta: x.meta, onPick: () => this.actions.pickCal(x.key) })),
        files: st.upFiles || [], consent: st.consent,
        confirmWhen: this.fmtDay(st.day, true) + (pickedTime ? " \u00b7 " + this.clockLabel(pickedTime) : ""),
        confirmDuration: cur.dur === "multi" ? (DURATIONS.filter((d) => d.id === st.dur)[0] || DURATIONS[0] || EMPTY_DURATION).label : cur.dur,
        /* The BOOKED facts come from the booking response when there is one:
           it is the server's record of what was actually reserved, it survives
           a catalogue that has moved since, and on the confirmation step the
           local selection is no longer the authority (operator, 2026-09-15 —
           Where and Service rendered blank here). The catalogue selection is
           the fallback for the pre-confirmation summary, where no response
           exists yet. */
        confirmService: (st.booking && st.booking.service && st.booking.service.name)
          ? String(st.booking.service.name) : cur.name,
        // Online-only: "Where" is the method, never a branch (CC, 29 Sept).
        confirmLocation: this.heldRemotely(st, bookedLoc) ? this.bookedMethodLabel(st)
          : (st.booking && st.booking.booking && st.booking.booking.locationName)
          ? String(st.booking.booking.locationName)
          /* NO first-location fallback (issue-004). An unknown selection must
             render as unknown: borrowing LOCATIONS[0] tells the customer they
             are booked at a place they never chose, which is worse than an
             honest blank — `confirmLocationMissing` below is what explains the
             gap. The server's booked facts above remain the authority.
             Guarded by alljourneys-runtime 235 and
             appointment-location-eligibility 321. */
          : (bookedLoc.name || ""),
        confirmAddress: this.heldRemotely(st, bookedLoc) ? ""
          : (st.booking && st.booking.booking && st.booking.booking.address)
          ? String(st.booking.booking.address)
          : (bookedLoc.addr || ""),
        /* WHY a value is missing, not just THAT it is (operator, 2026-09-15: an
           em dash in every slot is no better than a blank one). An empty
           catalogue is a tenant-data problem the operator can go and fix; a
           booking that carried no room is simply a fact about this booking. The
           same dashed-box, plain-sentence convention the service step already
           uses for an empty catalogue. */
        confirmServiceMissing: SERVICES.length === 0
          ? "No services in this tenant's catalogue yet"
          : "Not chosen on this booking",
        confirmLocationMissing: LOCATIONS.length === 0
          ? "No locations in this tenant's catalogue yet"
          : "Not chosen on this booking",
        confirmRoomMissing: "No room assigned",
        confirmWhenMissing: "No date and time on this booking",
        // The ASSIGNED adviser from the booking response (see
        // confirmedAdviserName) — never the selected or first roster member.
        staffName: String(this.confirmedAdviserName(st.booking) || "").trim(),
      },
    };
  }


  /**
   * The Preview dialog — CD harness only (see `previewInDialog`).
   *
   * The frame holds the SAME component the deployed page mounts — `Designer`,
   * drawing the customer page — but as a DRAFT mount: it is handed the journey,
   * settings, custom text and event rows the editor is already showing, and runs
   * no public read. That is both what an author wants (see the layout before
   * publishing) and why it cannot hang: there is nothing to wait for. The device
   * toggle is here rather than in the page because a real browser gets its width
   * from the browser.
   */
  previewDialog() {
    const slug = this.bookingSlug();
    const url = this.publicLink();
    const device = this.state.pvDevice;
    const w = resolveDeviceWidths()[device];
    const h = resolveDeviceHeights()[device];
    const close = this.actions.closePreviewDialog;
    /* WHICH journey the dialog previews. The mount used to pass neither
       `journey` nor `eventId` and the public Designer defaults to
       "appointment", so Preview mounted an appointment page against whatever
       slug was current — "Loading…" forever on appointments/walk-ins/event
       list/kiosk, and the appointment link-error card on a landing slug. It is
       now a draft mount of the journey the editor is actually on. */
    const pvJourney = this.state.journey;
    const pvEventId = pvJourney === "landing" ? String(this.state.evt || "") : "";
    /* A draft mount always has something to draw — it is the page the editor is
       already showing. The old slug/published gate is gone with the public
       mount it belonged to. */
    const canRender = true;
    return (
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Customer page preview"
        onClick={close}
        style={{ position: "fixed", inset: 0, zIndex: 400, background: "rgba(20,22,30,0.68)", backdropFilter: "blur(3px)", display: "flex", flexDirection: "column", padding: "18px clamp(10px, 4vw, 48px)", fontFamily: "var(--jrni-font-family-sans)" }}
      >
        <div onClick={(e) => e.stopPropagation()} style={{ margin: "0 auto", width: "100%", maxWidth: "1180px", flex: 1, minHeight: 0, display: "flex", flexDirection: "column", gap: "10px" }}>
          <div style={{ flex: "0 0 auto", display: "flex", alignItems: "center", flexWrap: "wrap", gap: "10px" }}>
            <span style={{ color: "#fff", fontSize: "13.5px", fontWeight: 700 }}>Customer page</span>
            <span style={{ fontSize: "11px", fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", color: "#fff", padding: "3px 8px", borderRadius: "999px", background: "rgba(255,255,255,0.18)" }}>What's on the page now</span>
            {url ? (
              <span style={{ color: "rgba(255,255,255,0.72)", fontSize: "12.5px" }}>{url}</span>
            ) : slug ? (
              <span style={{ color: "rgba(255,255,255,0.72)", fontSize: "12.5px" }}>/{slug} · the server has not returned a public address yet</span>
            ) : (
              <span style={{ color: "rgba(255,255,255,0.72)", fontSize: "12.5px" }}>Not published yet</span>
            )}
            <div style={{ flex: "1 1 auto" }}></div>
            {this.sampleNote() ? (
              <span style={{ color: "rgba(255,255,255,0.85)", fontSize: "12px" }}>{this.sampleNote()}</span>
            ) : null}
            <DevicePreviewToggle value={device} onChange={this.actions.setPvDevice} size="sm" ariaLabel="Preview width" />
            <div style={{ display: "flex", alignItems: "center", gap: "4px" }} role="group" aria-label={i18nT("shell.allJourneys.zoomPreview", "Zoom preview")}>
              <Button variant="ghost" size="sm" iconOnly="−" aria-label={i18nT("shell.allJourneys.zoomOut", "Zoom out")} onClick={() => this.actions.setPvZoom(this.state.pvZoom - 10)} disabled={this.state.pvZoom <= 50} />
              <button type="button" onClick={() => this.actions.setPvZoom(100)} title={i18nT("shell.allJourneys.resetTo100", "Reset to 100%")} style={{ font: "inherit", fontSize: "12px", fontWeight: 700, color: "#fff", background: "none", border: "none", cursor: "pointer", minWidth: "38px", textAlign: "center" }}>{this.state.pvZoom}%</button>
              <Button variant="ghost" size="sm" iconOnly="+" aria-label={i18nT("shell.allJourneys.zoomIn", "Zoom in")} onClick={() => this.actions.setPvZoom(this.state.pvZoom + 10)} disabled={this.state.pvZoom >= 150} />
            </div>
            {url ? (
              <Button variant="ghost" size="sm" iconRight={this.icon("IconExternal", 13)} onClick={this.actions.openPreviewTab}>Open in a tab</Button>
            ) : null}
            <Button hierarchy="primary" size="sm" onClick={close}>Close</Button>
          </div>
          <div style={{ flex: "1 1 auto", minHeight: 0, display: "flex", justifyContent: "center", alignItems: "flex-start", overflow: "auto" }}>
            <div style={{ width: device === "desktop" ? "100%" : (w * this.state.pvZoom / 100) + "px", height: device === "desktop" ? undefined : (h ? (h * this.state.pvZoom / 100) + "px" : undefined), minHeight: device === "desktop" ? "100%" : undefined, boxSizing: "border-box", background: "var(--jrni-color-surface-canvas)", border: "1px solid rgba(255,255,255,0.16)", borderRadius: "10px", overflow: "hidden", boxShadow: "0 18px 48px rgba(0,0,0,0.32)" }}>
            <div className="apd-devframe" style={{ width: device === "desktop" ? "100%" : w + "px", height: device === "desktop" ? undefined : (h ? h + "px" : undefined), minHeight: device === "desktop" ? "100%" : undefined, boxSizing: "border-box", transform: "scale(" + (this.state.pvZoom / 100) + ")", transformOrigin: "top left", overflowY: h ? "auto" : undefined }}>
              {canRender ? (
                <ErrorBoundary scope="alljourneys:preview-dialog" label="Customer page" variant="pane" resetKey={pvJourney + ":" + (pvEventId || slug) + ":" + device}>
                  <Designer previewJourney={pvJourney} draft={{ s: this.state.s, ct: this.state.ct }} draftRows={this.eventsRows} draftWalkIn={{ queues: this.walkInPreviewQueues(), queueIds: this.state.wiQueueIds || [], sample: !!this.state.wiSample }} draftEvent={pvEventId} slug={slug} tenant={this.props.tenant} tenantName={this.props.tenantName} brandColor={this.publicBrandColor() || undefined} publicHost={this.props.publicHost} device={device} />
                </ErrorBoundary>
              ) : null}
            </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  render() {
    if (this.state.journey === "onestop") {
      return (
        <>
          <style>{APD_CSS}</style>
          <OneStopTab tenant={this.props.tenant} tenantName={this.props.tenantName} brandColor={this.publicBrandColor() || undefined} publicHost={this.props.publicHost} journeyOptions={this.journeyOptions()} onSwitchJourney={(j) => this.actions.setJourney(j)} />
        </>
      );
    }
    const publicStillLoading = this.isPublic() && this.state.previewStep !== "linkError" && (
      this.state.journey === "events" ? !["ready", "error", "empty"].includes(this.state.evtListState)
        : this.state.journey === "landing" ? !["ready", "error"].includes(this.state.evtDetailState)
        : !this.state.apptJourney
    );
    if (publicStillLoading) {
      // The journey's own configured corner, resolved the same way the real
      // cards resolve it, so the ghost is not on a different radius.
      const ghostRadius = ((this.state.s && this.state.s.radiusScale) || 0) + "px";
      const ghostDevice = this.state.device || "desktop";
      return (
        <>
          <style>{APD_CSS}</style>
          {/* The step this page is about to draw, in its own shape, so nothing
              moves when the config and catalogue land. Full width, like the
              loaded journey frame, with its content held at the loaded card's
              content edge: the page's 12px gutter plus the card body's 24px. */}
          <LoadingRegion
            label={this.state.journey === "events" ? "Loading events"
              : this.state.journey === "landing" ? "Loading this event"
              : "Loading this booking page"}
            testId="public-journey-ghost"
            style={{ minHeight: "60vh", width: "100%", padding: "var(--jrni-space-6, 24px) calc(var(--jrni-space-3, 12px) + var(--jrni-space-6, 24px))", fontFamily: "var(--jrni-font-family-sans)" }}
          >
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <SkeletonBox variant="text" width="42%" height={22} />
              <SkeletonBox variant="text" width="66%" height={13} />
              {this.state.journey === "landing" ? (
                <>
                  <SkeletonBox width="100%" height={200} radius={ghostRadius} />
                  <GhostJourneyStep step="tickets" count={3} radius={ghostRadius} />
                </>
              ) : this.state.journey === "events" ? (
                /* Two rows of the configured card count, on the loaded event grid. */
                <GhostJourneyStep
                  step="service" count={6} radius={ghostRadius}
                  columns={eventCardGridColumns(ghostDevice, (this.state.s && this.state.s.cardCols) || 3)}
                  gap={EVENT_GRID_GAP}
                />
              ) : (
                /* Enough tiles to populate the real responsive tracks even on
                   wide desktop; narrower pages wrap on the loaded grid's exact
                   columns, so the first paint does not widen its cards. */
                <GhostJourneyStep
                  step="service" count={10} radius={ghostRadius}
                  columns={serviceGridColumns(ghostDevice)}
                  gap={SERVICE_GRID_GAP}
                />
              )}
            </div>
          </LoadingRegion>
        </>
      );
    }
    if (this.isPublic() && this.state.journey === "events" && this.state.evtListState === "error") {
      return (
        <>
          <style>{APD_CSS}</style>
          <div role="alert" style={{ minHeight: "60vh", display: "flex", flexDirection: "column", gap: 12, alignItems: "center", justifyContent: "center", color: "var(--jrni-color-text-soft)", fontFamily: "var(--jrni-font-family-sans)" }}>
            <span>Events could not be loaded{this.state.evtListError ? `: ${this.state.evtListError}` : "."}</span>
            <button type="button" onClick={() => this.refreshPublicEventsList(this.state.evtPage || 1)} style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid currentColor", background: "transparent", cursor: "pointer" }}>Retry</button>
          </div>
        </>
      );
    }
    if (this.isPublic() && this.state.journey === "events" && this.state.evtScopeUnsupported) {
      return (
        <>
          <style>{APD_CSS}</style>
          <div role="alert" style={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--jrni-color-text-soft)", fontFamily: "var(--jrni-font-family-sans)", textAlign: "center", padding: 24 }}>
            This event list's configuration isn't supported by the current public catalogue read yet.
          </div>
        </>
      );
    }
    const V: any = this.renderVals();
    /* Custom HTML templates (authoring Event List / Event Landing tabs only): which design the
       preview shows, and whether a custom template is what visitors see here. */
    const tplCtrl = this.templateTab() ? this.templateController() : null;
    const tplState = tplCtrl ? tplCtrl.view() : null;
    const tplView = tplState && tplState.mode === "custom" ? tplState : null;
    const tplScoped = !!(tplState && tplState.selection && tplState.selection.inUseSlug);
    const tplLive = tplView ? this.templateLiveLink(tplView) : { url: "", reason: "" };
    return (
      <>
        <style>{APD_CSS}</style>
        {this.state.pvOpen ? this.previewDialog() : null}
        <div style={{ height: "100%", flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden", background: "var(--jrni-color-surface-canvas)", fontFamily: "var(--jrni-font-family-sans)", color: "var(--jrni-color-text-strong)" }}>
          {V.pv.chrome ? (<>
            <header style={{ flex: "0 0 auto", display: "flex", alignItems: "center", flexWrap: "wrap", gap: "14px", rowGap: "8px", padding: "10px 16px", background: "var(--jrni-color-surface-card)", borderBottom: "1px solid var(--jrni-color-surface-border)", boxShadow: "var(--jrni-shadow-chrome)", zIndex: "5" }}>
              {/* The six-tab strip is ~560px of control. It stays a strip and
                  SCROLLS rather than wrapping or shrinking, so the tabs keep
                  their labels at every width; the status pill sits outside the
                  scroller so it is never scrolled out of reach. */}
              <div style={{ flex: "1 1 auto", minWidth: "0", display: "flex", alignItems: "center", gap: "10px" }}>
                <div className="apd-tabscroll" style={{ flex: "0 1 auto", minWidth: "0", padding: "2px 0" }}>
                  <SegmentedControl options={V.opts.journeys} value={V.journey} onChange={V.actions.setJourney} size="sm" aria-label="Journey type" />
                </div>
                {tplView ? (
                  <span style={{ flex: "0 1 auto", display: "inline-flex", minWidth: "0" }}><TemplateHeaderStatus controller={tplCtrl} /></span>
                ) : (
                <span style={{ flex: "0 0 auto", display: "inline-flex", alignItems: "center", gap: "6px", padding: "3px 9px", borderRadius: "999px", fontSize: "12px", fontWeight: "600", border: `1px solid ${V.pv.statusEdge}`, background: V.pv.statusFill, color: V.pv.statusInk }}>
                  <span style={{ width: "6px", height: "6px", borderRadius: "999px", background: V.pv.statusInk }}></span>{tplScoped ? templateTf("journeys.customTemplates.standardStatus", "Standard design: {status}", { status: V.pv.statusLabel }) : V.pv.statusLabel}
                </span>
                )}
              </div>
              {/* With a custom template shown or assigned the actions are longer (Open live page + Edit
                  standard design, or "Save & publish standard design"), so that row may wrap: it takes
                  the rest of the line, never more than the header, and its controls reflow right-aligned
                  onto further lines instead of running off a phone screen. Default header unchanged. */}
              <div style={tplView || tplScoped
                ? { flex: "1 1 auto", minWidth: "0", maxWidth: "100%", display: "flex", alignItems: "center", justifyContent: "flex-end", flexWrap: "wrap", gap: "8px", rowGap: "6px", whiteSpace: "nowrap" }
                : { flex: "0 0 auto", display: "flex", alignItems: "center", gap: "8px", whiteSpace: "nowrap" }}>
                {/* Below the three-pane width only one of settings/preview is on
                    screen, so the switch between them belongs in the page
                    chrome rather than on the preview toolbar (which is itself
                    hidden whenever the preview is). */}
                {V.pv.onePane ? (<>
                  <SegmentedControl options={V.opts.pane} value={V.pv.paneValue} onChange={V.actions.setPane} size="sm" aria-label="Show settings or preview" />
                </>) : null}
                {tplView ? (<>
                  {V.pv.dirty ? (
                    <span style={{ flex: "0 1 auto", minWidth: "0", whiteSpace: "normal", overflowWrap: "anywhere", textAlign: "right", fontSize: "12px", color: "var(--jrni-color-text-soft)" }}>{i18nT("journeys.customTemplates.standardUnsaved", "Standard design has unsaved changes")}</span>
                  ) : null}
                  <Button variant="ghost" size="sm" iconRight={V.icons.external} disabled={!tplLive.url} title={tplLive.url ? undefined : tplLive.reason}
                    onClick={() => { if (tplLive.url) { try { window.open(tplLive.url, "_blank", "noopener"); } catch (err) {} } }}>{i18nT("journeys.customTemplates.openLivePage", "Open live page")}
                  </Button>
                  <Button hierarchy="secondary" size="sm" onClick={() => tplCtrl.setView("standard")}>{i18nT("journeys.customTemplates.editStandard", "Edit standard design")}
                  </Button>
                </>) : (<>
                <Button variant="ghost" size="sm" iconRight={V.icons.external} onClick={V.actions.openPreview}>Preview
                </Button>
                {V.pv.showSavePublish ? (
                  <Button hierarchy="primary" size="sm" onClick={V.actions.publish} disabled={V.pv.saveDisabled}>{tplScoped && V.pv.savePublishLabel === "Save & publish" ? i18nT("journeys.customTemplates.saveStandard", "Save & publish standard design") : V.pv.savePublishLabel}
                  </Button>
                ) : null}
                </>)}
              </div>
              {V.pv.hasSaveNote && !tplView ? (<>
                <div style={{ flex: "0 0 100%", display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", color: "var(--jrni-color-text-soft)" }}>
                  <span style={{ width: "6px", height: "6px", borderRadius: "999px", background: V.pv.saveDotBg }}></span>
                  <span>{V.pv.saveNote}</span>
                  {V.pv.published && V.pv.liveUrl ? (<>
                    <Button variant="link" size="sm" onClick={V.actions.openPreview}>{V.pv.liveUrl}
                    </Button>
                  </>) : null}
                </div>
              </>) : null}
              {V.pv.hasSaveNotices && !tplView ? (<>
                <div style={{ flex: "0 0 100%", display: "flex", flexDirection: "column", gap: "4px", fontSize: "12.5px", color: "var(--jrni-color-text-soft)", paddingLeft: "14px" }}>
                  {V.pv.saveNotices.map((n) => (<div key={n}>{n}</div>))}
                </div>
              </>) : null}
              {V.pv.hasRefusals && !tplView ? (<>
                <div style={{ flex: "0 0 100%", display: "flex", flexDirection: "column", gap: "4px", fontSize: "12.5px", color: "var(--jrni-color-semantic-red-1)", paddingLeft: "14px" }}>
                  {V.pv.refusedFields.map((f) => (
                    <div key={f.path}>
                      <strong style={{ fontWeight: 600 }}>{f.label}</strong>{" \u2014 "}{f.message}
                    </div>
                  ))}
                </div>
              </>) : null}
            </header>
          </>) : null}
          {/* No `overflowX: auto` here any more. The row used to scroll sideways
              once the three panes' minimums stopped fitting, which moved the
              whole designer under the operator instead of reflowing it. The
              panes now shrink to nothing (`minWidth: 0`) and the layout drops a
              pane at a time — see ./designer-layout.ts and initResize. */}
          <div className="apd-scroll" style={{ flex: "1 1 auto", display: "flex", minHeight: "0", minWidth: "0", overflow: "hidden" }}>
            {V.pv.railPane ? (<>
              <nav className="apd-scroll" style={{ flex: "0 0 236px", width: "236px", minWidth: "236px", boxSizing: "border-box", overflowY: "auto", overflowX: "hidden", background: "var(--jrni-color-surface-card)", borderRight: "1px solid var(--jrni-color-surface-border)", padding: "16px 13px 20px", display: "flex", flexDirection: "column", gap: "6px" }}>
                <div style={{ padding: "0 13px 2px" }}>
                  <SectionLabel>Setup
                  </SectionLabel>
                </div>
                {/* 210 = the rail's 236px minus its 13px side padding: the setup rows must end on
                    the same right edge as the journey-step rows below, which are width:100%. */}
                <EditorSectionNav items={V.setupItems} value={V.setupValue} onChange={V.actions.goSection} width={210} />
                <div style={{ padding: "14px 13px 2px" }}>
                  <SectionLabel>Customer journey
                  </SectionLabel>
                </div>
                <div role="tablist" aria-label="Journey steps" style={{ display: "flex", flexDirection: "column", gap: "3px" }}>
                  {(V.journeyItems || []).map((j, __i0) => (<React.Fragment key={j.key ?? j.id ?? __i0}>
                    <button type="button" role="tab" aria-selected={j.selected} onClick={j.onClick} style={{ display: "flex", alignItems: "center", gap: "6px", width: "100%", boxSizing: "border-box", textAlign: "left", padding: "8px 12px", borderRadius: "var(--jrni-radius-control)", border: "1px solid transparent", cursor: "pointer", fontFamily: "inherit", fontSize: "14px", background: j.bg, borderColor: j.border, boxShadow: j.shadow, color: j.fg, opacity: j.opacity }}>
                      <span style={{ flex: "0 0 22px", height: "22px", borderRadius: "999px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "12px", fontWeight: "700", background: j.numBg, color: j.numFg }}>{j.num}</span>
                      <span style={{ flex: "1 1 auto", fontWeight: "500", minWidth: "0", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{j.label}</span>
                      {j.hidden ? (<>
                        <span style={{ flex: "0 0 auto", fontSize: "11px", letterSpacing: "0.02em", textTransform: "uppercase", color: "var(--jrni-color-text-soft)" }}>Off</span>
                      </>) : null}
                    </button>
                  </React.Fragment>))}
                </div>
                <div style={{ flex: "1 1 auto", minHeight: "16px" }}></div>
              </nav>
            </>) : null}
            {V.pv.formPane ? (<>
              <div className="apd-scroll" style={{ flex: "1 1 520px", minWidth: "0", overflow: "auto", padding: V.pv.phone ? "16px 16px 48px" : "24px 28px 56px", background: "var(--jrni-color-surface-canvas)" }}>
                {/* The setup rail, collapsed. At phone width a 236px rail leaves
                    nothing to read the settings in, so both halves of it become
                    one picker at the top of the pane it drives. */}
                {V.pv.phone ? (<>
                  <div style={{ marginBottom: "16px" }}>
                    <Field label="Section">
                      <Dropdown options={V.navOptions} value={V.navValue} onChange={V.actions.goNav} style={{  }} />
                    </Field>
                  </div>
                </>) : null}
                <div style={{ display: "flex", alignItems: "flex-start", gap: "16px", marginBottom: "20px", maxWidth: "880px" }}>
                  <div style={{ flex: "1 1 auto", minWidth: "0" }}>
                    <h1 style={{ margin: "0", fontSize: V.pv.phone ? "18px" : "22px", fontWeight: "700", letterSpacing: "-0.01em" }}>{V.panel.title}</h1>
                    <p style={{ margin: "6px 0 0", fontSize: "14px", lineHeight: "1.5", color: "var(--jrni-color-text-body)", maxWidth: "62ch", textWrap: "pretty" }}>{V.panel.desc}</p>
                  </div>
                </div>
                {tplView ? <CustomTemplateNotice controller={tplCtrl} /> : null}
                <div style={{ display: "flex", flexDirection: "column", gap: "16px", maxWidth: "880px" }}>
                  {V.is.general ? (<>
                    {V.is.kiosk ? (<>
                      <Section title="Where this kiosk is" description="A kiosk is fixed to one wall, so the location is a property of the unit rather than something the visitor picks. Everything it opens inherits it: no location step, and only this campus's services, slots and events.">
                        <Field label="Installed at">
                          <Dropdown options={V.opts.kioskLocations} value={V.s.kioskLocation} onChange={V.set.kioskLocation} style={{  }} />
                        </Field>
                      </Section>
                      <Section title="Between visitors" description="The entry screen is the resting state — there is no attract loop — so anything personal has to be cleared on a timer.">
                        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                          <Field label="Clear the screen after">
                            <Input type="number" value={V.s.kioskResetAfter} onChange={V.set.kioskResetAfter} trailing="sec" style={{  }} />
                          </Field>
                          <Field label="Greeting">
                            <SegmentedControl options={V.opts.kioskGreeting} value={V.s.kioskGreeting} onChange={V.set.kioskGreeting} size="sm" />
                          </Field>
                        </div>
                      </Section>
                      <Section title="Everyone who walks up to it" description="A shared screen in a public lobby has no settings of its own to inherit.">
                        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                          <ToggleRow title="Larger text button" description="Steps every size up one notch. Wall-mounted screens are read standing and from further away than a phone." checked={V.s.kioskBigText} onChange={V.set.kioskBigText} />
                          <ToggleRow title="Show the university header" description={i18nT("shell.allJourneys.theTenantLogoAndTitle", "The tenant logo and title bar across the top of the shared screen.")} checked={V.s.showHeader} onChange={V.set.showHeader} />
                        </div>
                      </Section>
                    </>) : null}
                    {V.is.appt ? (<>
                      {V.is.pinnedFromKiosk ? (<>
                        <div style={{ display: "flex", alignItems: "flex-start", gap: "12px", padding: "14px 16px", border: "1px solid var(--jrni-color-primary-3)", borderRadius: "var(--jrni-radius-md)", background: "var(--jrni-color-primary-4)" }}>
                          <span style={{ flex: "0 0 auto", color: "var(--jrni-color-primary-1)", paddingTop: "1px" }}>{V.icons.info}</span>
                          <span style={{ flex: "1 1 auto", minWidth: "0", fontSize: "13px", lineHeight: "1.55", color: "var(--jrni-color-text-strong)", textWrap: "pretty" }}>You opened this from the kiosk at
                            <b>{V.pv.pinnedLocName}</b>. A kiosk is fixed to one place, so the location is already decided — the {V.pv.pinnedStepWord} step is dropped and every service, slot and event is limited to that campus. The settings below still apply to web and mobile visitors.
                          </span>
                        </div>
                      </>) : null}
                      <Section title="Which step comes first">
                        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                          <SelectableTile showIndicator={false} title="Service" description="Recommended — lets you skip the location step for virtual appointments and show next available slots per location." selected={V.is.serviceFirst} onSelect={V.actions.serviceFirst} full={true} />
                          <SelectableTile showIndicator={false} disabled trailing={<Pill tone="neutral">Coming soon</Pill>} title="Location" description="Recommended when similar in-person services run across many locations." selected={V.is.locationFirst} onSelect={V.actions.locationFirst} full={true} />
                          <SelectableTile showIndicator={false} disabled trailing={<Pill tone="neutral">Coming soon</Pill>} title="Map only" description="Opens on a full map with a pin per location — no list. Best when people choose by where they are, not by name." selected={V.is.mapFirst} onSelect={V.actions.mapFirst} full={true} />
                        </div>
                      </Section>
                      <Section title="Skipping steps">
                        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                          <Checkbox checked={V.s.skipService} onChange={V.set.skipService} label="Skip the service step if only one service is available" supportingText={V.hints.skipService} />
                          <Checkbox checked={V.s.skipLocation} onChange={V.set.skipLocation} label="Skip the location step if only one location is available" supportingText={V.hints.skipLocation} />
                        </div>
                      </Section>
                    </>) : null}
                    {V.is.events ? (<>
                      {!this.props.mode && !this.props.previewJourney ? (
                        <CustomHtmlTemplatesPanel scope="events" controller={this.templateController()} onShowPreview={() => this.showTemplatePreview()} />
                      ) : null}
                      <Section title="What the list includes" description="The first page is a list of everything on sale.">
                        <div style={{ paddingBottom: "12px" }}>
                          <Field label="Sort events by">
                            <Dropdown options={V.opts.sortOrder} value={V.s.sortOrder} onChange={V.set.sortOrder} style={{  }} />
                          </Field>
                        </div>
                        <ToggleRow title="Keep past events on the list" description="Greyed out, no registration. Useful for series that repeat." checked={V.s.showPastEvents} onChange={V.actions.setShowPastEvents} />
                      </Section>
                      <Section title="Skipping steps">
                        <ToggleRow title="Show event details step" description="Show Events, Event page, Your info and Confirmation. Turn off to skip Event page when the selected event has one bookable date/time. Direct event links still show Event page." checked={!V.s.skipSingleEventSlot} onChange={V.actions.setShowEventDetailsStep} />
                      </Section>
                    </>) : null}
                    {V.is.queue ? (<>
                      {/* The author's EXPLICIT pick from the tenant's real
                          queues (`listQueues`, followed across pages). Nothing
                          is attached automatically and nothing is written on
                          load — the selection reaches the server on Save, like
                          every other setting on this page. The public join is
                          bound to exactly these queues: the join handler
                          refuses a queue outside the published `queueIds`. */}
                      <Section title="Which lines this page joins" description="Pick the queues a visitor can join here. A page with none cannot be published as a walk-in page.">
                        {V.pv.walkInQueueOptions.length ? (<>
                          <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                            {V.pv.walkInQueueOptions.map((q) => (
                              <Checkbox
                                key={q.id}
                                checked={q.selected}
                                onChange={() => V.actions.toggleWalkInQueue(q.id)}
                                label={q.name}
                                supportingText={q.meta}
                              />
                            ))}
                          </div>
                        </>) : (<>
                          <InlineEmptyState
                            title="No queues on this workspace yet"
                            description="Create a queue in Queues first; this page can only offer lines that already exist."
                          />
                        </>)}
                      </Section>
                    </>) : null}
                    {V.is.landingJourney ? (<>
                      {!this.props.mode && !this.props.previewJourney ? (
                        <CustomHtmlTemplatesPanel scope="landing" controller={this.templateController()} onShowPreview={() => this.showTemplatePreview()} />
                      ) : null}
                      <Section title="Template">
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "8px" }}>
                          <SelectableTile showIndicator={false} title="Banner and sidebar" description="Slim colour banner, details left, a sticky registration card right." selected={V.is.tplBanner} onSelect={V.actions.tplBanner} full={true} />
                          <SelectableTile showIndicator={false} title="Hero cover" description="Full-bleed image with the title over it, content below." selected={V.is.tplHero} onSelect={V.actions.tplHero} full={true} />
                          <SelectableTile showIndicator={false} title="Split panel" description="Image on one half, title and registration on the other." selected={V.is.tplSplit} onSelect={V.actions.tplSplit} full={true} />
                          <SelectableTile showIndicator={false} title="Long-form" description="No hero. Title, then every block stacked full width." selected={V.is.tplLong} onSelect={V.actions.tplLong} full={true} />
                          {/* Fifth tile, offered to every tenant (2026-09-23).
                              Spans both columns so it reads as its own thing
                              rather than a fourth arrangement. */}
                          {V.is.customTplOffered ? (<>
                            <div style={{ gridColumn: "1 / -1" }}>
                              <SelectableTile showIndicator={false} title="Create your own" description="Start from a blank page and place blocks on a 12-column grid. Drag to move, drag a handle to resize, and lay out desktop, tablet and mobile." selected={V.is.tplCustom} onSelect={V.actions.tplCustom} full={true} />
                            </div>
                          </>) : null}
                        </div>
                      </Section>
                      {V.is.tplCustom ? null : (<>
                      <Section title="Hero">
                        {V.pv.hasHero ? (<>
                          <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                            <Field label="Height">
                              <SegmentedControl options={V.opts.heroHeight} value={V.s.heroHeight} onChange={V.actions.setHeroHeight} />
                            </Field>
                            <Field label="Treatment">
                              <SegmentedControl options={V.opts.heroTreatment} value={V.s.heroTreatment} onChange={V.actions.setHeroTreatment} />
                            </Field>
                            <Field label="Cover image for this event" hint={i18nT("shell.allJourneys.alsoNeedsTheGeneralTab", "Also needs the General tab's “Show service & event images” turned on.")}>
                              <ImageDropField value={V.s.coverImage} onChange={V.actions.setCoverImage} aspect={2} maxWidth={320} placeholder="Drop a cover image, or use a URL" />
                            </Field>
                          </div>
                        </>) : null}
                        {V.pv.noHero ? (<>
                          <Banner tone="neutral" variant="explainer" size="compact" title="This template has no hero">{V.pv.noHeroNote}
                          </Banner>
                        </>) : null}
                      </Section>
                      <Section title="Registration bar">
                        <ToggleRow title="Sticky registration bar" description="Follows the scroll with the price and a Register button, so the call to action is never off screen." checked={V.s.stickyBar} onChange={V.actions.setStickyBar} />
                      </Section>
                      </>)}
                    </>) : null}
                    <Section title="Header and footer">
                      <ToggleRow title="Show header" description="A title bar above the journey." checked={V.s.showHeader} onChange={V.set.showHeader} />
                      {V.s.showHeader ? (
                        <ToggleRow title="Use branded header" description="Uses the header from Branding. Off shows the basic header." checked={!!V.s.brandedHeader} onChange={V.set.brandedHeader} />
                      ) : null}
                      <ToggleRow title="Show footer" description="Contact and legal links below the journey." checked={V.s.showFooter} onChange={V.set.showFooter} />
                      {V.s.showFooter ? (
                        <ToggleRow title="Use branded footer" description="Uses the footer from Branding. Off shows the basic footer." checked={!!V.s.brandedFooter} onChange={V.set.brandedFooter} />
                      ) : null}
                    </Section>
                    {/* Walk-ins only until another journey type has somewhere to store it (CC, 29 Sept):
                        appointments, events/landing and kiosk would drop it on save. */}
                    {V.is.queue ? (<Section title={i18nT("shell.allJourneys.serviceEventImages", "Service & event images")} description={i18nT("shell.allJourneys.aMasterSwitchForThis", "A master switch for this journey. Off hides every service and event image here, regardless of the Service/Events tab's own image toggle.")}>
                      <ToggleRow title={i18nT("shell.allJourneys.showServiceEventImages", "Show service & event images")} description={i18nT("shell.allJourneys.uploadedOnServiceAndEvent", "Uploaded on service and event profiles in Studio.")} checked={!!V.s.showMediaImages} onChange={V.set.showMediaImages} />
                    </Section>) : null}
                    {V.is.appt ? (<>
                      <Section title="Staff">
                        {/* `ToggleRow` takes no `trailing` (that is a
                            `SelectableTile` prop and it was silently dropped),
                            and its `disabled` styles the switch without
                            dimming the row or telling assistive tech — so the
                            pill rides in the title node, the way the
                            details-step toggles already do, and the wrapper
                            carries the dimming and `aria-disabled`. */}
                        <div aria-disabled="true" style={{ opacity: "0.5" }}>
                          <ToggleRow disabled title={<span style={{ display: "inline-flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>Let customers choose a staff member <Pill tone="neutral">Coming soon</Pill></span>} description="Adds a staff picker to the date &amp; time step. Waiting on worker-level availability: today a store's opening hours are the whole window and every worker on its roster inherits them, so choosing one would not change a single slot." checked={V.s.staffSelect} onChange={V.set.staffSelect} />
                        </div>
                        <div style={{ paddingTop: "12px" }}>
                          <Field label="Reschedule behaviour" hint="Personal booking links always reschedule with the same staff member.">
                            <Dropdown options={V.opts.reschedule} value={V.s.reschedule} onChange={V.set.reschedule} style={{  }} />
                          </Field>
                        </div>
                      </Section>
                    </>) : null}
                    {V.is.appt ? (<>
                      <Section title="Error page redirect" density="dense">
                        <ToggleRow title="Offer to book with another staff member" description={i18nT("shell.allJourneys.onTheErrorPageOffers", "On the error page, offers the same service with the next available staff member.")} checked={V.s.errBookStaff} onChange={V.set.errBookStaff} />
                        <ToggleRow title="Offer to explore other services" description={i18nT("shell.allJourneys.onTheErrorPageLinks", "On the error page, links to the services still open for booking today.")} checked={V.s.errOtherServices} onChange={V.set.errOtherServices} />
                      </Section>
                    </>) : null}
                  </>) : null}
                  {V.is.progress ? (<>
                    {/* ONE control (KP, 2026-09-16): the step rail is the same on every
                        journey — titles with the current step underlined — so the only
                        choice left is whether to show it. The old Numbered / Bar tiles
                        are gone; a journey saved with either renders titles. */}
                    <Section title="Step indicator" description="Shows customers where they are in the journey: What · Where · When · Your info. Identical on every journey.">
                      <ToggleRow title="Show step indicator" description="Step titles with the current step underlined." checked={!V.is.pNone} onChange={(on) => (on ? V.actions.pTitles() : V.actions.pNone())} />
                    </Section>
                  </>) : null}
                  {V.is.eventList ? (<>
                    {/* The page's window (CC 0359). "All upcoming" saves no window at all, which also
                        clears one an older page stored. */}
                    <Section title="Which events" description="How far ahead the page looks. Past events follow the setting under General.">
                      <div style={{ display: "flex", alignItems: "flex-end", gap: "16px", flexWrap: "wrap" }}>
                        <Field label="Show">
                          <Dropdown options={V.eventsWindowOptions} value={V.s.eventsWindow} onChange={V.set.eventsWindow} style={{  }} />
                        </Field>
                      </div>
                    </Section>
                    <Section title="How many events on the first page" description="The rest are reached by the control you pick below.">
                      <div style={{ display: "flex", alignItems: "flex-end", gap: "16px", flexWrap: "wrap", paddingBottom: "16px" }}>
                        <Field label="Events per page" hint={V.hints.perPage}>
                          <Input type="number" value={V.s.eventsPerPage} onChange={V.set.eventsPerPage} style={{  }} />
                        </Field>
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                        <SelectableTile showIndicator={false} title="Numbered pages" description="A pager under the list — best when people scan for a date." selected={V.is.pagePages} onSelect={V.actions.pagePages} full={true} />
                        <SelectableTile showIndicator={false} title="Load more button" description="Appends the next batch, keeping everything on one page." selected={V.is.pageLoadMore} onSelect={V.actions.pageLoadMore} full={true} />
                        <SelectableTile showIndicator={false} title="Infinite scroll" description="Loads as the visitor scrolls. Avoid when the list is long." selected={V.is.pageScroll} onSelect={V.actions.pageScroll} full={true} />
                      </div>
                    </Section>
                    <Section title="Layout">
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "8px" }}>
                        <SelectableTile showIndicator={false} title="Cards" description="Cover image on top, details underneath" selected={V.is.evGrid} onSelect={V.actions.evGrid} full={true} />
                        <SelectableTile showIndicator={false} title="List" description="One event per row, dense" selected={V.is.evList} onSelect={V.actions.evList} full={true} />
                      </div>
                      {V.is.evGrid ? (<>
                        <div style={{ paddingTop: "16px" }}>
                          <Field label="Cards per row" hint="Cards stack to one column on phones whatever you pick.">
                            <SegmentedControl options={V.opts.cardCols} value={V.s.cardCols} onChange={V.set.cardCols} size="sm" />
                          </Field>
                        </div>
                      </>) : null}
                    </Section>
                    <Section title="Search bar" description="Sits above the list, so visitors can narrow a long calendar.">
                      <ToggleRow title="Show the search bar" description={i18nT("shell.allJourneys.letsVisitorsNarrowALong", "Lets visitors narrow a long calendar by keyword before they scroll.")} checked={V.s.filterBar} onChange={V.set.filterBar} />
                      {V.s.filterBar ? (<>
                        <div style={{ display: "flex", flexDirection: "column", gap: "10px", padding: "12px 0 0 2px" }}>
                          <Checkbox checked={V.s.filterLocation} onChange={V.set.filterLocation} label="Location search" supportingText="With a use-my-location button" />
                          <Checkbox checked={V.s.filterType} onChange={V.set.filterType} label="Event type" />
                          <Checkbox checked={V.s.filterDates} onChange={V.set.filterDates} label="Start and end date" />
                        </div>
                      </>) : null}
                      <div style={{ paddingTop: "14px" }}>
                        <ToggleRow title="Show category chips instead" description="A quicker filter when you have four or five clear categories." checked={V.s.eventFilters} onChange={V.set.eventFilters} />
                      </div>
                    </Section>
                    <Section title="What each event shows">
                      <ToggleRow title="Cover image" description={i18nT("shell.allJourneys.uploadedOnTheEventIn", "Uploaded on the event in Studio. Falls back to a coloured tile. Also needs the General tab's “Show service & event images” turned on.")} checked={V.s.eventImages} onChange={V.set.eventImages} />
                      <ToggleRow title="Description" description="Two or three lines under the title." checked={V.s.showEventDesc} onChange={V.set.showEventDesc} />
                      <ToggleRow title="Date" description={i18nT("shell.allJourneys.theDayEachEventRuns", "The day each event runs, on every card in the list.")} checked={V.s.showEventDate} onChange={V.set.showEventDate} />
                      <ToggleRow title="Start and end time" description={i18nT("shell.allJourneys.bothEndsOfTheEvent", "Both ends of the event, so visitors can judge the commitment.")} checked={V.s.showEventTime} onChange={V.set.showEventTime} />
                      <ToggleRow title="Time zone" description="Worth keeping for online events booked from other states." checked={V.s.showTimezone} onChange={V.set.showTimezone} />
                      <ToggleRow title="Address" description={i18nT("shell.allJourneys.theVenueLineUnderThe", "The venue line under the title. Worth keeping for anything in person.")} checked={V.s.showEventVenue} onChange={V.set.showEventVenue} />
                      <ToggleRow title="Price" description={i18nT("shell.allJourneys.theTicketPriceOrFree", "The ticket price, or Free when there is no charge.")} checked={V.s.showEventPrice} onChange={V.set.showEventPrice} />
                      <ToggleRow title="Spaces remaining" description="A capacity meter and a count. Creates urgency, and pressure." checked={V.s.showRemaining} onChange={V.set.showRemaining} />
                    </Section>
                    <Section title="When an event is full">
                      <ToggleRow title="Keep full events on the list" description="Off removes them entirely, so nobody sees what they missed." checked={V.s.showSoldOut} onChange={V.set.showSoldOut} />
                      <ToggleRow title="Offer a waiting list" description={i18nT("shell.allJourneys.aFullEventStillTakes", "A full event still takes names, and places that free up are offered in order.")} checked={V.s.waitlist} onChange={V.set.waitlist} disabled={V.is.soldOutHidden} />
                    </Section>
                  </>) : null}
                  {V.is.service ? (<>
                    <Section title="Layout" description="With more than twelve services, the list view reads best.">
                      <Field label="Display of services">
                        <Dropdown options={V.opts.serviceDisplay} value={V.s.serviceDisplay} onChange={V.set.serviceDisplay} style={{  }} />
                      </Field>
                    </Section>
                    <Section title="What each service shows">
                      <ToggleRow title="Group services into categories" description={this.categoryHint()} checked={V.s.showCategories} onChange={V.set.showCategories} />
                      <ToggleRow title="Show service images" description={i18nT("shell.allJourneys.uploadedOnTheServiceProfiles", "Uploaded on the service profiles in Studio. Also needs the General tab's “Show service & event images” turned on.")} checked={V.s.showServiceImages} onChange={V.set.showServiceImages} />
                      {V.is.appt ? (<>
                        <ToggleRow title="Show duration" description={i18nT("shell.allJourneys.howLongEachAppointmentTakes", "How long each appointment takes, on the service in the list.")} checked={V.s.showDuration} onChange={V.set.showDuration} />
                        <ToggleRow title="Show price" description={i18nT("shell.allJourneys.theServicePriceAndNothing", "The service price, and nothing at all when a service is not chargeable.")} checked={V.s.showPrice} onChange={V.set.showPrice} />
                      </>) : null}
                      {V.is.queue ? (<>
                        <ToggleRow title="Ask which service point" description="Adds a step when the building runs several desks or windows. Off routes visitors automatically." checked={V.s.queueLocationStep} onChange={V.set.queueLocationStep} />
                        <ToggleRow title="Show the live wait per service" description="Visitors see how long each desk is running before they choose." checked={V.s.liveWaitPerService} onChange={V.set.liveWaitPerService} />
                      </>) : null}
                    </Section>
                  </>) : null}
                  {V.is.location ? (<>
                    {V.is.queue ? (<>
                      {/* The attached queues, read back from the queue service
                          itself — not the designer's old DESKS fixture, which
                          named three advising desks that belong to no tenant.
                          A queue that could not be read says so instead of
                          disappearing from the list. */}
                      <Section title="Service points" description="The queues this page joins. Attach or detach them in General; their names, locations and waits come from Queues.">
                        <ToggleRow
                          title={<span style={{ display: "inline-flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>Name the desk on the ticket <Pill tone="neutral">Coming soon</Pill></span>}
                          description="The public queue projection carries the queue's name but no desk assignment, so there is nothing true to print here yet."
                          checked={false}
                          disabled={true}
                        />
                        <div style={{ display: "flex", flexDirection: "column", paddingTop: "8px" }}>
                          {(V.pv.walkInQueueRows || []).map((dk) => (
                            <MediaRow key={dk.id} title={dk.name} subtitle={dk.sub} meta={dk.meta} as="div" />
                          ))}
                        </div>
                        {!(V.pv.walkInQueueRows || []).length ? (<>
                          <InlineEmptyState title="No lines attached" description="Pick at least one queue in General." />
                        </>) : null}
                      </Section>
                    </>) : null}
                    {V.is.appt ? (<>
                      <Section title="Availability on the list" description="Only applies to service-first journeys.">

                        <ToggleRow title="Show next available slot per location" description={i18nT("shell.allJourneys.eachLocationCarriesItsOwn", "Each location carries its own soonest free time, so the choice can be made on availability.")} checked={V.s.nextAvailable} onChange={V.set.nextAvailable} />
                        {V.s.nextAvailable && !V.pv.anyLocationNext ? (<>
                          <Banner tone="neutral" variant="explainer" size="compact" title="No per-location availability to show yet">The setting is stored, but no read carries a next-available slot per venue — availability resolves by service and staff, not by location. The line is left off the cards rather than printed empty.
                          </Banner>
                        </>) : null}
                        <ToggleRow nested title="Enable Quickbook" description="Books the next available slot directly, skipping the date & time step." checked={V.s.quickbook} onChange={V.set.quickbook} disabled={V.is.quickbookLocked} />
                        <ToggleRow title="Show opening hours and phone number" description={i18nT("shell.allJourneys.addsTheBranchHoursAnd", "Adds the branch hours and a phone number under its address.")} checked={V.s.extraLocationInfo} onChange={V.set.extraLocationInfo} />
                        <ToggleRow title="Show map" description={V.hints.map} checked={V.s.showMap} onChange={V.set.showMap} disabled={V.is.mapFirst} />
                        {V.s.showMap ? (<>
                          <div style={{ paddingTop: "12px" }}>

                            <Field label="Map height" hint="Or drag the handle under the map in the preview.">
                              <Input type="number" value={V.s.mapHeight} onChange={V.set.mapHeight} trailing="px" style={{  }} />
                            </Field>
                          </div>
                        </>) : null}
                      </Section>
                      {V.is.mapFirst ? (<>
                        <Banner tone="primary" variant="explainer" size="compact" title="This journey opens on the map">Map only is set in General, so the list is replaced by pins and a selected-location card. Load more and the result count do not apply.
                        </Banner>
                      </>) : null}
                      <Section title="How many results">
                        <ToggleRow title="Use a Load more button" description="Off means infinite scroll." checked={V.s.loadMore} onChange={V.set.loadMore} />
                        <div style={{ display: "flex", gap: "16px", paddingTop: "12px", flexWrap: "wrap" }}>
                          <Field label="Locations loaded initially">
                            <Input type="number" value={V.s.initialCount} onChange={V.set.initialCount} style={{  }} />
                          </Field>
                          <Field label="Maximum search radius" hint="0 means no limit.">
                            <div style={{ display: "flex", gap: "8px", alignItems: "flex-start" }}>
                              <Input type="number" value={V.s.radius} onChange={V.set.radius} style={{  }} />
                              <Dropdown options={V.opts.radiusUnit} value={V.s.radiusUnit} onChange={V.set.radiusUnit} style={{  }} />
                            </div>
                          </Field>
                        </div>
                      </Section>
                    </>) : null}
                  </>) : null}
                  {V.is.datetime ? (<>
                    <Section title="Calendar layout">
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "8px" }}>
                        <SelectableTile showIndicator={false} title="Month calendar" description="Full month, unavailable dates greyed out" selected={V.is.monthCal} onSelect={V.actions.monthCal} full={true} />
                        <SelectableTile showIndicator={false} title="Day strip" description="Seven days at a time, slots underneath" selected={V.is.dayStrip} onSelect={V.actions.dayStrip} full={true} />
                      </div>
                      <div style={{ paddingTop: "16px", display: "flex", flexDirection: "column", gap: "8px" }}>
                        <SectionLabel tone="soft">Which date is selected when the step opens
                        </SectionLabel>
                        <SelectableTile showIndicator={false} title="Today" description="Customers land on today, even when it is fully booked." selected={V.is.dateToday} onSelect={V.actions.dateToday} full={true} />
                        <SelectableTile showIndicator={false} title="First date with availability" description={V.hints.firstAvail} selected={V.is.dateFirst} onSelect={V.actions.dateFirstAvail} full={true} />
                      </div>
                    </Section>
                    <Section title="Time slots">
                      <Field label="Minutes between start times" hint={V.hints.slotInterval}>
                        <Input type="number" min={1} max={1140} value={V.s.slotInterval} onChange={V.set.slotInterval} trailing="min" style={{ width: "160px", flex: "0 0 auto" }} />
                      </Field>
                      <ToggleRow title="Group slots by part of day" description="Morning, afternoon, evening headings." checked={V.is.grouped} onChange={V.actions.setGrouped} />
                      <ToggleRow title="Show the time zone" description="Reassures customers booking virtual appointments from another state." checked={V.s.showTimezone} onChange={V.set.showTimezone} />
                    </Section>
                    {V.pv.staffPicker ? (<>
                      <Banner tone="primary" variant="explainer" size="compact" title="Staff picker is on">Customers pick an adviser above the calendar. Turn it off in General › Staff.
                      </Banner>
                    </>) : null}
                  </>) : null}
                  {V.is.details ? (<>
                    {V.is.queue ? (<>
                      {/* The join is validated against the UNION of the
                          queue's own `settings.joinForm` and what this page
                          asks for, so making a field optional here cannot
                          make it optional at a desk that demands it. Said out
                          loud rather than left for a customer to discover as
                          a refusal on a form that told them it was optional. */}
                      <Banner tone="neutral" variant="explainer" size="compact" title="Queues can ask for more than this page does">
                        A queue with its own required contact field still demands it. These settings can add a question or make one required — they cannot relax what a queue insists on.
                      </Banner>
                    </>) : null}
                    <Section title="What you ask for">
                      {/* Each ask-for field is one compact group: the enable ToggleRow with its
                          dependent controls left-aligned directly underneath at a modest
                          tokenized gap, wrapping instead of stranding a lone control at the
                          far right of the panel. */}
                      <div style={{ display: "flex", flexDirection: "column", gap: "var(--jrni-space-1, 4px)" }}>
                        <ToggleRow title={V.pv.emailSettingTitle} description={V.pv.emailSettingDescription} checked={V.pv.emailApiRequired ? true : V.s.askEmail} onChange={V.pv.emailApiRequired ? () => {} : V.set.askEmail} disabled={V.pv.emailApiRequired} />
                        {!V.pv.emailApiRequired && V.s.askEmail ? (<>
                          <div style={{ padding: "0 0 var(--jrni-space-3, 12px) 2px", display: "flex", flexWrap: "wrap", alignItems: "center", gap: "var(--jrni-space-2, 8px) var(--jrni-space-4, 16px)" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "var(--jrni-space-2, 8px)" }}>
                              <Checkbox checked={V.s.emailOptional} onChange={V.set.emailOptional} aria-label="Mark as optional" />
                              <span style={{ fontSize: "13px", color: "var(--jrni-color-text-body)" }}>Mark as optional</span>
                            </div>
                          </div>
                        </>) : null}
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: "var(--jrni-space-1, 4px)" }}>
                        <ToggleRow title="Phone number" description={i18nT("shell.allJourneys.neededForTextRemindersAnd", "Needed for text reminders, and for a call when something changes on the day.")} checked={V.s.askPhone} onChange={V.set.askPhone} />
                        {V.s.askPhone ? (<>
                          <div style={{ padding: "0 0 var(--jrni-space-3, 12px) 2px", display: "flex", flexWrap: "wrap", alignItems: "center", gap: "var(--jrni-space-2, 8px) var(--jrni-space-4, 16px)" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "var(--jrni-space-2, 8px)" }}>
                              <Checkbox checked={V.s.phoneOptional} onChange={V.set.phoneOptional} aria-label="Mark as optional" />
                              <span style={{ fontSize: "13px", color: "var(--jrni-color-text-body)" }}>Mark as optional</span>
                            </div>
                            {V.pv.phoneCountryControls ? (
                              <div style={{ display: "flex", alignItems: "center", gap: "var(--jrni-space-2, 8px)" }}>
                                <Checkbox checked={V.s.countryFlags} onChange={V.set.countryFlags} aria-label="Show country flags" />
                                <span style={{ fontSize: "13px", color: "var(--jrni-color-text-body)" }}>Show country flags</span>
                              </div>
                            ) : null}
                          </div>
                          {/* The country list is a phone-only dependent control, so it belongs
                              inside the phone group, under its own field — not stranded below
                              the postal-address group where it read as unrelated. */}
                          {V.pv.phoneCountryControls ? (
                          <div style={{ padding: "0 0 var(--jrni-space-3, 12px) 2px" }}>
                            <Field label="Countries suggested at the top" error={V.pv.countryError}>
                              <MultiSelect options={V.opts.countries} values={V.s.customCountries} onChange={V.actions.setCountries} width={360} />
                            </Field>
                          </div>
                          ) : null}
                        </>) : null}
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: "var(--jrni-space-1, 4px)" }}>
                        {/* The canonical queue join carries name, phone,
                            email, service, party and answers — there is no
                            address field on it, so on Walk-Ins this asks for
                            something no queue would ever receive. */}
                        <ToggleRow
                          title={V.is.queue ? (<span style={{ display: "inline-flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>Postal address <Pill tone="neutral">Coming soon</Pill></span>) : "Postal address"}
                          description={V.is.queue ? "The queue join does not carry an address, so nothing entered here would reach the desk." : undefined}
                          checked={V.is.queue ? false : V.s.askAddress}
                          onChange={V.is.queue ? undefined : V.set.askAddress}
                          disabled={V.is.queue}
                        />
                        {V.s.askAddress && !V.is.queue ? (<>
                          <div style={{ padding: "0 0 var(--jrni-space-3, 12px) 2px", display: "flex", flexWrap: "wrap", alignItems: "center", gap: "var(--jrni-space-2, 8px) var(--jrni-space-4, 16px)" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "var(--jrni-space-2, 8px)" }}>
                              <Checkbox checked={V.s.addressOptional} onChange={V.set.addressOptional} aria-label="Mark as optional" />
                              <span style={{ fontSize: "13px", color: "var(--jrni-color-text-body)" }}>Mark as optional</span>
                            </div>
                          </div>
                        </>) : null}
                      </div>
                    </Section>
                    <Section title={V.pv.notifSectionTitle}>
                      {V.pv.notQueue ? (<>
                        <ToggleRow title="Let customers choose how they are notified" description={i18nT("shell.allJourneys.addsAnEmailOrText", "Adds an email or text choice to the details step, with your default preselected.")} checked={V.s.notifPref} onChange={V.set.notifPref} />
                        {V.s.notifPref ? (<>
                          <div style={{ padding: "8px 0 12px 2px" }}>
                            <Field label="Default selection">
                              <Dropdown options={V.opts.notif} value={V.s.notifDefault} onChange={V.set.notifDefault} style={{  }} />
                            </Field>
                          </div>
                        </>) : null}
                      </>) : null}
                      {V.is.queue ? (<>
                        <ToggleRow title="Notify by text message" description="The only channel for walk-ins today, so it stays on." checked={true} disabled={true} />
                      </>) : null}
                      {V.is.appt ? (<>
                        <ToggleRow title="Allow additional attendees" description="A parent, guardian or interpreter joining the appointment." checked={V.s.attendees} onChange={V.set.attendees} />
                        {V.s.attendees ? (<>
                          <div style={{ padding: "12px 0 0 2px", display: "flex", flexDirection: "column", gap: "12px" }}>
                            <Field label="What you ask for each attendee" hint="At least one field is needed to identify them.">
                              <MultiSelect options={V.opts.attendeeFields} values={V.s.attendeeFields} onChange={V.set.attendeeFields} width={360} />
                            </Field>
                            <Field label="Maximum additional attendees" hint="Up to 20. The person booking is not counted.">
                              <Input type="number" min={1} max={20} value={V.s.maxParty} onChange={V.set.maxParty} />
                            </Field>
                          </div>
                        </>) : null}
                      </>) : null}
                    </Section>
                    {V.is.docs ? (<>
                      <Section title="Documents">
                        <ToggleRow title={V.is.detailsComingSoon ? (<span style={{ display: "inline-flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>Document checklist <Pill tone="neutral">Coming soon</Pill></span>) : "Document checklist"} description="Ask the customer to bring specific documents. The list is shown on the details step and repeated on the confirmation." checked={V.s.fileUpload} onChange={V.is.detailsComingSoon ? undefined : V.set.fileUpload} disabled={V.is.detailsComingSoon} />
                        {V.s.fileUpload ? (<>
                          <div style={{ display: "flex", flexDirection: "column", gap: "8px", paddingTop: "12px" }}>
                            {(V.s.requiredDocs || []).map((doc, i) => (
                              <div key={i} style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
                                {/* Coming soon on Events/Landing: the saved list stays visible and
                                    stays in state, but nothing in it may be edited — the rows are
                                    author controls too, not just the parent toggle. */}
                                <Input value={doc} placeholder="Photo ID" disabled={V.is.detailsComingSoon} onChange={V.is.detailsComingSoon ? undefined : ((e) => V.actions.setDoc(i, e && e.target ? e.target.value : e))} />
                                <Button variant="ghost" size="sm" disabled={V.is.detailsComingSoon} onClick={V.is.detailsComingSoon ? undefined : (() => V.actions.removeDoc(i))}>Remove</Button>
                              </div>
                            ))}
                            <AddRowButton disabled={V.is.detailsComingSoon} onClick={V.is.detailsComingSoon ? undefined : V.actions.addDoc}>Add a document</AddRowButton>
                          </div>
                        </>) : null}
                      </Section>
                    </>) : null}
                    <Section title="Payment" description="Only appears to the customer when there is something to pay.">
                      <ToggleRow title={V.is.detailsComingSoon ? (<span style={{ display: "inline-flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>Take payment on this step <Pill tone="neutral">Coming soon</Pill></span>) : "Take payment on this step"} description={V.hints.payment} checked={V.s.takePayment} onChange={V.is.detailsComingSoon ? undefined : V.set.takePayment} disabled={V.is.detailsComingSoon} />
                    </Section>
                    <Section title="Known customers">
                      <ToggleRow title={V.is.signedInDetailsComingSoon ? (<span style={{ display: "inline-flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>Fill and lock details for signed-in customers <Pill tone="neutral">Coming soon</Pill></span>) : "Fill and lock details for signed-in customers"} description="When a sign-in integration is set up, the primary person's name, e-mail, phone and address come from their account and cannot be edited." checked={V.is.signedInDetailsComingSoon ? false : V.s.readOnly} onChange={V.is.signedInDetailsComingSoon ? undefined : V.set.readOnly} disabled={V.is.signedInDetailsComingSoon} />
                    </Section>
                    {V.is.queue ? (<>
                      <Section title="Party size">
                        <ToggleRow title="Ask how many people are waiting together" description="One ticket for the group, so a family is not split across two advisers." checked={V.s.partySize} onChange={V.set.partySize} />
                        {V.s.partySize ? (<>
                          <div style={{ paddingTop: "12px" }}>
                            <Field label="Maximum party size">
                              <Input type="number" value={V.s.maxParty} onChange={V.set.maxParty} style={{  }} />
                            </Field>
                          </div>
                        </>) : null}
                      </Section>
                    </>) : null}
                    {/* Places is an event-registration concern, so it belongs on BOTH event
                        journeys (Events list and Landing), not Events alone. */}
                    {V.is.eventOrLanding ? (<>
                      <Section title="Places">
                        <ToggleRow title={V.is.detailsComingSoon ? (<span style={{ display: "inline-flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>Let people book more than one place <Pill tone="neutral">Coming soon</Pill></span>) : "Let people book more than one place"} description={V.is.detailsComingSoon ? i18nT("shell.allJourneys.theRegistrationCarriesOnePlace", "The registration carries one place per submission, so a quantity here would not reach the event.") : i18nT("shell.allJourneys.addsAQuantityPickerTo", "Adds a quantity picker to the details step, up to the event’s own per-registration maximum.")} checked={V.is.detailsComingSoon ? false : V.s.ticketQuantity} onChange={V.is.detailsComingSoon ? undefined : V.set.ticketQuantity} disabled={V.is.detailsComingSoon} />
                        {V.s.ticketQuantity && !V.is.detailsComingSoon ? (<>
                          <div style={{ padding: "12px 0" }}>
                            <Banner tone="neutral" variant="explainer" size="compact" title="The ceiling comes from the event">{V.pv.placeCapNote}
                            </Banner>
                          </div>
                        </>) : null}
                      </Section>
                    </>) : null}
                  </>) : null}
                  {V.is.confirmation ? (<>
                    {V.is.eventOrLanding ? (<>
                      <Section title="The ticket" description="Registrations are checked in on the door with the scanner in Studio.">
                        <ToggleRow title="Show a QR ticket" description="Scannable on arrival, and included in the confirmation email." checked={V.s.showQr} onChange={V.set.showQr} />
                        <ToggleRow title="Name the room" description={i18nT("shell.allJourneys.printsTheRoomHallOr", "Prints the room, hall or studio the session is held in.")} checked={V.s.showResource} onChange={V.set.showResource} />
                        <ToggleRow title="Name the host" description="Who is running the session." checked={V.s.showStaff} onChange={V.set.showStaff} />
                      </Section>
                      <Section title="What registrants can do next">
                        <ToggleRow title="Add to calendar" description={i18nT("shell.allJourneys.offersGoogleOutlookOrAn", "Offers Google, Outlook or an .ics download on the confirmation.")} checked={V.s.addToCalendar} onChange={V.set.addToCalendar} />
                        {/* MANAGE, NOW STORABLE ON AN EVENTS JOURNEY
                            (CC, 2026-09-23). Bound to the SAME `showManage`
                            the seam already reads for kind "events", so the
                            switch and the rendered action cannot disagree —
                            one setting, one meaning, shared with the
                            appointment journey's
                            `steps.confirmation.showManage`.

                            This needed a contract field: the shape behind both
                            event presentation records carried no `showManage`,
                            so until now the toggle would have flipped, saved
                            without error and come back on after a reload.

                            ON is not a promise. A duplicate submit and a
                            tenant with no manage surface both legitimately
                            return no link, and the action stays hidden then —
                            the seam decides that, not this switch.

                            NO RESCHEDULE for events: a registration is for a
                            fixed session and there is no operation to move
                            one. Multi-session switching is its own feature. */}
                        <ToggleRow title="Manage this registration" description="Opens the registrant's own booking page. Shown only when the registration comes back with a secure manage link." checked={V.s.showManage} onChange={V.set.showManage} />
                        <ToggleRow title="Cancel their registration" description="Frees the place for the waiting list." checked={V.s.allowCancelReg} onChange={V.set.allowCancelReg} />
                      </Section>
                    </>) : null}
                    {V.is.queue ? (<>
                      <Section title="The ticket">
                        <ToggleRow title="Show a ticket number" description="Called out on the waiting-room screen, so visitors keep their place without giving a name." checked={V.s.showTicketNumber} onChange={V.set.showTicketNumber} />
                        <ToggleRow title="Show position in line" description={i18nT("shell.allJourneys.theirPlaceInTheQueue", "Their place in the queue, updated as the people ahead are served.")} checked={V.s.showPosition} onChange={V.set.showPosition} />
                        <ToggleRow
                          title={<span style={{ display: "inline-flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>Name the desk <Pill tone="neutral">Coming soon</Pill></span>}
                          description="The entry carries no desk assignment on the public projection, so this would print nothing."
                          checked={false}
                          disabled={true}
                        />
                        <ToggleRow title="Show a QR code" description="Encodes this visitor's own status page on this site, so they can check their place from another device." checked={V.s.showQr} onChange={V.set.showQr} />
                      </Section>
                      <Section title="How you express the wait" description="A range keeps its promise on a busy morning; an exact figure rarely does.">
                        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                          <SelectableTile showIndicator={false} title="A range" description="12–18 minutes" selected={V.is.waitRange} onSelect={V.actions.waitRange} full={true} />
                          <SelectableTile showIndicator={false} title="An exact estimate" description="15 minutes" selected={V.is.waitExact} onSelect={V.actions.waitExact} full={true} />
                          <SelectableTile showIndicator={false} title="Position only" description="No time promised at all" selected={V.is.waitPosition} onSelect={V.actions.waitPosition} full={true} />
                        </div>
                      </Section>
                      <Section title="While they wait">
                        {/* Proactive near-the-front SMS is a queue-side send.
                            There is no published-journey field for it and no
                            operation this page could call, so the toggle and
                            its child threshold are disabled rather than
                            stored as a promise nobody keeps. */}
                        <ToggleRow
                          title={<span style={{ display: "inline-flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>Text them when they are near the front <Pill tone="neutral">Coming soon</Pill></span>}
                          description="Turn-approaching messages are sent by the queue itself; this page cannot switch them on yet."
                          checked={false}
                          disabled={true}
                        />
                        <div style={{ padding: "12px 0" }}>
                          <Field label="Send the text when this many are ahead">
                            <Input type="number" value={V.s.notifyAhead} disabled={true} style={{  }} />
                          </Field>
                        </div>
                        <ToggleRow title="Let them leave the queue" description="Posts a real leave, frees the slot, and shows the visitor that their ticket is finished." checked={V.s.allowLeaveQueue} onChange={V.set.allowLeaveQueue} />
                      </Section>
                    </>) : null}
                    {V.is.appt ? (<>
                      <Section title="What the confirmation shows">
                        <ToggleRow title="Staff member" description="Name and photo of the adviser." checked={V.s.showStaff} onChange={V.set.showStaff} />
                        <ToggleRow title={i18nT("shell.allJourneys.roomOrDesk", "Room or desk")} description={i18nT("shell.allJourneys.theRoomDeskOrBooth", "The room, desk or booth the appointment is assigned to.")} checked={V.s.showResource} onChange={V.set.showResource} />
                        <ToggleRow title="QR check-in code" description="Scanned at reception on arrival, and repeated in the confirmation email." checked={V.s.showQr} onChange={V.set.showQr} />
                      </Section>
                      <Section title="What customers can do next">
                        <ToggleRow title="Add to calendar" description={i18nT("shell.allJourneys.offersGoogleOutlookOrAn", "Offers Google, Outlook or an .ics download on the confirmation.")} checked={V.s.addToCalendar} onChange={V.set.addToCalendar} />
                        <ToggleRow title="Reschedule" description={i18nT("shell.allJourneys.letsTheCustomerMoveThe", "Lets the customer move the appointment themselves, without calling in.")} checked={V.s.allowReschedule} onChange={V.set.allowReschedule} />
                        <ToggleRow title="Manage this appointment" description="Opens the customer's own appointment page. Shown only when the booking comes back with a secure manage link." checked={V.s.showManage} onChange={V.set.showManage} />
                        <ToggleRow title="Cancel appointment" description={i18nT("shell.allJourneys.letsTheCustomerCancelFrom", "Lets the customer cancel from the confirmation, which frees the slot straight away.")} checked={V.s.allowCancel} onChange={V.set.allowCancel} />
                        {V.s.allowCancel ? (<>
                          <div style={{ padding: "12px 0 0", display: "flex", flexDirection: "column", gap: "16px" }}>
                            <Field label="Refund on a late cancellation">
                              <Dropdown options={V.opts.refund} value={V.s.refundMode} onChange={V.set.refundMode} style={{  }} />
                            </Field>
                          </div>
                        </>) : null}
                      </Section>
                    </>) : null}
                  </>) : null}
                  {V.is.landing && V.is.tplCustom ? (<>
                    {/* The canvas's controls belong in Content blocks, the panel
                       that has always held what goes ON the page — Page layout
                       stays about the template and the chrome around it
                       (KP, 2026-09-17). Add and Selected block are tabs rather
                       than new chrome; the tab follows the canvas selection. */}
                    <Section title="Your layout">
                      <div style={{ display: "flex", gap: "6px", paddingBottom: "14px" }}>
                        <Button variant={V.is.clAddTab ? "primary" : "ghost"} size="sm" aria-pressed={V.is.clAddTab} onClick={V.actions.clAddTabPick}>Add
                        </Button>
                        <Button variant={V.is.clBlockTab ? "primary" : "ghost"} size="sm" aria-pressed={V.is.clBlockTab} onClick={V.actions.clBlockTabPick}>Selected block
                        </Button>
                        <span style={{ flex: "1 1 auto" }}></span>
                        <Button variant="ghost" size="sm" aria-label="Undo" onClick={V.actions.clUndo} disabled={!V.is.clCanUndo}>↶
                        </Button>
                        <Button variant="ghost" size="sm" aria-label="Redo" onClick={V.actions.clRedo} disabled={!V.is.clCanRedo}>↷
                        </Button>
                      </div>
                      {V.is.clAddTab ? (<>
                        <CustomLayoutPalette brandColor={V.pv.clBrand} onAdd={V.actions.clAdd} />
                      </>) : null}
                      {V.is.clBlockTab ? (<>
                        <CustomLayoutInspector blocks={V.pv.clBlocks} device={V.pv.clDevice} selectedUid={V.pv.clSel} brandColor={V.pv.clBrand} onChange={V.actions.clChange} onSelect={V.actions.clSelect} />
                      </>) : null}
                    </Section>
                  </>) : null}
                  {V.is.landing && !V.is.tplCustom ? (<>
                    <Section title="Blocks, order and content">
                      <div style={{ display: "flex", flexDirection: "column" }}>
                        {(V.pv.blockRows || []).map((bk, __i2) => (<React.Fragment key={bk.key ?? bk.id ?? __i2}>
                          <div style={{ borderTop: "1px solid var(--jrni-color-neutral-6)" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "10px", padding: "8px 0" }}>
                              <button type="button" onClick={bk.onExpand} aria-expanded={bk.open} style={{ flex: "1 1 auto", display: "flex", alignItems: "center", gap: "8px", padding: "0", background: "none", border: "0", cursor: "pointer", fontFamily: "inherit", textAlign: "left" }}>
                                <span style={{ flex: "0 0 12px", display: "flex", justifyContent: "center", color: "var(--jrni-color-text-soft)", transform: bk.caret }}>{bk.caretGlyph}</span>
                                <span style={{ flex: "1 1 auto", fontSize: "14px", color: bk.fg }}>{bk.label}</span>
                                <span style={{ flex: "0 0 auto", fontSize: "12px", color: "var(--jrni-color-text-soft)" }}>{bk.meta}</span>
                              </button>
                              <Button variant="ghost" size="sm" aria-label="Move up" onClick={bk.onUp} disabled={bk.first}>↑
                              </Button>
                              <Button variant="ghost" size="sm" aria-label="Move down" onClick={bk.onDown} disabled={bk.last}>↓
                              </Button>
                              <Toggle checked={bk.on} onChange={bk.onToggle} aria-label={bk.label} />
                            </div>
                            {bk.open ? (<>
                              <div style={{ display: "flex", flexDirection: "column", gap: "12px", padding: "4px 0 16px 20px" }}>
                                {bk.isDesc ? (<>
                                  <Field label="Description">
                                    <Textarea value={V.s.lpDescText} onChange={V.actions.setDescText} rows={4} full={true} />
                                  </Field>
                                  <SectionLabel tone="soft">Agenda
                                  </SectionLabel>
                                  {(V.pv.agendaEdit || []).map((ag, __i3) => (<React.Fragment key={ag.key ?? ag.id ?? __i3}>
                                    <div style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
                                      <Input value={ag.time} onChange={ag.setTime} placeholder="9:00 AM" style={{  }} />
                                      <Input value={ag.what} onChange={ag.setWhat} placeholder="What happens" full={true} />
                                      <Button variant="link" size="sm" danger={true} onClick={ag.onRemove}>Remove
                                      </Button>
                                    </div>
                                  </React.Fragment>))}
                                  <AddRowButton onClick={V.actions.addAgenda}>Add an agenda line
                                  </AddRowButton>
                                </>) : null}
                                {bk.isSpeakers ? (<>
                                  {(V.pv.speakerEdit || []).map((sp, __i4) => (<React.Fragment key={sp.key ?? sp.id ?? __i4}>
                                    <div style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
                                      <Input value={sp.name} onChange={sp.setName} placeholder="Name" full={true} />
                                      <Input value={sp.role} onChange={sp.setRole} placeholder="Role" full={true} />
                                      <Button variant="link" size="sm" danger={true} onClick={sp.onRemove}>Remove
                                      </Button>
                                    </div>
                                  </React.Fragment>))}
                                  <AddRowButton onClick={V.actions.addSpeaker}>Add a speaker
                                  </AddRowButton>
                                </>) : null}
                                {bk.isIncluded ? (<>
                                  {(V.pv.includedEdit || []).map((inc, __i5) => (<React.Fragment key={inc.key ?? inc.id ?? __i5}>
                                    <div style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
                                      <Input value={inc.text} onChange={inc.setText} placeholder="What they get" full={true} />
                                      <Button variant="link" size="sm" danger={true} onClick={inc.onRemove}>Remove
                                      </Button>
                                    </div>
                                  </React.Fragment>))}

                                  <AddRowButton onClick={V.actions.addIncluded}>Add a line
                                  </AddRowButton>
                                </>) : null}
                                {bk.isTickets ? (<>
                                  <p style={{ margin: "0", fontSize: "13px", lineHeight: "1.5", color: "var(--jrni-color-text-soft)" }}>Ticket types, prices and their small print come from this event's configuration in Studio. The page shows whatever the event sells.</p>
                                  <div style={{ display: "flex", flexDirection: "column" }}>
                                    {(V.pv.ticketEdit || []).map((tk, __i6) => (<React.Fragment key={tk.key ?? tk.id ?? __i6}>
                                      <div style={{ display: "flex", alignItems: "baseline", gap: "12px", padding: "8px 0", borderTop: "1px solid var(--jrni-color-neutral-6)" }}>
                                        <span style={{ flex: "1 1 auto", fontSize: "14px", fontWeight: "600", color: "var(--jrni-color-text-strong)" }}>{tk.name}</span>
                                        <span style={{ flex: "0 0 auto", fontSize: "14px", fontWeight: "700" }}>{tk.price}</span>
                                      </div>
                                      <div style={{ fontSize: "12px", color: "var(--jrni-color-text-soft)", paddingBottom: "4px" }}>{tk.note}</div>
                                    </React.Fragment>))}
                                  </div>
                                  {V.pv.ticketsEmpty ? (<>
                                    <InlineEmptyState title={V.pv.ticketsEmptyTitle} description={V.pv.ticketsEmptyBody} />
                                  </>) : null}
                                </>) : null}
                                {bk.isFaq ? (<>
                                  {(V.pv.faqEdit || []).map((fq, __i7) => (<React.Fragment key={fq.key ?? fq.id ?? __i7}>
                                    <div style={{ display: "flex", flexDirection: "column", gap: "6px", paddingBottom: "8px" }}>
                                      <div style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
                                        <Input value={fq.q} onChange={fq.setQ} placeholder="Question" full={true} />
                                        <Button variant="link" size="sm" danger={true} onClick={fq.onRemove}>Remove
                                        </Button>
                                      </div>
                                      <Textarea value={fq.a} onChange={fq.setA} rows={2} placeholder="Answer" full={true} />
                                    </div>
                                  </React.Fragment>))}
                                  <AddRowButton onClick={V.actions.addFaq}>Add a question
                                  </AddRowButton>
                                </>) : null}
                                {bk.isSponsors ? (<>
                                  <ToggleRow title={i18nT("shell.allJourneys.sponsorTierGroupToggle", "Group by sponsor tier")} description={i18nT("shell.allJourneys.sponsorTierGroupToggleHelp", "Gold first, then silver, then supporters, each under its own heading.")} checked={V.s.sponsorTiers} onChange={V.actions.setSponsorTiers} />
                                  {(V.pv.sponsorEdit || []).map((sr, __i8) => (<React.Fragment key={sr.key ?? sr.id ?? __i8}>
                                    <div style={{ display: "flex", gap: "12px", alignItems: "flex-start", paddingBottom: "12px", borderBottom: "1px solid var(--jrni-color-neutral-6)" }}>
                                      <div style={{ flex: "0 0 120px" }}>
                                        <ImageDropField value={sr.logo} onChange={sr.setLogo} aspect={2.4} maxWidth={120} placeholder="Logo" />
                                      </div>
                                      <div style={{ flex: "1 1 auto", minWidth: "0", display: "flex", flexDirection: "column", gap: "8px" }}>
                                        <Input value={sr.name} onChange={sr.setName} placeholder={i18nT("shell.allJourneys.sponsorNamePlaceholder", "Sponsor name")} full={true} />
                                        <Dropdown options={V.opts.sponsorTier} value={sr.tier} onChange={sr.setTier} aria-label={i18nT("shell.allJourneys.sponsorTierFieldLabel", "Sponsor tier")} full={true} />
                                        <div>
                                          <Button variant="link" size="sm" danger={true} onClick={sr.onRemove}>Remove
                                          </Button>
                                        </div>
                                      </div>
                                    </div>
                                  </React.Fragment>))}
                                  <AddRowButton onClick={V.actions.addSponsor}>{i18nT("shell.allJourneys.addSponsor", "Add a sponsor")}
                                  </AddRowButton>
                                </>) : null}
                                {bk.isShare ? (<>
                                  <Field label="Channels">
                                    <MultiSelect options={V.opts.shareChannels} values={V.s.lpShareChannels} onChange={V.actions.setShareChannels} width={360} />
                                  </Field>
                                </>) : null}
                                {bk.isRelated ? (<>
                                  <Field label="How related events are chosen">
                                    <Dropdown options={V.opts.relatedRule} value={V.s.relatedRule} onChange={V.actions.setRelatedRule} style={{  }} />
                                  </Field>
                                  {V.pv.relatedAuto ? (<>
                                    <Field label="How many to show">
                                      <SegmentedControl options={V.opts.relatedCount} value={V.s.relatedCount} onChange={V.actions.setRelatedCount} />
                                    </Field>
                                  </>) : null}
                                  {V.pv.relatedManualMode ? (<>
                                    <Field label="Pick the events">
                                      <MultiSelect options={V.pv.eventOptions} values={V.s.relatedManual} onChange={V.actions.setRelatedManual} width={360} />
                                    </Field>
                                  </>) : null}
                                  <p style={{ margin: "0", fontSize: "13px", color: "var(--jrni-color-text-soft)" }}>{V.pv.relatedNote}</p>
                                </>) : null}
                                {bk.isDerived ? (<>
                                  <p style={{ margin: "0", fontSize: "13px", lineHeight: "1.5", color: "var(--jrni-color-text-soft)" }}>{bk.derivedNote}</p>
                                </>) : null}
                              </div>
                            </>) : null}
                          </div>
                        </React.Fragment>))}
                      </div>
                    </Section>
                    <Section title="When the event is full">
                      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                        <SelectableTile showIndicator={false} title="Waiting-list form" description="Name and e-mail, and they are told their position on the list." selected={V.is.soldWaitlist} onSelect={V.actions.soldWaitlist} full={true} />
                        <SelectableTile showIndicator={false} title="Related events only" description="No list to join — send them to the next comparable event." selected={V.is.soldRelated} onSelect={V.actions.soldRelated} full={true} />
                        <SelectableTile showIndicator={false} title="Both" description="The waiting list first, related events underneath." selected={V.is.soldBoth} onSelect={V.actions.soldBoth} full={true} />
                      </div>
                      <div style={{ paddingTop: "12px" }}>
                        <ToggleRow title="Preview the fully-booked page" description="Shows what a visitor sees once the last place goes." checked={V.pv.soldOutPreview} onChange={V.actions.setSoldOutPreview} />
                      </div>
                    </Section>
                  </>) : null}
                  {V.is.kioskEntry ? (<>
                    <Section title="Where it can send people" description="Each tile hands off to the journey that owns it. Turn one off and the tile goes — the others grow to fill the space.">
                      <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                        <ToggleRow title="Book an appointment" description="Opens the Appointments journey." checked={V.s.kioskAppts} onChange={V.set.kioskAppts} />
                        <ToggleRow title="Join the walk-in line" description="Opens the Walk-Ins journey." checked={V.s.kioskWalkin} onChange={V.set.kioskWalkin} />
                        <ToggleRow title="What's on" description="Opens the Event List Page journey." checked={V.s.kioskEvents} onChange={V.set.kioskEvents} />
                        <ToggleRow title="Check in" description="Stays on the kiosk — the only destination it handles itself." checked={V.s.kioskCheckin} onChange={V.set.kioskCheckin} />
                      </div>
                      <div style={{ paddingTop: "16px" }}>
                        <Field label="Tile size" hint="Large is right at arm's length on a wall. Compact only if you have five or more tiles.">
                          <SegmentedControl options={V.opts.kioskTileSize} value={V.s.kioskTileSize} onChange={V.set.kioskTileSize} size="sm" />
                        </Field>
                      </div>
                    </Section>
                    <Section title="Live information" description="Shown on the tiles and in the strip beneath them. All of it is read-only — nobody has to touch the screen to get the answer.">
                      <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                        <ToggleRow title="Walk-in wait time" description="Minutes and how many people are ahead. The single most asked question in a lobby." checked={V.s.kioskWaitTime} onChange={V.set.kioskWaitTime} />
                        <ToggleRow disabled title={<span style={{ display: "inline-flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>Next available appointment <Pill tone="neutral">Coming soon</Pill></span>} description="The soonest slot across every service at this campus. Waiting on an availability read that spans every service; until then the kiosk does not show one." checked={V.s.kioskNextSlot} onChange={V.set.kioskNextSlot} />
                        <ToggleRow title="Hours and address" description="Answers the question people ask on their way back out." checked={V.s.kioskHours} onChange={V.set.kioskHours} />
                      </div>
                    </Section>
                  </>) : null}
                  {V.is.kioskCheckin ? (<>
                    <Section title="How someone identifies themselves" description="Keep at least one method that works without the reader — it is the thing most likely to fail.">
                      <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                        <ToggleRow title="Scan a QR code" description="From the confirmation email or a printout." checked={V.s.ckQr} onChange={V.set.ckQr} />
                        <ToggleRow title="Scan a wallet pass" description="Apple Wallet and Google Wallet passes held up to the reader." checked={V.s.ckWallet} onChange={V.set.ckWallet} />
                        <ToggleRow title="Tap a wallet pass (NFC)" description="Needs an NFC reader in the enclosure. Faster than scanning and works in poor light." checked={V.s.ckNfc} onChange={V.set.ckNfc} />
                        <ToggleRow title="Type a booking reference" description="The fallback when the reader is down." checked={V.s.ckRef} onChange={V.set.ckRef} />
                        <ToggleRow title="Search by name" description="Shows only today's bookings at this campus, and never a full name list." checked={V.s.ckName} onChange={V.set.ckName} />
                      </div>
                      <div style={{ paddingTop: "16px" }}>
                        <Field label="Method shown first">
                          <SegmentedControl options={V.opts.ckDefault} value={V.s.ckDefault} onChange={V.set.ckDefault} size="sm" />
                        </Field>
                      </div>
                    </Section>
                    <Section title="After they check in" description="The screen returns to the entry page on its own — nothing of theirs is left up for the next person.">
                      <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                        <ToggleRow title="Show their place in the line" description="Walk-ins only — someone with a booked appointment has a time, not a queue position, so this never shows for them." checked={V.s.ckShowQueuePos} onChange={V.set.ckShowQueuePos} />
                      </div>
                      <div style={{ paddingTop: "16px" }}>
                        <Field label="Hold the confirmation for" hint={V.pv.ckDwellNote}>
                          <Input type="number" value={V.s.ckDoneDwell} onChange={V.set.ckDoneDwell} trailing="sec" style={{  }} />
                        </Field>
                      </div>
                    </Section>
                  </>) : null}
                  {V.is.scan ? (<>
                    <Section title="When a code is scanned on My Day"
                      description={"A member scans the code on a customer's confirmation. This is what happens next for " + V.scan.typeLabel + " — it is a tenant setting, not the member's choice."}>
                      {V.scan.loading ? (
                        <Banner tone="info" size="compact" title="Reading the scan settings…">This is the tenant's manage-settings document, not this journey's page record.
                        </Banner>
                      ) : V.scan.error ? (<>
                        <Banner tone="danger" size="compact" title="The scan settings could not be read">{V.scan.err + ". Nothing is shown rather than a default that is not the tenant's."}
                        </Banner>
                        <div style={{ paddingTop: "12px" }}>
                          <Button variant="secondary" onClick={V.scan.onRetry}>Try again</Button>
                        </div>
                      </>) : (<>
                        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                          <SelectableTile showIndicator={false} full={true} selected={V.scan.isOpen} onSelect={V.scan.setOpen}
                            title="Open the details" description="Land on the element and let the member decide. Nothing is written by the scan." />
                          <SelectableTile showIndicator={false} full={true} selected={V.scan.isCheckIn} onSelect={V.scan.setCheckIn}
                            title="Check in directly" description="The scan records the check-in: arrived for an appointment, checked in for an event registration or a queue entry." />
                        </div>
                        <div style={{ paddingTop: "16px" }}>
                          <ToggleRow title="ID check before check-in"
                            description="The member compares the record with an identity document and confirms the match before the check-in is recorded. The attestation is stored with it."
                            checked={V.scan.idCheck} disabled={!V.scan.isCheckIn} onChange={V.scan.setIdCheck} />
                          {V.scan.isOpen ? (
                            <div style={{ paddingTop: "8px", fontSize: "12.5px", color: "var(--jrni-color-text-soft)", lineHeight: 1.5 }}>
                              An ID check only has something to gate once a scan writes a check-in.
                            </div>
                          ) : null}
                        </div>
                        <div style={{ paddingTop: "18px", display: "flex", alignItems: "center", gap: "12px" }}>
                          <Button onClick={V.scan.onSave} disabled={!V.scan.dirty || V.scan.saving}>
                            {V.scan.saving ? "Saving…" : "Save scan settings"}
                          </Button>
                          <span style={{ fontSize: "12.5px", color: "var(--jrni-color-text-soft)" }}>{V.scan.foot}</span>
                        </div>
                      </>)}
                    </Section>
                  </>) : null}
                  {V.is.customtext ? (<>
                    <Section title="What to replace" description="Every heading and message this journey shows. Leave a field empty and the customer sees the wording on the left.">
                      <Field label="Show">
                        <SegmentedControl options={V.opts.ctFilter} value={V.ui.ctFilter} onChange={V.actions.setCtFilter} size="sm" aria-label="Which fields to show" />
                      </Field>
                    </Section>
                    {(V.ct.groups || []).map((grp, __i9) => (<React.Fragment key={grp.key ?? grp.id ?? __i9}>
                      <div style={{ border: "1px solid var(--jrni-color-surface-border)", borderRadius: "var(--jrni-radius-md)", background: "var(--jrni-color-surface-card)", overflow: "hidden" }}>
                        <button type="button" onClick={grp.onToggle} style={{ display: "flex", alignItems: "center", gap: "10px", width: "100%", padding: "13px 16px", border: "none", background: "transparent", cursor: "pointer", textAlign: "left", color: "var(--jrni-color-text-strong)" }}>
                          <span style={{ flex: "0 0 auto", display: "inline-flex", width: "14px", color: "var(--jrni-color-text-soft)", transform: grp.caret, transition: "transform 120ms ease" }}>▸</span>
                          <span style={{ flex: "1 1 auto", minWidth: "0", fontSize: "14px", fontWeight: "600" }}>{grp.label}</span>
                          {grp.hasCustom ? (<>
                            <span style={{ flex: "0 0 auto", width: "7px", height: "7px", borderRadius: "999px", background: "var(--jrni-color-primary-1)" }}></span>
                          </>) : null}
                          <span style={{ flex: "0 0 auto", fontSize: "12px", color: "var(--jrni-color-text-soft)" }}>{grp.countLabel}</span>
                        </button>
                        {grp.open ? (<>
                          <div style={{ padding: "4px 16px 16px", display: "flex", flexDirection: "column", gap: "18px", borderTop: "1px solid var(--jrni-color-surface-border)" }}>
                            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "20px", paddingTop: "12px" }}>
                              <span style={{ fontSize: "11px", fontWeight: "600", letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--jrni-color-text-soft)" }}>Current default</span>
                              <span style={{ fontSize: "11px", fontWeight: "600", letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--jrni-color-text-soft)" }}>Your wording</span>
                            </div>
                            {(grp.fields || []).map((fld, __i10) => (<React.Fragment key={fld.key ?? fld.id ?? __i10}>
                              <div style={{ display: "flex", flexDirection: "column", gap: "7px" }}>
                                <div style={{ display: "flex", alignItems: "baseline", gap: "10px" }}>
                                  <span style={{ flex: "1 1 auto", minWidth: "0", fontSize: "13px", fontWeight: "600" }}>{fld.label}</span>
                                  {fld.changed ? (<>
                                    <Button variant="ghost" size="sm" onClick={fld.onRevert}>Revert
                                    </Button>
                                  </>) : null}
                                </div>
                                {fld.hint ? (<>
                                  <span style={{ fontSize: "12px", lineHeight: "1.45", color: "var(--jrni-color-text-soft)", textWrap: "pretty" }}>{fld.hint}</span>
                                </>) : null}
                                <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "20px", alignItems: "start" }}>
                                  <Textarea value={fld.def} rows={2} autoGrow={true} style={{  }} aria-label="Current default" />
                                  <Textarea value={fld.value} onChange={fld.onChange} rows={2} autoGrow={true} placeholder="Leave empty to keep the default" style={{  }} aria-label={fld.label} />
                                </div>
                              </div>
                            </React.Fragment>))}
                          </div>
                        </>) : null}
                      </div>
                    </React.Fragment>))}
                  </>) : null}
                  {V.is.launch ? (<>
                    <Section title="Public link">
                      {V.pv.publicUrl ? (<>
                        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                          <CopyableUrl url={V.pv.publicUrl} />
                          {V.pv.publicUrlProvisional ? (<>
                            <p style={{ margin: "0", fontSize: "13px", lineHeight: "1.5", color: "var(--jrni-color-text-soft)", textWrap: "pretty" }}>{V.pv.publicUrlNote}</p>
                          </>) : null}
                        </div>
                      </>) : (<>
                        <Banner tone="neutral" variant="explainer" size="compact" title="No address yet">This journey has no public address on the server yet. Save it once and the link appears here.
                        </Banner>
                      </>)}
                    </Section>
                    <Section title="Embed on your own page">
                      <div style={{ display: "flex", flexDirection: "column", gap: "14px", padding: "4px 0 16px" }}>
                        <Field label="Width">
                          <div style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
                            <SegmentedControl options={V.opts.sizeUnit} value={V.s.embedWidthUnit} onChange={V.set.embedWidthUnit} size="sm" aria-label="Width unit" />
                            {V.pv.pxWidth ? (<>
                              <Input type="number" value={V.s.embedWidth} onChange={V.set.embedWidth} trailing="px" style={{ width: "132px", flex: "0 0 auto" }} />
                            </>) : null}
                          </div>
                        </Field>
                        <Field label="Height" hint={V.pv.resizeHint}>
                          <div style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
                            <SegmentedControl options={V.opts.embedResize} value={V.s.embedResize} onChange={V.set.embedResize} size="sm" aria-label="Height mode" />
                            {V.pv.fixedHeight ? (<>
                              <Input type="number" value={V.s.embedHeight} onChange={V.set.embedHeight} trailing="px" style={{ width: "132px", flex: "0 0 auto" }} />
                            </>) : null}
                          </div>
                        </Field>
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                          <SectionLabel tone="soft">Paste this where the journey should appear
                          </SectionLabel>
                          <div style={{ flex: "1 1 auto" }}></div>
                          <Button variant="secondary" size="sm" iconLeft={V.icons.copy} onClick={V.actions.copyEmbed} disabled={!V.pv.publicUrl}>{V.pv.copyLabel}
                          </Button>
                        </div>
                        {V.pv.publicUrl ? (<>
                          <pre style={{ margin: "0", padding: "14px", borderRadius: "var(--jrni-radius-sm)", background: "var(--jrni-color-neutral-1)", color: "var(--jrni-color-base-white)", fontFamily: "var(--jrni-font-family-mono)", fontSize: "12px", lineHeight: "1.6", overflowX: "auto", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{V.pv.embedCode}</pre>
                          <p style={{ margin: "0", fontSize: "12px", lineHeight: "1.5", color: "var(--jrni-color-text-soft)", textWrap: "pretty" }}>Includes every permission the journey can ask for and a strict sandbox allowlist. Delete any line your page does not need.</p>
                        </>) : (<>
                          <Banner tone="neutral" variant="explainer" size="compact" title="No address yet">This journey has no public address on the server yet, so there is no embed code to copy. Save it once and the code appears here.
                          </Banner>
                        </>)}
                      </div>
                    </Section>
                  </>) : null}
                </div>
              </div>
            </>) : null}
            {V.pv.previewPane ? (<>
            <section style={{ flex: "1 1 620px", minWidth: this.isPublic() || this.state.previewOnly || V.pv.onePane ? "0" : "440px", display: "flex", flexDirection: "column", minHeight: "0", background: "var(--jrni-color-neutral-6)" }}>
              {V.pv.chrome ? (<>
                <div style={{ flex: "0 0 auto", display: "flex", alignItems: "center", gap: "12px", rowGap: "8px", flexWrap: "wrap", padding: "10px 16px", borderBottom: "1px solid var(--jrni-color-surface-border)", background: "var(--jrni-color-surface-card)" }}>
                  {V.pv.onePane ? null : (
                    <Button variant="ghost" size="sm" iconOnly={V.icons.panel} aria-label={V.pv.paneLabel} onClick={V.actions.togglePane} />
                  )}
                  {tplView ? (
                    <TemplateCanvasTitle controller={tplCtrl} />
                  ) : (<>
                  <span style={{ fontSize: "13px", fontWeight: "600", color: "var(--jrni-color-text-strong)" }}>{V.pv.stepTitle}</span>
                  {this.sampleNote() ? (
                    <span style={{ fontSize: "12px", color: "var(--jrni-color-text-soft)" }}>{this.sampleNote()}</span>
                  ) : null}
                  </>)}
                  {tplCtrl ? <TemplateDesignSwitch controller={tplCtrl} /> : null}
                  <div style={{ flex: "1 1 auto" }}></div>
                  {tplView ? null : (<>
                  <Button variant="secondary" size="sm" onClick={V.actions.prevStep} disabled={V.pv.atFirstStep}>Back
                  </Button>
                  <Button hierarchy="primary" size="sm" onClick={V.actions.nextStep} disabled={V.pv.atLastStep || V.pv.continueBlocked} title={V.pv.continueLockReason || undefined}>Next
                  </Button>
                  <div style={{ width: "1px", height: "24px", background: "var(--jrni-color-surface-border)" }}></div>
                  </>)}
                  <DevicePreviewToggle value={V.ui.device} onChange={V.actions.setDevice} size="sm" />
                  <div style={{ display: "flex", alignItems: "center", gap: "4px" }} role="group" aria-label={i18nT("shell.allJourneys.zoomPreview", "Zoom preview")}>
                    <Button variant="ghost" size="sm" iconOnly="−" aria-label={i18nT("shell.allJourneys.zoomOut", "Zoom out")} onClick={() => V.actions.setPaneZoom(V.pv.paneZoom - 10)} disabled={V.pv.paneZoom <= 50} />
                    <button type="button" onClick={() => V.actions.setPaneZoom(100)} title={i18nT("shell.allJourneys.resetTo100", "Reset to 100%")} style={{ font: "inherit", fontSize: "12px", fontWeight: 600, color: "var(--jrni-color-text-soft)", background: "none", border: "none", cursor: "pointer", minWidth: "38px", textAlign: "center" }}>{V.pv.paneZoom}%</button>
                    <Button variant="ghost" size="sm" iconOnly="+" aria-label={i18nT("shell.allJourneys.zoomIn", "Zoom in")} onClick={() => V.actions.setPaneZoom(V.pv.paneZoom + 10)} disabled={V.pv.paneZoom >= 150} />
                  </div>
                </div>
              </>) : null}
              {tplView ? (
                <CustomTemplateCanvas controller={tplCtrl} device={V.ui.device} widthPx={V.pv.deviceWpx} heightPx={V.pv.deviceHpx} zoom={V.pv.paneZoom} onGoToSettings={() => this.goTemplateSettings()} />
              ) : (<>
              {tplCtrl ? <TemplateCheckNotice controller={tplCtrl} /> : null}
              <div className="apd-scroll" style={{ flex: "1 1 auto", overflow: "auto", padding: "12px", display: "flex", justifyContent: "center", alignItems: "flex-start" }}>
                {/* Public mode never draws the fixed-size device frame (CC, 2026-10-04): a visitor's phone IS the device, and
                    the 393 px frame + 12 px padding clipped the page on a 390 px screen. Studio's preview is unchanged. */}
                <div style={{ width: (V.ui.device === "desktop" || this.isPublic()) ? "100%" : (V.pv.deviceWpx * V.pv.paneZoom / 100) + "px", height: (V.ui.device === "desktop" || this.isPublic()) ? undefined : (V.pv.deviceHpx ? (V.pv.deviceHpx * V.pv.paneZoom / 100) + "px" : undefined), minHeight: (V.ui.device === "desktop" || this.isPublic()) ? "100%" : undefined, boxSizing: "border-box", overflow: (V.ui.device === "desktop" || this.isPublic()) ? undefined : "hidden" } as any}>
                <div className="apd-devframe" style={{ width: (V.ui.device === "desktop" || this.isPublic()) ? "100%" : V.pv.deviceWpx + "px", height: (V.ui.device === "desktop" || this.isPublic()) ? undefined : (V.pv.deviceHpx ? V.pv.deviceHpx + "px" : undefined), minHeight: (V.ui.device === "desktop" || this.isPublic()) ? "100%" : undefined, boxSizing: "border-box", transform: "scale(" + (V.pv.paneZoom / 100) + ")", transformOrigin: "top left", overflowY: (V.ui.device === "desktop" || this.isPublic()) ? undefined : "auto" } as any}>
                {/* Walk-Ins draws through the SAME component the standalone
                    join page mounts (`WalkInJourney`, also used by
                    public-walkins.tsx), so the designer preview and the
                    customer page are one implementation rather than two that
                    have to be kept looking alike. `preview` is passed
                    explicitly and is NOT `mode`: this preview mounts the
                    Designer in `mode: "public"` for the customer chrome, so a
                    component that read `mode` would treat every designer
                    preview as a real customer and post joins into the
                    tenant's own queue. */}
                {V.is.queue ? (
                  <WalkInJourney

                    preview={true}
                    embedded={true}
                    frameWidth={V.pv.frameWidth}
                    frameHeight={V.pv.frameHeight}
                    slug={String(V.s.slug || WALKIN_DEFAULT_SLUG)}
                    config={V.pv.walkInConfig}
                    customText={V.pv.walkInText}
                    presentation={V.pv.walkInPresentation}
                    queues={V.pv.walkInQueues}
                    loading={V.pv.walkInQueuesLoading}
                    error={V.pv.walkInError}
                    step={V.pv.walkInStep}

                    left={V.pv.walkInLeft}
                    onStepChange={V.actions.setWalkInStep}
                  />
                ) : (
                <div data-testid="appointment-journey" data-mode={V.pv.rootMode} style={{ width: "100%", maxWidth: V.pv.frameWidth, height: V.pv.frameHeight || undefined, minHeight: V.pv.frameHeight ? undefined : "100%", overflowY: V.pv.frameHeight ? "auto" : "hidden", boxSizing: "border-box", containerType: "inline-size", containerName: "journey-preview", display: "flex", flexDirection: "column", background: "var(--jrni-color-base-white)", border: "1px solid var(--jrni-color-surface-border)", borderRadius: "var(--jrni-radius-lg)", boxShadow: "var(--jrni-shadow-lg)", overflowX: "hidden" }}>
                  {V.pv.brandedHeader ? (
                    <BrandedHeaderMount studio={!this.isPublic()} tenantName={this.tenantDisplayName() || undefined} trailing={V.pv.showPageTitle ? V.pv.pageTitle : undefined} />
                  ) : V.pv.brandHeader ? (<>
                    <div style={{ display: "flex", alignItems: "center", gap: "12px", rowGap: "8px", flexWrap: "wrap", padding: "14px 20px", borderBottom: "1px solid var(--jrni-color-surface-border)", background: V.pv.brand }}>
                      <span style={{ fontSize: "15px", fontWeight: "700", color: "#FFFFFF", letterSpacing: "-0.01em" }}>{this.tenantDisplayName() || this.state.apptJourney?.brand?.logoText || ""}</span>
                      <div style={{ flex: "1 1 auto" }}></div>
                      {V.pv.showPageTitle ? (<>
                        <span style={{ fontSize: "12px", color: "rgba(255, 255, 255, 0.85)" }}>{V.pv.pageTitle}</span>
                      </>) : null}
                    </div>
                  </>) : null}
                  {V.pv.progressTitles ? (<>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 20px", padding: "16px 24px 0", minWidth: 0 }}>
                      {(V.pv.steps || []).map((st, __i11) => (<React.Fragment key={st.key ?? st.id ?? __i11}>
                        <button type="button" onClick={st.onClick} disabled={st.locked} title={st.lockReason || undefined} aria-disabled={st.locked ? "true" : undefined} style={{ background: "none", border: "0", padding: "0 0 10px", cursor: st.locked ? "not-allowed" : "pointer", fontFamily: "inherit", fontSize: "13px", textAlign: "left", borderBottom: `2px solid ${st.underline}`, color: st.fg, opacity: st.opacity, fontWeight: st.weight }}>{st.label}</button>
                      </React.Fragment>))}
                    </div>
                  </>) : null}
                  {V.pv.progressNumbers ? (<>
                    {/* Fits the page, never scrolls (KP, 2026-09-16): on a phone the
                        five labelled steps overflowed and the student had to scroll
                        the header sideways to see step 4. Connectors now FLEX to fill
                        the width, and below 480px only the current step keeps its
                        label — the others are chips (aria-label carries the name). */}
                    <div className="apd-steps" style={{ display: "flex", alignItems: "center", gap: "8px", padding: "16px 24px 4px", minWidth: 0 }}>
                      {(V.pv.steps || []).map((st, __i12) => (<React.Fragment key={st.key ?? st.id ?? __i12}>
                        <button type="button" onClick={st.onClick} disabled={st.locked} title={st.lockReason || undefined} aria-disabled={st.locked ? "true" : undefined} aria-label={st.label} aria-current={st.current ? "step" : undefined} className={st.current ? "apd-step apd-step-current" : "apd-step"} style={{ display: "flex", alignItems: "center", gap: "8px", background: "none", border: "0", padding: "0", cursor: st.locked ? "not-allowed" : "pointer", fontFamily: "inherit", fontSize: "13px", color: st.fg, opacity: st.opacity, fontWeight: st.weight, flexShrink: 0, minWidth: 0, whiteSpace: "nowrap" }}>
                          <span style={{ width: "22px", height: "22px", borderRadius: "999px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "12px", fontWeight: "700", background: st.numBg, color: st.numFg, flexShrink: 0 }}>{st.num}</span><span className="apd-step-label">{st.label}</span>
                        </button>
                        {st.connector ? (<>
                          <span aria-hidden="true" style={{ flex: "1 1 8px", minWidth: "8px", height: "2px", borderRadius: "999px", background: st.connector }}></span>
                        </>) : null}
                      </React.Fragment>))}
                    </div>
                  </>) : null}
                  {V.pv.progressBar ? (<>
                    <div style={{ padding: "16px 24px 4px" }}>
                      <div style={{ height: "6px", borderRadius: "999px", background: "var(--jrni-color-neutral-5)", overflow: "hidden" }}>
                        <div style={{ height: "100%", width: V.pv.progressPct, background: V.pv.brand }}></div>
                      </div>
                    </div>
                  </>) : null}
                  {V.pv.continueLockText ? (<>
                    {/* Not a tooltip. The tabs' `title` is a desktop nicety; on
                        touch — and this designer now reaches 375px — hover does
                        not exist, so the reason the later steps are locked is
                        stated in the page, once, where the customer is reading.
                        Hidden, not removed, once met: the page below keeps its place. */}
                    <p aria-live="polite" style={{ margin: "0", padding: "10px 24px 0", fontSize: "13px", lineHeight: "1.5", color: "var(--jrni-color-text-soft)", textWrap: "pretty", visibility: V.pv.continueLockReason ? "visible" : "hidden" }}>{V.pv.continueLockText}</p>
                  </>) : null}
                  <div style={{ flex: "1 1 auto", display: "flex", alignItems: V.pv.bodyAlign, gap: "20px", padding: "22px 24px 28px", flexWrap: V.pv.bodyWrap, flexDirection: V.pv.bodyDir }}>
                    <div style={{ flex: V.pv.mainFlex, minWidth: "0", display: "flex", flexDirection: "column", gap: "18px" }}>
                      {V.pv.showTopSummary ? (<>
                        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                          {V.pv.sumShowWhen ? (<>
                            <div style={{ display: "flex", gap: "12px", alignItems: "center", padding: "12px", border: "1px solid var(--jrni-color-surface-border)", borderRadius: "var(--jrni-radius-md)", background: "var(--jrni-color-surface-card-soft)" }}>
                              <div style={{ flex: "1 1 auto", minWidth: "0" }}>
                                <div style={{ fontSize: "14px", fontWeight: "700" }}>{V.pv.sumWhen}</div>
                                <div style={{ fontSize: "13px", color: "var(--jrni-color-text-soft)" }}>{V.pv.sumEnds}</div>
                              </div>
                              {V.pv.canChange ? (<>
                                <Button variant="link" size="sm" onClick={V.pv.sumGoWhen}>Change
                                </Button>
                              </>) : null}
                            </div>
                          </>) : null}
                          {(V.pv.summaryRows || []).map((sm, __i50) => (<React.Fragment key={sm.key ?? sm.id ?? __i50}>
                            <div style={{ display: "flex", gap: "12px", alignItems: "center", padding: "12px", border: "1px solid var(--jrni-color-surface-border)", borderRadius: "var(--jrni-radius-md)", background: "var(--jrni-color-surface-card-soft)" }}>
                              <div style={{ flex: "1 1 auto", minWidth: "0" }}>
                                <div style={{ fontSize: "14px", fontWeight: "700" }}>{sm.title}</div>
                                {sm.sub ? (<div style={{ fontSize: "13px", color: "var(--jrni-color-text-soft)" }}>{sm.sub}</div>) : null}
                              </div>
                              {V.pv.canChange && sm.onChange ? (<>
                                <Button variant="link" size="sm" onClick={sm.onChange}>Change
                                </Button>
                              </>) : null}
                            </div>
                          </React.Fragment>))}
                        </div>
                      </>) : null}
                      {V.pv.isKioskEntry ? (<>
                        <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
                          {V.pv.kioskShowGreeting ? (<>
                            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                              <h2 style={{ margin: "0", fontSize: V.pv.kioskTitleSize, fontWeight: "700", letterSpacing: "-0.02em", lineHeight: "1.1" }}>{V.pv.kioskGreeting}</h2>
                              <p style={{ margin: "0", fontSize: V.pv.kioskSubSize, color: "var(--jrni-color-text-body)" }}>{V.pv.kioskSub}</p>
                            </div>
                          </>) : null}
                          <div style={{ display: "grid", gridTemplateColumns: V.pv.kioskTileCols, gap: "16px" }}>
                            {(V.pv.kioskTiles || []).map((tile, __i13) => (<React.Fragment key={tile.key ?? tile.id ?? __i13}>
                              <button type="button" onClick={tile.onClick} style={{ display: "flex", flexDirection: V.pv.kioskTileDir, alignItems: V.pv.kioskTileAlign, gap: V.pv.kioskTileGap, minHeight: V.pv.kioskTileMinH, padding: V.pv.kioskTilePad, border: "1px solid var(--jrni-color-surface-border)", borderRadius: "var(--jrni-radius-lg)", background: "var(--jrni-color-surface-card)", boxShadow: "var(--jrni-shadow-sm)", cursor: "pointer", textAlign: "left", fontFamily: "inherit", color: "var(--jrni-color-text-strong)" }}>
                                <span style={{ flex: "0 0 auto", display: "inline-flex", alignItems: "center", justifyContent: "center", width: V.pv.kioskIconBox, height: V.pv.kioskIconBox, borderRadius: "var(--jrni-radius-md)", background: tile.hue, color: "#FFFFFF" }}>{tile.icon}</span>
                                {V.pv.kioskTileRoomy ? (<>
                                  <span style={{ fontSize: V.pv.kioskTileTitleSize, fontWeight: "700", lineHeight: "1.2" }}>{tile.title}</span>
                                  <span style={{ fontSize: V.pv.kioskTileDescSize, color: "var(--jrni-color-text-body)", lineHeight: "1.4" }}>{tile.desc}</span>
                                  {tile.meta ? (<>
                                    <span style={{ marginTop: "auto", fontSize: V.pv.kioskTileDescSize, fontWeight: "600", color: tile.hue }}>{tile.meta}</span>
                                  </>) : null}
                                </>) : null}
                                {V.pv.kioskTileCompact ? (<>
                                  <span style={{ flex: "1 1 auto", minWidth: "0", display: "flex", flexDirection: "column", gap: "2px" }}>
                                    <span style={{ fontSize: V.pv.kioskTileTitleSize, fontWeight: "700", lineHeight: "1.25" }}>{tile.title}</span>
                                    {tile.meta ? (<>
                                      <span style={{ fontSize: V.pv.kioskTileDescSize, fontWeight: "600", color: tile.hue }}>{tile.meta}</span>
                                    </>) : null}
                                  </span>
                                </>) : null}
                              </button>
                            </React.Fragment>))}
                          </div>
                          {V.pv.kioskShowStrip ? (<>
                            <div style={{ display: "flex", alignItems: "stretch", gap: "0", flexWrap: "wrap", border: "1px solid var(--jrni-color-surface-border)", borderRadius: "var(--jrni-radius-md)", background: "var(--jrni-color-surface-card-soft)", overflow: "hidden" }}>
                              {V.pv.kioskWait ? (<>
                                <div style={{ flex: "1 1 200px", display: "flex", flexDirection: "column", gap: "3px", padding: "14px 18px" }}>
                                  <span style={{ fontSize: "12px", fontWeight: "600", letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--jrni-color-text-soft)" }}>Walk-in wait</span>
                                  <span style={{ fontSize: V.pv.kioskBodySize, fontWeight: "700" }}>{V.pv.kioskWaitLine}</span>
                                </div>
                              </>) : null}
                              {V.pv.kioskHours ? (<>
                                <div style={{ flex: "1 1 240px", display: "flex", flexDirection: "column", gap: "3px", padding: "14px 18px", borderLeft: V.pv.kioskWait ? "1px solid var(--jrni-color-surface-border)" : "0" }}>
                                  <span style={{ fontSize: "12px", fontWeight: "600", letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--jrni-color-text-soft)" }}>Open today</span>
                                  {V.pv.kioskHoursToday ? (<>
                                    <span style={{ fontSize: V.pv.kioskBodySize, fontWeight: "700" }}>{V.pv.kioskHoursToday}</span>
                                  </>) : null}
                                  {V.pv.kioskHoursAddr ? (<>
                                    <span style={{ fontSize: "13px", color: "var(--jrni-color-text-soft)" }}>{V.pv.kioskHoursAddr}</span>
                                  </>) : null}
                                </div>
                              </>) : null}
                            </div>
                          </>) : null}
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "14px", flexWrap: "wrap", paddingTop: "4px", borderTop: "1px solid var(--jrni-color-surface-border)" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "10px", paddingTop: "12px" }}>
                              {V.pv.kioskBigTextToggle ? (<>
                                <Button variant="outline" size="md" onClick={V.actions.toggleBigText}>{V.pv.bigTextLabel}
                                </Button>
                              </>) : null}
                            </div>
                          </div>
                        </div>
                      </>) : null}
                      {V.pv.isKioskCheckin ? (<>
                        <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
                          <div style={{ display: "flex", alignItems: "flex-start", gap: "14px", flexWrap: "wrap" }}>
                            <div style={{ flex: "1 1 320px", minWidth: "0", display: "flex", flexDirection: "column", gap: "5px" }}>
                              <h2 style={{ margin: "0", fontSize: V.pv.kioskTitleSize, fontWeight: "700", letterSpacing: "-0.02em", lineHeight: "1.15" }}>{V.pv.ckHeader}</h2>
                            </div>
                            <Button variant="ghost" size="md" onClick={V.actions.kioskReset}>Start over
                            </Button>
                          </div>
                          {V.pv.ckAnyMethod ? (<>
                            <SegmentedControl options={V.pv.ckMethods} value={V.pv.ckMethodActive} onChange={V.actions.setCkMethod} aria-label="How to check in" />
                          </>) : null}
                          {V.pv.ckScanMode ? (<>
                            <div style={{ display: "flex", gap: "20px", flexWrap: "wrap", alignItems: "stretch" }}>
                              <div style={{ flex: "1 1 300px", minWidth: "260px", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "14px", minHeight: "260px", padding: "24px", border: "2px dashed var(--jrni-color-neutral-4)", borderRadius: "var(--jrni-radius-lg)", background: "var(--jrni-color-surface-card-soft)" }}>
                                <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: "84px", height: "84px", borderRadius: "var(--jrni-radius-md)", background: "var(--jrni-color-surface-card)", border: "1px solid var(--jrni-color-surface-border)", color: "var(--jrni-color-primary-1)" }}>{V.icons.qrLarge}</span>
                                <span style={{ fontSize: V.pv.kioskBodySize, fontWeight: "600", textAlign: "center", maxWidth: "24ch", textWrap: "pretty" }}>{V.pv.ckScanLine}</span>
                                <span style={{ display: "inline-flex", alignItems: "center", gap: "7px", fontSize: "13px", color: "var(--jrni-color-text-soft)" }}>
                                  <span style={{ width: "8px", height: "8px", borderRadius: "999px", background: "var(--jrni-color-semantic-green-1)", animation: "apd-pulse 1.6s ease-in-out infinite" }}></span>Reader ready
                                </span>
                                {V.pv.ckShowWalletHint ? (<>
                                  <span style={{ fontSize: "13px", color: "var(--jrni-color-text-soft)", textAlign: "center", maxWidth: "30ch", textWrap: "pretty" }}>{V.pv.ckWalletHint}</span>
                                </>) : null}
                              </div>
                              <div style={{ flex: "1 1 260px", minWidth: "240px", display: "flex", flexDirection: "column", gap: "12px", justifyContent: "center" }}>
                                <span style={{ fontSize: "12px", fontWeight: "600", letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--jrni-color-text-soft)" }}>Simulate a scan</span>
                                <Button hierarchy="primary" size="lg" onClick={V.actions.kioskCheckInNow}>Simulate a successful scan
                                </Button>
                                <Button variant="outline" size="md" onClick={V.actions.kioskScanFail}>Scan something unreadable
                                </Button>
                                <span style={{ fontSize: "13px", color: "var(--jrni-color-text-soft)", textWrap: "pretty" }}>Preview only — the two buttons stand in for the reader so both outcomes can be seen.</span>
                              </div>
                            </div>
                          </>) : null}
                          {V.pv.ckRefMode ? (<>
                            <div style={{ display: "flex", flexDirection: "column", gap: "14px", maxWidth: "520px" }}>
                              <h3 style={{ margin: "0", fontSize: V.pv.kioskBodySize, fontWeight: "700" }}>{V.pv.ckRefHeader}</h3>
                              <Input value={V.pv.ckRefValue} onChange={V.actions.setCkRef} onFocus={V.actions.openKeys} inputmode={V.pv.ckInputMode} placeholder="Booking reference" size="lg" style={{  }} aria-label="Booking reference" />
                              <span style={{ fontSize: "13px", color: "var(--jrni-color-text-soft)" }}>It's at the top of your confirmation email.</span>
                              <div>
                                <Button variant="outline" size="md" onClick={V.actions.kioskLookupRef} disabled={!V.pv.ckCanLookUp}>{V.pv.ckLooking ? "Looking up…" : "Find my booking"}
                                </Button>
                              </div>
                              {V.pv.ckHasRefMatches ? (<>
                                <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                                  {(V.pv.ckRefMatches || []).map((m, __i14) => (<React.Fragment key={m.key ?? m.id ?? __i14}>
                                    <button type="button" onClick={m.onPick} style={{ display: "flex", flexDirection: "column", gap: "3px", padding: "14px 16px", border: "1px solid var(--jrni-color-surface-border)", borderRadius: "var(--jrni-radius-md)", background: "var(--jrni-color-surface-card)", cursor: "pointer", textAlign: "left", fontFamily: "inherit", color: "var(--jrni-color-text-strong)" }}>
                                      <span style={{ fontSize: "15px", fontWeight: "700" }}>{m.name} · {m.ref}</span>
                                      <span style={{ fontSize: "13px", color: "var(--jrni-color-text-soft)" }}>{m.svcWhen}</span>
                                    </button>
                                  </React.Fragment>))}
                                </div>
                              </>) : null}
                              {V.pv.ckNoRefMatch ? (<>
                                <span style={{ fontSize: "14px", color: "var(--jrni-color-semantic-orange-1)", fontWeight: "600" }}>No booking was found for that reference. Check it against your email, or ask at the welcome desk.</span>
                              </>) : null}
                            </div>
                          </>) : null}
                          {V.pv.ckNameMode ? (<>
                            <div style={{ display: "flex", flexDirection: "column", gap: "14px", maxWidth: "520px" }}>
                              <h3 style={{ margin: "0", fontSize: V.pv.kioskBodySize, fontWeight: "700" }}>{V.pv.ckNameHeader}</h3>
                              <Input value={V.pv.ckNameValue} onChange={V.actions.setCkName} onFocus={V.actions.openKeys} inputmode={V.pv.ckInputMode} placeholder="Start typing your last name" leading={V.icons.search} size="lg" style={{  }} aria-label="Your name" />
                              <span style={{ fontSize: "13px", color: "var(--jrni-color-text-soft)" }}>Only today's bookings at this campus are searched, and nothing is listed until you type.</span>
                              {V.pv.ckHasNameMatches ? (<>
                                <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                                  {(V.pv.ckNameMatches || []).map((m, __i15) => (<React.Fragment key={m.key ?? m.id ?? __i15}>
                                    <button type="button" onClick={m.onPick} style={{ display: "flex", flexDirection: "column", gap: "3px", padding: "14px 16px", border: "1px solid var(--jrni-color-surface-border)", borderRadius: "var(--jrni-radius-md)", background: "var(--jrni-color-surface-card)", cursor: "pointer", textAlign: "left", fontFamily: "inherit", color: "var(--jrni-color-text-strong)" }}>
                                      <span style={{ fontSize: "15px", fontWeight: "700" }}>{m.name}</span>
                                      <span style={{ fontSize: "13px", color: "var(--jrni-color-text-soft)" }}>{m.svcWhen}</span>
                                    </button>
                                  </React.Fragment>))}
                                </div>
                              </>) : null}
                              {V.pv.ckNoNameMatch ? (<>
                                <span style={{ fontSize: "14px", color: "var(--jrni-color-semantic-orange-1)", fontWeight: "600" }}>Finding a booking by name isn't available on this kiosk yet. Use your booking reference, or ask at the welcome desk.</span>
                              </>) : null}
                            </div>
                          </>) : null}
                          {V.pv.ckNativeKeyboardNote ? (<>
                            <span style={{ fontSize: "13px", color: "var(--jrni-color-text-soft)", textWrap: "pretty" }}>{V.pv.ckNativeKeyboardNote}</span>
                          </>) : null}
                          {V.pv.ckKeysOpen ? (<>
                            <div style={{ display: "flex", flexDirection: "column", gap: "10px", padding: "14px", border: "1px solid var(--jrni-color-surface-border)", borderRadius: "var(--jrni-radius-lg)", background: "var(--jrni-color-surface-card-soft)", boxShadow: "var(--jrni-shadow-sm)" }}>
                              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                <span style={{ flex: "1 1 auto", minWidth: "0", fontSize: "12px", fontWeight: "600", letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--jrni-color-text-soft)" }}>{V.pv.ckKeyboardLabel}</span>
                                <Button variant="ghost" size="sm" onClick={V.actions.closeKeys}>Close
                                </Button>
                              </div>
                              {V.pv.ckQwertyMode ? (<>
                                <div style={{ display: "flex", flexDirection: "column", gap: "7px", alignItems: "center" }}>
                                  {V.pv.ckNumberRow ? (<>
                                    <div style={{ display: "flex", gap: "6px", justifyContent: "center" }}>
                                      {(V.pv.ckDigitKeys || []).map((k, __i16) => (<React.Fragment key={k.key ?? k.id ?? __i16}>
                                        <button type="button" onClick={k.onTap} style={{ flex: "1 1 0", minWidth: "32px", maxWidth: "52px", height: "46px", border: "1px solid var(--jrni-color-surface-border)", borderRadius: "var(--jrni-radius-md)", background: "var(--jrni-color-surface-card)", fontFamily: "inherit", fontSize: "16px", fontWeight: "700", color: "var(--jrni-color-text-strong)", cursor: "pointer" }}>{k.ch}</button>
                                      </React.Fragment>))}
                                    </div>
                                  </>) : null}
                                  {(V.pv.ckQwertyRows || []).map((row, __i17) => (<React.Fragment key={row.key ?? row.id ?? __i17}>
                                    <div style={{ display: "flex", gap: "6px", justifyContent: "center", flexWrap: "nowrap" }}>
                                      {(row.keys || []).map((k, __i18) => (<React.Fragment key={k.key ?? k.id ?? __i18}>
                                        <button type="button" onClick={k.onTap} style={{ flex: "1 1 0", minWidth: "34px", maxWidth: "56px", height: "52px", border: "1px solid var(--jrni-color-surface-border)", borderRadius: "var(--jrni-radius-md)", background: "var(--jrni-color-surface-card)", fontFamily: "inherit", fontSize: "17px", fontWeight: "700", color: "var(--jrni-color-text-strong)", cursor: "pointer" }}>{k.ch}</button>
                                      </React.Fragment>))}
                                    </div>
                                  </React.Fragment>))}
                                  {V.pv.ckSpaceKey ? (<>
                                    <div style={{ display: "flex", gap: "6px", justifyContent: "center", width: "100%" }}>
                                      <button type="button" onClick={V.actions.keySpace} style={{ flex: "1 1 auto", maxWidth: "240px", height: "52px", border: "1px solid var(--jrni-color-surface-border)", borderRadius: "var(--jrni-radius-md)", background: "var(--jrni-color-surface-card)", fontFamily: "inherit", fontSize: "14px", fontWeight: "600", color: "var(--jrni-color-text-body)", cursor: "pointer" }}>space</button>
                                    </div>
                                  </>) : null}
                                </div>
                              </>) : null}
                              <div style={{ display: "flex", gap: "8px", justifyContent: "center" }}>
                                <button type="button" onClick={V.actions.keyBack} style={{ height: "48px", padding: "0 22px", border: "1px solid var(--jrni-color-surface-border)", borderRadius: "var(--jrni-radius-md)", background: "var(--jrni-color-surface-card)", fontFamily: "inherit", fontSize: "14px", fontWeight: "600", color: "var(--jrni-color-text-body)", cursor: "pointer" }}>Delete</button>
                                <button type="button" onClick={V.actions.keyClear} style={{ height: "48px", padding: "0 22px", border: "1px solid var(--jrni-color-surface-border)", borderRadius: "var(--jrni-radius-md)", background: "var(--jrni-color-surface-card)", fontFamily: "inherit", fontSize: "14px", fontWeight: "600", color: "var(--jrni-color-text-body)", cursor: "pointer" }}>Clear</button>
                              </div>
                            </div>
                          </>) : null}
                          {V.pv.ckNotScanMode ? (<>
                            <div style={{ display: "flex", alignItems: "center", gap: "12px", paddingTop: "4px", borderTop: "1px solid var(--jrni-color-surface-border)" }}>
                              <span style={{ paddingTop: "14px", fontSize: "13px", color: "var(--jrni-color-text-soft)" }}>Found yourself above?</span>
                              <span style={{ paddingTop: "14px" }}>
                                <Button hierarchy="primary" size="md" onClick={V.actions.kioskCheckInNow} disabled={!V.pv.ckHasBooking}>{V.pv.ckHasBooking ? "Check in as " + V.pv.ckPickedName : "Check in"}
                                </Button>
                              </span>
                            </div>
                          </>) : null}
                        </div>
                      </>) : null}
                      {V.pv.isKioskProblem ? (<>
                        <div style={{ display: "flex", flexDirection: "column", gap: "20px", maxWidth: "620px" }}>
                          <div style={{ display: "flex", alignItems: "flex-start", gap: "16px", padding: "22px 24px", borderRadius: "var(--jrni-radius-lg)", background: V.pv.ckProbTint, border: `1px solid ${V.pv.ckProbTone}` }}>
                            <span style={{ flex: "0 0 auto", display: "inline-flex", alignItems: "center", justifyContent: "center", width: "46px", height: "46px", borderRadius: "999px", background: V.pv.ckProbTone, color: "#FFFFFF" }}>{V.icons.warnLarge}</span>
                            <div style={{ flex: "1 1 auto", minWidth: "0", display: "flex", flexDirection: "column", gap: "7px" }}>
                              <h2 style={{ margin: "0", fontSize: "24px", fontWeight: "700", letterSpacing: "-0.01em", lineHeight: "1.2" }}>{V.pv.ckProbTitle}</h2>
                              <p style={{ margin: "0", fontSize: V.pv.kioskBodySize, lineHeight: "1.5", color: "var(--jrni-color-text-strong)", textWrap: "pretty" }}>{V.pv.ckProbBody}</p>
                            </div>
                          </div>
                          {V.pv.ckProbShowBooking ? (<>
                            <div style={{ display: "flex", flexDirection: "column", gap: "4px", padding: "16px 18px", border: "1px solid var(--jrni-color-surface-border)", borderRadius: "var(--jrni-radius-md)", background: "var(--jrni-color-surface-card-soft)" }}>
                              <span style={{ fontSize: "15px", fontWeight: "700" }}>{V.pv.ckPickedName} · {V.pv.ckPickedRef}</span>
                              <span style={{ fontSize: "14px", color: "var(--jrni-color-text-body)" }}>{V.pv.ckPickedSvcWhen}</span>
                            </div>
                          </>) : null}
                          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                            {V.pv.ckProbRecoverable ? (<>
                              <Button hierarchy="primary" size="lg" onClick={V.actions.kioskTryName}>Find my booking by name
                              </Button>
                            </>) : null}
                            <Button hierarchy="primary" size="lg" onClick={V.actions.kioskReset}>Back to the start
                            </Button>
                          </div>
                        </div>
                      </>) : null}
                      {V.pv.isKioskDone ? (<>
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "22px", padding: "24px 0", textAlign: "center" }}>
                          <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: "84px", height: "84px", borderRadius: "999px", background: "var(--jrni-color-semantic-green-1)", color: "#FFFFFF" }}>{V.icons.tickLarge}</span>
                          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                            <h2 style={{ margin: "0", fontSize: V.pv.kioskTitleSize, fontWeight: "700", letterSpacing: "-0.02em", lineHeight: "1.15" }}>{V.pv.ckDoneHeader}</h2>
                            <p style={{ margin: "0", fontSize: V.pv.kioskSubSize, color: "var(--jrni-color-text-body)", maxWidth: "40ch", textWrap: "pretty" }}>{V.pv.ckDoneSub}</p>
                          </div>
                          {V.pv.ckHasBooking ? (<>
                          <div style={{ display: "flex", flexDirection: "column", gap: "0", width: "100%", maxWidth: "460px", border: "1px solid var(--jrni-color-surface-border)", borderRadius: "var(--jrni-radius-lg)", background: "var(--jrni-color-surface-card)", overflow: "hidden", textAlign: "left" }}>
                            <div style={{ display: "flex", flexDirection: "column", gap: "3px", padding: "16px 20px" }}>
                              <span style={{ fontSize: "12px", fontWeight: "600", letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--jrni-color-text-soft)" }}>Booking</span>
                              <span style={{ fontSize: "17px", fontWeight: "700" }}>{V.pv.ckPickedSvc}</span>
                              <span style={{ fontSize: "14px", color: "var(--jrni-color-text-body)" }}>{V.pv.ckPickedWhenStaff}</span>
                            </div>
                            {V.pv.ckShowQueuePos ? (<>
                              <div style={{ display: "flex", flexDirection: "column", gap: "3px", padding: "16px 20px", borderTop: "1px solid var(--jrni-color-surface-border)", background: "var(--jrni-color-surface-card-soft)" }}>
                                <span style={{ fontSize: "12px", fontWeight: "600", letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--jrni-color-text-soft)" }}>In the line</span>
                                <span style={{ fontSize: "17px", fontWeight: "700" }}>{V.pv.ckQueuePos}</span>
                              </div>
                            </>) : null}
                          </div>
                          </>) : (<>
                            <div style={{ width: "100%", maxWidth: "460px", textAlign: "left" }}>
                              <InlineEmptyState title="No booking looked up" description="Find a booking by its reference to see it here. The preview shows only what a real lookup returns." />
                            </div>
                          </>)}
                          <span style={{ fontSize: "13px", color: "var(--jrni-color-text-soft)" }}>Preview only — nobody is checked in from here.</span>
                          <span style={{ fontSize: "14px", color: "var(--jrni-color-text-soft)" }}>{V.pv.ckDwellNote}</span>
                          <Button hierarchy="primary" size="md" onClick={V.actions.kioskReset}>Done
                          </Button>
                        </div>
                      </>) : null}
                      {V.pv.personalLine ? (
                        <div data-testid="personal-link-line" style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", color: "var(--jrni-color-text-body)", marginBottom: "12px" }}>
                          <span style={{ display: "flex", color: "var(--jrni-color-text-soft)" }}>{V.icons.users}</span>
                          <span>{V.pv.personalLine}</span>
                        </div>
                      ) : null}
                      {V.pv.isLinkError ? (<>
                        <div style={{ display: "flex", flexDirection: "column", gap: "18px", maxWidth: "560px" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                            <span style={{ flex: "0 0 auto", width: "24px", height: "24px", borderRadius: "999px", display: "flex", alignItems: "center", justifyContent: "center", background: "var(--jrni-color-semantic-orange-2)", color: "var(--jrni-color-semantic-orange-1)", fontSize: "14px", fontWeight: "700" }}>!</span>
                            <h2 style={{ margin: "0", fontSize: "19px", fontWeight: "700", letterSpacing: "-0.01em" }}>{V.pv.errTitle}</h2>
                          </div>
                          <p style={{ margin: "0", fontSize: "14px", lineHeight: "1.6", color: "var(--jrni-color-text-body)", textWrap: "pretty" }}>{V.pv.errBody}</p>
                          {V.pv.errHasRoutes ? (<>
                            <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                              {V.s.errBookStaff ? (<>
                                <div role="button" tabIndex="0" onClick={V.actions.errStaff} style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: "12px", padding: "14px", border: "1px solid var(--jrni-color-surface-border)", borderRadius: "var(--jrni-radius-md)", background: "var(--jrni-color-surface-card)", boxShadow: "var(--jrni-shadow-chrome)" }}>
                                  <span style={{ flex: "0 0 auto", display: "flex" }}>{V.pv.errStaffTile}</span>
                                  <span style={{ flex: "1 1 auto", minWidth: "0" }}>
                                    <span style={{ display: "block", fontSize: "14px", fontWeight: "600", color: "var(--jrni-color-text-strong)" }}>{V.pv.errStaffTitle}</span>
                                    <span style={{ display: "block", fontSize: "13px", color: "var(--jrni-color-text-soft)" }}>{V.pv.errStaffSub}</span>
                                  </span>
                                  <span style={{ flex: "0 0 auto", display: "flex", color: "var(--jrni-color-text-soft)" }}>{V.icons.chevron}</span>
                                </div>
                              </>) : null}
                              {V.s.errOtherServices ? (<>
                                <div role="button" tabIndex="0" onClick={V.actions.errServices} style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: "12px", padding: "14px", border: "1px solid var(--jrni-color-surface-border)", borderRadius: "var(--jrni-radius-md)", background: "var(--jrni-color-surface-card)", boxShadow: "var(--jrni-shadow-chrome)" }}>
                                  <span style={{ flex: "0 0 auto", display: "flex" }}>{V.pv.errSvcTile}</span>
                                  <span style={{ flex: "1 1 auto", minWidth: "0" }}>
                                    <span style={{ display: "block", fontSize: "14px", fontWeight: "600", color: "var(--jrni-color-text-strong)" }}>{V.pv.errSvcTitle}</span>
                                    <span style={{ display: "block", fontSize: "13px", color: "var(--jrni-color-text-soft)" }}>{V.pv.errSvcSub}</span>
                                  </span>
                                  <span style={{ flex: "0 0 auto", display: "flex", color: "var(--jrni-color-text-soft)" }}>{V.icons.chevron}</span>
                                </div>
                              </>) : null}
                            </div>
                          </>) : null}
                          {V.pv.errNoRoutes ? (<>
                            <Banner tone="neutral" variant="explainer" size="compact" title="Nothing to offer">Both recovery options are off, so this page is a dead end. Turn one on in General.
                            </Banner>
                          </>) : null}
                          {V.pv.errShowStart ? (
                          <div>
                            <Button variant="secondary" onClick={V.actions.errStart}>{V.pv.errStartLabel}
                            </Button>
                          </div>
                          ) : null}
                        </div>
                      </>) : null}
                      {V.pv.isLandingPage && V.is.tplCustom ? (<>
                        {/* The canvas IS the preview for this template — the
                            built-in renderer below understands `blockOrder`,
                            not freeform geometry, so it is not used here. */}
                        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                          <CustomLayoutCanvas blocks={V.pv.clBlocks} device={V.pv.clDevice} selectedUid={V.pv.clSel} brandColor={V.pv.clBrand} frameWidth={V.pv.clFrameWidth} onSelect={V.actions.clSelect} onChange={V.actions.clChange} />
                        </div>
                      </>) : null}
                      {V.pv.isLandingPage && !V.is.tplCustom ? (<>
                        {V.pv.tplBannerHero ? (<>
                          {V.pv.lpBannerCover ? (
                            <div role="img" aria-label={V.pv.lpTitle} data-testid="landing-banner-cover" style={{ height: V.pv.lpHeroH, marginBottom: "12px", borderRadius: "var(--jrni-radius-md)", backgroundImage: V.pv.lpBannerCover, backgroundSize: "cover", backgroundPosition: "center" }}></div>
                          ) : null}
                          {/* At phone width the band stacks (CC 0415): the title takes a line of its own and the date line wraps
                              under it, both inside the card. Wider frames keep title left, date right: the row wraps only when the
                              two cannot share a line (title basis 220px), which a multi-day range at 393px no longer can. */}
                          <div data-testid="landing-banner-band" style={{ display: "flex", flexWrap: "wrap", alignItems: "center", columnGap: "12px", rowGap: "4px", padding: "14px 18px", borderRadius: "var(--jrni-radius-md)", color: "#FFFFFF", background: V.pv.brand }}>
                            {/* The event title is the page's h1 in every hero layout (CC 0402). */}
                            <h1 style={{ flex: "1 1 220px", minWidth: "0", margin: "0", fontSize: "17px", fontWeight: "700", letterSpacing: "-0.01em", overflowWrap: "anywhere", textWrap: "pretty" }}>{V.pv.lpTitle}</h1>
                            <span style={{ flex: "0 1 auto", minWidth: "0", fontSize: "13px", opacity: "0.9", overflowWrap: "anywhere" }}>{V.pv.lpWhenWhere}</span>
                          </div>
                        </>) : null}
                        {V.pv.tplHeroHero ? (<>
                          <div style={{ position: "relative", display: "flex", flexDirection: "column", justifyContent: "flex-end", gap: "6px", padding: "20px", height: V.pv.lpHeroH, borderRadius: "var(--jrni-radius-md)", overflow: "hidden", background: V.pv.lpHeroBg, backgroundSize: "cover", backgroundPosition: "center" }}>
                            <div style={{ position: "absolute", inset: "0", background: V.pv.lpHeroScrim }}></div>
                            <h1 style={{ position: "relative", margin: "0", fontSize: "26px", fontWeight: "700", letterSpacing: "-0.02em", color: "#FFFFFF", textWrap: "pretty" }}>{V.pv.lpTitle}</h1>
                            <div style={{ position: "relative", fontSize: "14px", color: "rgba(255, 255, 255, 0.9)" }}>{V.pv.lpWhen} · {V.pv.lpVenue}</div>
                          </div>
                        </>) : null}
                        {/* Under every hero layout (CC 0416): the event-unavailable page's 1d wording, in the page's own Banner. */}
                        {V.pv.lpEventCancelled && !V.pv.tplSplitHero ? (
                          <div data-testid="lp-event-cancelled" style={{ marginTop: "12px" }}>
                            <Banner tone="danger" size="compact" title={V.pv.lpCancelledTitle}>{V.pv.lpCancelledText}</Banner>
                          </div>
                        ) : null}
                        {V.pv.tplSplitHero ? (<>
                          {V.pv.lpEventCancelled ? (
                            <div data-testid="lp-event-cancelled" style={{ marginBottom: "12px" }}>
                              <Banner tone="danger" size="compact" title={V.pv.lpCancelledTitle}>{V.pv.lpCancelledText}</Banner>
                            </div>
                          ) : null}
                          <div style={{ display: "flex", gap: "20px", flexWrap: "wrap", alignItems: "stretch" }}>
                            <div style={{ flex: "1 1 260px", minHeight: V.pv.lpHeroH, borderRadius: "var(--jrni-radius-md)", background: V.pv.lpHeroBg, backgroundSize: "cover", backgroundPosition: "center" }}></div>
                            <div style={{ flex: "1 1 260px", minWidth: "0", display: "flex", flexDirection: "column", gap: "12px", justifyContent: "center" }}>
                              <h1 style={{ margin: "0", fontSize: "24px", fontWeight: "700", letterSpacing: "-0.02em", textWrap: "pretty" }}>{V.pv.lpTitle}</h1>
                              <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap", fontSize: "14px", color: "var(--jrni-color-text-soft)" }}>
                                <span>{V.pv.lpWhen} · {V.pv.lpVenue}</span>
                              </div>
                              <div style={{ display: "flex", flexDirection: "column", gap: "12px", padding: "16px", border: "1px solid var(--jrni-color-surface-border)", borderRadius: "var(--jrni-radius-md)", background: "var(--jrni-color-surface-card)", boxShadow: "var(--jrni-shadow-sm)" }}>
                                <div style={{ fontSize: "13px", color: "var(--jrni-color-text-soft)" }}>{V.pv.lpWhen}</div>
                                <div style={{ fontSize: "22px", fontWeight: "700", letterSpacing: "-0.01em" }}>{V.pv.lpPrice}</div>
                                {V.pv.lpOpen ? (<>
                                  <button type="button" onClick={V.actions.lpRegister} style={{ border: "0", cursor: "pointer", fontFamily: "inherit", fontSize: "15px", fontWeight: "600", color: "#FFFFFF", height: "44px", background: V.pv.brand, borderRadius: V.pv.radius }}>{V.pv.lpCta}</button>
                                  <div style={{ fontSize: "12px", color: "var(--jrni-color-text-soft)" }}>{V.pv.lpSpacesLabel}</div>
                                </>) : null}
                                {V.pv.lpClosed ? (<>
                                  <Pill tone={V.pv.lpClosedTone}>{V.pv.lpClosedLabel}
                                  </Pill>
                                  {V.pv.lpShowWaitlist ? (<>
                                    {V.pv.wlDone ? (<>
                                      <Banner tone="success" size="compact" title="You are on the list">{V.pv.wlNote}
                                      </Banner>
                                    </>) : null}
                                    {V.pv.wlOpen ? (<>
                                      <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                                        {/* A refusal is shown above the form, which stays so the guest can try again (CC 0320). */}
                                        {V.pv.wlError ? <Banner tone="danger" size="compact" title="You were not added to the waitlist">{V.pv.wlError}</Banner> : null}
                                        <div style={{ fontSize: "13px", color: "var(--jrni-color-text-body)" }}>Join the waitlist and we will e-mail you the moment a place opens.</div>
                                        {V.pv.wlShowTierChoice ? <Dropdown options={V.pv.wlTierChoices} value={V.pv.wlTierValue} onChange={V.actions.setSelectedTicketId} placeholder="Choose a ticket" full={true} /> : null}
                                        <Input value={V.pv.wlName} onChange={V.actions.setWlName} placeholder="Your name" full={true} />
                                        <Input type="email" value={V.pv.wlEmail} onChange={V.actions.setWlEmail} placeholder="you@example.com" full={true} />
                                        <Checkbox ref={V.pv.consentRef} checked={V.pv.consent} aria-label={V.pv.consentText} error={V.pv.consentError ? V.pv.consentRequiredText : undefined} onChange={V.actions.setConsent} label={V.pv.consentText} />
                                        
                                        <Button hierarchy="primary" full={true} onClick={V.actions.joinWaitlist} disabled={V.pv.wlSubmitting} aria-busy={V.pv.wlSubmitting || undefined}>{V.pv.wlSubmitting ? "Joining…" : "Join the waitlist"}
                                        </Button>
                                      </div>
                                    </>) : null}
                                  </>) : null}
                                </>) : null}
                              </div>
                            </div>
                          </div>
                        </>) : null}
                        {V.pv.tplLongHero ? (<>
                          <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                            <h1 style={{ margin: "0", fontSize: "24px", fontWeight: "700", letterSpacing: "-0.02em", textWrap: "pretty" }}>{V.pv.lpTitle}</h1>
                            <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap", fontSize: "14px", color: "var(--jrni-color-text-soft)" }}>
                              <span>{V.pv.lpWhen} · {V.pv.lpVenue}</span>
                            </div>
                          </div>
                        </>) : null}
                        <div style={{ display: "flex", gap: "24px", flexWrap: "wrap", alignItems: "flex-start" }}>
                          <div style={{ flex: `1 1 ${V.pv.lpMainW}`, minWidth: "0", display: "flex", flexDirection: "column", gap: "22px" }}>
                            {(V.pv.landingBlocks || []).map((bl, __i19) => (<React.Fragment key={bl.key ?? bl.id ?? __i19}>
                              <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                                {bl.isDesc ? (<>
                                  <h3 style={{ margin: "0", fontSize: "16px", fontWeight: "700" }}>About this event</h3>
                                  <p style={{ margin: "0", fontSize: "14px", lineHeight: "1.6", color: "var(--jrni-color-text-body)", textWrap: "pretty" }}>{V.pv.lpDesc}</p>
                                  <div style={{ display: "flex", flexDirection: "column", gap: "6px", paddingTop: "4px" }}>
                                    {(V.pv.lpAgenda || []).map((ag, __i20) => (<React.Fragment key={ag.key ?? ag.id ?? __i20}>
                                      <div style={{ display: "flex", gap: "12px", fontSize: "14px", padding: "6px 0", borderTop: "1px solid var(--jrni-color-neutral-6)" }}>
                                        <span style={{ flex: "0 0 92px", fontWeight: "600", color: "var(--jrni-color-text-strong)" }}>{ag.time}</span>
                                        <span style={{ flex: "1 1 auto", color: "var(--jrni-color-text-body)" }}>{ag.what}</span>
                                      </div>
                                    </React.Fragment>))}
                                  </div>
                                </>) : null}
                                {bl.isSpeakers ? (<>
                                  <h3 style={{ margin: "0", fontSize: "16px", fontWeight: "700" }}>Who you will hear from</h3>
                                  <div style={{ display: "grid", gap: "12px", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))" }}>
                                    {(V.pv.lpSpeakers || []).map((sp, __i21) => (<React.Fragment key={sp.key ?? sp.id ?? __i21}>
                                      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "8px", textAlign: "center" }}>
                                        {sp.hasPhoto ? (<>
                                          <div role="img" aria-label={sp.name} style={{ width: "100%", aspectRatio: "1 / 1", borderRadius: "var(--jrni-radius-md)", backgroundImage: sp.photoCss, backgroundSize: "cover", backgroundPosition: "center" }}></div>
                                        </>) : null}
                                        {sp.noPhoto ? (<>
                                          <div style={{ width: "100%", aspectRatio: "1 / 1", borderRadius: "var(--jrni-radius-md)", display: "flex", alignItems: "center", justifyContent: "center", background: "var(--jrni-color-neutral-6)" }}>
                                            {String(sp.name || "").trim() ? <Avatar initials={nameInitials(sp.name)} size={56} /> : null}
                                          </div>
                                        </>) : null}
                                        <div style={{ minWidth: "0" }}>
                                          <div style={{ fontSize: "14px", fontWeight: "600", color: "var(--jrni-color-text-strong)" }}>{sp.name}</div>
                                          <div style={{ fontSize: "12px", color: "var(--jrni-color-text-soft)" }}>{sp.role}</div>
                                        </div>
                                      </div>
                                    </React.Fragment>))}
                                  </div>
                                </>) : null}
                                {bl.isIncluded ? (<>
                                  <h3 style={{ margin: "0", fontSize: "16px", fontWeight: "700" }}>What's included</h3>
                                  <div style={{ display: "grid", gap: "8px", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))" }}>
                                    {(V.pv.lpIncluded || []).map((inc, __i22) => (<React.Fragment key={inc.key ?? inc.id ?? __i22}>
                                      <div style={{ display: "flex", alignItems: "flex-start", gap: "8px", fontSize: "14px", color: "var(--jrni-color-text-body)" }}>
                                        <span style={{ flex: "0 0 auto", color: "var(--jrni-color-semantic-green-1)", fontWeight: "700" }}>✓</span>
                                        <span>{inc.text}</span>
                                      </div>
                                    </React.Fragment>))}
                                  </div>
                                </>) : null}
                                {bl.isTickets ? (<>
                                  <h3 style={{ margin: "0", fontSize: "16px", fontWeight: "700" }}>Tickets</h3>
                                  <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                                    {(V.pv.lpTickets || []).map((tk, __i23) => (<React.Fragment key={tk.key ?? tk.id ?? __i23}>
                                      {/* ui-raw: a selectable row — the choice carries into the registration form (state.selectedTicketId) */}
                                      <button type="button" aria-pressed={!!tk.selected} onClick={() => V.actions.setSelectedTicketId(tk.key)} style={{ display: "flex", alignItems: "baseline", gap: "12px", padding: "12px 14px", width: "100%", textAlign: "left", cursor: "pointer", font: "inherit", color: "inherit", border: tk.selected ? "2px solid var(--jrni-color-primary-1)" : "1px solid var(--jrni-color-surface-border)", borderRadius: "var(--jrni-radius-md)", background: "var(--jrni-color-surface-card)" }}>
                                        <div style={{ flex: "1 1 auto", minWidth: "0" }}>
                                          <div style={{ fontSize: "14px", fontWeight: "600" }}>{tk.name}</div>
                                          <div style={{ fontSize: "12px", color: "var(--jrni-color-text-soft)" }}>{tk.note}</div>
                                        </div>
                                        <div style={{ flex: "0 0 auto", fontSize: "15px", fontWeight: "700" }}>{tk.price}</div>
                                      </button>
                                    </React.Fragment>))}
                                  </div>
                                  {V.pv.ticketsEmpty && !this.isPublic() ? (<>
                                    <InlineEmptyState title={V.pv.ticketsEmptyTitle} description={V.pv.ticketsEmptyBody} />
                                  </>) : null}
                                </>) : null}
                                {bl.isSpaces ? (<>
                                  <h3 style={{ margin: "0", fontSize: "16px", fontWeight: "700" }}>Availability</h3>
                                  <div style={{ maxWidth: "320px" }}>
                                    <MeterBar value={V.pv.lpTaken} max={V.pv.lpCap} tone={V.pv.lpMeterTone} label={V.pv.lpSpacesLabel} />
                                  </div>
                                </>) : null}
                                {bl.isMap ? (this.isPublic() && !hasLocatedPin(V.pv.lpMapLocations || []) ? (
                                  /* A visitor never sees the staff "locate them in Locations" empty state (CC,
                                     2026-10-05): with no coordinates the block shows the address, or nothing. */
                                  V.pv.lpAddr ? (<>
                                    <h3 style={{ margin: "0", fontSize: "16px", fontWeight: "700" }}>Getting there</h3>
                                    <div style={{ fontSize: "14px", lineHeight: "1.5" }}>{V.pv.lpAddr}</div>
                                  </>) : null
                                ) : (<>
                                  <h3 style={{ margin: "0", fontSize: "16px", fontWeight: "700" }}>Getting there</h3>
                                  <LocationsMap pins={V.pv.lpMapLocations || []} height={190} caption={V.pv.lpAddr} />
                                </>)) : null}
                                {bl.isFaq ? (<>
                                  <h3 style={{ margin: "0", fontSize: "16px", fontWeight: "700" }}>Questions</h3>
                                  <div style={{ display: "flex", flexDirection: "column" }}>
                                    {(V.pv.lpFaq || []).map((fq, __i24) => (<React.Fragment key={fq.key ?? fq.id ?? __i24}>
                                      <div style={{ borderTop: "1px solid var(--jrni-color-neutral-6)" }}>
                                        <button type="button" onClick={fq.onToggle} aria-expanded={fq.open} style={{ display: "flex", alignItems: "center", gap: "10px", width: "100%", textAlign: "left", padding: "12px 0", background: "none", border: "0", cursor: "pointer", fontFamily: "inherit", fontSize: "14px", fontWeight: "600", color: "var(--jrni-color-text-strong)" }}>
                                          <span style={{ flex: "1 1 auto" }}>{fq.q}</span>
                                          <span style={{ flex: "0 0 auto", display: "flex", color: "var(--jrni-color-text-soft)", transform: fq.caret }}>{V.icons.chevron}</span>
                                        </button>
                                        {fq.open ? (<>
                                          <p style={{ margin: "0", padding: "0 0 12px", fontSize: "14px", lineHeight: "1.6", color: "var(--jrni-color-text-body)" }}>{fq.a}</p>
                                        </>) : null}
                                      </div>
                                    </React.Fragment>))}
                                  </div>
                                </>) : null}
                                {bl.isRelated && (V.pv.lpRelated || []).length ? (<>
                                  <h3 style={{ margin: "0", fontSize: "16px", fontWeight: "700" }}>You might also like</h3>
                                  <div style={{ display: "grid", gap: "12px", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))" }}>
                                    {(V.pv.lpRelated || []).map((rl, __i25) => (<React.Fragment key={rl.key ?? rl.id ?? __i25}>
                                      <div role="button" tabIndex="0" onClick={rl.onSelect} style={{ cursor: "pointer", display: "flex", flexDirection: "column", gap: "4px", padding: "12px 14px", border: "1px solid var(--jrni-color-surface-border)", borderRadius: "var(--jrni-radius-md)", background: "var(--jrni-color-surface-card)" }}>
                                        <span style={{ fontSize: "14px", fontWeight: "600", color: "var(--jrni-color-text-strong)" }}>{rl.name}</span>
                                        <span style={{ fontSize: "12px", color: "var(--jrni-color-text-soft)" }}>{rl.meta}</span>
                                      </div>
                                    </React.Fragment>))}
                                  </div>
                                </>) : null}
                                {bl.isSponsors ? (<>
                                  <h3 style={{ margin: "0", fontSize: "16px", fontWeight: "700" }}>{i18nT("shell.allJourneys.sponsorsHeading", "With support from")}</h3>
                                  {(V.pv.lpSponsorGroups || []).map((sg, __i26) => (<React.Fragment key={sg.key ?? sg.id ?? __i26}>
                                    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                                      {sg.showTier ? (<>
                                        <SectionLabel tone="soft">{sg.tier}
                                        </SectionLabel>
                                      </>) : null}
                                      <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", alignItems: "center" }}>
                                        {(sg.items || []).map((sr, __i27) => (<React.Fragment key={sr.key ?? sr.id ?? __i27}>
                                          <div style={{ flex: "0 0 auto", width: sr.w, height: sr.h, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: "var(--jrni-radius-sm)", border: sr.border, backgroundImage: sr.logoCss, backgroundSize: "contain", backgroundRepeat: "no-repeat", backgroundPosition: "center", fontSize: "12px", fontWeight: "600", color: "var(--jrni-color-text-soft)" }}>{sr.fallback}</div>
                                        </React.Fragment>))}
                                      </div>
                                    </div>
                                  </React.Fragment>))}
                                </>) : null}
                                {bl.isShare ? (<>
                                  <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                                    <span style={{ fontSize: "14px", fontWeight: "600" }}>Share</span>
                                    {(V.pv.lpShare || []).map((sh, __i28) => (<React.Fragment key={sh.key ?? sh.id ?? __i28}>
                                      <Button variant="secondary" size="sm">{sh.label}
                                      </Button>
                                    </React.Fragment>))}
                                  </div>
                                </>) : null}
                              </div>
                            </React.Fragment>))}
                          </div>
                          {V.pv.lpShowAside ? (<>
                            <aside style={{ flex: "1 1 260px", minWidth: "0", maxWidth: "320px", position: "sticky", top: "0" }}>
                              <div style={{ display: "flex", flexDirection: "column", gap: "12px", padding: "16px", border: "1px solid var(--jrni-color-surface-border)", borderRadius: "var(--jrni-radius-md)", background: "var(--jrni-color-surface-card)", boxShadow: "var(--jrni-shadow-sm)" }}>
                                <div style={{ fontSize: "13px", color: "var(--jrni-color-text-soft)" }}>{V.pv.lpWhen}</div>
                                <div style={{ fontSize: "22px", fontWeight: "700", letterSpacing: "-0.01em" }}>{V.pv.lpPrice}</div>
                                {V.pv.lpOpen ? (<>
                                  <button type="button" onClick={V.actions.lpRegister} style={{ border: "0", cursor: "pointer", fontFamily: "inherit", fontSize: "15px", fontWeight: "600", color: "#FFFFFF", height: "44px", background: V.pv.brand, borderRadius: V.pv.radius }}>{V.pv.lpCta}</button>
                                  <div style={{ fontSize: "12px", color: "var(--jrni-color-text-soft)" }}>{V.pv.lpSpacesLabel}</div>
                                </>) : null}
                                {V.pv.lpClosed ? (<>
                                  <Pill tone={V.pv.lpClosedTone}>{V.pv.lpClosedLabel}
                                  </Pill>
                                  {V.pv.lpShowWaitlist ? (<>
                                    {V.pv.wlDone ? (<>
                                      <Banner tone="success" size="compact" title="You are on the list">{V.pv.wlNote}
                                      </Banner>
                                    </>) : null}
                                    {V.pv.wlOpen ? (<>
                                      <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                                        {/* A refusal is shown above the form, which stays so the guest can try again (CC 0320). */}
                                        {V.pv.wlError ? <Banner tone="danger" size="compact" title="You were not added to the waitlist">{V.pv.wlError}</Banner> : null}
                                        <div style={{ fontSize: "13px", color: "var(--jrni-color-text-body)" }}>Join the waitlist and we will e-mail you the moment a place opens.</div>
                                        {V.pv.wlShowTierChoice ? <Dropdown options={V.pv.wlTierChoices} value={V.pv.wlTierValue} onChange={V.actions.setSelectedTicketId} placeholder="Choose a ticket" full={true} /> : null}
                                        <Input value={V.pv.wlName} onChange={V.actions.setWlName} placeholder="Your name" full={true} />
                                        <Input type="email" value={V.pv.wlEmail} onChange={V.actions.setWlEmail} placeholder="you@example.com" full={true} />
                                        <Checkbox ref={V.pv.consentRef} checked={V.pv.consent} aria-label={V.pv.consentText} error={V.pv.consentError ? V.pv.consentRequiredText : undefined} onChange={V.actions.setConsent} label={V.pv.consentText} />
                                        
                                        <Button hierarchy="primary" full={true} onClick={V.actions.joinWaitlist} disabled={V.pv.wlSubmitting} aria-busy={V.pv.wlSubmitting || undefined}>{V.pv.wlSubmitting ? "Joining…" : "Join the waitlist"}
                                        </Button>
                                      </div>
                                    </>) : null}
                                  </>) : null}
                                </>) : null}
                              </div>
                            </aside>
                          </>) : null}
                        </div>
                        {V.pv.lpInlineCta ? (<>
                          <div style={{ maxWidth: "360px" }}>
                            <div style={{ display: "flex", flexDirection: "column", gap: "12px", padding: "16px", border: "1px solid var(--jrni-color-surface-border)", borderRadius: "var(--jrni-radius-md)", background: "var(--jrni-color-surface-card)", boxShadow: "var(--jrni-shadow-sm)" }}>
                              <div style={{ fontSize: "13px", color: "var(--jrni-color-text-soft)" }}>{V.pv.lpWhen}</div>
                              <div style={{ fontSize: "22px", fontWeight: "700", letterSpacing: "-0.01em" }}>{V.pv.lpPrice}</div>
                              {V.pv.lpOpen ? (<>
                                <button type="button" onClick={V.actions.lpRegister} style={{ border: "0", cursor: "pointer", fontFamily: "inherit", fontSize: "15px", fontWeight: "600", color: "#FFFFFF", height: "44px", background: V.pv.brand, borderRadius: V.pv.radius }}>{V.pv.lpCta}</button>
                                <div style={{ fontSize: "12px", color: "var(--jrni-color-text-soft)" }}>{V.pv.lpSpacesLabel}</div>
                              </>) : null}
                              {V.pv.lpClosed ? (<>
                                <Pill tone={V.pv.lpClosedTone}>{V.pv.lpClosedLabel}
                                </Pill>
                                {V.pv.lpShowWaitlist ? (<>
                                  {V.pv.wlDone ? (<>
                                    <Banner tone="success" size="compact" title="You are on the list">{V.pv.wlNote}
                                    </Banner>
                                  </>) : null}
                                  {V.pv.wlOpen ? (<>
                                    <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                                      {/* A refusal is shown above the form, which stays so the guest can try again (CC 0320). */}
                                      {V.pv.wlError ? <Banner tone="danger" size="compact" title="You were not added to the waitlist">{V.pv.wlError}</Banner> : null}
                                      <div style={{ fontSize: "13px", color: "var(--jrni-color-text-body)" }}>Join the waitlist and we will e-mail you the moment a place opens.</div>
                                      {V.pv.wlShowTierChoice ? <Dropdown options={V.pv.wlTierChoices} value={V.pv.wlTierValue} onChange={V.actions.setSelectedTicketId} placeholder="Choose a ticket" full={true} /> : null}
                                      <Input value={V.pv.wlName} onChange={V.actions.setWlName} placeholder="Your name" full={true} />
                                      <Input type="email" value={V.pv.wlEmail} onChange={V.actions.setWlEmail} placeholder="you@example.com" full={true} />
                                      <Checkbox ref={V.pv.consentRef} checked={V.pv.consent} aria-label={V.pv.consentText} error={V.pv.consentError ? V.pv.consentRequiredText : undefined} onChange={V.actions.setConsent} label={V.pv.consentText} />
                                        
                                        <Button hierarchy="primary" full={true} onClick={V.actions.joinWaitlist} disabled={V.pv.wlSubmitting} aria-busy={V.pv.wlSubmitting || undefined}>{V.pv.wlSubmitting ? "Joining…" : "Join the waitlist"}
                                      </Button>
                                    </div>
                                  </>) : null}
                                </>) : null}
                              </>) : null}
                            </div>
                          </div>
                        </>) : null}
                        {V.s.stickyBar ? (<>
                          <div style={{ position: "sticky", bottom: "0", display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap", margin: "8px -24px -28px", padding: "12px 24px", borderTop: "1px solid var(--jrni-color-surface-border)", background: "var(--jrni-color-base-white)", boxShadow: "0 -6px 18px rgba(22, 34, 51, 0.08)" }}>
                            <div style={{ flex: "1 1 auto", minWidth: "0" }}>
                              <div style={{ fontSize: "14px", fontWeight: "700", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{V.pv.lpTitle}</div>
                              <div style={{ fontSize: "12px", color: "var(--jrni-color-text-soft)" }}>{V.pv.lpPrice ? <>{V.pv.lpPrice} · </> : null}{V.pv.lpSpacesLabel}</div>
                            </div>
                            {V.pv.lpOpen ? (<>
                              <button type="button" onClick={V.actions.lpRegister} style={{ flex: "0 0 auto", border: "0", cursor: "pointer", fontFamily: "inherit", fontSize: "14px", fontWeight: "600", color: "#FFFFFF", padding: "0 22px", height: "40px", background: V.pv.brand, borderRadius: V.pv.radius }}>{V.pv.lpCta}</button>
                            </>) : null}
                            {V.pv.lpClosed ? (<>
                              <Pill tone={V.pv.lpClosedTone}>{V.pv.lpClosedLabel}
                              </Pill>
                            </>) : null}
                          </div>
                        </>) : null}
                      </>) : null}
                      {V.pv.isEvents ? (<>
                        <div style={{ display: "flex", alignItems: "baseline", gap: "12px", flexWrap: "wrap" }}>
                          <h2 style={{ margin: "0", fontSize: "19px", fontWeight: "700", letterSpacing: "-0.01em" }}>{V.pv.eventsHeader}</h2>
                          <span style={{ fontSize: "13px", color: "var(--jrni-color-text-soft)" }}>{V.pv.eventShownLabel}</span>
                        </div>
                        {V.pv.showFilterBar ? (<>
                          <div style={{ position: "relative" }}>
                            <Field label="Event name">
                              <Input leading={V.icons.search} value={V.pv.evtQuery} onChange={V.actions.setEvtQuery} placeholder="Start typing an event name" full={true} />
                            </Field>
                            {V.pv.hasEvtMatches ? (<>
                              <div role="listbox" aria-label="Matching events" style={{ marginTop: "4px", maxHeight: "232px", overflowY: "auto", padding: "6px", display: "flex", flexDirection: "column", gap: "2px", background: "var(--jrni-color-base-white)", border: "1px solid var(--jrni-color-neutral-4)", borderRadius: "var(--jrni-radius-sm)", boxShadow: "var(--jrni-shadow-md)" }}>
                                {(V.pv.evtMatches || []).map((em, __i29) => (<React.Fragment key={em.key ?? em.id ?? __i29}>
                                  <button type="button" role="option" aria-selected={false} onClick={em.onPick} style={{ display: "flex", flexDirection: "column", gap: "1px", width: "100%", textAlign: "left", padding: "7px 9px", border: "0", borderRadius: "var(--jrni-radius-sm)", background: "transparent", cursor: "pointer", fontFamily: "inherit" }}>
                                    <span style={{ fontSize: "14px", fontWeight: "600", color: "var(--jrni-color-text-strong)" }}>{em.name}</span>
                                    <span style={{ fontSize: "12px", color: "var(--jrni-color-text-soft)" }}>{em.meta}</span>
                                  </button>
                                </React.Fragment>))}
                              </div>
                            </>) : null}
                          </div>
                          <div className="apd-evt-filters">
                            {V.s.filterLocation ? (<>
                              <div style={{ minWidth: "0" }}>
                                {/* A real filter, not a search box: the options are the read
                                    door's own location facet ({id, name}) and the picked id goes
                                    straight through `setEvtLocationFilter` → the list refresh's
                                    `locationId` param. The "use my location" pin that used to sit
                                    beside it is gone: there is no geosearch endpoint on this door
                                    (no lat/lng/radius param anywhere in the public event reads
                                    contract), so it could only ever have been decorative. */}
                                <Field label="Location">
                                  <Dropdown aria-label="Location" options={V.pv.locationOptions} value={V.pv.evtLocationValue}
                                    onChange={V.actions.setEvtLocationFilter}
                                    placeholder={V.pv.hasLocationOptions ? "All locations" : "No locations"}
                                    disabled={!V.pv.hasLocationOptions} />
                                </Field>
                              </div>
                            </>) : null}
                            {V.s.filterType ? (<>
                              <div style={{ minWidth: "0" }}>

                                <Field label="Type">
                                  <Dropdown options={V.pv.typeOptions} value={V.pv.eventFilterValue} onChange={V.actions.setEvtCat} />
                                </Field>
                              </div>
                            </>) : null}
                            {V.s.filterDates ? (<>
                              <div style={{ minWidth: "0" }}>
                                <DatePicker label="Start date" value={V.pv.evtFrom} onChange={V.actions.setEvtFrom} showTime={false} allowPast={true} leading={V.icons.calSm} full={true} />
                              </div>
                            </>) : null}
                            {V.s.filterDates ? (<>
                              <div style={{ minWidth: "0" }}>
                                <DatePicker label="End date" value={V.pv.evtTo} onChange={V.actions.setEvtTo} showTime={false} allowPast={true} leading={V.icons.calSm} full={true} />
                              </div>
                            </>) : null}
                            {/* Was a raw <button>: no focus ring, no hover/active treatment, and
                                the tenant's PREVIEW radius (V.pv.radius) sitting beside four
                                design-system controls that all read --jrni-radius-default. The

                                shared Button carries the state layer (tokens.css paints
                                hover/active as `filter: brightness()`, so the brand surface below
                                survives both) and the canonical `:focus-visible` ring. The brand
                                FILL is kept — this is the customer-facing preview, where the row

                                must read as the tenant's — while the RADIUS follows the row,
                                because its four neighbours are DS controls that cannot take a
                                per-tenant radius at all. Height/padding are the row's shared
                                control geometry; no handler is added, this control was and stays
                                inert in the preview. */}
                            <Button type="button" hierarchy="primary" className="apd-evt-search"
                              style={{ height: "var(--jrni-control-height-md)", padding: "0 22px", background: V.pv.brand, borderRadius: "var(--jrni-radius-control)" }}>Search events</Button>
                          </div>
                        </>) : null}
                        {V.s.eventFilters ? (<>
                          <ChipToggleGroup options={V.opts.eventFilterChips} value={V.pv.eventFilterValue} onChange={V.actions.setEvtCat} size="sm" aria-label="Event categories" />
                        </>) : null}
                        {V.pv.eventEmpty ? (<>
                          <InlineEmptyState title={V.pv.eventEmptyText} description="Change the filters, or allow full events back onto the list." />
                        </>) : null}
                        {V.is.evList ? (<>
                          <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                            {/* Same card as the appointment service step (KP, 2026-09-16):
                                image on top only when the page shows images, title, when · where
                                line in the brand colour, description; the Register / waitlist /
                                closed control sits bottom-right. */}
                            {(V.pv.eventCards || []).map((ev, __i30) => (<React.Fragment key={ev.key ?? ev.id ?? __i30}>
                              <div role={ev.interactive ? "button" : undefined} tabIndex={ev.interactive ? 0 : undefined} aria-disabled={ev.interactive ? undefined : true} onClick={ev.interactive ? ev.onSelect : undefined} style={{ display: "flex", flexDirection: "column", overflow: "hidden", cursor: ev.interactive ? "pointer" : "default", background: "var(--jrni-color-surface-card)", border: "1px solid var(--jrni-color-surface-border)", boxShadow: "var(--jrni-shadow-chrome)", borderRadius: V.pv.radius, opacity: ev.dim }}>
                                {V.s.eventImages && V.s.showMediaImages && ev.wide ? (<>
                                  <div style={{ width: "100%" }}>{ev.wide}</div>
                                </>) : null}
                                <div style={{ display: "flex", flexDirection: "column", gap: "6px", padding: "14px 16px 16px" }}>
                                  <div style={{ fontSize: "15px", fontWeight: "700", lineHeight: "1.3", letterSpacing: "-0.01em" }}>{ev.name}</div>
                                  {ev.sub ? (<div style={{ fontSize: "13px", fontWeight: "600", color: "var(--jrni-color-primary-text)" }}>{ev.sub}</div>) : null}
                                  {ev.showDesc && ev.desc ? (<div style={{ fontSize: "13px", lineHeight: "1.5", color: "var(--jrni-color-text-body)", textWrap: "pretty" }}>{ev.desc}</div>) : null}
                                  {ev.showMeter ? (<>
                                    <div style={{ maxWidth: "220px", paddingTop: "2px" }}>
                                      <MeterBar value={ev.taken} max={ev.cap} tone={ev.meterTone} label={ev.spaces} size="sm" />
                                    </div>
                                  </>) : null}
                                  <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: "10px", paddingTop: "4px" }}>
                                    {ev.bookable ? (<>
                                      <button type="button" onClick={ev.onSelect} style={{ border: "0", cursor: "pointer", fontFamily: "inherit", fontSize: "14px", fontWeight: "600", color: "#FFFFFF", padding: "0 18px", height: "44px", background: V.pv.brand, borderRadius: V.pv.radius }}>Register</button>
                                    </>) : null}
                                    {ev.showWaitlist ? (<>
                                      <Button variant="secondary" style={{ height: "44px" }} onClick={ev.onSelect}>{ev.cta}
                                      </Button>
                                    </>) : null}
                                    {ev.closed ? (<>
                                      <Pill tone={ev.closedTone}>{ev.spaces}
                                      </Pill>
                                    </>) : null}
                                  </div>
                                </div>
                              </div>
                            </React.Fragment>))}
                          </div>
                        </>) : null}
                        {V.is.evGrid ? (<>
                          <div style={{ display: "grid", gap: EVENT_GRID_GAP, gridTemplateColumns: V.pv.cardGrid }}>
                            {(V.pv.eventCards || []).map((ev, __i31) => (<React.Fragment key={ev.key ?? ev.id ?? __i31}>
                              <div role={ev.interactive ? "button" : undefined} tabIndex={ev.interactive ? 0 : undefined} aria-disabled={ev.interactive ? undefined : true} aria-pressed={ev.interactive ? ev.selected : undefined} onClick={ev.interactive ? ev.onSelect : undefined} style={{ display: "flex", flexDirection: "column", gap: "12px", minWidth: "0", height: "100%", cursor: ev.interactive ? "pointer" : "default", opacity: ev.dim }}>
                                {V.s.eventImages && V.s.showMediaImages ? (<>
                                  <div style={{ width: "100%", overflow: "hidden", borderRadius: V.pv.radius }}>{ev.wide}</div>
                                </>) : null}
                                <h3 style={{ margin: "0", fontSize: "19px", fontWeight: "600", lineHeight: "1.28", letterSpacing: "-0.01em", textWrap: "pretty" }}>{ev.name}</h3>
                                {ev.showDesc ? (<>
                                  <p style={{ margin: "0", fontSize: "14px", lineHeight: "1.5", color: "var(--jrni-color-text-body)", textWrap: "pretty" }}>{ev.desc}</p>
                                </>) : null}
                                <div style={{ display: "flex", flexDirection: "column", gap: "7px", fontSize: "13px", color: "var(--jrni-color-text-strong)" }}>
                                  {ev.showAddr ? (<>
                                    <div style={{ display: "flex", gap: "8px", alignItems: "flex-start" }}>
                                      <span style={{ flex: "0 0 16px", display: "flex", paddingTop: "2px", color: "var(--jrni-color-text-soft)" }}>{V.icons.pinSm}</span>
                                      <span style={{ flex: "1 1 auto", minWidth: "0" }}>{ev.addr}</span>
                                    </div>
                                  </>) : null}
                                  {ev.showPrice ? (<>
                                    <div style={{ fontWeight: "600" }}>{ev.priceLine}</div>
                                  </>) : null}
                                </div>
                                {/* Date/time sit beside the CTA, not stacked above the spaces-left meter
                                    (2026-09-28, CD) — "Register now" read as a standalone action floating
                                    below the availability bar; putting the two facts a visitor actually
                                    reads together (when, then can-I-go) on one row, CTA right-aligned,
                                    fixes that, and the meter now reads as a caption under the row rather
                                    than a wall between the facts and the button. */}
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
                                  <div style={{ display: "flex", flexDirection: "column", gap: "7px", fontSize: "13px", color: "var(--jrni-color-text-strong)" }}>
                                    {ev.showDate ? (<>
                                      <div style={{ display: "flex", gap: "8px", alignItems: "flex-start" }}>
                                        <span style={{ flex: "0 0 16px", display: "flex", paddingTop: "2px", color: "var(--jrni-color-text-soft)" }}>{V.icons.calSm}</span>
                                        <span style={{ flex: "1 1 auto", minWidth: "0" }}>{ev.date}</span>
                                      </div>
                                    </>) : null}
                                    {ev.showTime ? (<>
                                      <div style={{ display: "flex", gap: "8px", alignItems: "flex-start" }}>
                                        <span style={{ flex: "0 0 16px", display: "flex", paddingTop: "2px", color: "var(--jrni-color-text-soft)" }}>{V.icons.clockSm}</span>
                                        <span style={{ flex: "1 1 auto", minWidth: "0" }}>{ev.time}</span>
                                      </div>
                                    </>) : null}
                                  </div>
                                  <div style={{ flex: "0 0 auto" }}>
                                    {ev.bookable ? (<>
                                      <button type="button" onClick={ev.onSelect} style={{ border: "0", cursor: "pointer", fontFamily: "inherit", fontSize: "14px", fontWeight: "600", color: "#FFFFFF", padding: "0 26px", height: "44px", background: V.pv.brand, borderRadius: V.pv.radius }}>Register now</button>
                                    </>) : null}
                                    {ev.showWaitlist ? (<>
                                      <Button variant="secondary" style={{ height: "44px" }} onClick={ev.onSelect}>{ev.cta}
                                      </Button>
                                    </>) : null}
                                    {ev.closed ? (<>
                                      <Pill tone={ev.closedTone}>{ev.spaces}
                                      </Pill>
                                    </>) : null}
                                  </div>
                                </div>
                                {ev.showMeter ? (<>
                                  <MeterBar value={ev.taken} max={ev.cap} tone={ev.meterTone} label={ev.spaces} size="sm" />
                                </>) : null}
                              </div>
                            </React.Fragment>))}
                          </div>
                        </>) : null}
                        {V.pv.showPager ? (<>
                          <div style={{ display: "flex", justifyContent: "center", paddingTop: "6px" }}>
                            <Pagination totalPages={V.pv.eventPages} currentPage={V.pv.eventPage} onPageChange={V.actions.setEvtPage} showStatus={true} size="sm" />
                          </div>
                        </>) : null}
                        {V.pv.showLoadMoreEvents ? (<>
                          <Button variant="secondary" full={true} onClick={V.actions.moreEvents}>Load more events
                          </Button>
                        </>) : null}
                        {V.pv.showScrollSentinel ? (<>
                          <div ref={(el) => this.attachScrollSentinel(el)} data-testid="events-scroll-sentinel" style={{ height: "1px" }} />
                        </>) : null}
                      </>) : null}
                      {V.pv.isService ? (<>
                        <h2 style={{ margin: "0", fontSize: "19px", fontWeight: "700", letterSpacing: "-0.01em" }}>{V.pv.serviceHeader}</h2>
                        {V.pv.serviceCatalogEmpty ? (<>
                          <div role="status" style={{ border: "1px dashed var(--jrni-color-surface-border)", borderRadius: V.pv.radius, padding: "18px 16px", color: "var(--jrni-color-text-body)", background: "var(--jrni-color-surface-card)" }}>{V.pv.serviceCatalogEmptyText}</div>
                        </>) : null}
                        {V.s.showCategories ? (<>
                          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                            {(V.pv.categories || []).map((c, __i32) => (<React.Fragment key={c.key ?? c.id ?? __i32}>
                              <Chip selected={c.selected} onClick={c.onClick}>{c.label}
                              </Chip>
                            </React.Fragment>))}
                          </div>
                        </>) : null}
                        {V.pv.serviceGrid ? (<>
                          <div style={{ display: "grid", gap: SERVICE_GRID_GAP, gridTemplateColumns: V.pv.serviceCols }}>
                            {(V.pv.visibleServices || []).map((sv, __i33) => (<React.Fragment key={sv.key ?? sv.id ?? __i33}>
                              <div role="button" tabIndex="0" aria-pressed={sv.selected} onClick={sv.onSelect} style={{ position: "relative", display: "flex", flexDirection: "column", cursor: "pointer", overflow: "hidden", background: "var(--jrni-color-surface-card)", border: `${sv.borderWidth} solid ${sv.border}`, boxShadow: sv.shadow, borderRadius: V.pv.radius }}>
                                {V.s.showServiceImages && V.s.showMediaImages ? (<>
                                  <div style={{ width: "100%" }}>{sv.cover}</div>
                                </>) : null}
                                <div style={{ display: "flex", flexDirection: "column", gap: "6px", padding: "14px 16px 16px" }}>
                                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px" }}>
                                    <div style={{ fontSize: "15px", fontWeight: "700", lineHeight: "1.3", letterSpacing: "-0.01em" }}>{sv.name}</div>
                                    {sv.selected ? (<span aria-hidden="true" style={{ flexShrink: 0, width: "24px", height: "24px", borderRadius: "999px", background: sv.checkBg, color: "#FFFFFF", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: "14px", fontWeight: "700", lineHeight: "1" }}>{"\u2713"}</span>) : null}
                                  </div>
                                  {sv.hasMeta ? (<>
                                    <div style={{ fontSize: "13px", fontWeight: "600", color: "var(--jrni-color-primary-text)" }}>{sv.meta}</div>
                                  </>) : null}
                                  <div style={{ fontSize: "13px", lineHeight: "1.5", color: "var(--jrni-color-text-body)", textWrap: "pretty" }}>{sv.desc}</div>
                                </div>
                              </div>
                            </React.Fragment>))}
                          </div>
                        </>) : null}
                        {V.pv.serviceRows ? (<>
                          {/* "Compact" is the same card, single column (KP, 2026-09-16): every
                              journey's service step reads title · duration · description. */}
                          <div style={{ display: "grid", gap: "10px", gridTemplateColumns: "minmax(0, 1fr)" }}>
                            {(V.pv.visibleServices || []).map((sv, __i34) => (<React.Fragment key={sv.key ?? sv.id ?? __i34}>
                              <div role="button" tabIndex="0" aria-pressed={sv.selected} onClick={sv.onSelect} style={{ position: "relative", display: "flex", flexDirection: "column", cursor: "pointer", overflow: "hidden", background: "var(--jrni-color-surface-card)", border: `${sv.borderWidth} solid ${sv.border}`, boxShadow: sv.shadow, borderRadius: V.pv.radius }}>
                                {V.s.showServiceImages && V.s.showMediaImages && sv.hasImage ? (<>
                                  <div style={{ width: "100%" }}>{sv.cover}</div>
                                </>) : null}
                                <div style={{ display: "flex", flexDirection: "column", gap: "6px", padding: "14px 16px 16px" }}>
                                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px" }}>
                                    <div style={{ fontSize: "15px", fontWeight: "700", lineHeight: "1.3", letterSpacing: "-0.01em" }}>{sv.name}</div>
                                    {sv.selected ? (<span aria-hidden="true" style={{ flexShrink: 0, width: "24px", height: "24px", borderRadius: "999px", background: sv.checkBg, color: "#FFFFFF", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: "14px", fontWeight: "700", lineHeight: "1" }}>{"\u2713"}</span>) : null}
                                  </div>
                                  {sv.metaLine ? (<div style={{ fontSize: "13px", fontWeight: "600", color: "var(--jrni-color-primary-text)" }}>{sv.metaLine}</div>) : null}
                                  {sv.desc ? (<div style={{ fontSize: "13px", lineHeight: "1.5", color: "var(--jrni-color-text-body)", textWrap: "pretty" }}>{sv.desc}</div>) : null}
                                </div>
                              </div>
                            </React.Fragment>))}
                          </div>
                        </>) : null}
                        {V.pv.showDurations ? (<>
                          <div style={{ display: "flex", flexDirection: "column", gap: "10px", paddingTop: "4px", borderTop: "1px solid var(--jrni-color-neutral-6)" }}>
                            <h3 style={{ margin: "12px 0 0", fontSize: "15px", fontWeight: "700" }}>{V.pv.durationHeader}</h3>
                            <div style={{ display: "grid", gap: "10px", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))" }}>
                              {(V.pv.durations || []).map((d, __i35) => (<React.Fragment key={d.key ?? d.id ?? __i35}>
                                <ChoiceTile showCheck={false} label={d.label} sublabel={d.sub} selected={d.selected} onSelect={d.onSelect} full={true} />
                              </React.Fragment>))}
                            </div>
                          </div>
                        </>) : null}
                      </>) : null}
                      {V.pv.isMapOnly ? (<>
                        <h2 style={{ margin: "0", fontSize: "19px", fontWeight: "700", letterSpacing: "-0.01em" }}>{V.pv.locationHeader}</h2>
                        {V.pv.locIdle ? (<>
                          <InlineEmptyState title="Choose a service first" description="Locations with open times appear once a service is selected." />
                        </>) : null}
                        {V.pv.locLoading ? (<>
                          <Banner tone="info" size="compact" title="Checking which locations have open times">Only locations with a bookable time for this service are listed.</Banner>
                        </>) : null}
                        {V.pv.locError ? (<>
                          <Banner tone="danger" size="compact" title="Locations could not be loaded">Open times for this service could not be read, so no location can be offered yet. Try again in a moment.</Banner>
                        </>) : null}
                        {V.pv.locEmpty ? (<>
                          <InlineEmptyState title="No location has open times for this service" description="None of the saved locations has a bookable time in the booking window." />
                        </>) : null}
                        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                          {/* The search sits ABOVE the map now rather than floating
                              over it: there is a real map underneath, and covering
                              it with a panel was only tolerable while the canvas
                              was decorative. */}
                          <div style={{ display: "flex", gap: "8px" }}>
                            <div style={{ flex: "1 1 auto", maxWidth: "340px", position: "relative" }}>
                              <Input leading={V.icons.search} placeholder="Search a campus, town or ZIP code" value={V.pv.locQuery} onChange={V.actions.setLocQuery} full={true} />
                              {V.pv.hasSuggestions ? (<>
                                <div role="listbox" aria-label="Matching locations" style={{ marginTop: "4px", maxHeight: "232px", overflowY: "auto", padding: "6px", display: "flex", flexDirection: "column", gap: "2px", background: "var(--jrni-color-base-white)", border: "1px solid var(--jrni-color-neutral-4)", borderRadius: "var(--jrni-radius-sm)", boxShadow: "var(--jrni-shadow-md)" }}>
                                  {(V.pv.suggestions || []).map((sg, __i36) => (<React.Fragment key={sg.key ?? sg.id ?? __i36}>
                                    <button type="button" role="option" aria-selected={sg.selected} onClick={sg.onPick} style={{ display: "flex", flexDirection: "column", gap: "1px", width: "100%", textAlign: "left", padding: "7px 9px", border: "0", borderRadius: "var(--jrni-radius-sm)", background: sg.bg, cursor: "pointer", fontFamily: "inherit" }}>
                                      <span style={{ fontSize: "14px", fontWeight: "600", color: "var(--jrni-color-text-strong)" }}>{sg.name}</span>
                                      <span style={{ fontSize: "12px", color: "var(--jrni-color-text-soft)" }}>{sg.meta}</span>
                                    </button>
                                  </React.Fragment>))}
                                </div>
                              </>) : null}
                            </div>
                            <Button variant="secondary" iconOnly={V.icons.locate} aria-label="Use my location" />
                          </div>
                          {/* The pins are the bookable locations, so they are
                              stale until the window lands. The ghost keeps the
                              authored map height, so the step does not resize. */}
                          {V.pv.locLoading ? (
                            <LoadingRegion label="Checking which locations have open times" testId="appointment-map-ghost">
                              <SkeletonBox width="100%" height={parseInt(String(V.pv.mapHeight), 10) || 220} radius={V.pv.radius} />
                            </LoadingRegion>
                          ) : (
                          <LocationsMap pins={V.pv.mapLocations || []} height={parseInt(String(V.pv.mapHeight), 10) || 220} caption={V.pv.mapCaption} />
                          )}
                        </div>
                        <div onPointerDown={V.actions.startMapResize} role="separator" aria-label="Drag to resize the map" title="Drag to resize the map" style={{ marginTop: "-10px", height: "16px", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", cursor: "ns-resize", touchAction: "none" }}>
                          <span style={{ width: "40px", height: "4px", borderRadius: "999px", background: "var(--jrni-color-neutral-4)" }}></span>
                          {V.pv.resizingMap ? (<>
                            <span style={{ fontSize: "12px", color: "var(--jrni-color-text-soft)" }}>{V.pv.mapHeightLabel}</span>
                          </>) : null}
                        </div>
                        {!V.pv.locLoading && V.pv.locPicked ? (<>
                        <div style={{ display: "flex", gap: "14px", alignItems: "center", padding: "14px", border: "1px solid var(--jrni-color-surface-border)", borderRadius: "var(--jrni-radius-md)", background: "var(--jrni-color-surface-card)", boxShadow: "var(--jrni-shadow-sm)" }}>
                          <div style={{ flex: "1 1 auto", minWidth: "0" }}>
                            <div style={{ fontSize: "15px", fontWeight: "700" }}>{V.pv.pinName}</div>
                            <div style={{ fontSize: "13px", color: "var(--jrni-color-text-body)" }}>{V.pv.selectedAddr}</div>
                            {V.pv.showNextLine ? (<>
                              <div style={{ fontSize: "13px", color: "var(--jrni-color-text-soft)" }}>Next available {V.pv.selectedNext}</div>
                            </>) : null}
                            {V.s.extraLocationInfo ? (<>
                              <div style={{ fontSize: "13px", color: "var(--jrni-color-text-soft)" }}>{V.pv.selectedHours}</div>
                            </>) : null}
                          </div>
                          <button type="button" onClick={V.actions.nextStep} style={{ flex: "0 0 auto", border: "0", cursor: "pointer", fontFamily: "inherit", fontSize: "14px", fontWeight: "600", color: "#FFFFFF", padding: "0 22px", height: "42px", background: V.pv.brand, borderRadius: V.pv.radius }}>Choose this location</button>
                        </div>
                        </>) : null}
                        {V.pv.hasVirtual ? (<>
                          <Button variant="link" size="sm" onClick={V.pv.onVirtual}>{V.pv.virtualLabel}
                          </Button>
                        </>) : null}
                      </>) : null}
                      {V.pv.isLocation ? (<>
                        <h2 style={{ margin: "0", fontSize: "19px", fontWeight: "700", letterSpacing: "-0.01em" }}>{V.pv.locationHeader}</h2>
                        {V.is.appt ? (<>
                          <div style={{ display: "flex", gap: "10px", alignItems: "flex-start" }}>
                            <div style={{ flex: "1 1 auto", minWidth: "0", position: "relative" }}>
                              <Input leading={V.icons.search} placeholder="Enter a campus, town or ZIP code" value={V.pv.locQuery} onChange={V.actions.setLocQuery} full={true} />
                              {V.pv.hasSuggestions ? (<>
                                <div role="listbox" aria-label="Matching locations" style={{ marginTop: "4px", maxHeight: "232px", overflowY: "auto", padding: "6px", display: "flex", flexDirection: "column", gap: "2px", background: "var(--jrni-color-base-white)", border: "1px solid var(--jrni-color-neutral-4)", borderRadius: "var(--jrni-radius-sm)", boxShadow: "var(--jrni-shadow-md)" }}>
                                  {(V.pv.suggestions || []).map((sg, __i38) => (<React.Fragment key={sg.key ?? sg.id ?? __i38}>
                                    <button type="button" role="option" aria-selected={sg.selected} onClick={sg.onPick} style={{ display: "flex", flexDirection: "column", gap: "1px", width: "100%", textAlign: "left", padding: "7px 9px", border: "0", borderRadius: "var(--jrni-radius-sm)", background: sg.bg, cursor: "pointer", fontFamily: "inherit" }}>
                                      <span style={{ fontSize: "14px", fontWeight: "600", color: "var(--jrni-color-text-strong)" }}>{sg.name}</span>
                                      <span style={{ fontSize: "12px", color: "var(--jrni-color-text-soft)" }}>{sg.meta}</span>
                                    </button>
                                  </React.Fragment>))}
                                </div>
                              </>) : null}
                            </div>
                            <Button variant="secondary" style={{ height: "var(--jrni-control-height-md)" }} iconLeft={V.icons.pin} onClick={V.actions.useMyLocation} disabled={V.pv.locating}>{V.pv.geoLabel}
                            </Button>
                          </div>
                          {V.pv.geoDenied ? (<>
                            <p style={{ margin: "-4px 0 0", fontSize: "13px", color: "var(--jrni-color-text-soft)" }}>{V.pv.geoNote}</p>
                          </>) : null}
                        </>) : null}
                        {V.pv.showMapNow ? (<>
                          {/* Real pins, same as the map-only step. This block
                              carried the same gradient canvas, plus mock "Map /
                              Satellite" chips that controlled nothing. */}
                          {V.pv.locLoading ? (
                            <LoadingRegion label="Checking which locations have open times" testId="appointment-map-ghost">
                              <SkeletonBox width="100%" height={parseInt(String(V.pv.mapHeight), 10) || 190} radius={V.pv.radius} />
                            </LoadingRegion>
                          ) : (
                          <LocationsMap pins={V.pv.mapLocations || []} height={parseInt(String(V.pv.mapHeight), 10) || 190} caption={V.pv.mapCaption} />
                          )}
                          <div onPointerDown={V.actions.startMapResize} role="separator" aria-label="Drag to resize the map" title="Drag to resize the map" style={{ marginTop: "-6px", height: "16px", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", cursor: "ns-resize", touchAction: "none" }}>
                            <span style={{ width: "40px", height: "4px", borderRadius: "999px", background: "var(--jrni-color-neutral-4)" }}></span>
                            {V.pv.resizingMap ? (<>
                              <span style={{ fontSize: "12px", color: "var(--jrni-color-text-soft)" }}>{V.pv.mapHeightLabel}</span>
                            </>) : null}
                          </div>
                        </>) : null}
                        {V.pv.locIdle ? (<>
                          <InlineEmptyState title="Choose a service first" description="Locations with open times appear once a service is selected." />
                        </>) : null}
                        {V.pv.locLoading ? (<>
                          <Banner tone="info" size="compact" title="Checking which locations have open times">Only locations with a bookable time for this service are listed.</Banner>
                        </>) : null}
                        {V.pv.locError ? (<>
                          <Banner tone="danger" size="compact" title="Locations could not be loaded">Open times for this service could not be read, so no location can be offered yet. Try again in a moment.</Banner>
                        </>) : null}
                        {V.pv.locEmpty ? (<>
                          <InlineEmptyState title="No location has open times for this service" description="None of the saved locations has a bookable time in the booking window." />
                        </>) : null}
                        {/* Which locations are bookable is decided by the window
                            still being read, so the rows below are stale until it
                            lands: ghosts replace them rather than leaving old rows
                            clickable. Error and empty keep their own states. */}
                        {V.pv.locLoading ? (<>
                          <LoadingRegion label="Checking which locations have open times" testId="appointment-locations-ghost">
                            <GhostJourneyStep step="location" count={3} radius={V.pv.radius} />
                          </LoadingRegion>
                        </>) : null}
                        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                          {!V.pv.locLoading && V.pv.deskList ? (<>
                            {(V.pv.visibleLocations || []).map((lo, __i39) => (<React.Fragment key={lo.key ?? lo.id ?? __i39}>
                              <ChoiceTile showCheck={false} label={lo.name} sublabel={lo.addr} leading={lo.leading} trailing={lo.dist} selected={lo.selected} onSelect={lo.onSelect} full={true} />
                            </React.Fragment>))}
                          </>) : null}
                          {!V.pv.locLoading && V.pv.branchList ? (<>
                            {(V.pv.visibleLocations || []).map((lo, __i40) => (<React.Fragment key={lo.key ?? lo.id ?? __i40}>
                              <div role="button" tabIndex="0" aria-pressed={lo.selected} onClick={lo.onSelect} style={{ cursor: "pointer", padding: "16px 18px", display: "flex", flexDirection: "column", gap: "4px", background: "var(--jrni-color-surface-card)", border: `1px solid ${lo.border}`, borderRadius: "var(--jrni-radius-md)", boxShadow: lo.shadow }}>
                                <div style={{ display: "flex", alignItems: "baseline", gap: "12px" }}>
                                  <span style={{ flex: "0 0 auto", display: "flex", alignSelf: "center", color: "var(--jrni-color-text-soft)" }}>{V.icons.pinSm}</span>
                                  <span style={{ flex: "1 1 auto", minWidth: "0", fontSize: "16px", fontWeight: "700", letterSpacing: "-0.01em", color: "var(--jrni-color-text-strong)" }}>{lo.name}</span>
                                  <span style={{ flex: "0 0 auto", fontSize: "13px", color: "var(--jrni-color-text-soft)" }}>{lo.dist}</span>
                                </div>
                                <div style={{ fontSize: "14px", color: "var(--jrni-color-text-soft)" }}>{lo.addr}</div>
                                {lo.showPhone ? (<>
                                  <div style={{ fontSize: "14px", color: "var(--jrni-color-text-soft)" }}>{lo.phone}</div>
                                </>) : null}
                                {lo.showNext ? (<>
                                  <div style={{ fontSize: "14px", color: "var(--jrni-color-text-strong)" }}>
                                    <strong style={{ fontWeight: "700" }}>Next available:</strong> {lo.nextLabel}
                                  </div>
                                </>) : null}
                                {lo.quickbook ? (<>
                                  <div style={{ paddingTop: "8px" }}>
                                    <Button variant="secondary" onClick={lo.onQuickbook}>Quick book this appointment
                                    </Button>
                                  </div>
                                </>) : null}
                                {lo.showHours ? (<>
                                  <div style={{ paddingTop: "10px", display: "flex", flexDirection: "column", gap: "6px" }}>
                                    <button type="button" onClick={lo.onToggleHours} aria-expanded={lo.hoursOpen} style={{ alignSelf: "flex-start", display: "inline-flex", alignItems: "center", gap: "6px", padding: "0", background: "none", border: "0", cursor: "pointer", fontFamily: "inherit", fontSize: "14px", fontWeight: "700", color: "var(--jrni-color-text-strong)" }}>Branch hours
                                      <span style={{ display: "flex", color: "var(--jrni-color-text-soft)", transform: lo.hoursCaret }}>{V.icons.chevron}</span>
                                    </button>
                                    {lo.hoursOpen ? (<>
                                      <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                                        <div style={{ fontSize: "12px", color: "var(--jrni-color-text-soft)", paddingBottom: "4px" }}>{lo.tzLabel}</div>
                                        {(lo.week || []).map((wd, __i41) => (<React.Fragment key={wd.key ?? wd.id ?? __i41}>
                                          <div style={{ display: "flex", alignItems: "baseline", gap: "12px", fontSize: "13px", padding: "3px 0", borderTop: "1px solid var(--jrni-color-neutral-6)" }}>
                                            <span style={{ flex: "0 0 96px", fontWeight: "600", color: "var(--jrni-color-text-strong)" }}>{wd.day}</span>
                                            <span style={{ flex: "1 1 auto", color: "var(--jrni-color-text-soft)" }}>{wd.label}</span>
                                          </div>
                                        </React.Fragment>))}
                                      </div>
                                    </>) : null}
                                  </div>
                                </>) : null}
                              </div>
                            </React.Fragment>))}
                          </>) : null}
                          {V.pv.showLoadMore ? (<>
                            <Button variant="secondary" full={true} onClick={V.actions.moreLocations}>{V.pv.loadMoreLabel}
                            </Button>
                          </>) : null}
                        </div>
                      </>) : null}
                      {V.pv.isDatetime ? (<>
                        <h2 style={{ margin: "0", fontSize: "19px", fontWeight: "700", letterSpacing: "-0.01em" }}>{V.pv.dateHeader}</h2>
                        {V.pv.calendarError ? <Banner tone="danger" size="compact" title={V.pv.calendarError} /> : null}
                        <div role="status" aria-live="polite">
                          {V.pv.slotTakenNotice ? (<Banner tone="neutral" size="compact" title={V.pv.slotTakenNotice}></Banner>) : null}
                        </div>
                        {V.pv.staffPicker ? (<>
                          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                            <SectionLabel tone="soft">Adviser
                            </SectionLabel>
                            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                              {(V.pv.staff || []).map((p, __i42) => (<React.Fragment key={p.key ?? p.id ?? __i42}>
                                <Chip leading={p.leading} selected={p.selected} onClick={p.onClick}>{p.name}
                                </Chip>
                              </React.Fragment>))}
                            </div>
                          </div>
                        </>) : null}
                        <div style={{ display: "flex", alignItems: V.pv.dtAlign, gap: "24px", flexWrap: V.pv.dtWrap, flexDirection: V.pv.dtDir }}>
                          <div style={{ flex: V.pv.dtCalFlex, minWidth: "0", display: "flex", flexDirection: "column", gap: "14px" }}>
                            {V.is.dayStrip ? (<>
                              <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                  <Button variant="ghost" size="sm" iconOnly={V.icons.left} aria-label="Previous week" onClick={V.actions.stripPrev} disabled={V.pv.stripPrevDisabled} />
                                  <div style={{ flex: "1 1 auto", textAlign: "center", fontSize: "14px", fontWeight: "700" }}>{V.pv.stripLabel}</div>
                                  <Button variant="ghost" size="sm" iconOnly={V.icons.right} aria-label="Next week" onClick={V.actions.stripNext} disabled={V.pv.stripNextDisabled} />
                                </div>
                                {/* A day tile's enabled state is whether it has a
                                    slot, so the strip is stale until the window
                                    lands. Same count, width and radius as the real
                                    tiles, so the row does not resize. */}
                                {V.pv.availLoading ? (
                                  <LoadingRegion label="Loading available days" testId="appointment-daystrip-ghost">
                                    <div style={{ display: "flex", gap: "8px", overflowX: "hidden", paddingBottom: "4px" }}>
                                      {(V.pv.days || []).map((dy, __i43) => (
                                        <SkeletonBox key={dy.key ?? dy.id ?? __i43} width={64} height={52} radius="var(--jrni-radius-default)" style={{ flexShrink: 0 }} />
                                      ))}
                                    </div>
                                  </LoadingRegion>
                                ) : (
                                <div role="tablist" aria-label="Pick a day" style={{ display: "flex", gap: "8px", overflowX: "auto", paddingBottom: "4px" }}>
                                  {(V.pv.days || []).map((dy, __i43) => (<React.Fragment key={dy.key ?? dy.id ?? __i43}>
                                    <button type="button" role="tab" aria-selected={dy.selected} disabled={dy.disabled} onClick={dy.onSelect} style={{ flexShrink: "0", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "2px", minWidth: "64px", padding: "8px 10px", fontFamily: "inherit", borderRadius: "var(--jrni-radius-default)", border: `1px solid ${dy.border}`, background: dy.bg, opacity: dy.opacity, cursor: dy.cursor }}>
                                      <span style={{ fontSize: "13px", fontWeight: "600", color: dy.fg }}>{dy.label}</span>
                                      <span style={{ fontSize: "11px", color: dy.subFg }}>{dy.sublabel}</span>
                                    </button>
                                  </React.Fragment>))}
                                </div>
                                )}
                              </div>
                            </>) : null}
                            {V.is.monthCal ? (<>
                              <div style={{ maxWidth: "420px", border: "1px solid var(--jrni-color-surface-border)", borderRadius: "var(--jrni-radius-md)", background: "var(--jrni-color-surface-card)", padding: "14px 16px 16px" }}>
                                <div style={{ display: "flex", alignItems: "center", gap: "8px", paddingBottom: "10px" }}>
                                  <Button variant="ghost" size="sm" iconOnly={V.icons.left} aria-label="Previous month" onClick={V.actions.calPrev} disabled={V.pv.calPrevDisabled} />
                                  <div style={{ flex: "1 1 auto", textAlign: "center", fontSize: "14px", fontWeight: "700" }}>{V.pv.calTitle}</div>
                                  <Button variant="ghost" size="sm" iconOnly={V.icons.right} aria-label="Next month" onClick={V.actions.calNext} />
                                </div>
                                <div style={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))", gap: "4px", paddingBottom: "6px" }}>
                                  <span style={{ textAlign: "center", fontSize: "11px", letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--jrni-color-text-soft)" }}>Mon</span>
                                  <span style={{ textAlign: "center", fontSize: "11px", letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--jrni-color-text-soft)" }}>Tue</span>
                                  <span style={{ textAlign: "center", fontSize: "11px", letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--jrni-color-text-soft)" }}>Wed</span>
                                  <span style={{ textAlign: "center", fontSize: "11px", letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--jrni-color-text-soft)" }}>Thu</span>
                                  <span style={{ textAlign: "center", fontSize: "11px", letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--jrni-color-text-soft)" }}>Fri</span>
                                  <span style={{ textAlign: "center", fontSize: "11px", letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--jrni-color-text-soft)" }}>Sat</span>
                                  <span style={{ textAlign: "center", fontSize: "11px", letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--jrni-color-text-soft)" }}>Sun</span>
                                </div>
                                {/* Same grid either way, so the month does not
                                    resize when the window lands. While the read
                                    is open the cells are ghosts rather than a
                                    full month of unselectable days, which is
                                    what an empty window renders as. */}
                                {V.pv.availLoading ? (
                                  <LoadingRegion label="Loading available days" testId="appointment-calendar-ghost">
                                    <div style={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))", gap: "4px" }}>
                                      {(V.pv.calCells || []).map((c, __i44) => (
                                        <SkeletonBox key={c.key ?? c.id ?? __i44} width="100%" height={38} radius="999px" />
                                      ))}
                                    </div>
                                  </LoadingRegion>
                                ) : (
                                <div style={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))", gap: "4px" }}>
                                  {(V.pv.calCells || []).map((c, __i44) => (<React.Fragment key={c.key ?? c.id ?? __i44}>
                                    <button type="button" onClick={c.onSelect} disabled={c.disabled} aria-pressed={c.selected} style={{ height: "38px", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "inherit", fontSize: "13px", borderRadius: "999px", background: c.bg, color: c.fg, border: `1px solid ${c.border}`, cursor: c.cursor, fontWeight: c.weight }}>{c.label}</button>
                                  </React.Fragment>))}
                                </div>
                                )}
                              </div>
                            </>) : null}
                          </div>
                          <div style={{ flex: V.pv.dtTimeFlex, minWidth: "0", display: "flex", flexDirection: "column", gap: "12px" }}>
                            <h3 style={{ margin: "6px 0 0", fontSize: "15px", fontWeight: "700" }}>{V.pv.timeHeader}</h3>
                            <p style={{ margin: "-6px 0 0", fontSize: "13px", color: "var(--jrni-color-text-soft)" }}>{V.pv.selectedDayLabel}</p>
                            {V.pv.availLoading ? (<>
                              <LoadingRegion label="Loading available times" testId="appointment-slots-ghost">
                                <GhostJourneyStep step="slots" count={10} radius={V.pv.radius} />
                              </LoadingRegion>
                            </>) : null}
                            {!V.pv.availLoading && V.pv.noSlots ? (<>
                              <InlineEmptyState title={V.pv.noSlotsTitle} description={V.pv.noSlotsBody} />
                            </>) : null}
                            {!V.pv.availLoading && V.pv.groupedSlots ? (<>
                              <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
                                {(V.pv.slotGroups || []).map((grp, __i45) => (<React.Fragment key={grp.key ?? grp.id ?? __i45}>
                                  <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                                    <SectionLabel tone="soft">{grp.label}
                                    </SectionLabel>
                                    <SlotPicker slots={grp.slots} value={V.pv.slot} onChange={V.actions.setSlot} minColWidth={96} />
                                  </div>
                                </React.Fragment>))}
                              </div>
                            </>) : null}
                            {!V.pv.availLoading && V.pv.flatSlots ? (<>
                              <SlotPicker slots={V.pv.allSlots} value={V.pv.slot} onChange={V.actions.setSlot} minColWidth={96} />
                            </>) : null}
                            {V.s.showTimezone ? (<>
                              <div style={{ display: "flex", flexDirection: "column", gap: "4px", maxWidth: "320px", paddingTop: "4px", fontSize: "13px" }}>
                                <span style={{ fontWeight: "500" }}>{V.pv.tzLabelNode}</span>
                              </div>
                            </>) : null}
                          </div>
                        </div>
                      </>) : null}
                      {V.pv.isDetails ? (<>
                        {V.pv.showEventSummary ? (<>
                          <div style={{ display: "flex", gap: "12px", alignItems: "center", padding: "12px", border: "1px solid var(--jrni-color-surface-border)", borderRadius: "var(--jrni-radius-md)", background: "var(--jrni-color-surface-card-soft)" }}>
                            <div style={{ flex: "1 1 auto", minWidth: "0" }}>
                              <div style={{ fontSize: "14px", fontWeight: "700" }}>{V.pv.evtName}</div>
                              <div style={{ fontSize: "13px", color: "var(--jrni-color-text-soft)" }}>{V.pv.evtSummaryLine}</div>
                            </div>
                            <Button variant="link" size="sm" onClick={V.actions.prevStep}>Change
                            </Button>
                          </div>
                        </>) : null}
                        <h2 style={{ margin: "0", fontSize: "19px", fontWeight: "700", letterSpacing: "-0.01em" }}>{V.pv.detailsHeader}</h2>
                        {V.pv.ticketChoices && V.pv.ticketChoices.length > 1 ? (<>
                          <Field label="Ticket type" required={true} error={V.pv.ticketChoiceError} hint={V.pv.ticketWaitlistHint}>
                            <Dropdown options={V.pv.ticketChoices} value={V.pv.selectedTicketId} onChange={V.actions.setSelectedTicketId} placeholder="Choose a ticket type" full={true} />
                          </Field>
                        </>) : null}
                        {V.pv.showTickets ? (<>
                          <Field label={V.pv.ticketsLabel} hint={V.pv.ticketsHint} htmlFor="jrn-tickets">
                            <Input id="jrn-tickets" type="number" value={V.pv.tickets} onChange={V.actions.setTickets} style={{  }} />
                          </Field>
                        </>) : null}
                        {V.pv.signedIn ? (<>
                          <div style={{ display: "flex", alignItems: "center", gap: "10px", padding: "10px 12px", border: "1px solid var(--jrni-color-surface-border)", borderRadius: "var(--jrni-radius-md)", background: "var(--jrni-color-surface-card-soft)" }}>
                            <span style={{ flex: "0 0 auto", display: "flex", color: "var(--jrni-color-text-soft)" }}>{V.icons.lock}</span>
                            <span style={{ flex: "1 1 auto", fontSize: "13px", color: "var(--jrni-color-text-body)" }}>{V.pv.signedInLabel}</span>
                          </div>
                        </>) : null}
                        <p style={{ margin: "0", fontSize: "13px", fontStyle: "italic", color: "var(--jrni-color-text-soft)" }}>* Indicates required fields</p>
                        {/* EVERY INPUT CARRIES ITS VISIBLE LABEL (docket 2026-09-18 item 6):
                            `Field` links its label only when given an id, and `Input` mints
                            its own otherwise, so label and control were unrelated elements
                            and the field was announced with no name. `aria-required` carries
                            what the asterisk says visually. */}
                        <div style={{ display: "grid", gap: "14px", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))" }}>
                          <Field label="First name" required={true} htmlFor="jrn-first" error={V.pv.firstError} errorId="jrn-first-error">
                            <Input id="jrn-first" aria-required={true} aria-invalid={V.pv.firstError ? true : undefined} aria-describedby={V.pv.firstError ? "jrn-first-error" : undefined} autoComplete="given-name" value={V.pv.firstValue} onChange={V.actions.setFirst} onBlur={V.actions.blurFirst} forceState={V.pv.firstState} readOnly={V.pv.firstLocked} full={true} />
                          </Field>
                          <Field label="Last name" required={true} htmlFor="jrn-last" error={V.pv.lastError} errorId="jrn-last-error">
                            <Input id="jrn-last" aria-required={true} aria-invalid={V.pv.lastError ? true : undefined} aria-describedby={V.pv.lastError ? "jrn-last-error" : undefined} autoComplete="family-name" value={V.pv.lastValue} onChange={V.actions.setLast} onBlur={V.actions.blurLast} forceState={V.pv.lastState} readOnly={V.pv.lastLocked} full={true} />
                          </Field>
                        </div>
                        {(this.isEvents() || this.isLanding()) || V.s.askEmail ? (<>
                          {/* Events/landing: the current JSON registration contract always
                              requires email, regardless of the authored `askEmail` toggle —
                              shown and required unconditionally rather than hideable into an
                              impossible submission. */}
                          <Field label={(this.isEvents() || this.isLanding()) ? "Email" : V.pv.emailLabel} required={(this.isEvents() || this.isLanding()) ? true : V.pv.emailRequired} htmlFor="jrn-email" error={V.pv.emailError} errorId="jrn-email-error">
                            <Input id="jrn-email" aria-invalid={V.pv.emailError ? true : undefined} aria-describedby={V.pv.emailError ? "jrn-email-error" : undefined} aria-required={(this.isEvents() || this.isLanding()) ? true : V.pv.emailRequired} autoComplete="email" type="email" value={V.pv.emailValue} onChange={V.actions.setEmail} onBlur={V.actions.blurEmail} forceState={V.pv.emailState} placeholder="you@example.com" readOnly={V.pv.emailLocked} full={true} />
                          </Field>
                        </>) : null}
                        {V.s.askPhone ? (<>
                          <Field label={V.pv.phoneLabel} required={V.pv.phoneRequired} htmlFor="jrn-phone" error={V.pv.phoneError} errorId="jrn-phone-error" hint={V.pv.phoneHint}>
                            <div style={{ display: "flex", gap: "8px" }}>
                              <Dropdown options={V.opts.dialCodes} value={V.pv.dialIso} onChange={V.actions.setPhoneCountry} disabled={V.pv.phoneLocked} aria-label="Country code" style={PHONE_COUNTRY_STYLE} />
                              <Input id="jrn-phone" aria-required={V.pv.phoneRequired} aria-invalid={V.pv.phoneError ? true : undefined} aria-describedby={V.pv.phoneError ? "jrn-phone-error" : undefined} autoComplete="tel-national" type="tel" value={V.pv.phoneValue} onChange={V.actions.setPhone} onBlur={V.actions.blurPhone} forceState={V.pv.phoneState} readOnly={V.pv.phoneLocked} style={PHONE_NUMBER_STYLE} full={true} />
                            </div>
                          </Field>
                        </>) : null}
                        {V.pv.hasBookingQuestions ? (<>
                          <div style={{ display: "flex", flexDirection: "column", gap: "14px", paddingTop: "6px", borderTop: "1px solid var(--jrni-color-surface-border)" }}>
                            <span style={{ fontSize: "13px", fontWeight: "700", color: "var(--jrni-color-text-strong)" }}>{V.pv.bookingQuestionsHeading}</span>
                            <BookingQuestionFields testid="jrn-booking-questions" idPrefix="jrn-bq-" questions={V.pv.bookingQuestionList || []} host={this.bookingQuestionsHost()} />
                          </div>
                        </>) : null}
                        {V.s.askAddress ? (
                          (this.state.journey === "events" || this.state.journey === "landing") ? (<>
                            {/* The event registration API accepts no address field — collecting it
                                here would be discarded silently, so it is disabled with the true
                                reason instead of offered. */}
                            <Field label={V.pv.addressLabel}>
                              <Input value="" disabled placeholder="Not collected for event registrations" full={true} />
                            </Field>
                          </>) : (<>
                          <Field label={V.pv.addressLabel} required={V.pv.addressRequired} htmlFor="jrn-address">
                            <div style={{ position: "relative" }}>
                              <Input id="jrn-address" aria-required={V.pv.addressRequired} autoComplete="street-address" value={V.pv.addrValue} onChange={V.actions.setAddr} forceState={V.pv.addrState} placeholder="Street, city, postcode" full={true} />
                            </div>
                          </Field>
                          </>)
                        ) : null}
                        {V.s.notifPref ? (
                          (this.state.journey === "events" || this.state.journey === "landing") ? (this.isPublic() ? null : <>
                            {/* The registration API accepts no notification-preference field — a
                                recorded choice here would be discarded silently. Guests are not
                                shown the "not available" note; the Studio preview keeps it. */}
                            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                              <span style={{ fontSize: "13px", fontWeight: "600" }}>Reminder preference</span>
                              <span style={{ fontSize: "12px", color: "var(--jrni-color-text-soft)" }}>Not available to set for event registrations yet.</span>
                            </div>
                          </>) : (<>
                          <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                            <span style={{ fontSize: "13px", fontWeight: "600" }}>How should we send your booking updates? *</span>
                            <div style={{ display: "flex", gap: "20px", flexWrap: "wrap" }}>
                              <Checkbox checked={V.pv.notifEmail} onChange={V.actions.setNotifEmail} label="Email" />
                              {V.pv.notifTextOffered ? <Checkbox checked={V.pv.notifSms} onChange={V.actions.setNotifText} label="Text message" /> : null}
                            </div>
                            {V.pv.notifTextOffered && V.pv.notifTextOnly ? (
                              <span style={{ fontSize: "12px", color: "var(--jrni-color-text-soft)" }}>We'll send updates to your mobile number only.</span>
                            ) : null}
                            {V.pv.notifRefused ? (
                              <span role="status" style={{ fontSize: "12px", color: "var(--jrni-color-text-soft)" }}>Choose at least one way to get your updates.</span>
                            ) : null}
                            {V.pv.notifServerError ? (
                              <span role="alert" style={{ fontSize: "12px", color: "var(--jrni-color-danger-text, #B42318)" }}>{V.pv.notifServerError}</span>
                            ) : null}
                          </div>
                          </>)
                        ) : null}
                        {V.pv.showParty ? (<>
                          <Field label="How many of you are waiting together?" hint={V.pv.partyHint}>
                            <Input type="number" value={V.pv.party} onChange={V.actions.setParty} style={{  }} />
                          </Field>
                        </>) : null}
                        {V.pv.showAttendees ? (<>
                          <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                            {(V.pv.attendeeList || []).map((at, __i47) => (<React.Fragment key={at.key ?? at.id ?? __i47}>
                              <div style={{ display: "flex", flexDirection: "column", gap: "12px", padding: "14px", border: "1px solid var(--jrni-color-surface-border)", borderRadius: "var(--jrni-radius-md)", background: "var(--jrni-color-surface-card-soft)" }}>
                                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                                  <span style={{ flex: "1 1 auto", fontSize: "13px", fontWeight: "700" }}>{at.title}</span>
                                  <Button variant="link" size="sm" danger={true} onClick={at.onRemove}>Remove
                                  </Button>
                                </div>
                                <div style={{ display: "grid", gap: "12px", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))" }}>
                                  {V.pv.attFirst ? (<>
                                    <Field label="First name" required={true} htmlFor={`jrn-att-${at.key ?? at.id ?? __i47}-first`} error={at.firstError} errorId={`jrn-att-${at.key ?? at.id ?? __i47}-first-error`}>
                                      <Input id={`jrn-att-${at.key ?? at.id ?? __i47}-first`} aria-invalid={at.firstError ? true : undefined} aria-describedby={at.firstError ? `jrn-att-${at.key ?? at.id ?? __i47}-first-error` : undefined} aria-required={true} value={at.first} onChange={at.setFirst} onBlur={at.blurFirst} forceState={at.firstState} full={true} />
                                    </Field>
                                  </>) : null}
                                  {V.pv.attLast ? (<>
                                    <Field label="Last name" required={true} htmlFor={`jrn-att-${at.key ?? at.id ?? __i47}-last`} error={at.lastError} errorId={`jrn-att-${at.key ?? at.id ?? __i47}-last-error`}>
                                      <Input id={`jrn-att-${at.key ?? at.id ?? __i47}-last`} aria-invalid={at.lastError ? true : undefined} aria-describedby={at.lastError ? `jrn-att-${at.key ?? at.id ?? __i47}-last-error` : undefined} aria-required={true} value={at.last} onChange={at.setLast} onBlur={at.blurLast} forceState={at.lastState} full={true} />
                                    </Field>
                                  </>) : null}
                                  {V.pv.attEmail ? (<>
                                    <Field label={V.pv.emailLabel} required={V.pv.attendeeRequired} htmlFor={`jrn-att-${at.key ?? at.id ?? __i47}-email`} error={at.emailError} errorId={`jrn-att-${at.key ?? at.id ?? __i47}-email-error`}>
                                      <Input id={`jrn-att-${at.key ?? at.id ?? __i47}-email`} aria-invalid={at.emailError ? true : undefined} aria-describedby={at.emailError ? `jrn-att-${at.key ?? at.id ?? __i47}-email-error` : undefined} aria-required={V.pv.attendeeRequired} type="email" value={at.email} onChange={at.setEmail} onBlur={at.blurEmail} forceState={at.emailState} placeholder="you@example.com" full={true} />
                                    </Field>
                                  </>) : null}
                                  {V.pv.attPhone ? (<>
                                    <Field label={V.pv.phoneLabel} required={V.pv.attendeePhoneRequired} htmlFor={`jrn-att-${at.key ?? at.id ?? __i47}-phone`} error={at.phoneError} hint={at.phoneHint}>
                                      <div style={{ display: "flex", gap: "8px" }}>
                                        <Dropdown options={V.opts.dialCodes} value={at.dialIso} onChange={at.setCountry} aria-label="Country code" style={PHONE_COUNTRY_STYLE} />
                                        <Input id={`jrn-att-${at.key ?? at.id ?? __i47}-phone`} aria-required={V.pv.attendeePhoneRequired} type="tel" value={at.phone} onChange={at.setPhone} onBlur={at.blurPhone} forceState={at.phoneState} style={PHONE_NUMBER_STYLE} full={true} />
                                      </div>
                                    </Field>
                                  </>) : null}
                                  {V.pv.attCompany ? (<>
                                    <Field label={i18nT("shell.allJourneys.companyOptional", "Company (optional)")} required={false} htmlFor={`jrn-att-${at.key ?? at.id ?? __i47}-company`} error={at.companyError}>
                                      <Input id={`jrn-att-${at.key ?? at.id ?? __i47}-company`} value={at.company} onChange={at.setCompany} onBlur={at.blurCompany} forceState={at.companyState} full={true} />
                                    </Field>
                                  </>) : null}
                                </div>
                              </div>
                            </React.Fragment>))}
                            {V.pv.canAddAttendee ? (<>
                              <AddRowButton onClick={V.actions.addAttendee}>{V.pv.addAttendeeLabel}
                              </AddRowButton>
                            </>) : null}
                            {V.pv.attendeeLimitNote ? (<>
                              <span style={{ fontSize: "12px", color: "var(--jrni-color-text-soft)" }}>{V.pv.attendeeLimitNote}</span>
                            </>) : null}
                          </div>
                        </>) : null}
                        {/* Everyone on the registration, host first, before submit. */}
                        {V.pv.showPartyReview ? (<>
                          <div style={{ display: "flex", flexDirection: "column", gap: "8px", paddingTop: "4px", borderTop: "1px solid var(--jrni-color-neutral-6)" }}>
                            <h3 style={{ margin: "12px 0 0", fontSize: "15px", fontWeight: "700" }}>{V.pv.placesLine}</h3>
                            <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: "6px" }}>
                              {(V.pv.partyReview || []).map((p) => (
                                <li key={p.key} style={{ display: "flex", gap: "12px", alignItems: "baseline", justifyContent: "space-between", flexWrap: "wrap", fontSize: "13px" }}>
                                  <span style={{ display: "flex", flexDirection: "column", gap: "2px", minWidth: 0 }}>
                                    <span style={{ fontWeight: "600" }}>{p.name || "\u2014"}</span>
                                    {p.detail ? (<span style={{ fontSize: "12px", color: "var(--jrni-color-text-soft)" }}>{p.detail}</span>) : null}
                                  </span>
                                  <span style={{ fontSize: "12px", color: "var(--jrni-color-text-soft)" }}>{p.role}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </>) : null}
                        {V.s.fileUpload ? (
                          (this.state.journey === "events" || this.state.journey === "landing") ? (this.isPublic() ? null : (<>
                            {/* Public event pages draw nothing here (CC 0057 finding 1): a registrant
                                uploads through a file QUESTION, which works. The Studio preview says so. */}
                            <div style={{ display: "flex", flexDirection: "column", gap: "6px", paddingTop: "4px", borderTop: "1px solid var(--jrni-color-neutral-6)" }}>
                              <h3 style={{ margin: "12px 0 0", fontSize: "15px", fontWeight: "700" }}>{V.pv.uploadHeader}</h3>
                              <span style={{ fontSize: "12px", color: "var(--jrni-color-text-soft)" }}>Not shown on the public event page. To collect files from registrants, add a file question.</span>
                            </div>
                          </>)) : (<>
                          <div style={{ display: "flex", flexDirection: "column", gap: "10px", paddingTop: "4px", borderTop: "1px solid var(--jrni-color-neutral-6)" }}>
                            <h3 style={{ margin: "12px 0 0", fontSize: "15px", fontWeight: "700" }}>{V.pv.uploadHeader}</h3>
                            <FileUpload files={V.pv.files} onFilesAdded={V.actions.addFiles} onFileRemoved={V.actions.removeFile} accept={V.pv.uploadAccept} disabled={V.pv.uploadFull} hint={V.pv.uploadHint} multiple={true} />

                          </div>
                          </>)
                        ) : null}
                        {/* The payment section is hidden for an event with no
                            usable provider — and the config read leaves it that
                            way while it is still open, so the area vanished and
                            then popped in. This reserves it while the read is
                            pending, claiming no total. The form is not
                            skeletonized and entered values stay put. */}
                        {V.s.takePayment && (this.state.journey === "events" || this.state.journey === "landing")
                          && !this.hasUsablePaymentProvider() && this.state.evtPaymentState === "loading" ? (<>
                          <div style={{ display: "flex", flexDirection: "column", gap: "12px", paddingTop: "4px", borderTop: "1px solid var(--jrni-color-neutral-6)" }}>
                            <h3 style={{ margin: "12px 0 0", fontSize: "15px", fontWeight: "700" }}>{V.pv.paymentHeader}</h3>
                            <LoadingRegion label="Checking how this event takes payment" testId="event-payment-ghost">
                              <SkeletonBox width="100%" height={44} radius={V.pv.radius} />
                            </LoadingRegion>
                          </div>
                        </>) : null}
                        {V.s.takePayment && !((this.state.journey === "events" || this.state.journey === "landing") && !this.hasUsablePaymentProvider()) ? (<>
                          <div style={{ display: "flex", flexDirection: "column", gap: "12px", paddingTop: "4px", borderTop: "1px solid var(--jrni-color-neutral-6)" }}>
                            <h3 style={{ margin: "12px 0 0", fontSize: "15px", fontWeight: "700" }}>{V.pv.paymentHeader}</h3>
                            {V.pv.hasPaymentInfo ? (<>
                              <p style={{ margin: "0", fontSize: "13px", lineHeight: "1.5", color: "var(--jrni-color-text-body)" }}>{V.pv.paymentInfo}</p>
                            </>) : null}
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: "12px", padding: "10px 12px", borderRadius: "var(--jrni-radius-default)", background: "var(--jrni-color-surface-card-soft)", border: "1px solid var(--jrni-color-surface-border)" }}>
                              <span style={{ fontSize: "13px", color: "var(--jrni-color-text-soft)" }}>Total due today</span>
                              <span style={{ fontSize: "15px", fontWeight: "700" }}>{V.pv.totalDue}</span>
                            </div>
                            <Field label="Card number" required={true}>
                              <Input placeholder="•••• •••• •••• 4242" full={true} />
                            </Field>
                            <div style={{ display: "grid", gap: "12px", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))" }}>
                              <Field label="Expiry" required={true}>
                                <Input placeholder="MM / YY" full={true} />

                              </Field>
                              <Field label="Security code" required={true}>
                                <Input placeholder="CVC" full={true} />
                              </Field>
                            </div>
                          </div>
                        </>) : null}
                        <div style={{ display: "flex", flexDirection: "column", gap: "10px", paddingTop: "4px", borderTop: "1px solid var(--jrni-color-neutral-6)" }}>
                          <h3 style={{ margin: "12px 0 0", fontSize: "15px", fontWeight: "700" }}>{V.pv.consentHeader}</h3>
                          <Checkbox ref={V.pv.consentRef} testid="aj-consent" checked={V.pv.consent} aria-label={V.pv.consentText} error={V.pv.consentError ? V.pv.consentRequiredText : undefined} onChange={V.actions.setConsent} label={V.pv.consentText} />
                        </div>
                        {V.pv.memberPayEl}
                        {V.pv.bookingError ? (
                          <div role="alert" style={{ marginTop: "10px", padding: "10px 12px", borderRadius: V.pv.radius, background: "var(--jrni-color-danger-surface, #FDECEC)", color: "var(--jrni-color-danger-text, #8A1C1C)", fontSize: "13px", lineHeight: "1.4" }}>{V.pv.bookingError}</div>
                        ) : null}
                        <div style={{ display: "flex", alignItems: "center", gap: "12px", paddingTop: "6px" }}>
                          {V.pv.showCustomerBack ? (<>
                            <Button variant="secondary" style={{ height: "44px" }} onClick={V.actions.prevStep}>Back
                            </Button>
                          </>) : null}
                          <div style={{ flex: "1 1 auto" }}></div>
                          <button type="button" data-testid="aj-register-submit" onClick={V.actions.nextStep} disabled={V.pv.submitting || V.pv.submitLocked} style={{ border: "0", cursor: V.pv.submitting ? "progress" : V.pv.submitLocked ? "not-allowed" : "pointer", opacity: V.pv.submitting || V.pv.submitLocked ? "0.7" : "1", fontFamily: "inherit", fontSize: "15px", fontWeight: "600", color: "#FFFFFF", padding: "0 24px", height: V.pv.ctaHeight, background: V.pv.brand, borderRadius: V.pv.radius }}>{V.pv.submitting ? "Confirming…" : V.pv.ctaLabel}</button>
                        </div>
                      </>) : null}
                      {V.pv.isRegistration ? (<>
                        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                          {V.pv.notCancelled && !V.pv.regPending ? (<>
                            <span style={{ flex: "0 0 auto", width: "24px", height: "24px", borderRadius: "999px", display: "flex", alignItems: "center", justifyContent: "center", background: "var(--jrni-color-semantic-green-2)", color: "var(--jrni-color-semantic-green-1)", fontSize: "13px", fontWeight: "700" }}>✓</span>
                          </>) : null}
                          <h2 style={{ margin: "0", fontSize: "19px", fontWeight: "700", letterSpacing: "-0.01em" }}>{V.pv.confirmMsg}</h2>
                          {V.pv.cancelled ? (<>
                            <Pill tone="danger">Cancelled
                            </Pill>
                          </>) : null}
                        </div>
                        <Banner tone={V.pv.flagTone} size="compact" title={V.pv.flagHeader}>{V.pv.flagText}{V.pv.flagTextExtra ? (<div style={{ marginTop: "6px" }}>{V.pv.flagTextExtra}</div>) : null}
                        </Banner>
                        {V.pv.cancelled ? (<>
                          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", paddingTop: "4px" }}>
                            <Button hierarchy="primary" onClick={V.actions.backToEvents}>{V.pv.backToEventsLabel}
                            </Button>
                          </div>
                        </>) : null}
                        <div style={{ display: "flex", gap: "20px", flexWrap: "wrap", alignItems: "flex-start" }}>
                          <div style={{ flex: "1 1 220px", minWidth: "0", display: "flex", flexDirection: "column", gap: "14px", fontSize: "13px", lineHeight: "1.5" }}>
                            <div>
                              <SectionLabel tone="soft">Event
                              </SectionLabel>
                              <div style={{ fontWeight: "600", paddingTop: "4px" }}>{V.pv.evtName}</div>
                              <div style={{ color: "var(--jrni-color-text-soft)" }}>{V.pv.evtWhen}</div>
                            </div>
                            <div>
                              <SectionLabel tone="soft">Where
                              </SectionLabel>
                              <div style={{ fontWeight: "600", paddingTop: "4px" }}>{V.pv.evtVenue}</div>
                              {/* No room line (CC, 2026-10-04): it printed one fixed room name under every event's
                                  venue; an event carries no room field to print instead. */}
                            </div>
                            <div>
                              <SectionLabel tone="soft">Places
                              </SectionLabel>
                              <div style={{ fontWeight: "600", paddingTop: "4px" }}>{V.pv.placesLine}</div>
                              {V.pv.bookingReference ? (<div style={{ color: "var(--jrni-color-text-soft)" }}>Reference {V.pv.bookingReference}</div>) : null}
                            </div>
                            {/* The host of an EVENT is the event's own staffName from the public
                                projection, never the appointment journey's selected adviser. With
                                no host on the record there is nothing authoritative to name, so
                                the whole section is hidden rather than filled with a stand-in. */}
                            {V.s.showStaff && V.pv.eventHostName ? (<>
                              <div>
                                <SectionLabel tone="soft">Host
                                </SectionLabel>
                                <div style={{ display: "flex", alignItems: "center", gap: "8px", paddingTop: "6px" }}>
                                  {String(V.pv.eventHostName || "").trim() ? <Avatar initials={nameInitials(V.pv.eventHostName)} size={24} /> : null}
                                  <span style={{ fontWeight: "600" }}>{V.pv.eventHostName}</span>
                                </div>
                              </div>
                            </>) : null}
                          </div>
                          {V.s.showQr && V.pv.qrValue ? (<>
                            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "10px", padding: "16px", border: "1px solid var(--jrni-color-surface-border)", borderRadius: "var(--jrni-radius-md)", background: "var(--jrni-color-surface-card)" }}>
                              <QrCode value={V.pv.qrValue} size={168} label="Event ticket" />
                              <span style={{ fontSize: "13px", fontWeight: "600", color: "var(--jrni-color-text-strong)", textAlign: "center" }}>Check in with this code</span>
                            </div>
                          </>) : V.s.showQr && this.isPublic() && V.pv.regHoldsSeat ? (<>
                            <div style={{ flex: "0 0 auto", display: "flex", flexDirection: "column", alignItems: "center", gap: "8px", padding: "14px", border: "1px solid var(--jrni-color-surface-border)", borderRadius: "var(--jrni-radius-md)", background: "var(--jrni-color-surface-card)", fontSize: "12px", color: "var(--jrni-color-text-soft)", maxWidth: 160, textAlign: "center" }}>
                              A ticket credential wasn't issued for this registration.
                            </div>
                          </>) : null}
                        </div>
                        {V.pv.eventWalletUrl ? (<>
                          <div style={{ paddingTop: "4px" }}>
                            <WalletPassBadge href={V.pv.eventWalletUrl} note="Keep the pass on your phone for check-in."
                              fallback={<span style={{ display: "inline-flex", alignItems: "center", height: "44px", padding: "0 16px", border: "1px solid var(--jrni-color-neutral-4)", borderRadius: "var(--jrni-radius-sm)", fontWeight: "600", color: "var(--jrni-color-text-strong)" }}>Add to Apple Wallet</span>} />
                          </div>
                        </>) : null}
                        {/* One row per companion, each with the reference and Manage
                            destination issued to THAT person. */}
                        {V.pv.showAttendeeTickets ? (<>
                          <div style={{ display: "flex", flexDirection: "column", gap: "8px", paddingTop: "10px", borderTop: "1px solid var(--jrni-color-neutral-6)", marginTop: "4px" }}>
                            <h3 style={{ margin: "0", fontSize: "14px", fontWeight: "700" }}>{i18nT("shell.allJourneys.everyoneOnThisRegistration", "Everyone on this registration")}</h3>
                            {(V.pv.attendeeTickets || []).map((at) => (
                              <div key={at.key} style={{ display: "flex", gap: "12px", alignItems: "baseline", flexWrap: "wrap", justifyContent: "space-between", padding: "10px 12px", border: "1px solid var(--jrni-color-surface-border)", borderRadius: "var(--jrni-radius-default)", background: "var(--jrni-color-surface-card-soft)" }}>
                                <div style={{ display: "flex", flexDirection: "column", gap: "2px", minWidth: 0 }}>
                                  <span style={{ fontSize: "13px", fontWeight: "700" }}>{at.name}</span>
                                  {at.email ? (<span style={{ fontSize: "12px", color: "var(--jrni-color-text-soft)" }}>{at.email}</span>) : null}
                                  {at.reference ? (<span style={{ fontSize: "12px", color: "var(--jrni-color-text-soft)" }}>Reference {at.reference}</span>) : null}
                                </div>
                                <div style={{ display: "flex", gap: "10px", alignItems: "baseline", flexWrap: "wrap" }}>
                                  <span style={{ fontSize: "12px", color: "var(--jrni-color-text-soft)" }}>{at.status}</span>
                                  {at.manageUrl
                                    ? (<a href={at.manageUrl} target="_blank" rel="noopener nofollow" style={{ fontSize: "12px", fontWeight: "600" }}>{i18nT("shell.allJourneys.manage", "Manage")}</a>)
                                    : (<span style={{ fontSize: "12px", color: "var(--jrni-color-text-soft)" }}>{i18nT("shell.allJourneys.noManageLinkWasIssued", "No manage link was issued")}</span>)}
                                </div>
                              </div>
                            ))}
                          </div>
                        </>) : null}
                        {/* Same emphasis concept as the appointment confirmation: no
                            rule, spacing carries the separation, Cancel is danger ink
                            at the lowest weight rather than a solid red slab. Both
                            actions here are disabled for the reasons below, so neither
                            takes the brand fill — a primary nobody can press is worse
                            than no primary at all. */}
                        {/* SAME CONTROLS AS THE APPOINTMENT CONFIRMATION (KP, 2026-09-17).
                            Add to calendar is the brand-filled button with the same
                            Outlook / Outlook web / Google / .ics menu; Cancel is the filled
                            danger button and now runs the public
                            `cancelEventRegistration` operation (api-client events.ts) against
                            this registration's own reference — the op the earlier "not available
                            yet" note was waiting for. */}
                        <div style={{ display: V.pv.confirmActionsDisplay, gridTemplateColumns: V.pv.confirmActionsCols, gap: "10px", flexWrap: "wrap", alignItems: "center", paddingTop: "20px", visibility: V.pv.regActionsVis }}>
                          {V.s.addToCalendar && V.pv.regHoldsSeat ? (<>
                            <div style={{ position: "relative", display: "flex" }}>
                              <Button hierarchy="primary" iconLeft={V.icons.calendar} hasCaret={true} onClick={V.actions.toggleCal} style={{ flex: "1 1 auto" }}>Add to calendar
                              </Button>
                              {V.pv.calMenuOpen ? (<>
                                <div role="menu" aria-label="Add to calendar" style={{ position: "absolute", zIndex: "6", bottom: "calc(100% + 6px)", left: "0", minWidth: "232px", padding: "6px", display: "flex", flexDirection: "column", gap: "2px", background: "var(--jrni-color-base-white)", border: "1px solid var(--jrni-color-neutral-4)", borderRadius: "var(--jrni-radius-sm)", boxShadow: "var(--jrni-shadow-md)" }}>
                                  {(V.pv.calTargets || []).map((ct, __i48) => (<React.Fragment key={ct.key ?? ct.id ?? __i48}>
                                    <button type="button" role="menuitem" onClick={ct.onPick} style={{ display: "flex", flexDirection: "column", gap: "1px", width: "100%", textAlign: "left", padding: "8px 9px", border: "0", borderRadius: "var(--jrni-radius-sm)", background: "transparent", cursor: "pointer", fontFamily: "inherit" }}>
                                      <span style={{ fontSize: "14px", fontWeight: "600", color: "var(--jrni-color-text-strong)" }}>{ct.label}</span>
                                      <span style={{ fontSize: "12px", color: "var(--jrni-color-text-soft)" }}>{ct.meta}</span>
                                    </button>
                                  </React.Fragment>))}
                                </div>
                              </>) : null}
                            </div>
                          </>) : null}
                          {/* MANAGE, ON THE EVENT CONFIRMATION TOO (CC, 2026-09-22).
                              The worker returns `data.manage_url` on a fresh
                              registration, the api-client now forwards it, and the seam
                              admits kind "events" — so a registrant gets the same
                              outlined secondary the appointment confirmation has, between
                              Add to calendar and Cancel.

                              Rendered ONLY in the `enabled` state: absent on a duplicate
                              submit (200 by design) and absent when the tenant publishes
                              no manage surface, and in both cases a button that opens
                              nothing is worse than no button. No `preview` arm either —
                              the authoring preview has no registration, so there is
                              nothing to manage. */}
                          {V.pv.manageAction.state === "enabled" ? (<>
                            <Button variant="secondary" iconLeft={V.icons.manage} data-testid="event-manage-action" onClick={() => { openManageInNewTab(V.pv.manageAction.href); }}>Manage this registration
                            </Button>
                          </>) : V.pv.manageAction.state === "preview" ? (<>
                            {/* Authoring preview (Pat / CC, 2026-09-23): the event and
                                landing confirmations now have a `showManage` switch, so the
                                author sees what it controls — the same disabled action the
                                appointment confirmation draws, with the reason announced.
                                No address: there is no registration in a preview. */}
                            <Button variant="secondary" iconLeft={V.icons.manage} data-testid="event-manage-action" disabled aria-disabled="true" aria-describedby="event-manage-preview-reason" title={V.pv.manageAction.reason}>Manage this registration
                            </Button>
                            <span id="event-manage-preview-reason" style={{ position: "absolute", width: "1px", height: "1px", padding: "0", margin: "-1px", overflow: "hidden", clip: "rect(0, 0, 0, 0)", whiteSpace: "nowrap", border: "0" }}>{V.pv.manageAction.reason}</span>
                          </>) : null}
                          {V.s.allowCancelReg && !V.pv.regNeutral ? (<>{/* LOCAL PATCH (platform side, 2026-10-04 JAI-2279): no cancel against a registration the neutral duplicate answer does not identify. CD to absorb. */}
                            <Button variant="danger" iconLeft={V.icons.cancel} disabled={V.pv.submitting} onClick={V.actions.askCancel}>{V.pv.cancelRegLabel}
                            </Button>
                          </>) : null}
                        </div>
                        {V.pv.cancelRegError ? (<>
                          <Banner tone="danger" size="compact" title="The registration was not cancelled">{V.pv.cancelRegError}</Banner>
                        </>) : null}
                        {/* THE DIALOG THE CANCEL BUTTON OPENS (docket 2026-09-18 item 8).
                            There was exactly one `cancelAsking` dialog in this file and it
                            was rendered inside the APPOINTMENT confirmation branch, so on an
                            event confirmation the button set the flag and nothing opened: no
                            dialog, no request, no error — and `cancelRegTitle`,
                            `cancelRegKeepLabel` and `cancelRegConsequence` were read by
                            nothing. This is that dialog, with the registration's own copy,
                            confirming into the same `doCancel`. */}
                        <ConfirmDialog open={V.pv.cancelAsking} onCancel={V.actions.dismissCancel} onConfirm={V.actions.doCancel} tone="danger" title={V.pv.cancelRegTitle} consequence={V.pv.cancelRegConsequence} icon={false} confirmLabel={V.pv.cancelRegLabel} cancelLabel={V.pv.cancelRegKeepLabel} />
                      </>) : null}
                      {V.pv.isTicket ? (<>
                        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                          {V.pv.inQueue ? (<>
                            <span style={{ flex: "0 0 auto", width: "24px", height: "24px", borderRadius: "999px", display: "flex", alignItems: "center", justifyContent: "center", background: "var(--jrni-color-semantic-green-2)", color: "var(--jrni-color-semantic-green-1)", fontSize: "13px", fontWeight: "700" }}>✓</span>
                          </>) : null}
                          <h2 style={{ margin: "0", fontSize: "19px", fontWeight: "700", letterSpacing: "-0.01em" }}>{V.pv.queueHeader}</h2>
                          {V.pv.leftQueue ? (<>
                            <Pill tone="danger">Left the line
                            </Pill>
                          </>) : null}
                        </div>
                        {V.pv.leftQueue ? (<>
                          <Banner tone="danger" size="compact" title={V.pv.leftHeader}>{V.pv.leftText}
                          </Banner>
                          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", paddingTop: "4px" }}>
                            <Button hierarchy="primary" onClick={V.actions.rejoinQueue}>{V.pv.rejoinLabel}
                            </Button>
                            <Button variant="secondary" onClick={V.actions.bookInstead}>{V.pv.bookInsteadLabel}
                            </Button>
                          </div>
                        </>) : null}
                        {V.pv.inQueue ? (<>
                          <div style={{ display: "flex", gap: "14px", flexWrap: "wrap" }}>
                            {V.s.showTicketNumber ? (<>
                              <div style={{ flex: "1 1 150px", padding: "18px", textAlign: "center", borderRadius: "var(--jrni-radius-md)", color: "#FFFFFF", background: V.pv.brand }}>
                                <div style={{ fontSize: "12px", letterSpacing: "0.06em", textTransform: "uppercase", opacity: "0.85" }}>Ticket</div>
                                <div style={{ fontSize: "34px", fontWeight: "700", lineHeight: "1.15", letterSpacing: "-0.02em" }}>{V.pv.ticketNumber}</div>
                              </div>
                            </>) : null}
                            {V.s.showPosition ? (<>
                              <div style={{ flex: "1 1 150px", padding: "18px", textAlign: "center", borderRadius: "var(--jrni-radius-md)", border: "1px solid var(--jrni-color-surface-border)", background: "var(--jrni-color-surface-card-soft)" }}>
                                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "6px" }}>
                                  <span style={{ width: "7px", height: "7px", borderRadius: "999px", background: "var(--jrni-color-semantic-green-1)", animation: "apd-pulse 1.8s ease-in-out infinite" }}></span>
                                  <span style={{ fontSize: "12px", letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--jrni-color-text-soft)" }}>{V.pv.posLabel}</span>
                                </div>
                                <div style={{ fontSize: "34px", fontWeight: "700", lineHeight: "1.15", letterSpacing: "-0.02em", paddingTop: "4px" }}>{V.pv.position}</div>
                              </div>
                            </>) : null}
                            {V.pv.showWait ? (<>
                              <div style={{ flex: "1 1 150px", padding: "18px", textAlign: "center", borderRadius: "var(--jrni-radius-md)", border: "1px solid var(--jrni-color-surface-border)", background: "var(--jrni-color-surface-card-soft)" }}>
                                <div style={{ fontSize: "12px", letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--jrni-color-text-soft)" }}>{V.pv.waitLabel}</div>
                                <div style={{ fontSize: "22px", fontWeight: "700", lineHeight: "1.4", paddingTop: "6px" }}>{V.pv.waitText}</div>
                              </div>
                            </>) : null}
                          </div>
                          <p style={{ margin: "0", fontSize: "13px", color: "var(--jrni-color-text-soft)" }}>{V.pv.staffLine}</p>
                          {V.s.notifyWhenClose ? (<>
                            <Banner tone="info" size="compact" title="Stay nearby">{V.pv.notifyLine}
                            </Banner>
                          </>) : null}
                          <div style={{ display: "grid", gap: "20px", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", fontSize: "13px", lineHeight: "1.5" }}>
                            <div>
                              <SectionLabel tone="soft">Reason for visit
                              </SectionLabel>
                              <div style={{ fontWeight: "600", paddingTop: "4px" }}>{V.pv.confirmService}</div>
                            </div>
                          </div>
                          {V.s.showQr ? (<>
                            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "10px", padding: "16px", border: "1px solid var(--jrni-color-surface-border)", borderRadius: "var(--jrni-radius-md)", background: "var(--jrni-color-surface-card)" }}>
                              <QrCode value={V.pv.queueQrValue} size={168} label="Queue ticket code" />
                              <span style={{ fontSize: "13px", fontWeight: "600", color: "var(--jrni-color-text-strong)", textAlign: "center" }}>Check in with this code</span>
                            </div>
                          </>) : null}
                          {V.pv.hasArrivalNote ? (<>
                            <p style={{ margin: "0", fontSize: "13px", color: "var(--jrni-color-text-soft)" }}>{V.pv.arrivalNote}</p>
                          </>) : null}
                          {V.s.allowLeaveQueue ? (<>
                            <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", alignItems: "center", paddingTop: "20px" }}>
                              <Button variant="danger" iconLeft={V.icons.cancel} onClick={V.actions.askLeave}>{V.pv.leaveLabel}
                              </Button>
                            </div>
                          </>) : null}
                        </>) : null}
                        <ConfirmDialog open={V.pv.leaveAsking} onCancel={V.actions.dismissLeave} onConfirm={V.actions.doLeave} tone="danger" title={V.pv.leaveTitle} consequence={V.pv.leaveConsequence} icon={false} confirmLabel={V.pv.leaveConfirmLabel} cancelLabel={V.pv.leaveKeepLabel} />
                      </>) : null}
                      {V.pv.isConfirmation ? (<>
                        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                          {V.pv.notCancelled ? (<>
                            <span style={{ flex: "0 0 auto", width: "24px", height: "24px", borderRadius: "999px", display: "flex", alignItems: "center", justifyContent: "center", background: "var(--jrni-color-semantic-green-2)", color: "var(--jrni-color-semantic-green-1)", fontSize: "13px", fontWeight: "700" }}>✓</span>
                          </>) : null}
                          <h2 style={{ margin: "0", fontSize: "19px", fontWeight: "700", letterSpacing: "-0.01em" }}>{V.pv.confirmMsg}</h2>
                          {V.pv.cancelled ? (<>
                            <Pill tone="danger">Cancelled
                            </Pill>
                          </>) : null}
                        </div>
                        <Banner tone={V.pv.flagTone} size="compact" title={V.pv.flagHeader}>{V.pv.flagText}{V.pv.flagTextExtra ? (<div style={{ marginTop: "6px" }}>{V.pv.flagTextExtra}</div>) : null}
                        </Banner>
                        <div style={{ display: "grid", gap: "20px", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", fontSize: "13px", lineHeight: "1.5", textDecoration: V.pv.strike, color: V.pv.detailFg }}>
                          <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
                            <div>
                              <SectionLabel tone="soft">When
                              </SectionLabel>
                              {/* Values are NOT bold (operator, 2026-09-15): the label
                                  above already carries the emphasis, and bolding both
                                  left the block with no hierarchy at all. */}
                              <div style={{ paddingTop: "4px" }}>{V.pv.confirmWhen || (<span style={{ color: "var(--jrni-color-text-soft)" }}>{V.pv.confirmWhenMissing}</span>)}</div>
                              <div style={{ color: "var(--jrni-color-text-soft)" }}>{V.pv.confirmDuration}</div>
                            </div>
                            <div>
                              <SectionLabel tone="soft">Service
                              </SectionLabel>
                              <div style={{ paddingTop: "4px" }}>{V.pv.confirmService || (<span style={{ color: "var(--jrni-color-text-soft)" }}>{V.pv.confirmServiceMissing}</span>)}</div>
                              {V.pv.bookingReference ? (<div style={{ color: "var(--jrni-color-text-soft)" }}>Reference {V.pv.bookingReference}</div>) : null}
                            </div>
                          </div>
                          <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
                            <div>
                              <SectionLabel tone="soft">Where
                              </SectionLabel>
                              <div style={{ paddingTop: "4px" }}>{V.pv.confirmLocation || (<span style={{ color: "var(--jrni-color-text-soft)" }}>{V.pv.confirmLocationMissing}</span>)}</div>
                              <div style={{ color: "var(--jrni-color-text-soft)" }}>{V.pv.confirmAddress}</div>
                            </div>
                            {(V.s.showStaff && V.pv.staffName) || V.pv.personalStaffName ? (<>
                              <div>
                                <SectionLabel tone="soft">Adviser
                                </SectionLabel>
                                <div style={{ display: "flex", alignItems: "center", gap: "8px", paddingTop: "6px" }}>
                                  <Avatar initials={nameInitials(V.pv.personalStaffName || V.pv.staffName)} size={24} />
                                  <span>{V.pv.personalStaffName || V.pv.staffName}</span>
                                </div>
                              </div>
                            </>) : null}
                            {V.s.showResource && (V.pv.bookingRoom || !this.isPublic()) ? (<>
                              <div>
                                <SectionLabel tone="soft">Room
                                </SectionLabel>
                                {/* A store with no bookable room genuinely has none to
                                    name; an em dash says so instead of a blank line. */}
                                <div style={{ paddingTop: "4px" }}>{V.pv.bookingRoom || (<span style={{ color: "var(--jrni-color-text-soft)" }}>{V.pv.confirmRoomMissing}</span>)}</div>
                              </div>
                            </>) : null}
                          </div>
                        </div>
                        {V.s.showQr && V.pv.apptQrValue ? (<>
                          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "10px", padding: "16px", border: "1px solid var(--jrni-color-surface-border)", borderRadius: "var(--jrni-radius-md)", background: "var(--jrni-color-surface-card)" }}>
                            <QrCode value={V.pv.apptQrValue} size={168} label="Appointment check-in code" />
                            <span style={{ fontSize: "13px", fontWeight: "600", color: "var(--jrni-color-text-strong)", textAlign: "center" }}>Check in with this code</span>
                          </div>
                        </>) : V.s.showQr && !this.isPublic() ? (<>
                          <div style={{ padding: "14px", border: "1px dashed var(--jrni-color-surface-border)", borderRadius: "var(--jrni-radius-md)", fontSize: "13px", lineHeight: "1.5", color: "var(--jrni-color-text-soft)" }}>
                            Preview: the reception check-in code appears here once a real booking returns one.
                          </div>

                        </>) : null}
                        {V.pv.cancelled ? (<>
                          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", alignItems: "center", paddingTop: "20px" }}>
                            <Button hierarchy="primary" onClick={V.actions.bookAgain}>{V.pv.againLabel}
                            </Button>
                          </div>
                        </>) : null}
                        {V.pv.notCancelled ? (<>
                          {/* COLOUR AND EMPHASIS CONCEPT (operator, 2026-09-15).
                              One brand-filled action — Add to calendar, the only
                              thing a confirmed customer actually wants next.
                              Reschedule and Manage are outlined secondaries: real
                              actions, no competition for the eye. Cancel is danger
                              INK on no fill, lowest weight on the row and pushed to
                              the far side, because a solid red button was the
                              loudest thing on a page whose news is good. Every one
                              carries a glyph that names its verb. */}
                          {/* ORDER AND GRID (KP, 2026-09-17; grid revised 2026-09-20): Add to
                              calendar · Manage, then Reschedule · Cancel. The phone row used to be
                              a FIXED 2-column grid, which is what clipped "Manage this appointment"
                              at the card's edge; it now fits as many 240px columns as there is room
                              for and drops to one per row on a narrow phone, so the pairing above
                              describes the wider end of the band, not every width. On desktop the
                              same order flows in one line. Manage and Reschedule are bordered;
                              Add to calendar and Cancel are filled, so they carry no border
                              at all — Cancel takes the danger fill, the same weight as Leave
                              the queue. */}
                          <div style={{ display: V.pv.confirmActionsDisplay, gridTemplateColumns: V.pv.confirmActionsCols, gap: "10px", flexWrap: "wrap", alignItems: "center", paddingTop: "20px" }}>
                            {V.s.addToCalendar ? (<>
                              <div style={{ position: "relative", display: "flex" }}>
                                <Button hierarchy="primary" iconLeft={V.icons.calendar} hasCaret={true} onClick={V.actions.toggleCal} style={{ flex: "1 1 auto" }}>Add to calendar
                                </Button>
                                {V.pv.calMenuOpen ? (<>
                                  <div role="menu" aria-label="Add to calendar" style={{ position: "absolute", zIndex: "6", bottom: "calc(100% + 6px)", left: "0", minWidth: "232px", padding: "6px", display: "flex", flexDirection: "column", gap: "2px", background: "var(--jrni-color-base-white)", border: "1px solid var(--jrni-color-neutral-4)", borderRadius: "var(--jrni-radius-sm)", boxShadow: "var(--jrni-shadow-md)" }}>
                                    {(V.pv.calTargets || []).map((ct, __i49) => (<React.Fragment key={ct.key ?? ct.id ?? __i49}>
                                      <button type="button" role="menuitem" onClick={ct.onPick} style={{ display: "flex", flexDirection: "column", gap: "1px", width: "100%", textAlign: "left", padding: "8px 9px", border: "0", borderRadius: "var(--jrni-radius-sm)", background: "transparent", cursor: "pointer", fontFamily: "inherit" }}>
                                        <span style={{ fontSize: "14px", fontWeight: "600", color: "var(--jrni-color-text-strong)" }}>{ct.label}</span>
                                        <span style={{ fontSize: "12px", color: "var(--jrni-color-text-soft)" }}>{ct.meta}</span>
                                      </button>
                                    </React.Fragment>))}
                                  </div>
                                </>) : null}
                              </div>
                            </>) : null}
                            {V.pv.manageAction.state === "enabled" ? (<>
                              <Button variant="secondary" iconLeft={V.icons.manage} data-testid="appointment-manage-action" onClick={() => { openManageInNewTab(V.pv.manageAction.href); }}>Manage this appointment
                              </Button>
                            </>) : V.pv.manageAction.state === "preview" ? (<>
                              {/* Authoring preview: shown so the operator sees what the setting
                                  controls, but disabled with no address — there is no booking,
                                  so there is no server-issued manage link to open. */}
                              <Button variant="secondary" iconLeft={V.icons.manage} data-testid="appointment-manage-action" disabled aria-disabled="true" aria-describedby="appointment-manage-preview-reason" title={V.pv.manageAction.reason}>Manage this appointment
                              </Button>
                              <span id="appointment-manage-preview-reason" style={{ position: "absolute", width: "1px", height: "1px", padding: "0", margin: "-1px", overflow: "hidden", clip: "rect(0, 0, 0, 0)", whiteSpace: "nowrap", border: "0" }}>{V.pv.manageAction.reason}</span>
                            </>) : null}
                            {V.pv.apptChange.showReschedule ? (<>
                              <Button variant="secondary" iconLeft={V.icons.reschedule} onClick={V.pv.apptChange.onReschedule}>Reschedule
                              </Button>
                            </>) : null}
                            {V.pv.apptChange.showCancel ? (<>
                              <Button variant="danger" iconLeft={V.icons.cancel} onClick={V.pv.apptChange.onCancel}>Cancel appointment
                              </Button>
                            </>) : null}
                          </div>
                          {V.pv.apptChange.closedNote ? (<>
                            <p style={{ margin: "10px 0 0", fontSize: "13px", color: "var(--jrni-color-text-soft)" }}>{V.pv.apptChange.closedNote}</p>
                          </>) : null}
                          {V.pv.apptWalletUrl ? (<>
                            <div style={{ paddingTop: "12px" }}>
                              <WalletPassBadge href={V.pv.apptWalletUrl} note="Keep the pass on your phone for check-in."
                                fallback={<span style={{ display: "inline-flex", alignItems: "center", height: "44px", padding: "0 16px", border: "1px solid var(--jrni-color-neutral-4)", borderRadius: "var(--jrni-radius-sm)", fontWeight: "600", color: "var(--jrni-color-text-strong)" }}>Add to Apple Wallet</span>} />
                            </div>
                          </>) : null}
                        </>) : null}
                        <ConfirmDialog open={V.pv.cancelAsking} onCancel={V.actions.dismissCancel} onConfirm={V.actions.doCancel} tone="danger" title={V.pv.cancelTitle} consequence={V.pv.cancelConsequence} icon={false} confirmLabel={V.pv.cancelConfirmLabel} cancelLabel={V.pv.cancelKeepLabel} />
                      </>) : null}
                      {V.pv.showContinue ? (<>
                        <div style={{ display: "flex", alignItems: "center", gap: "12px", paddingTop: "16px", marginTop: "2px", borderTop: "1px solid var(--jrni-color-neutral-6)" }}>
                          {V.pv.showCustomerBack ? (<>
                            <Button variant="secondary" style={{ height: "44px" }} onClick={V.actions.prevStep}>Back
                            </Button>
                          </>) : null}
                          <div style={{ flex: "1 1 auto" }}></div>
                          <button type="button" onClick={V.actions.nextStep} disabled={V.pv.continueBlocked} style={{ border: "0", fontFamily: "inherit", fontSize: "15px", fontWeight: "600", color: "#FFFFFF", padding: "0 26px", height: V.pv.ctaHeight, background: V.pv.continueBg, cursor: V.pv.continueCursor, borderRadius: V.pv.radius }}>{V.pv.continueLabel}</button>
                        </div>
                      </>) : null}
                    </div>
                    
                  </div>
                  {V.pv.brandedFooter ? <BrandedFooterMount studio={!this.isPublic()} tenantName={this.tenantDisplayName() || undefined} /> : V.is.appt ? <AppointmentFooter footer={this.state.apptJourney?.footer} showCustom={V.s.showFooter} hideJrni={V.s.hideJrni ?? this.state.apptJourney?.advanced?.hideJrni} /> : V.s.showFooter ? (<>
                    <div style={{ display: "flex", gap: "16px", alignItems: "center", flexWrap: "wrap", padding: "14px 24px", borderTop: "1px solid var(--jrni-color-surface-border)", background: "var(--jrni-color-neutral-6)", fontSize: "12px", color: "var(--jrni-color-text-soft)" }}>
                      <BasicFooterCopyright studio={!this.isPublic()} tenantName={this.tenantDisplayName() || undefined} />
                      <a href="#privacy">Privacy</a>
                      <a href="#accessibility">Accessibility</a>
                      <div style={{ flex: "1 1 auto" }}></div>
                      <BasicFooterContact studio={!this.isPublic()} />
                    </div>
                  </>) : null}
                </div>
                )}
                </div>
                </div>
              </div>
              </>)}
            </section>
            </>) : null}
          </div>
        </div>
      </>
    );
  }
}

/** The designer's opening shape while the tenant's journeys are being confirmed: the header strip
 *  (journey tabs, status, actions) over the setup rail and the preview. Shapes only: no tab, label
 *  or record is drawn until the module list is known. Reflows to one column on a phone. */
function JourneyModulesSkeleton({ label }: { label: string }) {
  return (
    <LoadingRegion label={label} testId="alljourneys-modules-loading"
      style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden", background: "var(--jrni-color-surface-canvas)" }}>
      <div style={{ flex: "0 0 auto", display: "flex", alignItems: "center", flexWrap: "wrap", gap: "14px", rowGap: "8px", padding: "10px 16px", background: "var(--jrni-color-surface-card)", borderBottom: "1px solid var(--jrni-color-surface-border)" }}>
        <SkeletonBox width="min(420px, 100%)" height={32} radius="var(--jrni-radius-sm)" />
        <SkeletonBox width={96} height={22} radius="999px" />
        <div style={{ flex: "1 1 auto" }}></div>
        <SkeletonBox width={88} height={32} radius="var(--jrni-radius-sm)" />
        <SkeletonBox width={128} height={32} radius="var(--jrni-radius-sm)" />
      </div>
      <div style={{ flex: 1, minHeight: 0, display: "flex", flexWrap: "wrap", gap: "16px", padding: "16px", overflow: "hidden" }}>
        <div style={{ flex: "1 1 280px", maxWidth: "380px", minWidth: 0, display: "flex", flexDirection: "column", gap: "12px" }}>
          <SkeletonBox height={18} width="60%" variant="text" />
          <SkeletonBox height={40} />
          <SkeletonBox height={40} />
          <SkeletonBox height={40} />
          <SkeletonBox height={40} />
          <SkeletonBox height={120} />
        </div>
        <div style={{ flex: "999 1 320px", minWidth: 0, display: "flex", flexDirection: "column", gap: "12px" }}>
          <SkeletonBox height={44} radius="var(--jrni-radius-md)" />
          <SkeletonBox height={320} radius="var(--jrni-radius-md)" />
        </div>
      </div>
    </LoadingRegion>
  );
}

/** Route entry: the designer fills the app frame; Screen lights the "All Journeys" rail item.
 *
 *  The journey tabs follow the tenant's modules, so the designer mounts ONLY with a known list
 *  that composes at least one journey module (2026-10-20); a known list without one (empty, or
 *  only identity, workforce and the like) is the empty state. Before, a pending, refused or failed read handed it
 *  `null`, which means "show all six": a cold load painted Appointments, Walk-Ins and Kiosk for a
 *  tenant composing none of them, and a failed read left them there. Every other state is its own
 *  screen, and none of them mounts the designer or starts its reads. Nothing is carried over: the
 *  hook reports pending the moment the tenant, its generation, the signed-in or acted-as person,
 *  View as or the capability changes, so the designer unmounts with the session it belonged to. */
export default function AllJourneys() {
  const tenant = useTenant() as any;
  const moduleState = useTenantModulesState();
  const slug: string = tenant?.slug ?? "default";
  /* Both from served sources rather than slug-keyed tables (2026-09-08): the
     host from the tenant's public config, the nouns from its vocabulary. The
     hook re-renders when either arrives, so the first paint's generic words are
     replaced rather than frozen. */
  const vocabulary = useTenantVocab();
  const host = publicHost("book", slug);
  const status = moduleState.status;
  const modules = status === "ready" ? moduleState.modules : null;
  let body: React.ReactNode;
  if (modules && Designer.composesAJourney(modules)) {
    // key on tenant so a persona switch remounts with that tenant's records, never a stale stash
    body = <Designer key={slug} tenant={slug} tenantName={tenant?.name} brandColor={tenant?.brandColor} publicHost={host} vocab={wordsFromVocab(vocabulary)} modules={modules} />;
  } else if (status === "ready") {
    body = (
      <div style={{ flex: 1, minHeight: 0, overflow: "auto", padding: "24px 16px", display: "flex", justifyContent: "center" }}>
        <UI.PageState empty testid="alljourneys-modules-empty" style={{ maxWidth: 560, width: "100%" }}
          emptyTitle={i18nT("journeys.allJourneys.modulesEmptyTitle", "No journeys in this workspace")}
          emptyBody={i18nT("journeys.allJourneys.modulesEmptyBody", "This workspace doesn't include appointments, walk-ins or events, so there is no journey to design. Ask an admin if you expected one.")} />
      </div>
    );
  } else if (status === "error" || status === "malformed") {
    body = (
      <div style={{ flex: 1, minHeight: 0, overflow: "auto", padding: "24px 16px" }}>
        <UI.PageState testid="alljourneys-modules" onRetry={moduleState.retry}
          error={status === "malformed"
            ? i18nT("journeys.allJourneys.modulesMalformedDetail", "The workspace profile answered without a usable list of journeys.")
            : i18nT("journeys.allJourneys.modulesErrorDetail", "The workspace profile couldn't be read.")}
          errorTitle={i18nT("journeys.allJourneys.modulesErrorTitle", "Couldn't check which journeys this workspace offers")}
          errorBody={i18nT("journeys.allJourneys.modulesErrorBody", "Nothing has been changed. The journey designer opens once this is confirmed, so you never edit a journey this workspace doesn't offer.")}
          retryLabel={i18nT("common.actions.retry", "Retry")} />
      </div>
    );
  } else if (status === "denied") {
    /* Signed out, or the person being acted as cannot read the workspace profile. Not a grant of
       every journey: the designer stays closed, and a Retry cannot change a refusal by role. */
    body = (
      <div style={{ flex: 1, minHeight: 0, overflow: "auto", padding: "24px 16px", display: "flex", justifyContent: "center" }}>
        <UI.PageState empty testid="alljourneys-modules-denied" style={{ maxWidth: 560, width: "100%" }}
          icon={<UI.Icon.lock size={20} />}
          emptyTitle={i18nT("journeys.allJourneys.modulesDeniedTitle", "You can't open the journey designer")}
          emptyBody={i18nT("journeys.allJourneys.modulesDeniedBody", "Your role can't read which journeys this workspace offers, so the designer stays closed. Ask an admin if you need access.")} />
      </div>
    );
  } else {
    body = <JourneyModulesSkeleton label={i18nT("journeys.allJourneys.modulesLoading", "Loading your journeys")} />;
  }
  return (
    <Screen rail="all-journeys">
      <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        {body}
      </div>
    </Screen>
  );
}
